import React, { useState, useEffect, useContext, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import Header from '../components/Header';
import LoadingSpinner from '@/components/LoadingSpinner';
import ClipboardPasteModal from '@/components/ClipboardPasteModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Download, QrCode, AlertCircle, Users, Edit, Edit2, Trash2, X, Upload, Printer, Eye, Share2, Settings, Image as ImageIcon, StickyNote, ZoomIn, ZoomOut, RotateCcw, Loader2, ChevronDown, ChevronUp, Info, Pencil, ClipboardPaste, ShieldAlert, LayoutGrid, Gem, PanelTop, Table2, Building2, FileText, ShieldCheck, Grid3x3, Square, DoorOpen, Wrench, Droplets, Map as MapIcon, Scan } from 'lucide-react';
import SimpleWindowTypeForm from '@/components/SimpleWindowTypeForm';
import { NumericInput } from '@/components/NumericInput';
import CurtainwallPanelDetailDialog from '@/components/CurtainwallPanelDetailDialog';
import MirrorDetailDialog from '@/components/MirrorDetailDialog';
import DoorDetailDialog from '@/components/DoorDetailDialog';
import ImageCropModal from '@/components/ImageCropModal';
import PDFPageSelector from '@/components/PDFPageSelector';
import ConfirmationModal from '@/components/ConfirmationModal';
import ColorPickerPopover from '@/components/ColorPickerPopover';
import { formatInchesToFtIn, COMMON_GLASS_THICKNESSES } from '@/utils/imperialUnits';
import { validatePositiveDimensions } from '@/utils/dimensionValidation';
import { getApiErrorMessage } from '@/utils/apiError';
import { describeGlassTypeConflict, isGlassTypeConflict } from '@/utils/glassTypeConflict';
import { parseNumericInput, parseIntegerInput } from '@/utils/numericValidation';
import Logo from '@/components/Logo';

// Feature Components (Refactored)
import { DoorTypesSection } from '@/features/doors/DoorTypesSection';
import { DoorGlassTypesSection } from '@/features/doors/DoorGlassTypesSection';
import { DoorMetalPanelsSection } from '@/features/doors/DoorMetalPanelsSection';
import { DoorHardwareSetsSection } from '@/features/doors/DoorHardwareSetsSection';
import { DoorSchedulesSection } from '@/features/doors/DoorSchedulesSection';
import { DoorFloorPlansSection } from '@/features/doors/DoorFloorPlansSection';
import { WindowTypesSection } from '@/features/windows/WindowTypesSection';
import { WindowGlassTypesSection } from '@/features/windows/WindowGlassTypesSection';
import { WindowMetalPanelsSection } from '@/features/windows/WindowMetalPanelsSection';
import { WindowScheduleSection } from '@/features/windows/WindowScheduleSection';
import { WindowElevationsSection } from '@/features/windows/WindowElevationsSection';
import { CWTypesSection } from '@/features/curtainwalls/CWTypesSection';
import { CWGlassTypesSection } from '@/features/curtainwalls/CWGlassTypesSection';
import { CWMetalTypesSection } from '@/features/curtainwalls/CWMetalTypesSection';
import { CWPanelListSection } from '@/features/curtainwalls/CWPanelListSection';
import { CWElevationsSection } from '@/features/curtainwalls/CWElevationsSection';
import { SFTypesSection } from '@/features/storefronts/SFTypesSection';
import { SFGlassTypesSection } from '@/features/storefronts/SFGlassTypesSection';
import { SFMetalTypesSection } from '@/features/storefronts/SFMetalTypesSection';
import { SFPanelListSection } from '@/features/storefronts/SFPanelListSection';
import { newLiteId, getTypePictureUrl } from '@/components/TypeImageMarkingSection';
import { ScheduleDetailsStep, hasLiteErrors } from '@/components/ScheduleDetailsStep';
import { SFElevationsSection } from '@/features/storefronts/SFElevationsSection';
import MirrorTypesSection from '@/features/mirrors/MirrorTypesSection';
import { MirrorSchedulesSection } from '@/features/mirrors/MirrorSchedulesSection';
import { MirrorFloorPlansSection } from '@/features/mirrors/MirrorFloorPlansSection';
import { ShowerTypesSection } from '@/features/showers/ShowerTypesSection';
import { ShowerGlassTypesSection } from '@/features/showers/ShowerGlassTypesSection';
import { ShowerHardwareSetsSection } from '@/features/showers/ShowerHardwareSetsSection';
import { ShowerSchedulesSection } from '@/features/showers/ShowerSchedulesSection';
import ShowerDetailDialog from '@/components/ShowerDetailDialog';
import { ShowerFloorPlansSection } from '@/features/showers/ShowerFloorPlansSection';
import { ThermalInsulationsSection } from '@/features/thermal-insulations/ThermalInsulationsSection';
import { AirBarriersSection } from '@/features/air-barriers/AirBarriersSection';
import { WeatherBarriersSection } from '@/features/weather-barriers/WeatherBarriersSection';
import { RoofingSection } from '@/features/roofing/RoofingSection';
import { FlashingSection } from '@/features/flashing-sheet-metal/FlashingSection';
import { JointProtectionSection } from '@/features/joint-protection/JointProtectionSection';
import { ElectricalSystemsSection } from '@/features/electrical-systems/ElectricalSystemsSection';
import { CommunicationSystemsSection } from '@/features/communication-systems/CommunicationSystemsSection';
import { SubmittalsSection } from '@/features/shared/SubmittalsSection';
import { ShareSection } from '@/features/shared/ShareSection';
import { WarrantySection } from '@/features/shared/WarrantySection';
import { ProjectConfigModal } from '@/features/shared/ProjectConfigModal';
import { getProjectImageUrl, getProjectPlaceholderUrl } from '@/utils/projectImage';
import { getWarrantyError, hasWarrantyErrors } from '@/utils/validateWarranty';
import { getDefaultSubTab } from '@/utils/csiHelpers';
import navigationGuard from '@/utils/navigationGuard';
import { useAutoExpandFirstItem } from '@/hooks/useAutoExpandFirstItem';
import { ConfirmDialog } from '@/components/ConfirmDialog';

// Custom Hooks (Phase 6) - Data state managed by useProjectData
import { useProjectData } from '@/hooks/useProjectData';
import { useWindowState } from '@/hooks/useWindowState';
import { useDoorState } from '@/hooks/useDoorState';
import { useCurtainwallState } from '@/hooks/useCurtainwallState';
import { useStorefrontState } from '@/hooks/useStorefrontState';
import { useMirrorState } from '@/hooks/useMirrorState';
import { useShowerState } from '@/hooks/useShowerState';

// Debounced Note Textarea Component - Prevents performance issues from frequent re-renders
const DebouncedNoteTextarea = memo(({ value, onChange, tempId, placeholder, maxLength = 100 }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const timeoutRef = useRef(null);
  
  // Sync with external value changes (e.g., when modal opens)
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Debounce the parent state update
    timeoutRef.current = setTimeout(() => {
      onChange(tempId, newValue);
    }, 150);
  }, [onChange, tempId]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <input
        type="text"
        placeholder={placeholder || "Add note..."}
        value={localValue}
        onChange={handleChange}
        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
        maxLength={maxLength}
      />
      <span className="text-[10px] text-gray-400 flex-shrink-0">{localValue.length}/{maxLength}</span>
    </div>
  );
});

DebouncedNoteTextarea.displayName = 'DebouncedNoteTextarea';

// Import shared ElevationLoadingIndicator
import { ElevationLoadingIndicator } from '@/features/shared/ElevationViewer';
import { isZoomAtDefault } from '@/utils/zoom';
import { MarkerActionsPopup } from '../features/shared/MarkerActionsPopup';

const FLOOR_PLAN_TYPES = ['door', 'mirror', 'shower', 'doorElevation', 'mirrorElevation', 'showerElevation'];
const getEntityLabel = (type, plural = false) => {
  const isFloorPlan = FLOOR_PLAN_TYPES.includes(type);
  return plural
    ? (isFloorPlan ? 'Floor Plans' : 'elevations')
    : (isFloorPlan ? 'Floor Plan' : 'elevation');
};

const DOOR_DIMENSION_FIELDS = [
  { key: 'doorWidth_in', label: 'Door Width (in)', placeholder: 'e.g., 36', testId: 'door-width-input' },
  { key: 'doorHeight_in', label: 'Door Height (in)', placeholder: 'e.g., 84', testId: 'door-height-input' },
  { key: 'roWidth_in', label: 'Rough Opening Width (in)', placeholder: 'e.g., 37.5', testId: 'door-ro-width-input' },
  { key: 'roHeight_in', label: 'Rough Opening Height (in)', placeholder: 'e.g., 85.5', testId: 'door-ro-height-input' },
];

const emptyDoorSchedule = () => ({
  doorTypeId: '',
  hardwareSetId: '',
  label: '',
  location: '',
  doorWidth_in: '',
  doorHeight_in: '',
  roWidth_in: '',
  roHeight_in: '',
  lites: [],
  liteMarks: [],
  liteMarksTypeId: null,
});

// Saved door schedule -> edit form. Lites get stable ids so their marks survive,
// and marks only apply to the Door Type they were placed on.
const doorScheduleToForm = (schedule) => {
  const lites = (schedule.lites || []).map(lite => ({
    ...lite,
    id: lite.id || newLiteId(),
    width_in: lite.width_in ?? '',
    height_in: lite.height_in ?? '',
    glassTypeId: lite.glassTypeId || '',
    metalPanelTypeId: lite.metalPanelTypeId || '',
  }));
  const liteIds = new Set(lites.map(l => l.id));
  const marksMatchType = !schedule.liteMarksTypeId || schedule.liteMarksTypeId === schedule.doorTypeId;
  const liteMarks = marksMatchType
    ? (schedule.liteMarks || []).filter(mark => liteIds.has(mark.liteId))
    : [];
  return {
    doorTypeId: schedule.doorTypeId || '',
    hardwareSetId: schedule.hardwareSetId || '',
    label: schedule.label || '',
    location: schedule.location || '',
    doorWidth_in: schedule.doorWidth_in ?? '',
    doorHeight_in: schedule.doorHeight_in ?? '',
    roWidth_in: schedule.roWidth_in ?? '',
    roHeight_in: schedule.roHeight_in ?? '',
    lites,
    liteMarks,
    liteMarksTypeId: liteMarks.length > 0 ? schedule.doorTypeId : null,
  };
};


const ProjectDetail = () => {
  const { projectId, tab, subTab, elevationId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ============================================
  // PHASE 6: Custom Hooks Integration
  // ============================================
  
  // Data state from useProjectData hook
  const {
    project, setProject,
    loading, setLoading,
    accessDenied, setAccessDenied,
    csiItems, setCsiItems,
    windowTypes, setWindowTypes,
    windowGlassTypes, setWindowGlassTypes,
    windowMetalPanelTypes, setWindowMetalPanelTypes,
    windowList, setWindowList,
    curtainwallTypes, setCurtainwallTypes,
    glassTypes, setGlassTypes,
    metalPanelTypes, setMetalPanelTypes,
    panelList, setPanelList,
    storefrontTypes, setStorefrontTypes,
    storefrontGlassTypes, setStorefrontGlassTypes,
    storefrontMetalPanelTypes, setStorefrontMetalPanelTypes,
    storefrontPanelList, setStorefrontPanelList,
    doorTypes, setDoorTypes,
    doorGlassTypes, setDoorGlassTypes,
    doorMetalPanelTypes, setDoorMetalPanelTypes,
    doorHardwareSets, setDoorHardwareSets,
    doorSchedules, setDoorSchedules,
    mirrorTypes, setMirrorTypes,
    mirrorSchedules, setMirrorSchedules,
    mirrorElevations, setMirrorElevations,
    showerTypes, setShowerTypes,
    showerGlassTypes, setShowerGlassTypes,
    showerHardwareSets, setShowerHardwareSets,
    showerSchedules, setShowerSchedules,
    showerElevations, setShowerElevations,
    elevations, setElevations,
    windowElevations, setWindowElevations,
    curtainwallElevations, setCurtainwallElevations,
    storefrontElevations, setStorefrontElevations,
    doorElevations, setDoorElevations,
    submittals, setSubmittals,
    glassTypeSpecs, setGlassTypeSpecs
  } = useProjectData();
  
  // Window UI state from useWindowState hook
  const {
    operationLoading: windowOperationLoading,
    setOperationLoading: setWindowOperationLoading,
    showWindowDialog, setShowWindowDialog,
    editingWindow, setEditingWindow,
    windowSearch, setWindowSearch,
    windowFilters, setWindowFilters,
    windowSortBy, setWindowSortBy,
    selectedWindowTypeIds, setSelectedWindowTypeIds,
    selectedWindowGlassTypeIds, setSelectedWindowGlassTypeIds,
    selectedWindowMetalPanelTypeIds, setSelectedWindowMetalPanelTypeIds,
    selectedWindowListIds, setSelectedWindowListIds,
    selectedWindowElevationIds, setSelectedWindowElevationIds,
    expandedWindowGlassTypeIds, setExpandedWindowGlassTypeIds,
    selectedWindowElevation, setSelectedWindowElevation,
    selectedWindowForMarker, setSelectedWindowForMarker,
    showDeleteWindowTypeDialog, setShowDeleteWindowTypeDialog,
    windowTypeToDelete, setWindowTypeToDelete,
    showDeleteWindowDialog, setShowDeleteWindowDialog,
    windowToDelete, setWindowToDelete,
    showDeleteAllWindowsDialog, setShowDeleteAllWindowsDialog,
    pendingWindowTypePicture, setPendingWindowTypePicture,
    windowTypePictureUploading, setWindowTypePictureUploading,
    viewingDrawing, setViewingDrawing,
    newWindow, setNewWindow,
    resetNewWindow,
    // Window Metal Panel Type state
    showWindowMetalPanelTypeDialog, setShowWindowMetalPanelTypeDialog,
    editingWindowMetalPanelType, setEditingWindowMetalPanelType,
    newWindowMetalPanelType, setNewWindowMetalPanelType,
    showDeleteWindowMetalPanelTypeModal, setShowDeleteWindowMetalPanelTypeModal,
    deleteWindowMetalPanelTypeId, setDeleteWindowMetalPanelTypeId,
    // Handlers
    confirmDeleteWindowType,
    confirmDeleteWindow,
    handleEditWindow,
    handleCloseWindowDialog,
    clearWindowSelections,
    handleViewWindowDetail,
    closeDeleteWindowTypeDialog,
    closeDeleteWindowDialog
  } = useWindowState();
  
  // Door UI state from useDoorState hook
  const {
    operationLoading: doorOperationLoading,
    setOperationLoading: setDoorOperationLoading,
    showDoorTypeDialog, setShowDoorTypeDialog,
    editingDoorType, setEditingDoorType,
    selectedDoorTypeIds, setSelectedDoorTypeIds,
    doorTypePictureUploading, setDoorTypePictureUploading,
    pendingDoorTypePicture, setPendingDoorTypePicture,
    newDoorType, setNewDoorType,
    showDeleteDoorTypePictureModal, setShowDeleteDoorTypePictureModal,
    deleteDoorTypePictureId, setDeleteDoorTypePictureId,
    showDeleteDoorTypeModal, setShowDeleteDoorTypeModal,
    deleteDoorTypeId, setDeleteDoorTypeId,
    showDoorGlassTypeDialog, setShowDoorGlassTypeDialog,
    editingDoorGlassType, setEditingDoorGlassType,
    selectedDoorGlassTypeIds, setSelectedDoorGlassTypeIds,
    expandedDoorGlassTypeIds, setExpandedDoorGlassTypeIds,
    activeDoorSubTab, setActiveDoorSubTab,
    newDoorGlassType, setNewDoorGlassType,
    showDeleteDoorGlassTypeModal, setShowDeleteDoorGlassTypeModal,
    deleteDoorGlassTypeId, setDeleteDoorGlassTypeId,
    showDoorMetalPanelTypeDialog, setShowDoorMetalPanelTypeDialog,
    editingDoorMetalPanelType, setEditingDoorMetalPanelType,
    selectedDoorMetalPanelTypeIds, setSelectedDoorMetalPanelTypeIds,
    newDoorMetalPanelType, setNewDoorMetalPanelType,
    showDeleteDoorMetalPanelTypeModal, setShowDeleteDoorMetalPanelTypeModal,
    deleteDoorMetalPanelTypeId, setDeleteDoorMetalPanelTypeId,
    showDoorHardwareSetDialog, setShowDoorHardwareSetDialog,
    doorHardwareSetStep, setDoorHardwareSetStep,
    editingDoorHardwareSet, setEditingDoorHardwareSet,
    selectedDoorHardwareSetIds, setSelectedDoorHardwareSetIds,
    newDoorHardwareSet, setNewDoorHardwareSet,
    newDoorHardwareItem, setNewDoorHardwareItem,
    editingDoorHardwareItemIndex, setEditingDoorHardwareItemIndex,
    showDeleteDoorHardwareSetModal, setShowDeleteDoorHardwareSetModal,
    deleteDoorHardwareSetId, setDeleteDoorHardwareSetId,
    showDoorScheduleDialog, setShowDoorScheduleDialog,
    editingDoorSchedule, setEditingDoorSchedule,
    selectedDoorScheduleIds, setSelectedDoorScheduleIds,
    newDoorSchedule, setNewDoorSchedule,
    showDeleteDoorScheduleModal, setShowDeleteDoorScheduleModal,
    deleteDoorScheduleId, setDeleteDoorScheduleId,
    doorScheduleSearch, setDoorScheduleSearch,
    doorScheduleFilters, setDoorScheduleFilters,
    doorScheduleSortBy, setDoorScheduleSortBy,
    showDoorDetailDialog, setShowDoorDetailDialog,
    selectedDoorDetail, setSelectedDoorDetail,
    // Door Floor Plans (Elevations) state
    selectedDoorElevation, setSelectedDoorElevation,
    selectedDoorElevationIds, setSelectedDoorElevationIds,
    doorBulkMarkerMode, setDoorBulkMarkerMode,
    doorBulkMarkerSchedule, setDoorBulkMarkerSchedule,
    pendingDoorBulkMarkers, setPendingDoorBulkMarkers,
    savingDoorBulkMarkers, setSavingDoorBulkMarkers,
    // Simple handlers
    resetNewDoorType,
    resetNewDoorGlassType,
    resetNewDoorMetalPanelType,
    resetNewDoorHardwareSet,
    resetNewDoorSchedule,
    clearDoorSelections,
    handleEditDoorType,
    handleCloseDoorTypeDialog,
    closeDeleteDoorTypeModal,
    handleViewDoorDetail
  } = useDoorState();
  
  // Curtain Wall UI state from useCurtainwallState hook
  const {
    operationLoading: cwOperationLoading,
    setOperationLoading: setCwOperationLoading,
    selectedCurtainwallTypeIds, setSelectedCurtainwallTypeIds,
    selectedCWGlassTypeIds, setSelectedCWGlassTypeIds,
    selectedCWMetalPanelTypeIds, setSelectedCWMetalPanelTypeIds,
    selectedCWPanelListIds, setSelectedCWPanelListIds,
    selectedCurtainwallElevationIds, setSelectedCurtainwallElevationIds,
    expandedCWGlassTypeIds, setExpandedCWGlassTypeIds,
    expandedCWMetalPanelIds, setExpandedCWMetalPanelIds,
    selectedCurtainwallElevation, setSelectedCurtainwallElevation,
    cwScheduleSearch, setCwScheduleSearch,
    cwScheduleFilters, setCwScheduleFilters,
    cwScheduleSortBy, setCwScheduleSortBy,
    showCWTypeDialog, setShowCWTypeDialog,
    editingCWType, setEditingCWType,
    newCWType, setNewCWType,
    pendingCWTypePicture, setPendingCWTypePicture,
    cwTypePictureUploading, setCWTypePictureUploading,
    cwTypePictureUrl, setCWTypePictureUrl,
    showCWDrawingDialog, setShowCWDrawingDialog,
    selectedCWTypeForView, setSelectedCWTypeForView,
    showCWDrawingMarkerDialog, setShowCWDrawingMarkerDialog,
    pendingCWMarker, setPendingCWMarker,
    activeCWTypeForMarker, setActiveCWTypeForMarker,
    cwBulkMarkerMode, setCwBulkMarkerMode,
    cwBulkMarkerType, setCwBulkMarkerType,
    pendingCwBulkMarkers, setPendingCwBulkMarkers,
    savingCwBulkMarkers, setSavingCwBulkMarkers,
    resetNewCWType,
    handleCloseCWTypeDialog,
    handleCancelCwBulkMarkers,
    handleRemovePendingCwMarker,
    clearCurtainwallSelections
  } = useCurtainwallState();
  
  // Storefront UI state from useStorefrontState hook
  const {
    selectedStorefrontTypeIds, setSelectedStorefrontTypeIds,
    operationLoading: sfOperationLoading,
    setOperationLoading: setSfOperationLoading,
    selectedSFGlassTypeIds, setSelectedSFGlassTypeIds,
    selectedSFMetalPanelTypeIds, setSelectedSFMetalPanelTypeIds,
    selectedSFPanelListIds, setSelectedSFPanelListIds,
    selectedStorefrontElevationIds, setSelectedStorefrontElevationIds,
    expandedSFGlassTypeIds, setExpandedSFGlassTypeIds,
    expandedSFMetalPanelIds, setExpandedSFMetalPanelIds,
    selectedStorefrontElevation, setSelectedStorefrontElevation,
    sfScheduleSearch, setSfScheduleSearch,
    sfScheduleFilters, setSfScheduleFilters,
    sfScheduleSortBy, setSfScheduleSortBy,
    showSFTypeDialog, setShowSFTypeDialog,
    editingSFType, setEditingSFType,
    newSFType, setNewSFType,
    showSFGlassTypeDialog, setShowSFGlassTypeDialog,
    editingSFGlassType, setEditingSFGlassType,
    newSFGlassType, setNewSFGlassType,
    showSFMetalTypeDialog, setShowSFMetalTypeDialog,
    editingSFMetalType, setEditingSFMetalType,
    newSFMetalType, setNewSFMetalType,
    showSFPanelDialog, setShowSFPanelDialog,
    editingPanel, setEditingPanel,
    newSFPanel, setNewSFPanel,
    pendingSFTypePicture, setPendingSFTypePicture,
    sfTypePictureUploading, setSFTypePictureUploading,
    sfTypePictureUrl, setSFTypePictureUrl,
    showSFDrawingDialog, setShowSFDrawingDialog,
    selectedSFTypeForView, setSelectedSFTypeForView,
    activeSFTypeForMarker, setActiveSFTypeForMarker,
    sfBulkMarkerMode, setSfBulkMarkerMode,
    sfBulkMarkerSchedule, setSfBulkMarkerSchedule,
    pendingSfBulkMarkers, setPendingSfBulkMarkers,
    savingSfBulkMarkers, setSavingSfBulkMarkers,
    resetNewSFType,
    resetNewSFGlassType,
    resetNewSFMetalType,
    resetNewSFPanel,
    handleCancelSfBulkMarkers,
    handleRemovePendingSfMarker,
    clearStorefrontSelections
  } = useStorefrontState();
  
  // Mirror UI state from useMirrorState hook
  const {
    mirrorOperationLoading, setMirrorOperationLoading,
    selectedMirrorTypeIds, setSelectedMirrorTypeIds,
    showMirrorTypeDialog, setShowMirrorTypeDialog,
    editingMirrorType, setEditingMirrorType,
    newMirrorType, setNewMirrorType,
    pendingMirrorTypePicture, setPendingMirrorTypePicture,
    mirrorTypePictureUploading, setMirrorTypePictureUploading,
    mirrorTypePictureUrl, setMirrorTypePictureUrl,
    showDeleteMirrorTypeModal, setShowDeleteMirrorTypeModal,
    deleteMirrorTypeId, setDeleteMirrorTypeId,
    resetMirrorTypeDialog,
    openEditMirrorTypeDialog,
    confirmDeleteMirrorType,
    cancelDeleteMirrorType,
    toggleMirrorTypeSelection,
    selectAllMirrorTypes,
    clearMirrorTypeSelections,
    // Mirror Schedule states
    selectedMirrorScheduleIds, setSelectedMirrorScheduleIds,
    showMirrorScheduleDialog, setShowMirrorScheduleDialog,
    editingMirrorSchedule, setEditingMirrorSchedule,
    newMirrorSchedule, setNewMirrorSchedule,
    showMirrorScheduleDetailModal, setShowMirrorScheduleDetailModal,
    selectedMirrorScheduleForDetail, setSelectedMirrorScheduleForDetail,
    mirrorSchedulePictureUploading, setMirrorSchedulePictureUploading,
    mirrorScheduleSearch, setMirrorScheduleSearch,
    mirrorScheduleFilters, setMirrorScheduleFilters,
    mirrorScheduleSortBy, setMirrorScheduleSortBy,
    resetMirrorScheduleDialog,
    openEditMirrorScheduleDialog,
    openMirrorScheduleDetail,
    closeMirrorScheduleDetail,
    toggleMirrorScheduleSelection,
    selectAllMirrorSchedules,
    clearMirrorScheduleSelections,
    // Mirror Floor Plans (Elevation) state
    selectedMirrorElevation, setSelectedMirrorElevation,
    selectedMirrorElevationIds, setSelectedMirrorElevationIds,
    mirrorBulkMarkerMode, setMirrorBulkMarkerMode,
    mirrorBulkMarkerSchedule, setMirrorBulkMarkerSchedule,
    pendingMirrorBulkMarkers, setPendingMirrorBulkMarkers,
    savingMirrorBulkMarkers, setSavingMirrorBulkMarkers
  } = useMirrorState();
  
  // Shower UI state from useShowerState hook
  const {
    showerOperationLoading, setShowerOperationLoading,
    selectedShowerTypeIds, setSelectedShowerTypeIds,
    showShowerTypeDialog, setShowShowerTypeDialog,
    editingShowerType, setEditingShowerType,
    newShowerType, setNewShowerType,
    pendingShowerTypePicture, setPendingShowerTypePicture,
    showerTypePictureUploading, setShowerTypePictureUploading,
    showerTypePictureUrl, setShowerTypePictureUrl,
    showDeleteShowerTypeModal, setShowDeleteShowerTypeModal,
    deleteShowerTypeId, setDeleteShowerTypeId,
    resetNewShowerType,
    resetShowerTypeDialog,
    openEditShowerTypeDialog,
    confirmDeleteShowerType,
    cancelDeleteShowerType,
    toggleShowerTypeSelection,
    selectAllShowerTypes,
    clearShowerTypeSelections,
    // Shower Glass Type states
    showShowerGlassTypeDialog, setShowShowerGlassTypeDialog,
    editingShowerGlassType, setEditingShowerGlassType,
    selectedShowerGlassTypeIds, setSelectedShowerGlassTypeIds,
    expandedShowerGlassTypeIds, setExpandedShowerGlassTypeIds,
    newShowerGlassType, setNewShowerGlassType,
    showDeleteShowerGlassTypeModal, setShowDeleteShowerGlassTypeModal,
    deleteShowerGlassTypeId, setDeleteShowerGlassTypeId,
    resetNewShowerGlassType,
    // Shower Hardware Set states
    showShowerHardwareSetDialog, setShowShowerHardwareSetDialog,
    editingShowerHardwareSet, setEditingShowerHardwareSet,
    selectedShowerHardwareSetIds, setSelectedShowerHardwareSetIds,
    newShowerHardwareSet, setNewShowerHardwareSet,
    newShowerHardwareItem, setNewShowerHardwareItem,
    showerHardwareSetStep, setShowerHardwareSetStep,
    editingShowerHardwareItemIndex, setEditingShowerHardwareItemIndex,
    showDeleteShowerHardwareSetModal, setShowDeleteShowerHardwareSetModal,
    deleteShowerHardwareSetId, setDeleteShowerHardwareSetId,
    // Shower Schedule states
    showShowerScheduleDialog, setShowShowerScheduleDialog,
    editingShowerSchedule, setEditingShowerSchedule,
    selectedShowerScheduleIds, setSelectedShowerScheduleIds,
    newShowerSchedule, setNewShowerSchedule,
    showShowerDetailDialog, setShowShowerDetailDialog,
    selectedShowerDetail, setSelectedShowerDetail,
    showerScheduleSearch, setShowerScheduleSearch,
    showerScheduleFilters, setShowerScheduleFilters,
    showerScheduleSortBy, setShowerScheduleSortBy,
    // Shower Floor Plan states
    selectedShowerElevation, setSelectedShowerElevation,
    selectedShowerElevationIds, setSelectedShowerElevationIds,
    showerBulkMarkerMode, setShowerBulkMarkerMode,
    showerBulkMarkerSchedule, setShowerBulkMarkerSchedule,
    pendingShowerBulkMarkers, setPendingShowerBulkMarkers,
    savingShowerBulkMarkers, setSavingShowerBulkMarkers
  } = useShowerState();
  
  // ============================================
  // Local UI State (not in hooks)
  // ============================================
  
  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  
  // CSI Items and Division filtering
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [activeCsiTab, setActiveCsiTab] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('types');

  // Auto-expand first item when division changes or active item is deleted
  const divisionItems = React.useMemo(
    () => csiItems.filter(item => item.divisionCode === selectedDivision),
    [csiItems, selectedDivision]
  );
  const handleAutoExpand = useCallback((item) => {
    const subTab = getDefaultSubTab(item);
    setActiveSubTab(subTab);
    navigate(`/project/${projectId}/${item.id}/${subTab}`, { replace: true });
  }, [projectId, navigate]);
  useAutoExpandFirstItem(divisionItems, activeCsiTab, setActiveCsiTab, handleAutoExpand);

  // Share functionality
  const [shareCompanySearch, setShareCompanySearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // Confirmation Modals (CW delete modals - API handlers in component)
  const [showUnshareModal, setShowUnshareModal] = useState(false);
  const [unshareItem, setUnshareItem] = useState(null);
  const unshareGuardRef = useRef(false);
  const [showDeleteCWTypeModal, setShowDeleteCWTypeModal] = useState(false);
  const [deleteCWTypeId, setDeleteCWTypeId] = useState(null);
  const [showDeleteGlassModal, setShowDeleteGlassModal] = useState(false);
  const [deleteGlassId, setDeleteGlassId] = useState(null);
  const [showDeleteMetalModal, setShowDeleteMetalModal] = useState(false);
  const [deleteMetalId, setDeleteMetalId] = useState(null);
  const [showDeletePanelModal, setShowDeletePanelModal] = useState(false);
  const [deletePanelId, setDeletePanelId] = useState(null);
  const [showDeleteAllPanelsModal, setShowDeleteAllPanelsModal] = useState(false);
  // Storefront Delete Modals
  const [showDeleteSFTypeModal, setShowDeleteSFTypeModal] = useState(false);
  const [deleteSFTypeId, setDeleteSFTypeId] = useState(null);
  const [showDeleteSFGlassModal, setShowDeleteSFGlassModal] = useState(false);
  const [deleteSFGlassId, setDeleteSFGlassId] = useState(null);
  const [showDeleteSFMetalModal, setShowDeleteSFMetalModal] = useState(false);
  const [deleteSFMetalId, setDeleteSFMetalId] = useState(null);
  const [showDeleteSFPanelModal, setShowDeleteSFPanelModal] = useState(false);
  const [deleteSFPanelId, setDeleteSFPanelId] = useState(null);
  const [showDeletePictureModal, setShowDeletePictureModal] = useState(false);
  const [deletePictureTypeId, setDeletePictureTypeId] = useState(null);
  const [showDeleteDrawingModal, setShowDeleteDrawingModal] = useState(false);
  const [deleteDrawingCWId, setDeleteDrawingCWId] = useState(null);
  const [showDeleteSFDrawingModal, setShowDeleteSFDrawingModal] = useState(false);
  const [deleteSFDrawingId, setDeleteSFDrawingId] = useState(null);
  const [showDeleteAllSFPanelsModal, setShowDeleteAllSFPanelsModal] = useState(false);
  // SF Panel Detail Dialog (dimensions + lites)
  // CW Panel Detail Dialog (dimensions + lites)
  const [showCWPanelDetailDialog, setShowCWPanelDetailDialog] = useState(false);
  const [selectedCWPanelDetail, setSelectedCWPanelDetail] = useState(null);
  
  // Project Configuration Modal (includes metadata + CSI items)
  // URL state persistence for config modal
  const configParam = searchParams.get('config');
  const showConfigModal = configParam !== null;
  const configTab = configParam || 'details';
  
  // Helper to open/close config modal with URL update
  const openConfigModal = useCallback((tab = 'details') => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('config', tab);
      return newParams;
    });
  }, [setSearchParams]);
  
  const closeConfigModal = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('config');
      return newParams;
    });
  }, [setSearchParams]);
  
  const setConfigTab = useCallback((tab) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('config', tab);
      return newParams;
    });
  }, [setSearchParams]);
  
  const [configDivision, setConfigDivision] = useState('');
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemNotes, setItemNotes] = useState({}); // Store notes by tempId
  
  // Memoized callback for updating item notes (used by DebouncedNoteTextarea)
  const handleItemNoteChange = useCallback((tempId, value) => {
    setItemNotes(prev => ({
      ...prev,
      [tempId]: value
    }));
    setHasUnsavedConfigChanges(true);
  }, []);
  
  const [loadingAvailableItems, setLoadingAvailableItems] = useState(false);
  const [allDivisions, setAllDivisions] = useState([]);
  
  // Metadata fields in config modal
  const [configProjectName, setConfigProjectName] = useState('');
  const [configProjectAddress, setConfigProjectAddress] = useState('');
  const [configProjectImageFile, setConfigProjectImageFile] = useState(null);
  const [configProjectImagePreview, setConfigProjectImagePreview] = useState(null);
  const [configProjectImageToCrop, setConfigProjectImageToCrop] = useState(null);
  const [configProjectCroppedBlob, setConfigProjectCroppedBlob] = useState(null);
  const [configProjectImageRemoved, setConfigProjectImageRemoved] = useState(false); // Track if image was removed
  const [configProjectLatitude, setConfigProjectLatitude] = useState(null);
  const [configProjectLongitude, setConfigProjectLongitude] = useState(null);
  const [showConfigProjectCropModal, setShowConfigProjectCropModal] = useState(false);
  const [showConfigProjectPDFSelector, setShowConfigProjectPDFSelector] = useState(false);
  const configImageInputRef = useRef(null);
  
  // Unsaved changes tracking
  const [hasUnsavedConfigChanges, setHasUnsavedConfigChanges] = useState(false);
  const [showUnsavedConfigChangesModal, setShowUnsavedConfigChangesModal] = useState(false);
  const [originalConfigData, setOriginalConfigData] = useState({ name: '', address: '', image: '', items: [] });
  
  // Navigation guard for unsaved config changes
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const guardActiveRef = useRef(false);
  
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [uploadingElevation, setUploadingElevation] = useState(false);
  const [bulkDeletingElevations, setBulkDeletingElevations] = useState(false);
  const [deletingSingleElevation, setDeletingSingleElevation] = useState(false);
  const [deletingElevationCount, setDeletingElevationCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaveProgress, setConfigSaveProgress] = useState('');
  const [configSaveProgressPercent, setConfigSaveProgressPercent] = useState(0);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(null);
  const [selectedSubmittalIds, setSelectedSubmittalIds] = useState([]);
  const [selectedMetalPanelTypeIds, setSelectedMetalPanelTypeIds] = useState([]);
  const [selectedPanelListIds, setSelectedPanelListIds] = useState([]);
  
  const [showElevationPageSelector, setShowElevationPageSelector] = useState(false);
  const [elevationPreviewData, setElevationPreviewData] = useState(null);
  const [selectedElevationPages, setSelectedElevationPages] = useState([]);
  const [elevationPageNames, setElevationPageNames] = useState({});
  const [selectedElevation, setSelectedElevation] = useState(null);
  const [elevationZoom, setElevationZoom] = useState(1);
  const [elevationPan, setElevationPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showMarkerDialog, setShowMarkerDialog] = useState(false);
  const [pendingMarker, setPendingMarker] = useState(null);
  const [markerContext, setMarkerContext] = useState('window');
  
  // Door Schedule create wizard step + lite validation touch state
  const [doorScheduleStep, setDoorScheduleStep] = useState(1);
  const [doorLiteTouched, setDoorLiteTouched] = useState({});
  // Step 1 -> step 2 when creating; save otherwise
  const submitDoorScheduleStep = () => {
    if (!editingDoorSchedule && doorScheduleStep === 1) {
      setDoorScheduleStep(2);
      return;
    }
    handleCreateDoorSchedule();
  };
  const selectedDoorTypeForSchedule = doorTypes.find(t => t.id === newDoorSchedule.doorTypeId) || null;
  const selectedDoorTypeImageUrl = getTypePictureUrl(selectedDoorTypeForSchedule, API);

  
  // Inline edit draft for a single door hardware set item
  const [doorHardwareItemDraft, setDoorHardwareItemDraft] = useState({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
  
  // Bulk marker state for Windows Elevation
  const [bulkMarkerMode, setBulkMarkerMode] = useState(false);
  const [bulkMarkerSchedule, setBulkMarkerSchedule] = useState('');
  const [pendingBulkMarkers, setPendingBulkMarkers] = useState([]);
  const [savingBulkMarkers, setSavingBulkMarkers] = useState(false);
  
  const [showDeleteElevationDialog, setShowDeleteElevationDialog] = useState(false);
  const [elevationToDelete, setElevationToDelete] = useState(null);
  const [showExcelUploadDialog, setShowExcelUploadDialog] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPreview, setExcelPreview] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [doorExcelUploading, setDoorExcelUploading] = useState(false);
  const [showerExcelUploading, setShowerExcelUploading] = useState(false);
  const [mirrorExcelUploading, setMirrorExcelUploading] = useState(false);
  const [showDoorExcelDialog, setShowDoorExcelDialog] = useState(false);
  const [showShowerExcelDialog, setShowShowerExcelDialog] = useState(false);
  const [showMirrorExcelDialog, setShowMirrorExcelDialog] = useState(false);
  const [doorExcelPreview, setDoorExcelPreview] = useState(null);
  const [showerExcelPreview, setShowerExcelPreview] = useState(null);
  const [mirrorExcelPreview, setMirrorExcelPreview] = useState(null);
  const [draggingMarker, setDraggingMarker] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const dragJustEndedRef = useRef(false);
  
  // SF Marker drag states
  const [sfDragOffset, setSfDragOffset] = useState({ x: 0, y: 0 });
  const [sfDragStartPos, setSfDragStartPos] = useState({ x: 0, y: 0 });
  const [isSFDragging, setIsSFDragging] = useState(false);
  
  // Helper function to get elevation image URL (supports both Cloudinary and legacy)
  const getElevationImageUrl = (elevation, size = 'large') => {
    // Check for Cloudinary first
    if (elevation?.imageCloudinary?.urls) {
      return elevation.imageCloudinary.urls[size] || elevation.imageCloudinary.urls.large || elevation.imageCloudinary.urls.medium;
    }
    // Check imagePaths (legacy)
    if (elevation?.imagePaths && elevation.imagePaths.length > 0) {
      const path = elevation.imagePaths[0];
      // If path is already a full URL (Cloudinary), return as is
      if (path.startsWith('http')) {
        return path;
      }
      // Otherwise, prefix with API
      return `${API}${path}`;
    }
    // Check filepath (legacy)
    if (elevation?.filepath) {
      if (elevation.filepath.startsWith('http')) {
        return elevation.filepath;
      }
      return `${API}${elevation.filepath}`;
    }
    return null;
  };

  const [newType, setNewType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    frameMaterial: '',
    operationType: '',
    uValue: '',
    cr: '',
    finishInterior: '',
    finishExterior: '',
    hardware: '',
    frameWarrantyYears: null,
    finishWarrantyYears: null,
    hardwareWarrantyYears: null,
    installationWarrantyYears: null,
    installationManual: '',
    cleaningManual: ''
  });
  
  const [typePictureFile, setTypePictureFile] = useState(null);
  const [typePictureUrl, setTypePictureUrl] = useState(null);
  const [doorTypePictureUrl, setDoorTypePictureUrl] = useState(null);
  
  // Clipboard paste modal state
  const [clipboardPasteTarget, setClipboardPasteTarget] = useState(null);
  
  const [elevationUploading, setElevationUploading] = useState(null);
  
  const [showPrintPreviewDialog, setShowPrintPreviewDialog] = useState(false);
  const [printPreviewUrl, setPrintPreviewUrl] = useState(null);
  const [printPreviewType, setPrintPreviewType] = useState('window');
  const [selectedPanelForMarker, setSelectedPanelForMarker] = useState('');
  
  // CW Drawing Bulk Marker state
  const [cwDrawingBulkMode, setCwDrawingBulkMode] = useState(false);
  const [cwDrawingBulkSchedule, setCwDrawingBulkSchedule] = useState('');
  const [pendingCwDrawingMarkers, setPendingCwDrawingMarkers] = useState([]);
  const [savingCwDrawingMarkers, setSavingCwDrawingMarkers] = useState(false);
  
  // SF Drawing Bulk Marker state
  
  // CW/SF Drawing zoom states
  const [cwDrawingZoom, setCwDrawingZoom] = useState(1);
  const [sfDrawingZoom, setSfDrawingZoom] = useState(1);
  const [cwDrawingPan, setCwDrawingPan] = useState({ x: 0, y: 0 });
  const [sfDrawingPan, setSfDrawingPan] = useState({ x: 0, y: 0 });
  const [isCwPanning, setIsCwPanning] = useState(false);
  const [isSfPanning, setIsSfPanning] = useState(false);
  const [cwPanStartPos, setCwPanStartPos] = useState(null);
  const [sfPanStartPos, setSfPanStartPos] = useState(null);
  const wasCwPanningRef = React.useRef(false);
  const wasSfPanningRef = React.useRef(false);
  
  // Panel print preview modal states
  const [showPanelPrintPreview, setShowPanelPrintPreview] = useState(false);
  const [panelPrintPreviewUrl, setPanelPrintPreviewUrl] = useState(null);
  const [panelPrintPreviewLoading, setPanelPrintPreviewLoading] = useState(false);
  
  // Active marker popup state (click-based)
  const [activeMarkerPopup, setActiveMarkerPopup] = useState(null);
  
  // Close marker popup on ESC key or click outside
  useEffect(() => {
    if (!activeMarkerPopup) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveMarkerPopup(null);
      }
    };
    
    const handleClickOutside = (e) => {
      // Don't close if clicking on the popup itself
      if (e.target.closest('.marker-popup')) return;
      // Don't close if clicking on the marker box (toggle handled separately)
      if (e.target.closest('.marker-box')) return;
      // Close popup for any other click
      setActiveMarkerPopup(null);
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeMarkerPopup]);
  
  // SF Drawing Marker Dialog states
  
  // Submittals state
  const [showAddSubmittalDialog, setShowAddSubmittalDialog] = useState(false);
  const [submittalTitle, setSubmittalTitle] = useState('');
  const [submittalDescription, setSubmittalDescription] = useState('');
  const [submittalFile, setSubmittalFile] = useState(null);
  const [uploadingSubmittal, setUploadingSubmittal] = useState(false);
  const [showDeleteSubmittalModal, setShowDeleteSubmittalModal] = useState(false);
  const [submittalToDelete, setSubmittalToDelete] = useState(null);

  // Curtainwall UI state (Glass/Metal dialogs)
  const [showGlassTypeDialog, setShowGlassTypeDialog] = useState(false);
  const [showMetalTypeDialog, setShowMetalTypeDialog] = useState(false);
  const [editingGlassType, setEditingGlassType] = useState(null);
  const [editingMetalType, setEditingMetalType] = useState(null);
  
  const [newGlassType, setNewGlassType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness_in: null,
    specifications: [],
    colorCode: '',
    argonFilled: false,
    lowECoated: false,
    build: '',
    hasGrid: false,
    gridSize_in: '',
    warrantyYears: ''
  });
  
  const [newMetalType, setNewMetalType] = useState({
    name: '',
    manufacturer: '',
    model: '',
    thickness_mm: '',
    colorCode: ''
  });

  const [showPanelDialog, setShowPanelDialog] = useState(false);
  const [newPanel, setNewPanel] = useState({
    curtainwallTypeId: '',
    label: '',
    location: ''
  });
  const [showPanelExcelDialog, setShowPanelExcelDialog] = useState(false);
  const [panelExcelPreview, setPanelExcelPreview] = useState(null);
  const [uploadingPanelExcel, setUploadingPanelExcel] = useState(false);
  
  // Window Glass Type states
  const [newWindowGlassType, setNewWindowGlassType] = useState({
    name: '',
    glassManufacturer: '',
    glassModel: '',
    glassBuild: '',
    glassThickness_in: null,
    colorCode: '',
    glassTypes: [],
    hasGrid: false,
    gridSize_in: '',
    warrantyYears: ''
  });
  const [showDeleteGlassTypeDialog, setShowDeleteGlassTypeDialog] = useState(false);
  const [glassTypeToDelete, setGlassTypeToDelete] = useState(null);
  
  // Confirm dialog state (replaces all window.confirm calls)
  const [confirmState, setConfirmState] = useState({ open: false });
  const openConfirm = (opts) => setConfirmState({ open: true, ...opts });
  const closeConfirm = () => setConfirmState({ open: false });
  
  // Refs for auto-focus on grid size inputs
  const windowGridSizeRef = useRef(null);
  const cwGridSizeRef = useRef(null);
  const sfGridSizeRef = useRef(null);
  
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'general_contractor'
  });

  useEffect(() => {
    fetchProjectData();
    loadGlassTypeSpecs();
  }, [projectId]);

  const loadGlassTypeSpecs = async () => {
    try {
      const response = await axios.get(`${API}/glass-type-specs`);
      setGlassTypeSpecs(response.data || []);
    } catch (error) {
      console.error('Error loading Glass Type Specs:', error);
    }
  };
  
  // Load divisions if no CSI items (for "No Specifications" page)
  useEffect(() => {
    if (project && csiItems.length === 0) {
      loadActiveDivisions();
    }
  }, [project, csiItems]);
  
  // Initialize config modal when URL has config parameter
  // Also re-initialize when csiItems are loaded (for page refresh with deep link)
  useEffect(() => {
    if (showConfigModal && project && csiItems.length > 0 && !hasUnsavedConfigChanges) {
      initializeConfigModal();
    }
  }, [showConfigModal, project, csiItems.length, hasUnsavedConfigChanges]);
  
  // Track unsaved changes
  useEffect(() => {
    if (originalConfigData.name && (configProjectName || configProjectAddress || selectedItems.length > 0)) {
      checkConfigUnsavedChanges();
    }
  }, [configProjectName, configProjectAddress, configProjectCroppedBlob, selectedItems]);

  // Sync active tab with URL parameter
  useEffect(() => {
    if (tab && csiItems.length > 0) {
      // Find CSI item by ID
      const tabItem = csiItems.find(item => item.id === tab);
      if (tabItem) {
        setActiveCsiTab(tab);
        setSelectedDivision(tabItem.divisionCode);
        
        // Determine default sub-tab based on CSI item type
        const defaultSubTab = getDefaultSubTab(tabItem);
        
        // Set subTab from URL or default
        if (subTab) {
          setActiveSubTab(subTab);
        } else {
          setActiveSubTab(defaultSubTab);
          // Update URL with default sub-tab
          navigate(`/project/${projectId}/${tab}/${defaultSubTab}`, { replace: true });
        }
      }
    }
  }, [tab, subTab, csiItems, projectId, navigate]);

  // Re-fetch entity data when active CSI tab changes
  useEffect(() => {
    const fetchEntitiesForActiveTab = async () => {
      if (!activeCsiTab || !projectId) return;
      
      // CRITICAL: Clear state immediately to prevent flickering of old data
      setWindowTypes([]);
      setWindowGlassTypes([]);
      setWindowList([]);
      setCurtainwallTypes([]);
      setGlassTypes([]);
      setMetalPanelTypes([]);
      setPanelList([]);
      setElevations([]);
      setWindowElevations([]);
      setCurtainwallElevations([]);
      
      try {
        const csiItemParam = `?csi_item_id=${activeCsiTab}`;
        
        const [typesRes, windowGlassTypesRes, windowMetalPanelTypesRes, windowListRes, cwTypesRes, glassTypesRes, metalTypesRes, panelListRes, sfTypesRes, sfGlassTypesRes, sfMetalTypesRes, sfPanelListRes, doorTypesRes, doorGlassTypesRes, doorMetalPanelTypesRes, doorHardwareSetsRes, doorSchedulesRes, mirrorTypesRes, mirrorSchedulesRes, showerTypesRes, showerGlassTypesRes, showerHardwareSetsRes, showerSchedulesRes] = await Promise.all([
          axios.get(`${API}/window-types/${projectId}${csiItemParam}`),
          axios.get(`${API}/window-glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/window-metal-panel-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/window-list/${projectId}${csiItemParam}`),
          axios.get(`${API}/curtainwall-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/metal-panel-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/panel-list/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/storefront-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/storefront-glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/storefront-metal-panel-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/storefront-panel-list/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/door-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
          axios.get(`${API}/door-glass-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/door-metal-panel-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/door-hardware-sets/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/door-schedules/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/mirror-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/mirror-schedules/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/shower-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/shower-glass-types/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/shower-hardware-sets/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] })),
          axios.get(`${API}/shower-schedules/${projectId}/${activeCsiTab}`).catch(() => ({ data: [] }))
        ]);
        
        setWindowTypes(typesRes.data);
        setWindowGlassTypes(windowGlassTypesRes.data);
        setWindowMetalPanelTypes(windowMetalPanelTypesRes.data);
        setWindowList(windowListRes.data);
        setCurtainwallTypes(cwTypesRes.data);
        setGlassTypes(glassTypesRes.data);
        setMetalPanelTypes(metalTypesRes.data);
        setPanelList(panelListRes.data);
        setStorefrontTypes(sfTypesRes.data);
        setStorefrontGlassTypes(sfGlassTypesRes.data);
        setStorefrontMetalPanelTypes(sfMetalTypesRes.data);
        setStorefrontPanelList(sfPanelListRes.data);
        setStorefrontTypes(sfTypesRes.data);
        setStorefrontGlassTypes(sfGlassTypesRes.data);
        setStorefrontMetalPanelTypes(sfMetalTypesRes.data);
        setStorefrontPanelList(sfPanelListRes.data);
        setDoorTypes(doorTypesRes.data);
        setDoorGlassTypes(doorGlassTypesRes.data);
        setDoorMetalPanelTypes(doorMetalPanelTypesRes.data);
        setDoorHardwareSets(doorHardwareSetsRes.data);
        setDoorSchedules(doorSchedulesRes.data);
        setMirrorTypes(mirrorTypesRes.data);
        setMirrorSchedules(mirrorSchedulesRes.data);
        setShowerTypes(showerTypesRes.data);
        setShowerGlassTypes(showerGlassTypesRes.data);
        setShowerHardwareSets(showerHardwareSetsRes.data);
        setShowerSchedules(showerSchedulesRes.data);
        
        // Filter elevations by active CSI item
        if (project && project.elevations) {
          const filteredElevations = project.elevations.filter(e => e.csiItemId === activeCsiTab);
          setElevations(filteredElevations);
          
          // Separate by type
          const windowElevs = filteredElevations.filter(e => e.type === 'window' || !e.type);
          const curtainwallElevs = filteredElevations.filter(e => e.type === 'curtainwall');
          const storefrontElevs = filteredElevations.filter(e => e.type === 'storefront');
          const doorElevs = filteredElevations.filter(e => e.type === 'door');
          const mirrorElevs = filteredElevations.filter(e => e.type === 'mirror');
          const showerElevs = filteredElevations.filter(e => e.type === 'shower');
          
          setWindowElevations(windowElevs);
          setCurtainwallElevations(curtainwallElevs);
          setStorefrontElevations(storefrontElevs);
          setDoorElevations(doorElevs);
          setMirrorElevations(mirrorElevs);
          setShowerElevations(showerElevs);
        }
      } catch (error) {
        console.error('Error fetching entities for active CSI tab:', error);
      }
    };
    
    fetchEntitiesForActiveTab();
  }, [activeCsiTab, projectId, project]);

  // Auto-scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && showCompanySuggestions) {
      const dropdownElement = document.querySelector('.company-suggestions-dropdown');
      const highlightedElement = dropdownElement?.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, showCompanySuggestions]);
  
  // Eagerly fetch submittals when CSI tab changes (so tab label count is correct from the start)
  useEffect(() => {
    if (activeCsiTab) {
      fetchSubmittals(activeCsiTab);
    }
  }, [activeCsiTab]);

  // Sync elevation selection with URL (handles both opening and closing via browser back)
  useEffect(() => {
    // If no elevationId in URL, close any open elevation (handles browser back button)
    if (!elevationId) {
      // Only close if there's currently an elevation open
      if (selectedWindowElevation || selectedCurtainwallElevation || selectedStorefrontElevation || selectedDoorElevation || selectedMirrorElevation || selectedShowerElevation) {
        setSelectedWindowElevation(null);
        setSelectedCurtainwallElevation(null);
        setSelectedStorefrontElevation(null);
        setSelectedDoorElevation(null);
        setSelectedMirrorElevation(null);
        setSelectedShowerElevation(null);
        setSelectedElevation(null);
      }
      return;
    }
    
    // If elevationId exists but project not loaded yet, wait
    if (!project) return;
    
    // If this elevation is already selected, don't re-select
    if (selectedElevation?.id === elevationId) return;
    
    // Determine which elevation list to search based on activeSubTab
    let elevationList = [];
    let context = 'window';
    
    if (activeSubTab === 'elevations') {
      elevationList = windowElevations;
      context = 'window';
    } else if (activeSubTab === 'cw-elevations') {
      elevationList = curtainwallElevations;
      context = 'curtainwall';
    } else if (activeSubTab === 'sf-elevations') {
      elevationList = storefrontElevations;
      context = 'storefront';
    } else if (activeSubTab === 'door-floor-plans') {
      elevationList = doorElevations;
      context = 'door';
    } else if (activeSubTab === 'mirror-floor-plans') {
      elevationList = mirrorElevations;
      context = 'mirror';
    } else if (activeSubTab === 'shower-floor-plans') {
      elevationList = showerElevations;
      context = 'shower';
    }
    
    // Find the elevation by ID
    const elevation = elevationList.find(e => e.id === elevationId);
    if (elevation) {
      // Open the elevation
      setElevationZoom(1);
      setElevationPan({ x: 0, y: 0 });
      
      if (context === 'curtainwall') {
        setSelectedCurtainwallElevation(elevation);
        setMarkerContext('curtainwall');
      } else if (context === 'storefront') {
        setSelectedStorefrontElevation(elevation);
        setMarkerContext('storefront');
      } else if (context === 'door') {
        setSelectedDoorElevation(elevation);
        setMarkerContext('door');
      } else if (context === 'mirror') {
        setSelectedMirrorElevation(elevation);
        setMarkerContext('mirror');
      } else if (context === 'shower') {
        setSelectedShowerElevation(elevation);
        setMarkerContext('shower');
      } else {
        setSelectedWindowElevation(elevation);
        setMarkerContext('window');
      }
      setSelectedElevation(elevation);
      setCurrentPageIndex(0);
    } else if (elevationList.length > 0) {
      // elevationId is in URL but not found in the list — invalid/deleted
      navigate(`/project/${projectId}/${activeCsiTab}/${activeSubTab}`, { replace: true });
    }
  }, [elevationId, project, activeSubTab, windowElevations, curtainwallElevations, storefrontElevations, doorElevations, mirrorElevations, showerElevations, selectedElevation, selectedWindowElevation, selectedCurtainwallElevation, selectedStorefrontElevation, selectedDoorElevation, selectedMirrorElevation, selectedShowerElevation]);

  const fetchProjectData = async () => {
    try {
      // First fetch project and CSI items to determine active tab
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setProject(projectRes.data);
      
      // Fetch CSI items with latest titles from dedicated endpoint
      let currentActiveCsiTab = activeCsiTab;
      try {
        const csiItemsRes = await axios.get(`${API}/projects/${projectId}/csi-items`);
        const enrichedCsiItems = csiItemsRes.data;
        
        setCsiItems(enrichedCsiItems);
        
        // Set first CSI item as active by default (if no tab in URL)
        if (enrichedCsiItems.length > 0) {
          const firstItem = enrichedCsiItems[0];
          if (!tab) {
            currentActiveCsiTab = firstItem.id;
            setActiveCsiTab(firstItem.id);
            setSelectedDivision(firstItem.divisionCode);
            // Update URL with first tab
            navigate(`/project/${projectId}/${firstItem.id}`, { replace: true });
          }
        }
      } catch (error) {
        console.error('Error fetching CSI items:', error);
        // Fallback to project's csiItems if endpoint fails
        const fallbackItems = projectRes.data.csiItems || [];
        setCsiItems(fallbackItems);
        if (fallbackItems.length > 0 && !tab) {
          currentActiveCsiTab = fallbackItems[0].id;
          setActiveCsiTab(fallbackItems[0].id);
          setSelectedDivision(fallbackItems[0].divisionCode);
          // Update URL with first tab
          navigate(`/project/${projectId}/${fallbackItems[0].id}`, { replace: true });
        }
      }
      
      // Now fetch entity data filtered by active CSI tab
      // Use tab from URL if available, otherwise use currentActiveCsiTab
      const tabToUse = tab || currentActiveCsiTab;
      const csiItemParam = tabToUse ? `?csi_item_id=${tabToUse}` : '';
      
      const [typesRes, windowGlassTypesRes, windowMetalPanelTypesRes, windowListRes, cwTypesRes, glassTypesRes, metalTypesRes, panelListRes, sfTypesRes, sfGlassTypesRes, sfMetalTypesRes, sfPanelListRes] = await Promise.all([
        axios.get(`${API}/window-types/${projectId}${csiItemParam}`),
        axios.get(`${API}/window-glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/window-metal-panel-types/${projectId}/${tabToUse || 'all'}`).catch(() => ({ data: [] })),
        axios.get(`${API}/window-list/${projectId}${csiItemParam}`),
        axios.get(`${API}/curtainwall-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/metal-panel-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/panel-list/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/storefront-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/storefront-glass-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/storefront-metal-panel-types/${projectId}${csiItemParam}`).catch(() => ({ data: [] })),
        axios.get(`${API}/storefront-panel-list/${projectId}${csiItemParam}`).catch(() => ({ data: [] }))
      ]);
      
      setWindowTypes(typesRes.data);
      setWindowGlassTypes(windowGlassTypesRes.data);
      setWindowMetalPanelTypes(windowMetalPanelTypesRes.data);
      setWindowList(windowListRes.data);
      const allElevations = projectRes.data.elevations || [];
      setElevations(allElevations);
      
      // Separate elevations by type
      const windowElevs = allElevations.filter(e => e.type === 'window' || !e.type); // backward compatibility
      const curtainwallElevs = allElevations.filter(e => e.type === 'curtainwall');
      const storefrontElevs = allElevations.filter(e => e.type === 'storefront');
      const doorElevs = allElevations.filter(e => e.type === 'door');
      
      setWindowElevations(windowElevs);
      setCurtainwallElevations(curtainwallElevs);
      setStorefrontElevations(storefrontElevs);
      setDoorElevations(doorElevs);
      setCurtainwallTypes(cwTypesRes.data);
      setGlassTypes(glassTypesRes.data);
      setMetalPanelTypes(metalTypesRes.data);
      setPanelList(panelListRes.data);
      setStorefrontTypes(sfTypesRes.data);
      setStorefrontGlassTypes(sfGlassTypesRes.data);
      setStorefrontMetalPanelTypes(sfMetalTypesRes.data);
      setStorefrontPanelList(sfPanelListRes.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 403 || status === 404) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load project');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch submittals for active CSI item
  const fetchSubmittals = async (csiItemId) => {
    if (!csiItemId) return;
    try {
      const response = await axios.get(`${API}/projects/${projectId}/csi-items/${csiItemId}/submittals`);
      setSubmittals(response.data);
    } catch (error) {
      console.error('Error fetching submittals:', error);
      setSubmittals([]);
    }
  };
  
  // Load Divisions Function (separate for reuse)
  const loadActiveDivisions = async () => {
    try {
      const response = await axios.get(`${API}/csi-masterformat`);
      const allItems = response.data;
      
      // Get all visible level 4 items (actual specifications)
      // Note: Backend uses snake_case (is_hidden), not camelCase (isHidden)
      const level4Items = allItems.filter(item => 
        item.level === 4 && !item.is_hidden
      );
      
      // Get unique parent_ids from level 4 items
      const activeParentIds = [...new Set(level4Items.map(item => item.parent_id))];
      
      // Get divisions (level 3) that have visible children
      const activeDivisions = allItems.filter(item => 
        item.level === 3 && 
        !item.is_hidden && 
        activeParentIds.includes(item.id)
      );
      
      setAllDivisions(activeDivisions);
      return activeDivisions;
    } catch (error) {
      console.error('Error loading divisions:', error);
      return [];
    }
  };
  
  // Project Configuration Functions
  const initializeConfigModal = async () => {
    try {
      // Pre-populate metadata fields and store original values
      if (project) {
        setConfigProjectName(project.name);
        setConfigProjectAddress(project.address);
        // Set image preview - check Cloudinary first, then legacy
        if (project.projectImageCloudinary?.urls?.medium) {
          setConfigProjectImagePreview(project.projectImageCloudinary.urls.medium);
        } else if (project.projectImage) {
          setConfigProjectImagePreview(`${API}${project.projectImage}`);
        } else {
          setConfigProjectImagePreview(null);
        }
        
        // Store original data for comparison
        setOriginalConfigData({
          name: project.name,
          address: project.address,
          image: project.projectImageCloudinary?.urls?.medium || project.projectImage || '',
          items: csiItems.map(item => item.id)
        });
        
        // Load coordinates
        setConfigProjectLatitude(project.latitude ?? null);
        setConfigProjectLongitude(project.longitude ?? null);
      }
      
      // Reset unsaved changes flag and image removed flag
      setHasUnsavedConfigChanges(false);
      setConfigProjectImageRemoved(false);
      
      // Load active divisions (divisions that have visible level 4 items)
      const activeDivisions = await loadActiveDivisions();
      
      // Pre-populate with existing CSI items
      if (csiItems.length > 0) {
        const existingItems = csiItems.map(item => ({
          ...item,
          assignedCompanyName: item.assignedCompanyName || null,
          tempId: item.id,
          // CRITICAL: Preserve the project-specific ID for existing items
          // This allows the save logic to identify and UPDATE existing items
          // instead of deleting and recreating them (which changes IDs)
          id: item.id
        }));
        setSelectedItems(existingItems);
        
        // Load existing notes into itemNotes state for editing
        const existingNotes = {};
        csiItems.forEach(item => {
          if (item.note) {
            existingNotes[item.id] = item.note;
          }
        });
        setItemNotes(existingNotes);
        
        // Set first division as default
        if (csiItems[0].divisionCode) {
          setConfigDivision(csiItems[0].divisionCode);
          await loadAvailableItems(csiItems[0].divisionCode);
        } else if (activeDivisions.length > 0) {
          setConfigDivision(activeDivisions[0].code);
          await loadAvailableItems(activeDivisions[0].code);
        }
      } else {
        // Set first division as default
        if (activeDivisions.length > 0) {
          setConfigDivision(activeDivisions[0].code);
          await loadAvailableItems(activeDivisions[0].code);
        }
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
      toast.error('Failed to load divisions');
    }
  };

  // Check for unsaved changes in config
  const checkConfigUnsavedChanges = () => {
    const coordsChanged = 
      configProjectLatitude !== (project?.latitude ?? null) ||
      configProjectLongitude !== (project?.longitude ?? null);
    const metadataChanged = 
      configProjectName !== originalConfigData.name ||
      configProjectAddress !== originalConfigData.address ||
      configProjectCroppedBlob !== null ||
      coordsChanged;
    
    const currentItemIds = selectedItems.map(item => item.tempId || item.id).sort();
    const originalItemIds = originalConfigData.items.sort();
    const itemsChanged = JSON.stringify(currentItemIds) !== JSON.stringify(originalItemIds);
    
    const hasChanges = metadataChanged || itemsChanged;
    
    setHasUnsavedConfigChanges(hasChanges);
    return hasChanges;
  };
  
  // Config Modal - Image Upload Handlers
  const handleConfigProjectImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    if (file.type === 'application/pdf') {
      setConfigProjectImageFile(file);
      setShowConfigProjectPDFSelector(true);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setConfigProjectImageToCrop(event.target.result);
      setConfigProjectImageFile(file);
      setShowConfigProjectCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleConfigProjectPDFPageSelect = (imageDataUrl, pageNumber) => {
    setConfigProjectImageToCrop(imageDataUrl);
    setShowConfigProjectPDFSelector(false);
    setShowConfigProjectCropModal(true);
  };

  const handleConfigProjectCropComplete = (croppedBlob) => {
    setConfigProjectCroppedBlob(croppedBlob);
    setConfigProjectImagePreview(URL.createObjectURL(croppedBlob));
    setShowConfigProjectCropModal(false);
    setConfigProjectImageToCrop(null);
    setConfigProjectImageFile(null);
    if (configImageInputRef.current) configImageInputRef.current.value = '';
  };

  const removeConfigProjectImage = () => {
    setConfigProjectImageFile(null);
    setConfigProjectImagePreview(null);
    setConfigProjectCroppedBlob(null);
    setConfigProjectImageToCrop(null);
    setConfigProjectImageRemoved(true); // Mark that image should be removed
    if (configImageInputRef.current) configImageInputRef.current.value = '';
  };

  const handleConfigProjectImagePaste = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setConfigProjectImageToCrop(event.target.result);
      setConfigProjectImageFile(file);
      setShowConfigProjectCropModal(true);
    };
    reader.readAsDataURL(file);
  };


  const handleConfigCropModalClose = () => {
    setShowConfigProjectCropModal(false);
    setConfigProjectImageToCrop(null);
    setConfigProjectImageFile(null);
    if (configImageInputRef.current) configImageInputRef.current.value = '';
  };
  
  const loadAvailableItems = async (divisionCode) => {
    if (!divisionCode) return;
    
    setLoadingAvailableItems(true);
    try {
      const response = await axios.get(`${API}/csi-masterformat`);
      const allItems = response.data;
      
      // Find division and its children (level 4 items)
      const division = allItems.find(item => item.code === divisionCode && item.level === 3);
      if (division) {
        const items = allItems.filter(item => 
          item.parent_id === division.id && 
          item.level === 4 &&
          !item.isHidden
        );
        items.sort((a, b) => {
          if (!a.code && !b.code) return 0;
          if (!a.code) return 1;
          if (!b.code) return -1;
          return a.code.localeCompare(b.code, undefined, { numeric: true });
        });
        setAvailableItems(items);
      }
    } catch (error) {
      console.error('Error loading available items:', error);
      toast.error('Failed to load available items');
    } finally {
      setLoadingAvailableItems(false);
    }
  };

  const SINGLE_INSTANCE_ITEMS = new Set(['26 01 00', '27 01 00']);

  const handleAddItem = (item) => {
    // Single-instance guard
    if (SINGLE_INSTANCE_ITEMS.has(item.code)) {
      const alreadySelected = selectedItems.some(s => s.itemCode === item.code);
      if (alreadySelected) {
        toast.warning(`${item.title} can only be added once per project.`);
        return;
      }
    }

    // Get division title
    const division = allDivisions.find(d => d.code === configDivision);
    
    const newItem = {
      id: `${item.id}-${Date.now()}-${Math.random()}`,  // Unique ID for each addition
      csiItemId: item.id,
      itemCode: item.code,
      itemTitle: item.title,
      divisionCode: configDivision,
      divisionTitle: division ? division.title : '',
      assignedCompanyName: null,  // Will be populated from existing items
      tempId: `temp-${Date.now()}-${Math.random()}`
    };
    
    setSelectedItems(prev => [...prev, newItem]);
  };

  const handleRemoveItem = (tempId) => {
    setSelectedItems(prev => prev.filter(item => item.tempId !== tempId));
  };

  const handleSaveConfiguration = async () => {
    try {
      setConfigSaving(true);
      setConfigSaveProgress('Preparing to save...');
      setConfigSaveProgressPercent(5);
      
      // 1. Save Metadata (name, address, image, coordinates) if changed
      const coordsChanged = 
        configProjectLatitude !== (project.latitude ?? null) ||
        configProjectLongitude !== (project.longitude ?? null);
      const metadataChanged = 
        configProjectName !== project.name ||
        configProjectAddress !== project.address ||
        configProjectCroppedBlob !== null ||
        configProjectImageRemoved ||
        coordsChanged;
      
      if (metadataChanged) {
        setConfigSaveProgress('Saving project details...');
        setConfigSaveProgressPercent(15);
        
        const formData = new FormData();
        
        if (configProjectName !== project.name) {
          formData.append('name', configProjectName);
        }
        if (configProjectAddress !== project.address) {
          formData.append('address', configProjectAddress);
        }
        if (configProjectCroppedBlob) {
          formData.append('file', configProjectCroppedBlob, 'project-image.jpg');
        }
        // If image was removed (and no new image uploaded), tell backend to remove it
        if (configProjectImageRemoved && !configProjectCroppedBlob) {
          formData.append('removeImage', 'true');
        }
        // Send coordinates
        if (coordsChanged) {
          formData.append('latitude', configProjectLatitude != null ? String(configProjectLatitude) : '__CLEAR__');
          formData.append('longitude', configProjectLongitude != null ? String(configProjectLongitude) : '__CLEAR__');
        }
        
        await axios.put(`${API}/projects/${projectId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setConfigSaveProgressPercent(30);
      }
      
      // 2. Save CSI Items with smart diff logic to preserve IDs
      setConfigSaveProgress('Processing specification items...');
      setConfigSaveProgressPercent(35);
      
      // Prepare items to save with their notes and company assignments
      const itemsToSave = selectedItems.map(item => {
        // Determine note value
        // Priority: 1) itemNotes (if user edited in this session), 2) existing item.note, 3) null
        let noteValue = null;
        
        // Check if user edited note in this session (including deletion - empty string)
        if (item.tempId && itemNotes.hasOwnProperty(item.tempId)) {
          // User added/edited/deleted note in this session
          noteValue = itemNotes[item.tempId] || null;  // Convert empty string to null
        } else if (item.note) {
          // Preserve existing note from database (user didn't touch it)
          noteValue = item.note;
        }
        
        const baseItem = {
          csiItemId: item.csiItemId,
          itemCode: item.itemCode,
          itemTitle: item.itemTitle,
          divisionCode: item.divisionCode,
          divisionTitle: item.divisionTitle,
          note: noteValue
        };
        
        // If item already exists (has assignedCompanyId), preserve it
        if (item.assignedCompanyId) {
          baseItem.assignedCompanyId = item.assignedCompanyId;
        }
        
        return baseItem;
      });

      // Smart diff: Match items by their project-specific ID
      // CRITICAL: We match by 'id' field (project-specific), NOT by 'csiItemId'
      // This allows multiple instances of the same CSI item in a project
      const existingItemsMap = new Map();
      csiItems.forEach(item => {
        existingItemsMap.set(item.id, item);
      });
      
      // Separate items into three categories
      const itemsToUpdate = [];
      const itemsToDelete = [];
      const itemsToCreate = [];
      
      // Process selected items
      itemsToSave.forEach((item, index) => {
        // Check if this item has a project-specific ID (from selectedItems)
        const correspondingSelectedItem = selectedItems[index];
        const projectItemId = correspondingSelectedItem?.id;
        
        if (projectItemId && existingItemsMap.has(projectItemId)) {
          // Item has an ID and exists in project - UPDATE it (preserve ID)
          itemsToUpdate.push({
            id: projectItemId,  // Preserve the existing project-specific ID
            ...item
          });
          // Mark as processed
          existingItemsMap.delete(projectItemId);
        } else {
          // Item has no ID or ID not found - CREATE new item
          itemsToCreate.push(item);
        }
      });
      
      // Any remaining items in existingItemsMap were not in selectedItems - DELETE them
      existingItemsMap.forEach((item) => {
        itemsToDelete.push(item.id);
      });
      
      const totalOperations = itemsToUpdate.length + itemsToDelete.length + itemsToCreate.length;
      let completedOperations = 0;
      
      // Execute the operations
      // 1. Update existing items (preserves IDs for URL routing)
      if (itemsToUpdate.length > 0) {
        setConfigSaveProgress(`Updating ${itemsToUpdate.length} item(s)...`);
      }
      for (const item of itemsToUpdate) {
        const itemId = item.id;
        const { id, ...updateData } = item;  // Remove id from update payload
        await axios.put(`${API}/projects/${projectId}/csi-items/${itemId}`, updateData);
        completedOperations++;
        setConfigSaveProgressPercent(35 + Math.round((completedOperations / totalOperations) * 50));
      }
      
      // 2. Delete removed items
      if (itemsToDelete.length > 0) {
        setConfigSaveProgress(`Removing ${itemsToDelete.length} item(s)...`);
      }
      for (const itemId of itemsToDelete) {
        await axios.delete(`${API}/projects/${projectId}/csi-items/${itemId}`);
        completedOperations++;
        setConfigSaveProgressPercent(35 + Math.round((completedOperations / totalOperations) * 50));
      }
      
      // 3. Create new items
      if (itemsToCreate.length > 0) {
        setConfigSaveProgress(`Creating ${itemsToCreate.length} new item(s)...`);
      }
      for (const item of itemsToCreate) {
        await axios.post(`${API}/projects/${projectId}/csi-items`, item);
        completedOperations++;
        setConfigSaveProgressPercent(35 + Math.round((completedOperations / totalOperations) * 50));
      }

      setConfigSaveProgress('Finalizing...');
      setConfigSaveProgressPercent(95);

      toast.success('Project configuration saved successfully');
      closeConfigModal();
      
      // Reset metadata states
      setConfigProjectImageFile(null);
      setConfigProjectImagePreview(null);
      setConfigProjectCroppedBlob(null);
      setConfigProjectImageRemoved(false);
      setItemNotes({});
      setHasUnsavedConfigChanges(false);
      
      setConfigSaveProgressPercent(100);
      fetchProjectData(); // Reload project data
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Failed to save configuration');
    } finally {
      setConfigSaving(false);
      setConfigSaveProgress('');
      setConfigSaveProgressPercent(0);
    }
  };
  
  // Discard changes and close modal
  const handleDiscardConfigChanges = () => {
    guardActiveRef.current = false;
    navigationGuard.onBlocked = null;
    setShowUnsavedConfigChangesModal(false);
    setHasUnsavedConfigChanges(false);
    setPendingNavPath(null);

    // Reset form states
    setConfigProjectImageFile(null);
    setConfigProjectImagePreview(null);
    setConfigProjectCroppedBlob(null);
    setConfigProjectImageRemoved(false);
    setItemNotes({});

    closeConfigModal();
  };
  
  // Continue editing
  const handleContinueConfigEditing = () => {
    setShowUnsavedConfigChangesModal(false);
    setPendingNavPath(null); // Clear any pending nav from the same interaction
  };


  // --- Navigation Guard: unsaved config changes ---
  // Strategy: NO history manipulation at all (pushState / forward corrupts
  // React Router state). Instead we:
  //   1. Register a beforeunload handler for browser refresh/close.
  //   2. Register the module-level navigationGuard.onBlocked for Header links.
  //   3. Detect "modal closed while dirty" (browser back) via a useEffect
  //      and show the ConfirmDialog, re-opening the modal on "Stay".
  useEffect(() => {
    const isActive = showConfigModal && hasUnsavedConfigChanges;
    guardActiveRef.current = isActive;

    if (!isActive) {
      navigationGuard.onBlocked = null;
      return;
    }

    // In-app navigation guard (Header, etc.)
    navigationGuard.onBlocked = (path) => {
      setPendingNavPath(path);
    };

    // Browser refresh/close guard
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      navigationGuard.onBlocked = null;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [showConfigModal, hasUnsavedConfigChanges]);

  // Detect "modal closed while dirty" – this fires when the browser back
  // button removes ?config from the URL while there are unsaved changes.
  const prevShowConfigRef = useRef(showConfigModal);
  useEffect(() => {
    const wasOpen = prevShowConfigRef.current;
    prevShowConfigRef.current = showConfigModal;

    if (wasOpen && !showConfigModal && hasUnsavedConfigChanges) {
      // Modal just closed but there are unsaved changes (browser back).
      setPendingNavPath('__BACK__');
    }
  }, [showConfigModal, hasUnsavedConfigChanges]);

  const handleNavStay = () => {
    const target = pendingNavPath;
    setPendingNavPath(null);
    if (target === '__BACK__') {
      // Re-open the config modal – the back already removed ?config,
      // so push a new entry with ?config to restore the modal.
      openConfigModal(configTab || 'details');
    }
  };

  const handleNavLeave = () => {
    const target = pendingNavPath;
    setPendingNavPath(null);
    guardActiveRef.current = false;
    navigationGuard.onBlocked = null;
    setHasUnsavedConfigChanges(false);

    // Reset form states
    setConfigProjectImageFile(null);
    setConfigProjectImagePreview(null);
    setConfigProjectCroppedBlob(null);
    setConfigProjectImageRemoved(false);
    setItemNotes({});

    if (target === '__BACK__') {
      // Modal is already closed (back navigation already happened).
      // Nothing more to do – user is already on the previous page.
    } else if (target) {
      closeConfigModal();
      requestAnimationFrame(() => navigate(target));
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  const handleElevationUpload = async (e, elevationType = 'window') => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF, JPG, JPEG, and PNG files are allowed');
      return;
    }

    const isPDF = file.type === 'application/pdf';
    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    const label = getEntityLabel(elevationType);
    const labelPlural = getEntityLabel(elevationType, true);

    setUploadingElevation(true);
    setUploadProgress(isPDF ? 'Uploading PDF...' : 'Uploading image...');
    setUploadProgressPercent(0);
    
    // Processing progress simulation interval - can be removed as a block
    let processingInterval = null;
    const startProcessingProgress = () => {
      let currentPercent = 60;
      processingInterval = setInterval(() => {
        currentPercent += Math.random() * 5 + 2; // Random increment 2-7%
        if (currentPercent >= 95) {
          currentPercent = 95; // Cap at 95% until actually done
          clearInterval(processingInterval);
        }
        setUploadProgressPercent(Math.round(currentPercent));
      }, 500);
    };
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (isPDF) {
        // PDF Flow: Upload PDF and get page previews
        const responsePromise = axios.post(`${API}/projects/${projectId}/elevations/preview`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const uploadPercent = Math.round((progressEvent.loaded * 60) / progressEvent.total);
            setUploadProgressPercent(uploadPercent);
            setUploadProgress(`Uploading PDF... ${Math.round((progressEvent.loaded * 100) / progressEvent.total)}%`);
            
            if (progressEvent.loaded === progressEvent.total && !processingInterval) {
              setUploadProgress('Processing pages...');
              startProcessingProgress();
            }
          }
        });
        
        const response = await responsePromise;
        
        if (processingInterval) clearInterval(processingInterval);
        setUploadProgressPercent(100);

        // Show page selector modal with preview data
        setElevationPreviewData({
          ...response.data,
          elevationType,
          csiItemId: activeCsiTab
        });
        setSelectedElevationPages(response.data.pages.map((_, idx) => idx));
        setElevationPageNames({});
        setShowElevationPageSelector(true);
        
        toast.success(`PDF loaded. Please select pages to create as ${labelPlural}.`);
      } else if (isImage) {
        // Image Flow: Direct upload as single elevation
        formData.append('type', elevationType);
        formData.append('csiItemId', activeCsiTab);  // Add CSI item ID for proper filtering
        
        const responsePromise = axios.post(`${API}/projects/${projectId}/elevations/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const uploadPercent = Math.round((progressEvent.loaded * 60) / progressEvent.total);
            setUploadProgressPercent(uploadPercent);
            setUploadProgress(`Uploading image... ${Math.round((progressEvent.loaded * 100) / progressEvent.total)}%`);
            
            if (progressEvent.loaded === progressEvent.total && !processingInterval) {
              setUploadProgress('Processing image...');
              startProcessingProgress();
            }
          }
        });
        
        const response = await responsePromise;
        
        if (processingInterval) clearInterval(processingInterval);
        setUploadProgressPercent(100);

        // Refresh project data to show new elevation
        const projectResponse = await axios.get(`${API}/projects/${projectId}`);
        setProject(projectResponse.data);
        
        toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} "${response.data.elevation.name}" created successfully`);
      }
    } catch (error) {
      toast.error(isPDF ? 'Failed to process PDF' : `Failed to upload ${label}`);
      console.error('Upload error:', error);
    } finally {
      if (processingInterval) clearInterval(processingInterval);
      setUploadingElevation(false);
      setUploadProgress('');
      setUploadProgressPercent(0);
      e.target.value = '';
    }
  };

  const handleCreateElevationsFromPages = async () => {
    if (!elevationPreviewData || selectedElevationPages.length === 0) {
      toast.error('Please select at least one page');
      return;
    }

    // Validate that all selected pages have names
    const missingNames = selectedElevationPages.filter(pageIdx => {
      const name = elevationPageNames[pageIdx];
      return !name || name.trim() === '';
    });

    if (missingNames.length > 0) {
      toast.error('Please provide names for all selected pages');
      return;
    }

    const createLabel = getEntityLabel(elevationPreviewData?.elevationType, true);

    setUploadingElevation(true);
    setUploadProgress(`Creating ${selectedElevationPages.length} ${createLabel}...`);
    setUploadProgressPercent(0);
    
    // Processing progress simulation interval - can be removed as a block
    let processingInterval = null;
    const startProcessingProgress = () => {
      let currentPercent = 60;
      processingInterval = setInterval(() => {
        currentPercent += Math.random() * 3 + 1; // Slower increment 1-4%
        if (currentPercent >= 95) {
          currentPercent = 95;
          clearInterval(processingInterval);
        }
        setUploadProgressPercent(Math.round(currentPercent));
      }, 400);
    };
    
    try {
      // Step 2: Create elevations from selected pages
      const formData = new FormData();
      formData.append('session_id', elevationPreviewData.sessionId);
      formData.append('selected_pages', JSON.stringify(selectedElevationPages));
      formData.append('elevation_type', elevationPreviewData.elevationType);
      formData.append('csi_item_id', elevationPreviewData.csiItemId);
      formData.append('elevation_names', JSON.stringify(elevationPageNames));

      // Progress bar tracking - upload is 0-60%, processing is 60-100%
      const responsePromise = axios.post(`${API}/projects/${projectId}/elevations/create-from-pages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          // Scale upload progress to 0-60%
          const uploadPercent = Math.round((progressEvent.loaded * 60) / progressEvent.total);
          setUploadProgressPercent(uploadPercent);
          setUploadProgress(`Uploading... ${Math.round((progressEvent.loaded * 100) / progressEvent.total)}%`);
          
          // Start processing simulation when upload reaches 100%
          if (progressEvent.loaded === progressEvent.total && !processingInterval) {
            setUploadProgress(`Creating ${selectedElevationPages.length} ${createLabel}...`);
            startProcessingProgress();
          }
        }
      });
      
      await responsePromise;
      
      // Complete
      if (processingInterval) clearInterval(processingInterval);
      setUploadProgressPercent(100);

      toast.success(`Created ${selectedElevationPages.length} ${createLabel} successfully`);
      setShowElevationPageSelector(false);
      setElevationPreviewData(null);
      setSelectedElevationPages([]);
      setElevationPageNames({});
      fetchProjectData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to create ${createLabel}`));
      console.error('Creation error:', error);
    } finally {
      if (processingInterval) clearInterval(processingInterval);
      setUploadingElevation(false);
      setUploadProgress('');
      setUploadProgressPercent(0);
    }
  };

  const toggleElevationPageSelection = (pageIndex) => {
    setSelectedElevationPages(prev => {
      if (prev.includes(pageIndex)) {
        return prev.filter(idx => idx !== pageIndex);
      } else {
        return [...prev, pageIndex];
      }
    });
  };

  const updateElevationPageName = (pageIndex, name) => {
    setElevationPageNames(prev => ({
      ...prev,
      [pageIndex]: name
    }));
  };

  // Zoom controls for elevation images
  const handleZoomIn = () => {
    setElevationZoom(prev => Math.min(prev + 0.25, 3)); // Max 3x zoom
  };

  const handleZoomOut = () => {
    setElevationZoom(prev => Math.max(prev - 0.25, 0.5)); // Min 0.5x zoom
  };

  const handleResetZoom = () => {
    setElevationZoom(1);
    setElevationPan({ x: 0, y: 0 });
  };

  // Pan controls for zoomed images
  const [panStartPos, setPanStartPos] = useState(null);
  
  const handlePanStart = (e) => {
    if (elevationZoom > 1) {
      setPanStartPos({ x: e.clientX, y: e.clientY });
      setPanStart({
        x: e.clientX - elevationPan.x,
        y: e.clientY - elevationPan.y
      });
    }
  };

  const handlePanMove = (e) => {
    if (panStartPos && elevationZoom > 1) {
      // Check if mouse moved more than 5px (to distinguish click from drag)
      const deltaX = Math.abs(e.clientX - panStartPos.x);
      const deltaY = Math.abs(e.clientY - panStartPos.y);
      
      if (deltaX > 5 || deltaY > 5) {
        setIsPanning(true);
        setElevationPan({
          x: e.clientX - panStart.x,
          y: e.clientY - panStart.y
        });
      }
    }
  };

  const handlePanEnd = () => {
    setPanStartPos(null);
    // Delay isPanning reset to allow click detection
    setTimeout(() => {
      setIsPanning(false);
    }, 50);
  };

  // Reset zoom when changing elevations
  const handleElevationClick = (elevation, context = 'window') => {
    setElevationZoom(1);
    setElevationPan({ x: 0, y: 0 });
    
    if (context === 'curtainwall') {
      setSelectedCurtainwallElevation(elevation);
      setMarkerContext('curtainwall');
    } else if (context === 'storefront') {
      setSelectedStorefrontElevation(elevation);
      setMarkerContext('storefront');
    } else {
      setSelectedWindowElevation(elevation);
      setMarkerContext('window');
    }
    // Set selectedElevation to point to the correct one based on context
    setSelectedElevation(elevation);
    setCurrentPageIndex(0);
    
    // Update URL with elevation ID for state persistence on refresh
    // Don't use replace: true so browser back button works correctly
    navigate(`/project/${projectId}/${activeCsiTab}/${activeSubTab}/${elevation.id}`);
  };

  // This function is now defined above with zoom reset

  const handleImageClick = (e, contextOverride = null) => {
    // Close any open marker popup first
    setActiveMarkerPopup(null);
    
    // Use contextOverride if provided (for synchronous context setting)
    // This fixes the issue where setMarkerContext is async and hasn't updated yet
    const effectiveContext = contextOverride || markerContext;
    
    // Get the correct elevation based on context
    let currentElevation;
    if (effectiveContext === 'curtainwall') {
      currentElevation = selectedCurtainwallElevation;
    } else if (effectiveContext === 'storefront') {
      currentElevation = selectedStorefrontElevation;
    } else if (effectiveContext === 'door') {
      currentElevation = selectedDoorElevation;
    } else if (effectiveContext === 'mirror') {
      currentElevation = selectedMirrorElevation;
    } else if (effectiveContext === 'shower') {
      currentElevation = selectedShowerElevation;
    } else {
      currentElevation = selectedWindowElevation;
    }
    if (!currentElevation) return;
    
    // Get the image element directly
    const imgElement = e.currentTarget;
    const rect = imgElement.getBoundingClientRect();
    
    // Get click position relative to the viewport
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Get the natural (original) size of the image
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    
    // Current displayed size (after zoom)
    const displayedWidth = rect.width;
    const displayedHeight = rect.height;
    
    // Calculate the position in the original image
    // Scale back from displayed size to natural size
    const imageX = (clickX / displayedWidth) * naturalWidth;
    const imageY = (clickY / displayedHeight) * naturalHeight;
    
    // Convert to percentage of original image size
    const x = (imageX / naturalWidth) * 100;
    const y = (imageY / naturalHeight) * 100;
    
    setPendingMarker({ x, y, pageIndex: 0 });  // Each elevation is single page
    setShowMarkerDialog(true);
  };

  const handleAddMarker = async () => {
    if (!selectedWindowForMarker || !pendingMarker) return;
    
    // Get the correct elevation based on context
    let currentElevation;
    if (markerContext === 'curtainwall') {
      currentElevation = selectedCurtainwallElevation;
    } else if (markerContext === 'storefront') {
      currentElevation = selectedStorefrontElevation;
    } else if (markerContext === 'door') {
      currentElevation = selectedDoorElevation;
    } else if (markerContext === 'mirror') {
      currentElevation = selectedMirrorElevation;
    } else if (markerContext === 'shower') {
      currentElevation = selectedShowerElevation;
    } else {
      currentElevation = selectedWindowElevation;
    }
    if (!currentElevation) {
      toast.error(`No ${getEntityLabel(markerContext)} selected`);
      return;
    }
    
    try {
      if (markerContext === 'curtainwall') {
        // For curtainwall markers - using CW Type
        const cwType = curtainwallTypes.find(cw => cw.id === selectedWindowForMarker);
        if (!cwType) {
          toast.error('Curtainwall type not found');
          return;
        }
        
        await axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers`,
          {
            x: pendingMarker.x,
            y: pendingMarker.y,
            width: 5.0,
            height: 8.0,
            windowListId: selectedWindowForMarker, // CW Type ID
            windowName: cwType.name, // Type name
            pageIndex: pendingMarker.pageIndex
          }
        );
        
        toast.success('Curtainwall type marker added successfully');
      } else if (markerContext === 'storefront') {
        // For storefront markers - using SF Schedule (operational record)
        const sfSchedule = storefrontPanelList.find(p => p.id === selectedWindowForMarker);
        if (!sfSchedule) {
          toast.error('Storefront schedule not found');
          return;
        }
        
        await axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers`,
          {
            x: pendingMarker.x,
            y: pendingMarker.y,
            width: 5.0,
            height: 8.0,
            windowListId: selectedWindowForMarker, // SF Schedule ID
            windowName: sfSchedule.label, // Schedule label
            pageIndex: pendingMarker.pageIndex
          }
        );
        
        toast.success('Storefront schedule marker added successfully');
      } else if (markerContext === 'door') {
        // For door markers - using Door Schedules
        const doorItem = doorSchedules.find(d => d.id === selectedWindowForMarker);
        if (!doorItem) {
          toast.error('Door schedule not found');
          return;
        }
        
        await axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers`,
          {
            x: pendingMarker.x,
            y: pendingMarker.y,
            width: 5.0,
            height: 8.0,
            windowListId: selectedWindowForMarker, // Door Schedule ID
            windowName: doorItem.label, // Schedule label
            pageIndex: pendingMarker.pageIndex
          }
        );
        
        toast.success('Door schedule marker added successfully');
      } else if (markerContext === 'mirror') {
        // For mirror markers - using Mirror Schedules
        const mirrorItem = mirrorSchedules.find(m => m.id === selectedWindowForMarker);
        if (!mirrorItem) {
          toast.error('Mirror schedule not found');
          return;
        }
        
        await axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers`,
          {
            x: pendingMarker.x,
            y: pendingMarker.y,
            width: 5.0,
            height: 8.0,
            windowListId: selectedWindowForMarker, // Mirror Schedule ID
            windowName: mirrorItem.label, // Schedule label
            pageIndex: pendingMarker.pageIndex
          }
        );
        
        toast.success('Mirror schedule marker added successfully');
      } else if (markerContext === 'shower') {
        const showerItem = showerSchedules.find(s => s.id === selectedWindowForMarker);
        if (!showerItem) {
          toast.error('Shower schedule not found');
          return;
        }
        await axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers`,
          {
            x: pendingMarker.x, y: pendingMarker.y, width: 5.0, height: 8.0,
            windowListId: selectedWindowForMarker,
            windowName: showerItem.label,
            pageIndex: pendingMarker.pageIndex
          }
        );
        toast.success('Shower schedule marker added successfully');
      } else {
        // For window markers
        const windowItem = windowList.find(w => w.id === selectedWindowForMarker);
        if (!windowItem) {
          toast.error('Window not found');
          return;
        }
        
        await axios.post(
          `${API}/projects/${projectId}/elevations/${selectedElevation.id}/markers`,
          {
            x: pendingMarker.x,
            y: pendingMarker.y,
            width: 5.0,
            height: 8.0,
            windowListId: selectedWindowForMarker,
            windowName: windowItem.label,
            pageIndex: pendingMarker.pageIndex
          }
        );
        
        toast.success('Window marker added successfully');
      }
      
      // Refresh data and update selected elevation
      await fetchProjectData();
      
      // Update selected elevation with fresh data
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      const updatedElevation = projectRes.data.elevations?.find(e => e.id === currentElevation.id);
      if (updatedElevation) {
        setSelectedElevation(updatedElevation);
        if (markerContext === 'curtainwall') {
          setSelectedCurtainwallElevation(updatedElevation);
        } else if (markerContext === 'storefront') {
          setSelectedStorefrontElevation(updatedElevation);
        } else if (markerContext === 'door') {
          setSelectedDoorElevation(updatedElevation);
        } else if (markerContext === 'mirror') {
          setSelectedMirrorElevation(updatedElevation);
        } else if (markerContext === 'shower') {
          setSelectedShowerElevation(updatedElevation);
        } else {
          setSelectedWindowElevation(updatedElevation);
        }
      }
      
      setShowMarkerDialog(false);
      setPendingMarker(null);
      setSelectedWindowForMarker('');
    } catch (error) {
      const errorDetail = error.response?.data?.detail || `Failed to add marker`;
      toast.error(errorDetail);
      console.error('Marker error:', error);
    }
  };

  // Bulk marker functions for Windows Elevation
  const handleBulkMarkerClick = (e, type = 'window') => {
    // For window type
    if (type === 'window') {
      if (!bulkMarkerMode || !bulkMarkerSchedule || !selectedWindowElevation) return;
      
      const rect = e.target.getBoundingClientRect();
      const imageX = (e.clientX - rect.left) / elevationZoom;
      const imageY = (e.clientY - rect.top) / elevationZoom;
      
      const displayedWidth = e.target.width;
      const displayedHeight = e.target.height;
      
      const x = (imageX / displayedWidth) * 100;
      const y = (imageY / displayedHeight) * 100;
      
      const selectedSchedule = windowList.find(w => w.id === bulkMarkerSchedule);
      const windowType = selectedSchedule ? windowTypes.find(wt => wt.id === selectedSchedule.windowTypeId) : null;
      const markerColor = windowType?.markerColor || '#39FF14';
      
      const newMarker = {
        id: `temp-${Date.now()}-${Math.random()}`,
        x,
        y,
        width: 5.0,
        height: 8.0,
        windowListId: bulkMarkerSchedule,
        windowName: selectedSchedule?.label || '',
        pageIndex: currentPageIndex,
        color: markerColor
      };
      
      setPendingBulkMarkers(prev => [...prev, newMarker]);
    } 
    // For door type
    else if (type === 'door') {
      if (!doorBulkMarkerMode || !doorBulkMarkerSchedule || !selectedDoorElevation) return;
      
      const rect = e.target.getBoundingClientRect();
      const imageX = (e.clientX - rect.left) / elevationZoom;
      const imageY = (e.clientY - rect.top) / elevationZoom;
      
      const displayedWidth = e.target.width;
      const displayedHeight = e.target.height;
      
      const x = (imageX / displayedWidth) * 100;
      const y = (imageY / displayedHeight) * 100;
      
      const selectedSchedule = doorSchedules.find(d => d.id === doorBulkMarkerSchedule);
      const doorType = selectedSchedule ? doorTypes.find(dt => dt.id === selectedSchedule.doorTypeId) : null;
      const markerColor = doorType?.markerColor || '#39FF14';
      
      const newMarker = {
        id: `temp-${Date.now()}-${Math.random()}`,
        x,
        y,
        width: 5.0,
        height: 8.0,
        windowListId: doorBulkMarkerSchedule,
        windowName: selectedSchedule?.label || '',
        pageIndex: currentPageIndex,
        color: markerColor
      };
      
      setPendingDoorBulkMarkers(prev => [...prev, newMarker]);
    }
    // For mirror type
    else if (type === 'mirror') {
      if (!mirrorBulkMarkerMode || !mirrorBulkMarkerSchedule || !selectedMirrorElevation) return;
      
      const rect = e.target.getBoundingClientRect();
      const imageX = (e.clientX - rect.left) / elevationZoom;
      const imageY = (e.clientY - rect.top) / elevationZoom;
      
      const naturalWidth = e.target.naturalWidth;
      const naturalHeight = e.target.naturalHeight;
      
      const x = (imageX / (rect.width / elevationZoom)) * 100;
      const y = (imageY / (rect.height / elevationZoom)) * 100;
      
      const currentPageIndex = 0; // Each elevation is single page
      
      const selectedSchedule = mirrorSchedules.find(m => m.id === mirrorBulkMarkerSchedule);
      const mirrorType = selectedSchedule ? mirrorTypes.find(mt => mt.id === selectedSchedule.mirrorTypeId) : null;
      const markerColor = mirrorType?.markerColor || '#3B82F6';
      
      const newMarker = {
        id: `temp-${Date.now()}-${Math.random()}`,
        x,
        y,
        width: 5.0,
        height: 8.0,
        windowListId: mirrorBulkMarkerSchedule,
        windowName: selectedSchedule?.label || '',
        pageIndex: currentPageIndex,
        color: markerColor
      };
      
      setPendingMirrorBulkMarkers(prev => [...prev, newMarker]);
    }
    else if (type === 'shower') {
      if (!showerBulkMarkerMode || !showerBulkMarkerSchedule || !selectedShowerElevation) return;
      
      const rect = e.target.getBoundingClientRect();
      const imageX = (e.clientX - rect.left) / elevationZoom;
      const imageY = (e.clientY - rect.top) / elevationZoom;
      
      const x = (imageX / (rect.width / elevationZoom)) * 100;
      const y = (imageY / (rect.height / elevationZoom)) * 100;
      
      const selectedSchedule = showerSchedules.find(s => s.id === showerBulkMarkerSchedule);
      const showerType = selectedSchedule ? showerTypes.find(st => st.id === selectedSchedule.showerTypeId) : null;
      const markerColor = showerType?.markerColor || '#10B981';
      
      const newMarker = {
        id: `temp-${Date.now()}-${Math.random()}`,
        x, y, width: 5.0, height: 8.0,
        windowListId: showerBulkMarkerSchedule,
        windowName: selectedSchedule?.label || '',
        pageIndex: 0,
        color: markerColor
      };
      
      setPendingShowerBulkMarkers(prev => [...prev, newMarker]);
    }
  };

  const handleSaveBulkMarkers = async (type = 'window') => {
    // For window type
    if (type === 'window') {
      if (pendingBulkMarkers.length === 0 || !selectedWindowElevation) {
        toast.error('No markers to save');
        return;
      }
      
      const currentElevationId = selectedWindowElevation.id;
      setSavingBulkMarkers(true);
      
      try {
        const savePromises = pendingBulkMarkers.map(marker => 
          axios.post(
            `${API}/projects/${projectId}/elevations/${currentElevationId}/markers`,
            {
              x: marker.x,
              y: marker.y,
              width: marker.width,
              height: marker.height,
              windowListId: marker.windowListId,
              windowName: marker.windowName,
              pageIndex: marker.pageIndex
            }
          )
        );
        
        await Promise.all(savePromises);
        
        toast.success(`${pendingBulkMarkers.length} marker(s) added successfully`);
        
        setPendingBulkMarkers([]);
        setBulkMarkerMode(false);
        setBulkMarkerSchedule('');
        
        const response = await axios.get(`${API}/projects/${projectId}`);
        const updatedProject = response.data;
        setProject(updatedProject);
        
        if (updatedProject.elevations) {
          const windowElevs = updatedProject.elevations.filter(e => e.type === 'window' || !e.type);
          setWindowElevations(windowElevs);
          
          const updatedElevation = windowElevs.find(e => e.id === currentElevationId);
          if (updatedElevation) {
            setSelectedWindowElevation(updatedElevation);
            setSelectedElevation(updatedElevation);
          }
        }
      } catch (error) {
        console.error('Error saving bulk markers:', error);
        const errorDetail = error.response?.data?.detail || 'Failed to save markers';
        toast.error(errorDetail);
      } finally {
        setSavingBulkMarkers(false);
      }
    }
    // For door type
    else if (type === 'door') {
      if (pendingDoorBulkMarkers.length === 0 || !selectedDoorElevation) {
        toast.error('No markers to save');
        return;
      }
      
      const currentElevationId = selectedDoorElevation.id;
      setSavingDoorBulkMarkers(true);
      
      try {
        const savePromises = pendingDoorBulkMarkers.map(marker => 
          axios.post(
            `${API}/projects/${projectId}/elevations/${currentElevationId}/markers`,
            {
              x: marker.x,
              y: marker.y,
              width: marker.width,
              height: marker.height,
              windowListId: marker.windowListId,
              windowName: marker.windowName,
              pageIndex: marker.pageIndex
            }
          )
        );
        
        await Promise.all(savePromises);
        
        toast.success(`${pendingDoorBulkMarkers.length} marker(s) added successfully`);
        
        setPendingDoorBulkMarkers([]);
        setDoorBulkMarkerMode(false);
        setDoorBulkMarkerSchedule('');
        
        const response = await axios.get(`${API}/projects/${projectId}`);
        const updatedProject = response.data;
        setProject(updatedProject);
        
        if (updatedProject.elevations) {
          const doorElevs = updatedProject.elevations.filter(e => e.type === 'door');
          setDoorElevations(doorElevs);
          
          const updatedElevation = doorElevs.find(e => e.id === currentElevationId);
          if (updatedElevation) {
            setSelectedDoorElevation(updatedElevation);
          }
        }
      } catch (error) {
        console.error('Error saving bulk markers:', error);
        const errorDetail = error.response?.data?.detail || 'Failed to save markers';
        toast.error(errorDetail);
      } finally {
        setSavingDoorBulkMarkers(false);
      }
    }
    // For mirror type
    else if (type === 'mirror') {
      if (pendingMirrorBulkMarkers.length === 0 || !selectedMirrorElevation) {
        toast.error('No markers to save');
        return;
      }
      
      const currentElevationId = selectedMirrorElevation.id;
      setSavingMirrorBulkMarkers(true);
      
      try {
        const savePromises = pendingMirrorBulkMarkers.map(marker => 
          axios.post(
            `${API}/projects/${projectId}/elevations/${currentElevationId}/markers`,
            {
              x: marker.x,
              y: marker.y,
              width: marker.width,
              height: marker.height,
              windowListId: marker.windowListId,
              windowName: marker.windowName,
              pageIndex: marker.pageIndex
            }
          )
        );
        
        await Promise.all(savePromises);
        
        toast.success(`${pendingMirrorBulkMarkers.length} marker(s) added successfully`);
        
        setPendingMirrorBulkMarkers([]);
        setMirrorBulkMarkerMode(false);
        setMirrorBulkMarkerSchedule('');
        
        const response = await axios.get(`${API}/projects/${projectId}`);
        const updatedProject = response.data;
        setProject(updatedProject);
        
        if (updatedProject.elevations) {
          const mirrorElevs = updatedProject.elevations.filter(e => e.type === 'mirror');
          setMirrorElevations(mirrorElevs);
          
          const updatedElevation = mirrorElevs.find(e => e.id === currentElevationId);
          if (updatedElevation) {
            setSelectedMirrorElevation(updatedElevation);
          }
        }
      } catch (error) {
        console.error('Error saving bulk markers:', error);
        const errorDetail = error.response?.data?.detail || 'Failed to save markers';
        toast.error(errorDetail);
      } finally {
        setSavingMirrorBulkMarkers(false);
      }
    }
    else if (type === 'shower') {
      if (pendingShowerBulkMarkers.length === 0 || !selectedShowerElevation) {
        toast.error('No markers to save');
        return;
      }
      const currentElevationId = selectedShowerElevation.id;
      setSavingShowerBulkMarkers(true);
      try {
        const savePromises = pendingShowerBulkMarkers.map(marker =>
          axios.post(`${API}/projects/${projectId}/elevations/${currentElevationId}/markers`, {
            x: marker.x, y: marker.y, width: marker.width, height: marker.height,
            windowListId: marker.windowListId, windowName: marker.windowName, pageIndex: marker.pageIndex
          })
        );
        await Promise.all(savePromises);
        toast.success(`${pendingShowerBulkMarkers.length} marker(s) added successfully`);
        setPendingShowerBulkMarkers([]);
        setShowerBulkMarkerMode(false);
        setShowerBulkMarkerSchedule('');
        const response = await axios.get(`${API}/projects/${projectId}`);
        const updatedProject = response.data;
        setProject(updatedProject);
        if (updatedProject.elevations) {
          const showerElevs = updatedProject.elevations.filter(e => e.type === 'shower');
          setShowerElevations(showerElevs);
          const updatedElevation = showerElevs.find(e => e.id === currentElevationId);
          if (updatedElevation) setSelectedShowerElevation(updatedElevation);
        }
      } catch (error) {
        console.error('Error saving bulk markers:', error);
        toast.error(error.response?.data?.detail || 'Failed to save markers');
      } finally {
        setSavingShowerBulkMarkers(false);
      }
    }
  };

  const handleCancelBulkMarkers = () => {
    setPendingBulkMarkers([]);
    setBulkMarkerMode(false);
    setBulkMarkerSchedule('');
  };

  const handleRemovePendingMarker = (markerId) => {
    setPendingBulkMarkers(prev => prev.filter(m => m.id !== markerId));
  };

  // Bulk marker functions for Curtain Walls Elevation
  const handleCwBulkMarkerClick = (e) => {
    if (!cwBulkMarkerMode || !cwBulkMarkerType || !selectedCurtainwallElevation) return;
    
    const rect = e.target.getBoundingClientRect();
    const imageX = (e.clientX - rect.left) / elevationZoom;
    const imageY = (e.clientY - rect.top) / elevationZoom;
    
    const displayedWidth = e.target.width;
    const displayedHeight = e.target.height;
    
    const x = (imageX / displayedWidth) * 100;
    const y = (imageY / displayedHeight) * 100;
    
    const selectedType = curtainwallTypes.find(t => t.id === cwBulkMarkerType);
    const markerColor = selectedType?.markerColor || '#39FF14';
    
    const newMarker = {
      id: `temp-${Date.now()}-${Math.random()}`,
      x,
      y,
      width: 5.0,
      height: 8.0,
      windowListId: cwBulkMarkerType,
      windowName: selectedType?.name || '',
      pageIndex: currentPageIndex,
      color: markerColor
    };
    
    setPendingCwBulkMarkers(prev => [...prev, newMarker]);
  };

  const handleSaveCwBulkMarkers = async () => {
    if (pendingCwBulkMarkers.length === 0 || !selectedCurtainwallElevation) {
      toast.error('No markers to save');
      return;
    }
    
    const currentElevationId = selectedCurtainwallElevation.id;
    setSavingCwBulkMarkers(true);
    
    try {
      const savePromises = pendingCwBulkMarkers.map(marker => 
        axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevationId}/markers`,
          {
            x: marker.x,
            y: marker.y,
            width: marker.width,
            height: marker.height,
            windowListId: marker.windowListId,
            windowName: marker.windowName,
            pageIndex: marker.pageIndex
          }
        )
      );
      
      await Promise.all(savePromises);
      
      toast.success(`${pendingCwBulkMarkers.length} marker(s) added successfully`);
      
      setPendingCwBulkMarkers([]);
      setCwBulkMarkerMode(false);
      setCwBulkMarkerType('');
      
      const response = await axios.get(`${API}/projects/${projectId}`);
      const updatedProject = response.data;
      setProject(updatedProject);
      
      if (updatedProject.elevations) {
        const cwElevs = updatedProject.elevations.filter(e => e.type === 'curtainwall');
        setCurtainwallElevations(cwElevs);
        
        const updatedElevation = cwElevs.find(e => e.id === currentElevationId);
        if (updatedElevation) {
          setSelectedCurtainwallElevation(updatedElevation);
        }
      }
    } catch (error) {
      console.error('Error saving CW bulk markers:', error);
      toast.error('Failed to save markers');
    } finally {
      setSavingCwBulkMarkers(false);
    }
  };

  // Bulk marker functions for Storefronts Elevation
  const handleSfBulkMarkerClick = (e) => {
    if (!sfBulkMarkerMode || !sfBulkMarkerSchedule || !selectedStorefrontElevation) return;
    
    const rect = e.target.getBoundingClientRect();
    const imageX = (e.clientX - rect.left) / elevationZoom;
    const imageY = (e.clientY - rect.top) / elevationZoom;
    
    const displayedWidth = e.target.width;
    const displayedHeight = e.target.height;
    
    const x = (imageX / displayedWidth) * 100;
    const y = (imageY / displayedHeight) * 100;
    
    const selectedSchedule = storefrontPanelList.find(p => p.id === sfBulkMarkerSchedule);
    const scheduleType = storefrontTypes.find(t => t.id === selectedSchedule?.storefrontTypeId);
    const markerColor = scheduleType?.markerColor || '#39FF14';
    
    const newMarker = {
      id: `temp-${Date.now()}-${Math.random()}`,
      x,
      y,
      width: 5.0,
      height: 8.0,
      windowListId: sfBulkMarkerSchedule,
      windowName: selectedSchedule?.label || '',
      pageIndex: currentPageIndex,
      color: markerColor
    };
    
    setPendingSfBulkMarkers(prev => [...prev, newMarker]);
  };

  const handleSaveSfBulkMarkers = async () => {
    if (pendingSfBulkMarkers.length === 0 || !selectedStorefrontElevation) {
      toast.error('No markers to save');
      return;
    }
    
    const currentElevationId = selectedStorefrontElevation.id;
    setSavingSfBulkMarkers(true);
    
    try {
      const savePromises = pendingSfBulkMarkers.map(marker => 
        axios.post(
          `${API}/projects/${projectId}/elevations/${currentElevationId}/markers`,
          {
            x: marker.x,
            y: marker.y,
            width: marker.width,
            height: marker.height,
            windowListId: marker.windowListId,
            windowName: marker.windowName,
            pageIndex: marker.pageIndex
          }
        )
      );
      
      await Promise.all(savePromises);
      
      toast.success(`${pendingSfBulkMarkers.length} marker(s) added successfully`);
      
      setPendingSfBulkMarkers([]);
      setSfBulkMarkerMode(false);
      setSfBulkMarkerSchedule('');
      
      const response = await axios.get(`${API}/projects/${projectId}`);
      const updatedProject = response.data;
      setProject(updatedProject);
      
      if (updatedProject.elevations) {
        const sfElevs = updatedProject.elevations.filter(e => e.type === 'storefront');
        setStorefrontElevations(sfElevs);
        
        const updatedElevation = sfElevs.find(e => e.id === currentElevationId);
        if (updatedElevation) {
          setSelectedStorefrontElevation(updatedElevation);
        }
      }
    } catch (error) {
      console.error('Error saving SF bulk markers:', error);
      toast.error('Failed to save markers');
    } finally {
      setSavingSfBulkMarkers(false);
    }
  };

  const handleDeleteMarker = async (markerId, contextOverride = null) => {
    // Use contextOverride if provided (for synchronous context setting)
    // This fixes the issue where setMarkerContext is async and hasn't updated yet
    const effectiveContext = contextOverride || markerContext;
    
    // Get correct elevation based on context
    let currentElevation;
    if (effectiveContext === 'curtainwall') {
      currentElevation = selectedCurtainwallElevation;
    } else if (effectiveContext === 'storefront') {
      currentElevation = selectedStorefrontElevation;
    } else if (effectiveContext === 'door') {
      currentElevation = selectedDoorElevation;
    } else if (effectiveContext === 'mirror') {
      currentElevation = selectedMirrorElevation;
    } else {
      currentElevation = selectedWindowElevation;
    }
    
    if (!currentElevation) return;
    
    try {
      await axios.delete(
        `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers/${markerId}`
      );
      
      let markerType = 'Window';
      if (effectiveContext === 'curtainwall') {
        markerType = 'Curtainwall';
      } else if (effectiveContext === 'storefront') {
        markerType = 'Storefront';
      } else if (effectiveContext === 'door') {
        markerType = 'Door';
      } else if (effectiveContext === 'mirror') {
        markerType = 'Mirror';
      }
      toast.success(`${markerType} marker deleted`);
      
      // Refresh data and update correct elevation
      await fetchProjectData();
      
      // Update the appropriate elevation state with fresh data
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      const updatedElevation = projectRes.data.elevations?.find(e => e.id === currentElevation.id);
      if (updatedElevation) {
        if (effectiveContext === 'curtainwall') {
          setSelectedCurtainwallElevation(updatedElevation);
        } else if (effectiveContext === 'storefront') {
          setSelectedStorefrontElevation(updatedElevation);
        } else if (effectiveContext === 'door') {
          setSelectedDoorElevation(updatedElevation);
        } else if (effectiveContext === 'mirror') {
          setSelectedMirrorElevation(updatedElevation);
        } else {
          setSelectedWindowElevation(updatedElevation);
        }
        setSelectedElevation(updatedElevation); // Keep for backward compatibility
      }
    } catch (error) {
      toast.error('Failed to delete marker');
    }
  };

  // Marker drag handlers
  const handleMarkerMouseDown = (e, marker) => {
    if (!canEditCSIItems) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Navigate up to find the image container (relative inline-block div)
    // Structure: marker div (absolute group) -> transform container (relative inline-block)
    let imageContainer = e.currentTarget.parentElement;
    
    // Verify we found the correct container (has inline-block class)
    if (!imageContainer || !imageContainer.classList.contains('inline-block')) {
      // Fallback: navigate up until we find it
      imageContainer = e.currentTarget;
      while (imageContainer && !imageContainer.classList.contains('inline-block')) {
        imageContainer = imageContainer.parentElement;
      }
    }
    
    const rect = imageContainer ? imageContainer.getBoundingClientRect() : e.currentTarget.parentElement.getBoundingClientRect();
    const offsetX = ((e.clientX - rect.left) / rect.width) * 100 - marker.x;
    const offsetY = ((e.clientY - rect.top) / rect.height) * 100 - marker.y;
    
    setDraggingMarker(marker);
    setDragOffset({ x: offsetX, y: offsetY });
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setIsDragging(false);
  };

  const handleMarkerMouseMove = (e) => {
    if (!draggingMarker) return;
    e.preventDefault();
    
    // Check if mouse moved enough to be considered dragging
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - dragStartPos.x, 2) + Math.pow(e.clientY - dragStartPos.y, 2)
    );
    
    if (moveDistance > 5) {
      setIsDragging(true);
    }
    
    // Only update position if we're actually dragging
    if (!isDragging && moveDistance <= 5) return;
    
    const imageContainer = e.currentTarget;
    const rect = imageContainer.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
    const y = ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y;
    
    // Constrain to image bounds
    const constrainedX = Math.max(0, Math.min(100, x));
    const constrainedY = Math.max(0, Math.min(100, y));
    
    // Update marker position in the correct elevation state
    const updateElevationMarkers = (setter) => {
      setter(prev => {
        if (!prev || !prev.markers) return prev;
        return {
          ...prev,
          markers: prev.markers.map(m => 
            m.id === draggingMarker.id 
              ? { ...m, x: constrainedX, y: constrainedY }
              : m
          )
        };
      });
    };

    if (markerContext === 'curtainwall') {
      updateElevationMarkers(setSelectedCurtainwallElevation);
    } else if (markerContext === 'storefront') {
      updateElevationMarkers(setSelectedStorefrontElevation);
    } else if (markerContext === 'door') {
      updateElevationMarkers(setSelectedDoorElevation);
    } else if (markerContext === 'mirror') {
      updateElevationMarkers(setSelectedMirrorElevation);
    } else if (markerContext === 'shower') {
      updateElevationMarkers(setSelectedShowerElevation);
    } else {
      updateElevationMarkers(setSelectedWindowElevation);
    }
    
    // Also update selectedElevation for backward compatibility
    setSelectedElevation(prev => {
      if (!prev || !prev.markers) return prev;
      return {
        ...prev,
        markers: prev.markers.map(m => 
          m.id === draggingMarker.id 
            ? { ...m, x: constrainedX, y: constrainedY }
            : m
        )
      };
    });
  };

  const handleMarkerMouseUp = async () => {
    if (!draggingMarker) return;
    
    const wasDragging = isDragging;
    
    // Immediately stop dragging to prevent further movement
    setIsDragging(false);
    setDraggingMarker(null);
    setDragOffset({ x: 0, y: 0 });
    
    if (wasDragging) {
      // Prevent marker-box click from firing after drag
      dragJustEndedRef.current = true;
      setTimeout(() => { dragJustEndedRef.current = false; }, 200);
      try {
        // Get correct elevation based on context
        let currentElevation;
        if (markerContext === 'curtainwall') {
          currentElevation = selectedCurtainwallElevation;
        } else if (markerContext === 'storefront') {
          currentElevation = selectedStorefrontElevation;
        } else if (markerContext === 'door') {
          currentElevation = selectedDoorElevation;
        } else if (markerContext === 'mirror') {
          currentElevation = selectedMirrorElevation;
        } else if (markerContext === 'shower') {
          currentElevation = selectedShowerElevation;
        } else {
          currentElevation = selectedWindowElevation;
        }
        
        if (!currentElevation || !currentElevation.markers) {
          toast.error(`No ${getEntityLabel(markerContext)} selected`);
          return;
        }
        
        // Find the updated marker position
        const updatedMarker = currentElevation.markers.find(m => m.id === draggingMarker.id);
        
        if (updatedMarker) {
          // Send update to backend
          await axios.put(
            `${API}/projects/${projectId}/elevations/${currentElevation.id}/markers/${draggingMarker.id}`,
            {
              x: updatedMarker.x,
              y: updatedMarker.y
            }
          );
          
          toast.success('Marker position updated');
          
          // Refresh data
          await fetchProjectData();
          const projectRes = await axios.get(`${API}/projects/${projectId}`);
          const updatedElevation = projectRes.data.elevations?.find(e => e.id === currentElevation.id);
          if (updatedElevation) {
            if (markerContext === 'curtainwall') {
              setSelectedCurtainwallElevation(updatedElevation);
            } else if (markerContext === 'storefront') {
              setSelectedStorefrontElevation(updatedElevation);
            } else if (markerContext === 'door') {
              setSelectedDoorElevation(updatedElevation);
            } else if (markerContext === 'mirror') {
              setSelectedMirrorElevation(updatedElevation);
            } else if (markerContext === 'shower') {
              setSelectedShowerElevation(updatedElevation);
            } else {
              setSelectedWindowElevation(updatedElevation);
            }
            setSelectedElevation(updatedElevation);
          }
        }
      } catch (error) {
        toast.error('Failed to update marker position');
      }
    }
  };

  // Marker click to open Window Card (Print Preview)
  const handleMarkerClick = async (e, marker, contextOverride = null) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Use contextOverride if provided (for synchronous context setting)
    // This fixes the issue where setMarkerContext is async and hasn't updated yet
    const effectiveContext = contextOverride || markerContext;
    
    if (effectiveContext === 'curtainwall') {
      // For curtainwall markers - show CW Type details
      const cwType = curtainwallTypes.find(cw => cw.id === marker.windowListId);
      
      if (cwType) {
        // Open CW Type print preview or detail view
        handleOpenPanelPrintPreview(cwType.id, 'curtainwall');
      } else {
        // Fallback to panel list for backward compatibility
        const cwPanel = panelList.find(p => p.id === marker.windowListId);
        if (cwPanel) {
          handleOpenPanelPrintPreview(cwPanel.id, 'curtainwall');
        } else {
          toast.error('Curtainwall type not found');
        }
      }
    } else if (effectiveContext === 'storefront') {
      // For storefront markers - open the Storefront Schedule PDF (same output as
      // Storefronts > Schedule > Print Label)
      const sfSchedule = storefrontPanelList.find(p => p.id === marker.windowListId);
      
      if (sfSchedule) {
        handleOpenPanelPrintPreview(sfSchedule.id, 'storefront');
      } else {
        // Legacy markers stored a Storefront Type id; resolve it when unambiguous
        const legacySchedules = storefrontPanelList.filter(p => p.storefrontTypeId === marker.windowListId);
        if (legacySchedules.length === 1) {
          handleOpenPanelPrintPreview(legacySchedules[0].id, 'storefront');
        } else if (legacySchedules.length > 1) {
          toast.error('This legacy marker points to a Storefront Type with multiple schedules. Re-mark it against a schedule to view its card.');
        } else {
          toast.error('Storefront schedule not found');
        }
      }
    } else if (effectiveContext === 'door') {
      // For door markers - show door schedule details
      const doorItem = doorSchedules.find(d => d.id === marker.windowListId);
      
      if (doorItem) {
        // Open door detail modal
        setSelectedDoorDetail(doorItem);
        setShowDoorDetailDialog(true);
      } else {
        toast.error('Door schedule not found');
      }
    } else if (effectiveContext === 'mirror') {
      // For mirror markers - show Mirror Card (PDF)
      const mirrorItem = mirrorSchedules.find(m => m.id === marker.windowListId);
      
      if (mirrorItem) {
        // Open the print preview dialog (Mirror Card) - same as Window Card
        try {
          const response = await axios.get(`${API}/mirror-schedules/${mirrorItem.id}/print-label`, {
            responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          setPrintPreviewUrl(url);
          setPrintPreviewType('mirror');
          setShowPrintPreviewDialog(true);
        } catch (error) {
          toast.error('Failed to generate Mirror Card');
          console.error('Print error:', error);
        }
      } else {
        toast.error('Mirror schedule not found');
      }
    } else {
      // For window markers - show window card
      const windowItem = windowList.find(w => w.id === marker.windowListId);
      
      if (windowItem) {
        // Open the print preview dialog (Window Card)
        try {
          const response = await axios.get(`${API}/window-list/${windowItem.id}/print-label`, {
            responseType: 'blob'
          });
          
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          setPrintPreviewUrl(url);
          setPrintPreviewType('window');
          setShowPrintPreviewDialog(true);
        } catch (error) {
          toast.error('Failed to generate Window Card');
          console.error('Print error:', error);
        }
      }
    }
  };

  // Window Glass Type CRUD operations
  const handleCreateWindowGlassType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (windowOperationLoading) return;
    
    setWindowOperationLoading(true);
    try {
      const payload = {
        ...newWindowGlassType,
        gridSize_in: newWindowGlassType.hasGrid ? parseNumericInput(newWindowGlassType.gridSize_in) : null,
        warrantyYears: parseIntegerInput(newWindowGlassType.warrantyYears),
      };
      if (editingGlassType) {
        await axios.put(`${API}/window-glass-types/${editingGlassType.id}`, {
          ...payload,
          projectId
        });
        toast.success('Glass type updated');
      } else {
        await axios.post(`${API}/window-glass-types`, {
          ...payload,
          projectId,
          csiItemId: activeCsiTab  // Link to current CSI item instance
        });
        toast.success('Glass type created');
      }
      
      setShowGlassTypeDialog(false);
      setEditingGlassType(null);
      setNewWindowGlassType({
        name: '',
        glassManufacturer: '',
        glassModel: '',
        glassType: '',
        glassBuild: '',
        glassThickness_in: null,
        colorCode: '',
        glassTypes: [],
        hasGrid: false,
        gridSize_in: '',
        warrantyYears: ''
      });
      fetchProjectData();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error('Failed to save glass type');
      }
    } finally {
      setWindowOperationLoading(false);
    }
  };

  const handleEditWindowGlassType = (glassType) => {
    setEditingGlassType(glassType);
    setNewWindowGlassType({
      name: glassType.name || '',
      glassManufacturer: glassType.glassManufacturer || '',
      glassModel: glassType.glassModel || '',
      glassType: glassType.glassType || '',
      glassBuild: glassType.glassBuild || '',
      glassThickness_in: glassType.glassThickness_in || null,
      colorCode: glassType.colorCode || '',
      glassTypes: glassType.glassTypes || [],
      hasGrid: glassType.hasGrid || false,
      gridSize_in: glassType.gridSize_in?.toString() ?? '',
      warrantyYears: glassType.warrantyYears?.toString() ?? ''
    });
    setShowGlassTypeDialog(true);
  };

  const confirmDeleteWindowGlassType = (glassType) => {
    setGlassTypeToDelete(glassType);
    setShowDeleteGlassTypeDialog(true);
  };

  const handleDeleteWindowGlassType = async () => {
    if (!glassTypeToDelete) return;
    
    try {
      await axios.delete(`${API}/window-glass-types/${glassTypeToDelete.id}`);
      toast.success('Glass type deleted');
      setShowDeleteGlassTypeDialog(false);
      setGlassTypeToDelete(null);
      fetchProjectData();
    } catch (error) {
      // Glass type stays in the list; the server explains which records still use it
      toast.error(describeGlassTypeConflict(error), { duration: 9000 });
      setShowDeleteGlassTypeDialog(false);
      setGlassTypeToDelete(null);
    }
  };

  const handleCloseWindowGlassTypeDialog = (open) => {
    setShowGlassTypeDialog(open);
    if (!open) {
      setEditingGlassType(null);
      setNewWindowGlassType({
        name: '',
        glassManufacturer: '',
        glassModel: '',
        glassType: '',
        glassBuild: '',
        glassThickness_in: null,
        colorCode: '',
        glassTypes: [],
        hasGrid: false,
        gridSize_in: '',
        warrantyYears: ''
      });
    }
  };

  // Generate colors for window types
  const getWindowTypeColor = (windowTypeId) => {
    const index = windowTypes.findIndex(wt => wt.id === windowTypeId);
    const colors = [
      '#3B82F6', // blue
      '#10B981', // green
      '#F59E0B', // orange
      '#EF4444', // red
      '#8B5CF6', // purple
      '#EC4899', // pink
      '#14B8A6', // teal
      '#F97316', // orange-500
    ];
    return colors[index % colors.length];
  };

  const handleDeleteElevation = async () => {
    if (!elevationToDelete) return;
    
    const deleteLabel = getEntityLabel(elevationToDelete.type);
    const elevationName = elevationToDelete.name || (deleteLabel.charAt(0).toUpperCase() + deleteLabel.slice(1));
    setShowDeleteElevationDialog(false); // Close dialog immediately
    setDeletingSingleElevation(true);
    setUploadProgress(`Deleting "${elevationName}"...`);
    setUploadProgressPercent(0);
    
    // Simulated progress for delete operation - can be removed as a block
    let progressInterval = setInterval(() => {
      setUploadProgressPercent(prev => {
        const next = prev + Math.random() * 15 + 5;
        return next >= 90 ? 90 : Math.round(next);
      });
    }, 200);
    
    try {
      await axios.delete(
        `${API}/projects/${projectId}/elevations/${elevationToDelete.id}`
      );
      
      clearInterval(progressInterval);
      setUploadProgressPercent(100);
      
      toast.success(`${deleteLabel.charAt(0).toUpperCase() + deleteLabel.slice(1)} deleted successfully`);
      
      // If we're viewing the deleted elevation, close the viewer and go back to list
      if (selectedElevation?.id === elevationToDelete.id) {
        setSelectedElevation(null);
        setSelectedWindowElevation(null);
        setSelectedCurtainwallElevation(null);
        setSelectedStorefrontElevation(null);
        setSelectedShowerElevation(null);
        // Remove elevation ID from URL to go back to elevation list
        navigate(`/project/${projectId}/${activeCsiTab}/${activeSubTab}`, { replace: true });
      }
      
      // Refresh data
      await fetchProjectData();
      setElevationToDelete(null);
    } catch (error) {
      toast.error(`Failed to delete ${deleteLabel}`);
    } finally {
      clearInterval(progressInterval);
      setDeletingSingleElevation(false);
      setUploadProgress('');
      setUploadProgressPercent(0);
    }
  };

  // Bulk delete elevations handler
  const handleBulkDeleteElevations = async () => {
    let idsToDelete = [];
    if (bulkDeleteType === 'window') {
      idsToDelete = selectedWindowElevationIds;
    } else if (bulkDeleteType === 'curtainwall') {
      idsToDelete = selectedCurtainwallElevationIds;
    } else if (bulkDeleteType === 'storefront') {
      idsToDelete = selectedStorefrontElevationIds;
    } else if (bulkDeleteType === 'doorElevation') {
      idsToDelete = selectedDoorElevationIds;
    } else if (bulkDeleteType === 'mirrorElevation') {
      idsToDelete = selectedMirrorElevationIds;
    } else if (bulkDeleteType === 'showerElevation') {
      idsToDelete = selectedShowerElevationIds;
    }

    if (idsToDelete.length === 0) return;

    const bulkLabel = getEntityLabel(bulkDeleteType, true);
    setDeletingElevationCount(idsToDelete.length);
    setBulkDeletingElevations(true);
    setShowBulkDeleteDialog(false);
    setUploadProgress(`Deleting ${idsToDelete.length} ${bulkLabel}...`);
    setUploadProgressPercent(0);
    
    // Simulated progress for bulk delete - can be removed as a block
    let progressInterval = setInterval(() => {
      setUploadProgressPercent(prev => {
        const next = prev + Math.random() * 8 + 3;
        return next >= 90 ? 90 : Math.round(next);
      });
    }, 300);
    
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/elevations/bulk-delete`,
        { elevation_ids: idsToDelete }
      );
      
      clearInterval(progressInterval);
      setUploadProgressPercent(100);
      
      toast.success(`${idsToDelete.length} ${bulkLabel} deleted`);
      
      // Clear selections
      if (bulkDeleteType === 'window') {
        setSelectedWindowElevationIds([]);
      } else if (bulkDeleteType === 'curtainwall') {
        setSelectedCurtainwallElevationIds([]);
      } else if (bulkDeleteType === 'storefront') {
        setSelectedStorefrontElevationIds([]);
      } else if (bulkDeleteType === 'doorElevation') {
        setSelectedDoorElevationIds([]);
      } else if (bulkDeleteType === 'mirrorElevation') {
        setSelectedMirrorElevationIds([]);
      } else if (bulkDeleteType === 'showerElevation') {
        setSelectedShowerElevationIds([]);
      }
      
      // Refresh data
      await fetchProjectData();
      setBulkDeleteType(null);
    } catch (error) {
      toast.error(`Failed to delete ${bulkLabel}`);
    } finally {
      clearInterval(progressInterval);
      setBulkDeletingElevations(false);
      setDeletingElevationCount(0);
      setUploadProgress('');
      setUploadProgressPercent(0);
    }
  };

  const toggleElevationSelection = (elevationId, type, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    
    if (type === 'window') {
      setSelectedWindowElevationIds(prev => 
        prev.includes(elevationId) 
          ? prev.filter(id => id !== elevationId)
          : [...prev, elevationId]
      );
    } else if (type === 'curtainwall') {
      setSelectedCurtainwallElevationIds(prev => 
        prev.includes(elevationId) 
          ? prev.filter(id => id !== elevationId)
          : [...prev, elevationId]
      );
    } else if (type === 'storefront') {
      setSelectedStorefrontElevationIds(prev => 
        prev.includes(elevationId) 
          ? prev.filter(id => id !== elevationId)
          : [...prev, elevationId]
      );
    }
  };

  const selectAllElevations = (type) => {
    if (type === 'window') {
      const allIds = windowElevations.map(e => e.id);
      setSelectedWindowElevationIds(prev => 
        prev.length === allIds.length ? [] : allIds
      );
    } else if (type === 'curtainwall') {
      const allIds = curtainwallElevations.map(e => e.id);
      setSelectedCurtainwallElevationIds(prev => 
        prev.length === allIds.length ? [] : allIds
      );
    } else if (type === 'storefront') {
      const allIds = storefrontElevations.map(e => e.id);
      setSelectedStorefrontElevationIds(prev => 
        prev.length === allIds.length ? [] : allIds
      );
    }
  };

  const confirmBulkDelete = (type) => {
    setBulkDeleteType(type);
    setShowBulkDeleteDialog(true);
  };

  // Generic toggle selection for all list types
  const toggleItemSelection = (itemId, type, e) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    const setterMap = {
      'windowType': setSelectedWindowTypeIds,
      'windowGlassType': setSelectedWindowGlassTypeIds,
      'windowMetalPanelType': setSelectedWindowMetalPanelTypeIds,
      'windowList': setSelectedWindowListIds,
      'submittal': setSelectedSubmittalIds,
      'curtainwallType': setSelectedCurtainwallTypeIds,
      'cwGlassType': setSelectedCWGlassTypeIds,
      'metalPanelType': setSelectedMetalPanelTypeIds,
      'panelList': setSelectedPanelListIds,
      'storefrontType': setSelectedStorefrontTypeIds,
      'sfGlassType': setSelectedSFGlassTypeIds,
      'sfMetalPanelType': setSelectedSFMetalPanelTypeIds,
      'sfPanelList': setSelectedSFPanelListIds,
      'doorType': setSelectedDoorTypeIds,
      'doorGlassType': setSelectedDoorGlassTypeIds,
      'doorMetalPanelType': setSelectedDoorMetalPanelTypeIds,
      'doorHardwareSet': setSelectedDoorHardwareSetIds,
      'doorSchedule': setSelectedDoorScheduleIds,
      'showerGlassType': setSelectedShowerGlassTypeIds,
      'showerHardwareSet': setSelectedShowerHardwareSetIds,
      'showerSchedule': setSelectedShowerScheduleIds,
      'showerElevation': setSelectedShowerElevationIds,
    };
    const setter = setterMap[type];
    if (setter) {
      setter(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
    }
  };

  // Convenience wrapper for submittal selection
  const toggleSubmittalSelection = (submittalId, e) => {
    toggleItemSelection(submittalId, 'submittal', e);
  };

  // Handler for deleting a submittal (opens confirmation modal)
  const handleDeleteSubmittal = (submittal) => {
    setSubmittalToDelete(submittal);
    setShowDeleteSubmittalModal(true);
  };

  // Generic select all for all list types
  const selectAllItems = (type) => {
    const configMap = {
      'windowType': { items: windowTypes, setter: setSelectedWindowTypeIds, selected: selectedWindowTypeIds },
      'windowGlassType': { items: windowGlassTypes, setter: setSelectedWindowGlassTypeIds, selected: selectedWindowGlassTypeIds },
      'windowMetalPanelType': { items: windowMetalPanelTypes, setter: setSelectedWindowMetalPanelTypeIds, selected: selectedWindowMetalPanelTypeIds },
      'windowList': { items: windowList, setter: setSelectedWindowListIds, selected: selectedWindowListIds },
      'submittal': { items: submittals, setter: setSelectedSubmittalIds, selected: selectedSubmittalIds },
      'curtainwallType': { items: curtainwallTypes, setter: setSelectedCurtainwallTypeIds, selected: selectedCurtainwallTypeIds },
      'cwGlassType': { items: glassTypes, setter: setSelectedCWGlassTypeIds, selected: selectedCWGlassTypeIds },
      'metalPanelType': { items: metalPanelTypes, setter: setSelectedMetalPanelTypeIds, selected: selectedMetalPanelTypeIds },
      'panelList': { items: panelList, setter: setSelectedPanelListIds, selected: selectedPanelListIds },
      'storefrontType': { items: storefrontTypes, setter: setSelectedStorefrontTypeIds, selected: selectedStorefrontTypeIds },
      'sfGlassType': { items: storefrontGlassTypes, setter: setSelectedSFGlassTypeIds, selected: selectedSFGlassTypeIds },
      'sfMetalPanelType': { items: storefrontMetalPanelTypes, setter: setSelectedSFMetalPanelTypeIds, selected: selectedSFMetalPanelTypeIds },
      'sfPanelList': { items: storefrontPanelList, setter: setSelectedSFPanelListIds, selected: selectedSFPanelListIds },
      'doorType': { items: doorTypes, setter: setSelectedDoorTypeIds, selected: selectedDoorTypeIds },
      'doorGlassType': { items: doorGlassTypes, setter: setSelectedDoorGlassTypeIds, selected: selectedDoorGlassTypeIds },
      'doorMetalPanelType': { items: doorMetalPanelTypes, setter: setSelectedDoorMetalPanelTypeIds, selected: selectedDoorMetalPanelTypeIds },
      'doorHardwareSet': { items: doorHardwareSets, setter: setSelectedDoorHardwareSetIds, selected: selectedDoorHardwareSetIds },
      'doorSchedule': { items: doorSchedules, setter: setSelectedDoorScheduleIds, selected: selectedDoorScheduleIds },
      'showerType': { items: showerTypes, setter: setSelectedShowerTypeIds, selected: selectedShowerTypeIds },
      'showerGlassType': { items: showerGlassTypes, setter: setSelectedShowerGlassTypeIds, selected: selectedShowerGlassTypeIds },
      'showerHardwareSet': { items: showerHardwareSets, setter: setSelectedShowerHardwareSetIds, selected: selectedShowerHardwareSetIds },
      'showerSchedule': { items: showerSchedules, setter: setSelectedShowerScheduleIds, selected: selectedShowerScheduleIds },
      'showerElevation': { items: showerElevations, setter: setSelectedShowerElevationIds, selected: selectedShowerElevationIds },
    };
    const config = configMap[type];
    if (config) {
      const allIds = config.items.map(item => item.id);
      config.setter(config.selected.length === allIds.length ? [] : allIds);
    }
  };

  // Generic bulk delete handler
  const handleBulkDelete = async () => {
    const configMap = {
      'windowType': { ids: selectedWindowTypeIds, endpoint: '/window-types/bulk-delete', selectionSetter: setSelectedWindowTypeIds, listSetter: setWindowTypes, list: windowTypes },
      'windowGlassType': { ids: selectedWindowGlassTypeIds, endpoint: '/window-glass-types/bulk-delete', selectionSetter: setSelectedWindowGlassTypeIds, listSetter: setWindowGlassTypes, list: windowGlassTypes },
      'windowMetalPanelType': { ids: selectedWindowMetalPanelTypeIds, endpoint: '/window-metal-panel-types/bulk-delete', selectionSetter: setSelectedWindowMetalPanelTypeIds, listSetter: setWindowMetalPanelTypes, list: windowMetalPanelTypes },
      'windowList': { ids: selectedWindowListIds, endpoint: `/window-list/bulk-delete/${projectId}`, selectionSetter: setSelectedWindowListIds, listSetter: setWindowList, list: windowList },
      'submittal': { ids: selectedSubmittalIds, endpoint: `/submittals/bulk-delete/${projectId}`, selectionSetter: setSelectedSubmittalIds, listSetter: setSubmittals, list: submittals },
      'curtainwallType': { ids: selectedCurtainwallTypeIds, endpoint: '/curtainwall-types/bulk-delete', selectionSetter: setSelectedCurtainwallTypeIds, listSetter: setCurtainwallTypes, list: curtainwallTypes },
      'cwGlassType': { ids: selectedCWGlassTypeIds, endpoint: '/glass-types/bulk-delete', selectionSetter: setSelectedCWGlassTypeIds, listSetter: setGlassTypes, list: glassTypes },
      'metalPanelType': { ids: selectedMetalPanelTypeIds, endpoint: '/metal-panel-types/bulk-delete', selectionSetter: setSelectedMetalPanelTypeIds, listSetter: setMetalPanelTypes, list: metalPanelTypes },
      'panelList': { ids: selectedPanelListIds, endpoint: `/panel-list/bulk-delete/${projectId}`, selectionSetter: setSelectedPanelListIds, listSetter: setPanelList, list: panelList },
      'storefrontType': { ids: selectedStorefrontTypeIds, endpoint: '/storefront-types/bulk-delete', selectionSetter: setSelectedStorefrontTypeIds, listSetter: setStorefrontTypes, list: storefrontTypes },
      'sfGlassType': { ids: selectedSFGlassTypeIds, endpoint: '/storefront-glass-types/bulk-delete', selectionSetter: setSelectedSFGlassTypeIds, listSetter: setStorefrontGlassTypes, list: storefrontGlassTypes },
      'sfMetalPanelType': { ids: selectedSFMetalPanelTypeIds, endpoint: '/storefront-metal-panel-types/bulk-delete', selectionSetter: setSelectedSFMetalPanelTypeIds, listSetter: setStorefrontMetalPanelTypes, list: storefrontMetalPanelTypes },
      'sfPanelList': { ids: selectedSFPanelListIds, endpoint: `/storefront-panel-list/bulk-delete/${projectId}`, selectionSetter: setSelectedSFPanelListIds, listSetter: setStorefrontPanelList, list: storefrontPanelList },
      'doorType': { ids: selectedDoorTypeIds, endpoint: '/door-types/bulk-delete', selectionSetter: setSelectedDoorTypeIds, listSetter: setDoorTypes, list: doorTypes },
      'doorGlassType': { ids: selectedDoorGlassTypeIds, endpoint: '/door-glass-types/bulk-delete', selectionSetter: setSelectedDoorGlassTypeIds, listSetter: setDoorGlassTypes, list: doorGlassTypes },
      'doorMetalPanelType': { ids: selectedDoorMetalPanelTypeIds, endpoint: '/door-metal-panel-types/bulk-delete', selectionSetter: setSelectedDoorMetalPanelTypeIds, listSetter: setDoorMetalPanelTypes, list: doorMetalPanelTypes },
      'doorHardwareSet': { ids: selectedDoorHardwareSetIds, endpoint: '/door-hardware-sets/bulk-delete', selectionSetter: setSelectedDoorHardwareSetIds, listSetter: setDoorHardwareSets, list: doorHardwareSets },
      'doorSchedule': { ids: selectedDoorScheduleIds, endpoint: '/door-schedules/bulk-delete', selectionSetter: setSelectedDoorScheduleIds, listSetter: setDoorSchedules, list: doorSchedules },
      'mirrorType': { ids: selectedMirrorTypeIds, endpoint: '/mirror-types/bulk-delete', selectionSetter: setSelectedMirrorTypeIds, listSetter: setMirrorTypes, list: mirrorTypes },
      'showerType': { ids: selectedShowerTypeIds, endpoint: '/shower-types/bulk-delete', selectionSetter: setSelectedShowerTypeIds, listSetter: setShowerTypes, list: showerTypes },
      'showerGlassType': { ids: selectedShowerGlassTypeIds, endpoint: '/shower-glass-types/bulk-delete', selectionSetter: setSelectedShowerGlassTypeIds, listSetter: setShowerGlassTypes, list: showerGlassTypes },
      'showerHardwareSet': { ids: selectedShowerHardwareSetIds, endpoint: '/shower-hardware-sets/bulk-delete', selectionSetter: setSelectedShowerHardwareSetIds, listSetter: setShowerHardwareSets, list: showerHardwareSets },
      'showerSchedule': { ids: selectedShowerScheduleIds, endpoint: '/shower-schedules/bulk-delete', selectionSetter: setSelectedShowerScheduleIds, listSetter: setShowerSchedules, list: showerSchedules },
    };
    
    const config = configMap[bulkDeleteType];
    if (!config || config.ids.length === 0) return;

    try {
      const response = await axios.post(`${API}${config.endpoint}`, { ids: config.ids });
      const { deleted, errors } = response.data;
      
      // Show success message
      if (deleted > 0) {
        toast.success(`${deleted} item(s) deleted successfully`);
        
        // Immediately update local state to remove deleted items
        const deletedIds = config.ids;
        config.listSetter(prev => prev.filter(item => !deletedIds.includes(item.id)));
      }
      
      // Show error messages for items that couldn't be deleted (e.g., referenced by other items)
      if (errors && errors.length > 0) {
        errors.forEach(err => toast.error(err, { duration: 5000 }));
        // If there were errors, refresh to get accurate state
        fetchProjectData();
      }
      
      // Clear selection
      config.selectionSetter([]);
      setShowBulkDeleteDialog(false);
      setBulkDeleteType(null);
    } catch (error) {
      if (isGlassTypeConflict(error)) {
        // Nothing was deleted - keep the selection so the user can adjust and retry
        toast.error(describeGlassTypeConflict(error), { duration: 10000 });
        setShowBulkDeleteDialog(false);
        setBulkDeleteType(null);
        return;
      }
      toast.error(getApiErrorMessage(error, 'Failed to delete items'));
    }
  };

  // Get selected count for bulk delete dialog
  const getSelectedCount = () => {
    const countMap = {
      // Elevation types (used by confirmBulkDelete for elevations)
      'window': selectedWindowElevationIds.length,
      'curtainwall': selectedCurtainwallElevationIds.length,
      'storefront': selectedStorefrontElevationIds.length,
      'doorElevation': selectedDoorElevationIds.length,
      // Other types
      'windowType': selectedWindowTypeIds.length,
      'windowGlassType': selectedWindowGlassTypeIds.length,
      'windowList': selectedWindowListIds.length,
      'submittal': selectedSubmittalIds.length,
      'curtainwallType': selectedCurtainwallTypeIds.length,
      'cwGlassType': selectedCWGlassTypeIds.length,
      'metalPanelType': selectedMetalPanelTypeIds.length,
      'panelList': selectedPanelListIds.length,
      'storefrontType': selectedStorefrontTypeIds.length,
      'sfGlassType': selectedSFGlassTypeIds.length,
      'sfMetalPanelType': selectedSFMetalPanelTypeIds.length,
      'sfPanelList': selectedSFPanelListIds.length,
      'doorType': selectedDoorTypeIds.length,
      'doorGlassType': selectedDoorGlassTypeIds.length,
      'doorMetalPanelType': selectedDoorMetalPanelTypeIds.length,
      'doorHardwareSet': selectedDoorHardwareSetIds.length,
      'doorSchedule': selectedDoorScheduleIds.length,
      'mirrorType': selectedMirrorTypeIds.length,
      'showerType': selectedShowerTypeIds.length,
      'showerGlassType': selectedShowerGlassTypeIds.length,
      'showerHardwareSet': selectedShowerHardwareSetIds.length,
      'showerSchedule': selectedShowerScheduleIds.length,
      'showerElevation': selectedShowerElevationIds.length,
      'mirrorElevation': selectedMirrorElevationIds.length,
    };
    return countMap[bulkDeleteType] || 0;
  };

  const confirmDeleteElevation = (elevation, e) => {
    if (e) e.stopPropagation();
    setElevationToDelete(elevation);
    setShowDeleteElevationDialog(true);
  };

  const handleDeleteWindowType = async () => {
    if (!windowTypeToDelete) return;
    
    try {
      await axios.delete(`${API}/window-types/${windowTypeToDelete.id}`);
      toast.success('Window type deleted successfully');
      fetchProjectData();
      setShowDeleteWindowTypeDialog(false);
      setWindowTypeToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete window type');
    }
  };

  const handleDeleteWindow = async () => {
    if (!windowToDelete) return;
    
    try {
      await axios.delete(`${API}/window-list/${windowToDelete.id}`);
      toast.success('Window deleted successfully');
      fetchProjectData();
      setShowDeleteWindowDialog(false);
      setWindowToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete window');
    }
  };

  const handleDeleteAllWindows = async () => {
    try {
      // Delete all windows for this project
      const deletePromises = windowList.map(window => 
        axios.delete(`${API}/window-list/${window.id}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`Deleted ${windowList.length} windows successfully`);
      fetchProjectData();
      setShowDeleteAllWindowsDialog(false);
    } catch (error) {
      toast.error('Failed to delete all windows');
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are allowed');
      return;
    }
    
    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/window-list/upload-excel/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setExcelPreview(response.data);
      setShowExcelUploadDialog(true);
      toast.success(`Parsed ${response.data.totalRows} rows from Excel`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploadingExcel(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleBulkImport = async (skipDuplicates) => {
    if (!excelPreview) return;
    
    // Validate that all non-duplicate rows have a window type selected
    const rowsToImport = skipDuplicates 
      ? excelPreview.rows.filter(r => !r.isDuplicate)
      : excelPreview.rows;
    
    const missingTypes = rowsToImport.filter(r => !r.windowTypeId && !r.matchedWindowTypeId);
    
    if (missingTypes.length > 0) {
      toast.error(`${missingTypes.length} rows don't have a window type selected. Please select a type for all rows.`);
      return;
    }
    
    // Prepare rows with windowTypeId
    const preparedRows = rowsToImport.map(r => ({
      ...r,
      windowTypeId: r.windowTypeId || r.matchedWindowTypeId
    }));
    
    try {
      const response = await axios.post(
        `${API}/window-list/bulk-import/${projectId}`,
        {
          rows: preparedRows,
          skipDuplicates
        }
      );
      
      const result = response.data;
      const dupLabels = result.skipped_duplicate_labels || [];
      let msg = `Imported ${result.created} windows`;
      if (result.skipped > 0) {
        msg += `, skipped ${result.skipped} duplicates`;
        if (dupLabels.length > 0) {
          const shown = dupLabels.slice(0, 5).join(', ');
          msg += ` (${shown}${dupLabels.length > 5 ? '...' : ''})`;
        }
      }
      toast.success(msg);
      
      if (result.errors && result.errors.length > 0) {
        toast.error(`${result.errors.length} errors occurred. Check console for details.`);
        console.error('Import errors:', result.errors);
      }
      
      await fetchProjectData();
      
      // Only close dialog if import was successful
      if (result.created > 0 || result.skipped > 0) {
        setShowExcelUploadDialog(false);
        setExcelPreview(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import windows');
      console.error('Import error:', error);
      // Keep dialog open on error so user can retry
    }
  };

  const handleUpdatePreviewRow = (rowIndex, field, value) => {
    setExcelPreview(prev => ({
      ...prev,
      rows: prev.rows.map((row, idx) => 
        idx === rowIndex ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleDetectWindows = async () => {
    if (!selectedElevation) return;
    
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/elevations/${selectedElevation.id}/detect-windows?page_index=${currentPageIndex}`
      );
      
      toast.success(`Detected ${response.data.markers.length} windows using AI`);
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to detect windows with AI');
    }
  };


  const handleTypePictureUpload = async (e, windowTypeId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // If windowTypeId is undefined (creating new type), just store the file
    if (!windowTypeId) {
      setTypePictureFile(file);
      setTypePictureUrl(URL.createObjectURL(file));
      return;
    }
    
    // If windowTypeId exists (editing existing type), upload immediately
    setWindowTypePictureUploading(windowTypeId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/window-types/${windowTypeId}/upload-picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Picture uploaded successfully');
      
      // Refresh project data first to get updated data from backend
      await fetchProjectData();
      
      // Update the picture URL in the modal - check Cloudinary first, then legacy
      if (response.data.pictureCloudinary?.urls?.medium) {
        setTypePictureUrl(response.data.pictureCloudinary.urls.medium);
        
        // Also update editingType if it exists (for modal display)
        if (editingType && editingType.id === windowTypeId) {
          setEditingType({
            ...editingType,
            pictureCloudinary: response.data.pictureCloudinary,
            pictureUrl: null
          });
        }
      } else if (response.data.pictureUrl) {
        const updatedAt = response.data.updatedAt || new Date().toISOString();
        const forceUniqueTimestamp = `${updatedAt}_${Date.now()}`;
        const timestampedUrl = `${API}${response.data.pictureUrl}?t=${forceUniqueTimestamp}`;
        setTypePictureUrl(timestampedUrl);
        
        if (editingType && editingType.id === windowTypeId) {
          setEditingType({
            ...editingType,
            pictureUrl: response.data.pictureUrl,
            updatedAt: updatedAt
          });
        }
      }
    } catch (error) {
      toast.error('Failed to upload picture');
    } finally {
      setWindowTypePictureUploading(null);
    }
  };

  // Door Type Picture Upload Handler
  const handleDoorTypePictureUpload = async (e, doorTypeId) => {
    const file = e.target.files[0];
    if (!file || !doorTypeId) return;
    
    // Set uploading state
    setDoorTypePictureUploading(doorTypeId);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/door-types/${doorTypeId}/upload-picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Picture uploaded successfully');
      
      // Update the door type in local state
      setDoorTypes(prev => prev.map(dt => 
        dt.id === doorTypeId 
          ? { ...dt, pictureCloudinary: response.data.pictureCloudinary, pictureUrl: null }
          : dt
      ));
      
      // Update modal picture preview
      if (response.data.pictureCloudinary?.urls?.medium) {
        setDoorTypePictureUrl(response.data.pictureCloudinary.urls.medium);
      }
    } catch (error) {
      toast.error('Failed to upload picture');
    } finally {
      setDoorTypePictureUploading(null);
    }
  };

  // Open clipboard paste modal for Door Type (existing or new)
  const handleDoorTypePasteFromClipboard = (doorTypeId) => {
    if (doorTypeId) {
      setClipboardPasteTarget({ type: 'doorType', id: doorTypeId });
    } else {
      // For new door type - will save to pendingDoorTypePicture
      setClipboardPasteTarget({ type: 'newDoorType', id: null });
    }
  };

  // Open clipboard paste modal for Window Type (existing or new)
  const handleWindowTypePasteFromClipboard = (windowTypeId) => {
    if (windowTypeId) {
      setClipboardPasteTarget({ type: 'windowType', id: windowTypeId });
    } else {
      setClipboardPasteTarget({ type: 'newWindowType', id: null });
    }
  };

  // Open clipboard paste modal for Curtain Wall Type (existing or new)
  const handleCWTypePasteFromClipboard = (cwTypeId) => {
    if (cwTypeId) {
      setClipboardPasteTarget({ type: 'cwType', id: cwTypeId });
    } else {
      setClipboardPasteTarget({ type: 'newCWType', id: null });
    }
  };

  // Open clipboard paste modal for Storefront Type (existing or new)
  const handleSFTypePasteFromClipboard = (sfTypeId) => {
    if (sfTypeId) {
      setClipboardPasteTarget({ type: 'sfType', id: sfTypeId });
    } else {
      setClipboardPasteTarget({ type: 'newSFType', id: null });
    }
  };

  // Open clipboard paste modal for Mirror Type (existing or new)
  const handleMirrorTypePasteFromClipboard = (mirrorTypeId) => {
    if (mirrorTypeId) {
      setClipboardPasteTarget({ type: 'mirrorType', id: mirrorTypeId });
    } else {
      setClipboardPasteTarget({ type: 'newMirrorType', id: null });
    }
  };

  // Open clipboard paste modal for Elevation
  const handleElevationPasteFromClipboard = (elevationType) => {
    setClipboardPasteTarget({ type: 'elevation', id: elevationType });
  };

  // Handle clipboard image upload
  const handleClipboardUpload = async (file) => {
    if (!clipboardPasteTarget) return;

    const { type, id } = clipboardPasteTarget;

    if (type === 'doorType' && id) {
      // Existing door type - upload immediately
      setDoorTypePictureUploading(id);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/door-types/${id}/upload-picture`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Image uploaded successfully');

        // Update the door type in local state
        setDoorTypes(prev => prev.map(dt => 
          dt.id === id 
            ? { ...dt, pictureCloudinary: response.data.pictureCloudinary, pictureUrl: null }
            : dt
        ));

        // Update modal picture preview
        if (response.data.pictureCloudinary?.urls?.medium) {
          setDoorTypePictureUrl(response.data.pictureCloudinary.urls.medium);
        }
      } catch (error) {
        toast.error('Failed to upload image');
      } finally {
        setDoorTypePictureUploading(null);
        setClipboardPasteTarget(null);
      }
    } else if (type === 'newDoorType') {
      // New door type - save file for later upload after creation
      const previewUrl = URL.createObjectURL(file);
      setPendingDoorTypePicture({ file, previewUrl });
      toast.success('Image ready - will be uploaded when door type is created');
      setClipboardPasteTarget(null);
    } else if (type === 'windowType' && id) {
      // Existing window type - upload immediately
      setWindowTypePictureUploading(id);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/window-types/${id}/upload-picture`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Image uploaded successfully');

        // Update the window type in local state
        setWindowTypes(prev => prev.map(wt => 
          wt.id === id 
            ? { ...wt, pictureCloudinary: response.data.pictureCloudinary, pictureUrl: null }
            : wt
        ));

        // Update modal picture preview
        if (response.data.pictureCloudinary?.urls?.medium) {
          setTypePictureUrl(response.data.pictureCloudinary.urls.medium);
        }
      } catch (error) {
        toast.error('Failed to upload image');
      } finally {
        setWindowTypePictureUploading(null);
        setClipboardPasteTarget(null);
      }
    } else if (type === 'newWindowType') {
      // New window type - save file for later upload after creation
      const previewUrl = URL.createObjectURL(file);
      setPendingWindowTypePicture({ file, previewUrl });
      toast.success('Image ready - will be uploaded when window type is created');
      setClipboardPasteTarget(null);
    } else if (type === 'cwType' && id) {
      // Existing curtain wall type - upload immediately
      setCWTypePictureUploading(id);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/curtainwall-types/${id}/upload-drawing`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Drawing uploaded successfully');

        // Update the CW type in local state
        setCurtainwallTypes(prev => prev.map(cw => 
          cw.id === id 
            ? { ...cw, drawingCloudinary: response.data.drawingCloudinary, drawingUrl: null }
            : cw
        ));

        // Update modal picture preview
        if (response.data.drawingCloudinary?.urls?.medium) {
          setCWTypePictureUrl(response.data.drawingCloudinary.urls.medium);
        }
      } catch (error) {
        toast.error('Failed to upload drawing');
      } finally {
        setCWTypePictureUploading(null);
        setClipboardPasteTarget(null);
      }
    } else if (type === 'newCWType') {
      // New CW type - save file for later upload after creation
      const previewUrl = URL.createObjectURL(file);
      setPendingCWTypePicture({ file, previewUrl });
      toast.success('Drawing ready - will be uploaded when CW type is created');
      setClipboardPasteTarget(null);
    } else if (type === 'sfType' && id) {
      // Existing storefront type - upload immediately
      setSFTypePictureUploading(id);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/storefront-types/${id}/upload-drawing`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Drawing uploaded successfully');

        // Update the SF type in local state
        setStorefrontTypes(prev => prev.map(sf => 
          sf.id === id 
            ? { ...sf, drawingCloudinary: response.data.drawingCloudinary, drawingUrl: null }
            : sf
        ));

        // Update modal picture preview
        if (response.data.drawingCloudinary?.urls?.medium) {
          setSFTypePictureUrl(response.data.drawingCloudinary.urls.medium);
        }
      } catch (error) {
        toast.error('Failed to upload drawing');
      } finally {
        setSFTypePictureUploading(null);
        setClipboardPasteTarget(null);
      }
    } else if (type === 'newSFType') {
      // New SF type - save file for later upload after creation
      const previewUrl = URL.createObjectURL(file);
      setPendingSFTypePicture({ file, previewUrl });
      toast.success('Drawing ready - will be uploaded when SF type is created');
      setClipboardPasteTarget(null);
    } else if (type === 'mirrorType' && id) {
      // Existing mirror type - upload immediately
      setMirrorTypePictureUploading(id);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/mirror-types/${id}/upload-picture`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success('Picture uploaded successfully');

        // Update the mirror type in local state
        setMirrorTypes(prev => prev.map(mt => 
          mt.id === id 
            ? { ...mt, pictureCloudinary: response.data.pictureCloudinary, pictureUrl: response.data.pictureUrl }
            : mt
        ));

        // Update modal picture preview
        if (response.data.pictureCloudinary?.urls?.medium) {
          setMirrorTypePictureUrl(response.data.pictureCloudinary.urls.medium);
        }
      } catch (error) {
        toast.error('Failed to upload picture');
      } finally {
        setMirrorTypePictureUploading(null);
        setClipboardPasteTarget(null);
      }
    } else if (type === 'newMirrorType') {
      // New mirror type - save file for later upload after creation
      const previewUrl = URL.createObjectURL(file);
      setPendingMirrorTypePicture({ file, previewUrl });
      setMirrorTypePictureUrl(previewUrl);
      toast.success('Picture ready - will be uploaded when mirror type is created');
      setClipboardPasteTarget(null);
    } else if (type === 'elevation' && id) {
      // Elevation upload - id contains the elevation type (window, curtainwall, storefront, door)
      setElevationUploading(id);
      const pasteLabel = getEntityLabel(id);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', id);
        formData.append('csiItemId', activeCsiTab);

        const response = await axios.post(
          `${API}/projects/${projectId}/elevations/upload-image`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        toast.success(`${pasteLabel.charAt(0).toUpperCase() + pasteLabel.slice(1)} uploaded successfully`);
        fetchProjectData();
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Failed to upload ${pasteLabel}`);
      } finally {
        setElevationUploading(null);
        setClipboardPasteTarget(null);
      }
    }
  };

  const handlePrintWindowLabel = async (windowId) => {
    try {
      const response = await axios.get(`${API}/window-list/${windowId}/print-label`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('window');
      setShowPrintPreviewDialog(true);
      toast.success('Label preview ready');
    } catch (error) {
      toast.error('Failed to generate label');
      console.error('Print error:', error);
    }
  };

  const handlePrintPanelLabel = async (panelId) => {
    try {
      const response = await axios.get(`${API}/panel-list/${panelId}/print-label`, {
        responseType: 'blob'
      });
      
      // Show in preview modal instead of direct download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('cw-panel');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate panel label');
      console.error('Print error:', error);
    }
  };

  const handlePrintSFPanelLabel = async (panelId) => {
    try {
      const response = await axios.get(`${API}/storefront-panel-list/${panelId}/print-label`, {
        responseType: 'blob'
      });
      
      // Show in preview modal instead of direct download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('sf-panel');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate panel label');
      console.error('Print error:', error);
    }
  };

  const handlePrintPanelQR = async (panelId, panelLabel) => {
    try {
      const response = await axios.get(`${API}/panel-list/${panelId}/print-qr`, {
        responseType: 'blob'
      });
      
      // Show in preview modal instead of direct download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('cw-panel-qr');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate panel QR label');
      console.error('Panel QR print error:', error);
    }
  };

  const handlePrintSFPanelQR = async (panelId, panelLabel) => {
    try {
      const response = await axios.get(`${API}/storefront-panel-list/${panelId}/print-qr`, {
        responseType: 'blob'
      });
      
      // Show in preview modal instead of direct download
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('sf-panel-qr');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate storefront panel QR label');
      console.error('Storefront panel QR print error:', error);
    }
  };

  const handleDownloadFromPreview = () => {
    if (!printPreviewUrl) return;
    
    const link = document.createElement('a');
    link.href = printPreviewUrl;
    
    // Set appropriate filename based on preview type
    let filename = 'Label.pdf';
    if (printPreviewType === 'window') {
      filename = 'Window_Label.pdf';
    } else if (printPreviewType === 'panel') {
      filename = 'Panel_Label.pdf';
    } else if (printPreviewType === 'sf-panel') {
      filename = 'SF_Panel_Label.pdf';
    } else if (printPreviewType === 'sf-panel-qr') {
      filename = 'SF_Panel_QR.pdf';
    } else if (printPreviewType === 'cw-panel') {
      filename = 'CW_Panel_Label.pdf';
    } else if (printPreviewType === 'cw-panel-qr') {
      filename = 'CW_Panel_QR.pdf';
    }
    
    link.download = filename;
    link.click();
    toast.success('Label PDF downloaded');
  };

  const handlePrintSingleQR = async (windowId, windowLabel) => {
    try {
      const response = await axios.get(`${API}/window-list/${windowId}/print-qr`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('window');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate QR label');
    }
  };

  const handlePrintAllQR = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/print-all-qr`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('window');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate QR labels');
    }
  };

  const handleClosePrintPreview = () => {
    if (printPreviewUrl) {
      window.URL.revokeObjectURL(printPreviewUrl);
    }
    setPrintPreviewUrl(null);
    setShowPrintPreviewDialog(false);
  };

  // CW Drawing zoom controls
  const handleCwZoomIn = () => {
    setCwDrawingZoom(prev => Math.min(prev + 0.25, 3));
  };
  const handleCwZoomOut = () => {
    setCwDrawingZoom(prev => Math.max(prev - 0.25, 0.5));
  };
  const handleCwResetZoom = () => {
    setCwDrawingZoom(1);
    setCwDrawingPan({ x: 0, y: 0 });
  };

  // SF Drawing zoom controls  
  const handleSfZoomIn = () => {
    setSfDrawingZoom(prev => Math.min(prev + 0.25, 3));
  };
  const handleSfZoomOut = () => {
    setSfDrawingZoom(prev => Math.max(prev - 0.25, 0.5));
  };
  const handleSfResetZoom = () => {
    setSfDrawingZoom(1);
    setSfDrawingPan({ x: 0, y: 0 });
  };

  // CW Drawing pan controls
  const handleCwPanStart = (e) => {
    if (cwDrawingZoom > 1) {
      setIsCwPanning(true);
      setCwPanStartPos({ x: e.clientX - cwDrawingPan.x, y: e.clientY - cwDrawingPan.y });
    }
  };
  const handleCwPanMove = (e) => {
    if (cwPanStartPos && cwDrawingZoom > 1) {
      setCwDrawingPan({
        x: e.clientX - cwPanStartPos.x,
        y: e.clientY - cwPanStartPos.y
      });
      // Mark that actual panning occurred
      wasCwPanningRef.current = true;
    }
  };
  const handleCwPanEnd = () => {
    // If actual panning occurred, set flag to prevent click
    if (wasCwPanningRef.current) {
      setTimeout(() => {
        wasCwPanningRef.current = false;
      }, 100);
    }
    setIsCwPanning(false);
    setCwPanStartPos(null);
  };

  // SF Drawing pan controls
  const handleSfPanStart = (e) => {
    if (sfDrawingZoom > 1) {
      setIsSfPanning(true);
      setSfPanStartPos({ x: e.clientX - sfDrawingPan.x, y: e.clientY - sfDrawingPan.y });
    }
  };
  const handleSfPanMove = (e) => {
    if (sfPanStartPos && sfDrawingZoom > 1) {
      setSfDrawingPan({
        x: e.clientX - sfPanStartPos.x,
        y: e.clientY - sfPanStartPos.y
      });
      // Mark that actual panning occurred
      wasSfPanningRef.current = true;
    }
  };
  const handleSfPanEnd = () => {
    // If actual panning occurred, set flag to prevent click
    if (wasSfPanningRef.current) {
      setTimeout(() => {
        wasSfPanningRef.current = false;
      }, 100);
    }
    setIsSfPanning(false);
    setSfPanStartPos(null);
  };

  // Open CW Drawing in fullscreen dialog (for viewing, not marker add)
  const handleOpenCWDrawingView = (cwType) => {
    setSelectedCWTypeForView(cwType);
    setCwDrawingZoom(1);
    setCwDrawingPan({ x: 0, y: 0 });
    setShowCWDrawingDialog(true);
  };

  // Open SF Drawing in fullscreen dialog (for viewing, not marker add)
  const handleOpenSFDrawingView = (sfType) => {
    setSelectedSFTypeForView(sfType);
    setSfDrawingZoom(1);
    setSfDrawingPan({ x: 0, y: 0 });
    setShowSFDrawingDialog(true);
  };

  // Open panel/type print preview in modal
  const handleOpenPanelPrintPreview = async (id, panelType = 'curtainwall') => {
    // Drop any previously loaded preview so a failed/slow load can never show
    // the details of the schedule that was opened before
    if (panelPrintPreviewUrl) {
      window.URL.revokeObjectURL(panelPrintPreviewUrl);
    }
    setPanelPrintPreviewUrl(null);
    setPanelPrintPreviewLoading(true);
    setShowPanelPrintPreview(true);

    // Markers may reference either a Type record or a schedule row, so resolve the
    // id against the loaded lists instead of assuming one of them.
    const buildEndpoints = () => {
      if (panelType === 'storefront') {
        const typeEndpoint = `${API}/storefront-types/${id}/print-label`;
        const scheduleEndpoint = `${API}/storefront-panel-list/${id}/print-label`;
        return storefrontTypes.some(t => t.id === id)
          ? [typeEndpoint, scheduleEndpoint]
          : [scheduleEndpoint, typeEndpoint];
      }
      return [`${API}/panel-list/${id}/print-label`];
    };

    const endpoints = buildEndpoints();
    let lastError = null;

    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(endpoint, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        setPanelPrintPreviewUrl(window.URL.createObjectURL(blob));
        setPanelPrintPreviewLoading(false);
        return;
      } catch (error) {
        lastError = error;
        // Only fall back when the record simply is not of this kind
        if (error.response?.status !== 404) break;
      }
    }

    setPanelPrintPreviewLoading(false);
    setShowPanelPrintPreview(false);
    toast.error(
      lastError?.response?.status === 404
        ? 'Schedule details not found for this marker'
        : 'Failed to load print preview'
    );
  };

  const handleClosePanelPrintPreview = () => {
    if (panelPrintPreviewUrl) {
      window.URL.revokeObjectURL(panelPrintPreviewUrl);
    }
    setPanelPrintPreviewUrl(null);
    setShowPanelPrintPreview(false);
  };

  const handleCWDrawingClick = (e, cwType) => {
    if (!canEditCSIItems) return; // Only contractors can add markers
    
    // Close any open marker popup first
    setActiveMarkerPopup(null);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPendingCWMarker({ x, y });
    setActiveCWTypeForMarker(cwType);
    setShowCWDrawingMarkerDialog(true);
  };

  const handleAddCWDrawingMarker = async () => {
    if (!selectedPanelForMarker || !pendingCWMarker || !activeCWTypeForMarker) return;
    
    try {
      const panel = panelList.find(p => p.id === selectedPanelForMarker);
      if (!panel) {
        toast.error('Panel not found');
        return;
      }
      
      const marker = {
        id: Date.now().toString(),
        x: pendingCWMarker.x,
        y: pendingCWMarker.y,
        width: 10.0,  // Increased from 5.0 for better readability
        height: 12.0, // Increased from 8.0 for better readability
        panelId: panel.id,
        panelLabel: panel.label
      };
      
      await axios.post(
        `${API}/curtainwall-types/${activeCWTypeForMarker.id}/drawing-markers`,
        marker
      );
      
      toast.success('Panel marker added successfully');
      await fetchProjectData();
      
      setShowCWDrawingMarkerDialog(false);
      setPendingCWMarker(null);
      setSelectedPanelForMarker('');
      setActiveCWTypeForMarker(null);
    } catch (error) {
      toast.error('Failed to add panel marker');
      console.error('Marker error:', error);
    }
  };

  const handleDeleteCWDrawingMarker = async (cwTypeId, markerId) => {
    try {
      await axios.delete(`${API}/curtainwall-types/${cwTypeId}/drawing-markers/${markerId}`);
      toast.success('Marker deleted');
      await fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete marker');
    }
  };

  // CW Drawing Bulk Marker Handlers
  const handleCwDrawingBulkClick = (e, cwType) => {
    if (!cwDrawingBulkMode || !cwDrawingBulkSchedule || !cwType) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const selectedSchedule = panelList.find(p => p.id === cwDrawingBulkSchedule);
    const markerColor = cwType.markerColor || '#39FF14';
    
    const newMarker = {
      id: `temp-${Date.now()}-${Math.random()}`,
      x,
      y,
      panelId: cwDrawingBulkSchedule,
      panelLabel: selectedSchedule?.label || '',
      color: markerColor
    };
    
    setPendingCwDrawingMarkers(prev => [...prev, newMarker]);
  };

  const handleSaveCwDrawingBulkMarkers = async () => {
    if (pendingCwDrawingMarkers.length === 0 || !selectedCWTypeForView) {
      toast.error('No markers to save');
      return;
    }
    
    const cwTypeId = selectedCWTypeForView.id;
    setSavingCwDrawingMarkers(true);
    
    try {
      const savePromises = pendingCwDrawingMarkers.map(marker => 
        axios.post(`${API}/curtainwall-types/${cwTypeId}/drawing-markers`, {
          x: marker.x,
          y: marker.y,
          panelId: marker.panelId
        })
      );
      
      await Promise.all(savePromises);
      
      toast.success(`${pendingCwDrawingMarkers.length} marker(s) added successfully`);
      
      // Reset bulk marker state
      setPendingCwDrawingMarkers([]);
      setCwDrawingBulkMode(false);
      setCwDrawingBulkSchedule('');
      
      // Refresh data
      await fetchProjectData();
      
      // Update selected CW type with new markers
      const cwTypesRes = await axios.get(`${API}/curtainwall-types/${projectId}?csi_item_id=${activeCsiTab}`);
      const updatedCwType = cwTypesRes.data.find(cw => cw.id === cwTypeId);
      if (updatedCwType) {
        setSelectedCWTypeForView(updatedCwType);
      }
    } catch (error) {
      console.error('Error saving CW drawing bulk markers:', error);
      toast.error('Failed to save markers');
    } finally {
      setSavingCwDrawingMarkers(false);
    }
  };

  const handleCancelCwDrawingBulkMarkers = () => {
    setPendingCwDrawingMarkers([]);
    setCwDrawingBulkMode(false);
    setCwDrawingBulkSchedule('');
  };

  const handleRemovePendingCwDrawingMarker = (markerId) => {
    setPendingCwDrawingMarkers(prev => prev.filter(m => m.id !== markerId));
  };

  const handleUpdateMarkerPosition = async (cwTypeId, markerId, newX, newY) => {
    try {
      await axios.put(`${API}/curtainwall-types/${cwTypeId}/drawing-markers/${markerId}`, {
        x: newX,
        y: newY
      });
      
      // Refresh CW Types
      const cwTypesRes = await axios.get(`${API}/curtainwall-types/project/${projectId}`);
      setCurtainwallTypes(cwTypesRes.data);
      
      // Update selectedCWTypeForView if it was the marker's type
      if (selectedCWTypeForView && selectedCWTypeForView.id === cwTypeId) {
        const updatedCWType = cwTypesRes.data.find(cw => cw.id === cwTypeId);
        if (updatedCWType) {
          setSelectedCWTypeForView(updatedCWType);
        }
      }
    } catch (error) {
      toast.error('Failed to update marker position');
      console.error('Marker update error:', error);
    }
  };

  // CW Type Drawing Marker handlers - follows same pattern as elevation markers
  const handleCWMarkerMouseDown = (e, marker, cwTypeId) => {
    if (!canEditCSIItems) return;
    
    // Don't start drag if clicking on the marker-box (which opens popup)
    if (e.target.closest('.marker-box')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Navigate up to find the image container (relative inline-block div)
    let imageContainer = e.currentTarget.parentElement;
    
    // Verify we found the correct container (has inline-block class)
    if (!imageContainer || !imageContainer.classList.contains('inline-block')) {
      imageContainer = e.currentTarget;
      while (imageContainer && !imageContainer.classList.contains('inline-block')) {
        imageContainer = imageContainer.parentElement;
      }
    }
    
    const rect = imageContainer ? imageContainer.getBoundingClientRect() : e.currentTarget.parentElement.getBoundingClientRect();
    const offsetX = ((e.clientX - rect.left) / rect.width) * 100 - marker.x;
    const offsetY = ((e.clientY - rect.top) / rect.height) * 100 - marker.y;
    
    setDraggingMarker({ ...marker, cwTypeId, startX: marker.x, startY: marker.y });
    setDragOffset({ x: offsetX, y: offsetY });
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setIsDragging(false);
  };

  const handleCWMarkerMouseMove = (e, cwType) => {
    if (!draggingMarker || !draggingMarker.cwTypeId) return;
    e.preventDefault();
    
    // Check if mouse moved enough to be considered dragging
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - dragStartPos.x, 2) + Math.pow(e.clientY - dragStartPos.y, 2)
    );
    
    if (moveDistance > 5) {
      setIsDragging(true);
    }
    
    // Only update position if we're actually dragging
    if (!isDragging && moveDistance <= 5) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100 - dragOffset.x;
    const y = ((e.clientY - rect.top) / rect.height) * 100 - dragOffset.y;
    
    // Clamp values between 0 and 100
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));
    
    setDraggingMarker({ ...draggingMarker, x: clampedX, y: clampedY });
  };

  const handleMarkerDragEnd = async () => {
    if (!draggingMarker || !draggingMarker.cwTypeId) return;
    
    const wasDragging = isDragging;
    
    // Immediately stop dragging
    setIsDragging(false);
    const finalMarker = { ...draggingMarker };
    setDraggingMarker(null);
    setDragOffset({ x: 0, y: 0 });
    
    // Only save if actually dragged
    if (wasDragging) {
      await handleUpdateMarkerPosition(
        finalMarker.cwTypeId,
        finalMarker.id,
        finalMarker.x,
        finalMarker.y
      );
    }
  };

  const handleMarkerEyeClick = async (e, panelId, panelType = 'curtainwall') => {
    e.stopPropagation();
    // Open in modal instead of new tab
    handleOpenPanelPrintPreview(panelId, panelType);
  };

  const getAvailablePanels = () => {
    if (!activeCWTypeForMarker) return panelList;
    
    // Get already used panel IDs from markers
    const usedPanelIds = activeCWTypeForMarker.drawingMarkers?.map(m => m.panelId) || [];
    
    // Return only unused panels
    return panelList.filter(p => !usedPanelIds.includes(p.id));
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (windowOperationLoading) return;
    
    // Validate name field
    if (!newType.name || newType.name.trim() === '') {
      toast.error('Window Type Name is required');
      return;
    }
    
    setWindowOperationLoading(true);
    try {
      const typeData = { ...newType };
      let createdOrUpdatedTypeId = null;
      
      if (editingType) {
        // Update existing type
        await axios.put(`${API}/window-types/${editingType.id}`, {
          ...typeData,
          projectId
        });
        createdOrUpdatedTypeId = editingType.id;
        toast.success('Window type updated');
      } else {
        // Create new type
        const response = await axios.post(`${API}/window-types`, {
          ...typeData,
          projectId,
          csiItemId: activeCsiTab  // Link to current CSI item instance
        });
        createdOrUpdatedTypeId = response.data.id;
        toast.success('Window type created');
        
        // If a picture was selected during creation, upload it now
        if (typePictureFile && createdOrUpdatedTypeId) {
          const formData = new FormData();
          formData.append('file', typePictureFile);
          
          try {
            await axios.post(
              `${API}/window-types/${createdOrUpdatedTypeId}/upload-picture`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Picture uploaded successfully');
          } catch (picError) {
            console.error('Picture upload failed:', picError);
            toast.error('Type created but picture upload failed');
          }
        }
        
        // If a pending picture from clipboard was added, upload it now
        if (pendingWindowTypePicture?.file && createdOrUpdatedTypeId) {
          try {
            setWindowTypePictureUploading(createdOrUpdatedTypeId);
            const formData = new FormData();
            formData.append('file', pendingWindowTypePicture.file);
            
            await axios.post(
              `${API}/window-types/${createdOrUpdatedTypeId}/upload-picture`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Picture uploaded successfully');
          } catch (picError) {
            console.error('Picture upload failed:', picError);
            toast.error('Type created but picture upload failed');
          } finally {
            setWindowTypePictureUploading(null);
            if (pendingWindowTypePicture?.previewUrl) {
              URL.revokeObjectURL(pendingWindowTypePicture.previewUrl);
            }
            setPendingWindowTypePicture(null);
          }
        }
      }
      
      // Refresh project data to get updated window types
      // Note: If picture was uploaded, fetchProjectData() is already called in handleTypePictureUpload
      // Only refresh if no picture was uploaded
      if (!typePictureFile) {
        await fetchProjectData();
      }
      
      setShowTypeDialog(false);
      setEditingType(null);
      setTypePictureFile(null);
      setTypePictureUrl(null);
      setNewType({
        name: '',
        manufacturer: '',
        model: '',
        frameMaterial: '',
        operationType: '',
        windowWidth_in: null,
        windowHeight_in: null,
        roWidth_in: null,
        roHeight_in: null,
        glassManufacturer: '',
        glassType: '',
        glassBuild: '',
        glassThickness_in: null,
        glassTypes: [],
        glassWidth_in: null,
        glassHeight_in: null,
        hasGrid: false,
        gridSize_in: null,
        finishInterior: '',
        finishExterior: '',
        hardware: '',
        warrantyYears: 10
      });
      fetchProjectData();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || `Failed to ${editingType ? 'update' : 'create'} window type`);
      }
    } finally {
      setWindowOperationLoading(false);
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setNewType({
      name: type.name || '',
      manufacturer: type.manufacturer,
      model: type.model,
      frameMaterial: type.frameMaterial,
      operationType: type.operationType,
      uValue: type.uValue || '',
      finishInterior: type.finishInterior,
      finishExterior: type.finishExterior,
      hardware: type.hardware,
      frameWarrantyYears: type.frameWarrantyYears || null,
      finishWarrantyYears: type.finishWarrantyYears || null,
      hardwareWarrantyYears: type.hardwareWarrantyYears || null,
      installationWarrantyYears: type.installationWarrantyYears || null,
      installationManual: type.installationManual || '',
      cleaningManual: type.cleaningManual || '',
      markerColor: type.markerColor || '#39FF14'
    });
    // Set picture URL - check Cloudinary first, then legacy with cache-busting
    if (type.pictureCloudinary?.urls?.medium) {
      setTypePictureUrl(type.pictureCloudinary.urls.medium);
    } else if (type.pictureUrl) {
      setTypePictureUrl(`${API}${type.pictureUrl}?t=${type.updatedAt || type.createdAt || 'default'}`);
    } else {
      setTypePictureUrl(null);
    }
    setShowTypeDialog(true);
  };

  const handleCloseTypeDialog = (open) => {
    if (!open) {
      // Only reset when closing
      setShowTypeDialog(false);
      setEditingType(null);
      setNewType({
        name: '',
        manufacturer: '',
        model: '',
        frameMaterial: '',
        operationType: '',
        uValue: '',
        finishInterior: '',
        finishExterior: '',
        hardware: '',
        frameWarrantyYears: null,
        finishWarrantyYears: null,
        hardwareWarrantyYears: null,
        installationWarrantyYears: null,
        installationManual: '',
        cleaningManual: ''
      });
      setTypePictureFile(null);
      setTypePictureUrl(null);
      // Clean up pending window type picture
      if (pendingWindowTypePicture?.previewUrl) {
        URL.revokeObjectURL(pendingWindowTypePicture.previewUrl);
      }
      setPendingWindowTypePicture(null);
    } else {
      // Clear previous clipboard upload preview and reset form when opening Add dialog
      if (!editingType) {
        setTypePictureUrl(null);
        if (pendingWindowTypePicture?.previewUrl) {
          URL.revokeObjectURL(pendingWindowTypePicture.previewUrl);
        }
        setPendingWindowTypePicture(null);
        setNewType({
          name: '',
          manufacturer: '',
          model: '',
          frameMaterial: '',
          operationType: '',
          uValue: '',
          finishInterior: '',
          finishExterior: '',
          hardware: '',
          frameWarrantyYears: null,
          finishWarrantyYears: null,
          hardwareWarrantyYears: null,
          installationWarrantyYears: null,
          installationManual: '',
          cleaningManual: ''
        });
      }
      setShowTypeDialog(true);
    }
  };

  const handleCreateWindow = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (windowOperationLoading) return;

    // Dimension validation: 0 and negative not allowed (native HTML5 balloon shows)
    const dimError = validatePositiveDimensions({
      'Window Width (in)': newWindow.windowWidth_in,
      'Window Height (in)': newWindow.windowHeight_in,
      'Rough Opening Width (in)': newWindow.roWidth_in,
      'Rough Opening Height (in)': newWindow.roHeight_in
    });
    if (dimError) {
      toast.error(dimError);
      return;
    }

    // Normalize staged lites (step 2) to the backend format
    const lites = (newWindow.lites || []).map(lite => ({
      id: lite.id || newLiteId(),
      liteType: lite.liteType || 'glass',
      width_in: lite.width_in === '' || lite.width_in === null || lite.width_in === undefined ? null : parseFloat(lite.width_in),
      height_in: lite.height_in === '' || lite.height_in === null || lite.height_in === undefined ? null : parseFloat(lite.height_in),
      glassTypeId: lite.liteType === 'glass' && lite.glassTypeId ? lite.glassTypeId : null,
      metalPanelTypeId: lite.liteType === 'metalPanel' && lite.metalPanelTypeId ? lite.metalPanelTypeId : null
    }));
    const liteIds = new Set(lites.map(l => l.id));
    // Drop marks whose lite no longer exists so nothing is orphaned
    const liteMarks = (newWindow.liteMarks || [])
      .filter(mark => liteIds.has(mark.liteId))
      .map(mark => ({ id: mark.id, liteId: mark.liteId, x: Number(mark.x), y: Number(mark.y) }));

    const payload = {
      ...newWindow,
      lites,
      liteMarks,
      liteMarksTypeId: liteMarks.length > 0 ? newWindow.windowTypeId : null
    };

    setWindowOperationLoading(true);
    try {
      if (editingWindow) {
        // Update existing window (schedule info + details in a single request)
        await axios.put(`${API}/window-list/${editingWindow.id}`, {
          ...payload,
          projectId
        });
        toast.success('Window updated');
      } else {
        // Create new window (schedule info + details in a single request)
        await axios.post(`${API}/window-list`, {
          ...payload,
          projectId,
          csiItemId: activeCsiTab  // Link to current CSI item instance
        });
        toast.success('Window created');
      }
      
      fetchProjectData();
      setShowWindowDialog(false);
      setEditingWindow(null);
      setNewWindow({
        windowTypeId: '',
        label: '',
        location: '',
        notes: '',
        glassTypeId: '',
        windowWidth_in: null,
        windowHeight_in: null,
        roWidth_in: null,
        roHeight_in: null,
        lites: [],
        hasGrid: false,
        gridSize_in: null
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save window');
      }
    } finally {
      setWindowOperationLoading(false);
    }
  };

  // Curtainwall Type Handlers
  const handleCreateCWType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (cwOperationLoading) return;

    // Dimension validation: 0 and negative not allowed (native HTML5 balloon shows)
    const dimError = validatePositiveDimensions({
      'Width (in)': newCWType.sectionWidth_in,
      'Height (in)': newCWType.sectionHeight_in,
      'RO Width (in)': newCWType.roWidth_in,
      'RO Height (in)': newCWType.roHeight_in
    });
    if (dimError) return;

    setCwOperationLoading(true);
    try {
      let createdTypeId = null;
      
      if (editingCWType) {
        await axios.put(`${API}/curtainwall-types/${editingCWType.id}`, { ...newCWType, projectId });
        toast.success('Curtainwall type updated');
      } else {
        const response = await axios.post(`${API}/curtainwall-types`, { ...newCWType, projectId, csiItemId: activeCsiTab });
        createdTypeId = response.data.id;
        toast.success('Curtainwall type created');
        
        // Upload pending drawing if exists
        if (pendingCWTypePicture?.file && createdTypeId) {
          try {
            setCWTypePictureUploading(createdTypeId);
            const formData = new FormData();
            formData.append('file', pendingCWTypePicture.file);
            
            await axios.post(
              `${API}/curtainwall-types/${createdTypeId}/upload-drawing`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Drawing uploaded successfully');
          } catch (picError) {
            toast.error('Type created but drawing upload failed');
          } finally {
            setCWTypePictureUploading(null);
            if (pendingCWTypePicture?.previewUrl) {
              URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
            }
            setPendingCWTypePicture(null);
          }
        }
      }
      fetchProjectData();
      setShowCWTypeDialog(false);
      setEditingCWType(null);
      setCWTypePictureUrl(null);
      if (pendingCWTypePicture?.previewUrl) {
        URL.revokeObjectURL(pendingCWTypePicture.previewUrl);
      }
      setPendingCWTypePicture(null);
      setNewCWType({
        name: '',
        manufacturer: '',
        model: '',
        material: '',
        finishType: '',
        interiorFinishColor: '',
        exteriorFinishColor: '',
        sectionWidth_in: null,
        sectionHeight_in: null,
        roWidth_in: null,
        roHeight_in: null,
        productWarrantyYears: null,
        hardwareWarrantyYears: null,
        finishWarrantyYears: null,
        installationWarrantyYears: null
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to save curtainwall type'));
      }
    } finally {
      setCwOperationLoading(false);
    }
  };

  const handleEditCWType = (cwType) => {
    setEditingCWType(cwType);
    setCWTypePictureUrl(null); // Clear any previous clipboard upload preview
    setNewCWType({
      name: cwType.name,
      manufacturer: cwType.manufacturer,
      model: cwType.model,
      material: cwType.material || '',
      finishType: cwType.finishType,
      interiorFinishColor: cwType.interiorFinishColor || '',
      exteriorFinishColor: cwType.exteriorFinishColor || '',
      sectionWidth_in: cwType.sectionWidth_in || null,
      sectionHeight_in: cwType.sectionHeight_in || null,
      roWidth_in: cwType.roWidth_in || null,
      roHeight_in: cwType.roHeight_in || null,
      productWarrantyYears: cwType.productWarrantyYears || null,
      hardwareWarrantyYears: cwType.hardwareWarrantyYears || null,
      finishWarrantyYears: cwType.finishWarrantyYears || null,
      installationWarrantyYears: cwType.installationWarrantyYears || null,
      markerColor: cwType.markerColor || '#39FF14'
    });
    setShowCWTypeDialog(true);
  };

  const handleDeleteCWType = (cwTypeId) => {
    setDeleteCWTypeId(cwTypeId);
    setShowDeleteCWTypeModal(true);
  };
  
  const confirmDeleteCWType = async () => {
    if (!deleteCWTypeId) return;
    try {
      await axios.delete(`${API}/curtainwall-types/${deleteCWTypeId}`);
      toast.success('Curtainwall type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete curtainwall type');
    } finally {
      setDeleteCWTypeId(null);
    }
  };

  // Glass Type Handlers
  const handleCreateGlassType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (cwOperationLoading) return;
    
    setCwOperationLoading(true);
    try {
      const payload = {
        ...newGlassType,
        gridSize_in: newGlassType.hasGrid ? parseNumericInput(newGlassType.gridSize_in) : null,
        warrantyYears: parseIntegerInput(newGlassType.warrantyYears),
      };
      if (editingGlassType) {
        await axios.put(`${API}/glass-types/${editingGlassType.id}`, { ...payload, projectId });
        toast.success('Glass type updated');
      } else {
        await axios.post(`${API}/glass-types`, { ...payload, projectId, csiItemId: activeCsiTab });
        toast.success('Glass type created');
      }
      fetchProjectData();
      setShowGlassTypeDialog(false);
      setEditingGlassType(null);
      setNewGlassType({
        name: '',
        manufacturer: '',
        model: '',
        thickness_in: null,
        specifications: [],
        colorCode: '',
        argonFilled: false,
        lowECoated: false,
        build: '',
        hasGrid: false,
        gridSize_in: '',
        warrantyYears: ''
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save glass type');
      }
    } finally {
      setCwOperationLoading(false);
    }
  };

  const handleEditGlassType = (glassType) => {
    setEditingGlassType(glassType);
    setNewGlassType({
      name: glassType.name,
      manufacturer: glassType.manufacturer,
      model: glassType.model,
      thickness_in: glassType.thickness_in,
      specifications: glassType.specifications || [],
      colorCode: glassType.colorCode || '',
      argonFilled: glassType.argonFilled || false,
      lowECoated: glassType.lowECoated || false,
      warrantyYears: glassType.warrantyYears?.toString() ?? '',
      build: glassType.build || '',
      hasGrid: glassType.hasGrid || false,
      gridSize_in: glassType.gridSize_in?.toString() ?? ''
    });
    setShowGlassTypeDialog(true);
  };

  const handleDeleteGlassType = (glassTypeId) => {
    setDeleteGlassId(glassTypeId);
    setShowDeleteGlassModal(true);
  };
  
  const confirmDeleteGlassType = async () => {
    if (!deleteGlassId) return;
    try {
      await axios.delete(`${API}/glass-types/${deleteGlassId}`);
      toast.success('Glass type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(describeGlassTypeConflict(error), { duration: 9000 });
    } finally {
      setDeleteGlassId(null);
    }
  };

  // Metal Panel Type Handlers
  const handleCreateMetalType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (cwOperationLoading) return;
    
    setCwOperationLoading(true);
    try {
      const payload = { ...newMetalType, thickness_mm: parseNumericInput(newMetalType.thickness_mm) };
      if (editingMetalType) {
        await axios.put(`${API}/metal-panel-types/${editingMetalType.id}`, { ...payload, projectId });
        toast.success('Metal panel type updated');
      } else {
        await axios.post(`${API}/metal-panel-types`, { ...payload, projectId, csiItemId: activeCsiTab });
        toast.success('Metal panel type created');
      }
      fetchProjectData();
      setShowMetalTypeDialog(false);
      setEditingMetalType(null);
      setNewMetalType({
        name: '',
        manufacturer: '',
        model: '',
        thickness_mm: '',
        colorCode: ''
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save metal panel type');
      }
    } finally {
      setCwOperationLoading(false);
    }
  };

  const handleEditMetalType = (metalType) => {
    setEditingMetalType(metalType);
    setNewMetalType({
      name: metalType.name,
      manufacturer: metalType.manufacturer,
      model: metalType.model,
      thickness_mm: metalType.thickness_mm?.toString() ?? '',
      colorCode: metalType.colorCode || ''
    });
    setShowMetalTypeDialog(true);
  };

  const handleDeleteMetalType = (metalTypeId) => {
    setDeleteMetalId(metalTypeId);
    setShowDeleteMetalModal(true);
  };
  
  const confirmDeleteMetalType = async () => {
    if (!deleteMetalId) return;
    try {
      await axios.delete(`${API}/metal-panel-types/${deleteMetalId}`);
      toast.success('Metal panel type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete metal panel type');
    } finally {
      setDeleteMetalId(null);
    }
  };

  // ============ STOREFRONT HANDLERS ============
  
  // Storefront Type Handlers
  const handleCreateSFType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (sfOperationLoading) return;

    // Dimension validation: 0 and negative not allowed (native HTML5 balloon shows)
    const dimError = validatePositiveDimensions({
      'Width (in)': newSFType.sectionWidth_in,
      'Height (in)': newSFType.sectionHeight_in,
      'RO Width (in)': newSFType.roWidth_in,
      'RO Height (in)': newSFType.roHeight_in
    });
    if (dimError) return;

    setSfOperationLoading(true);
    try {
      let createdTypeId = null;
      
      if (editingSFType) {
        await axios.put(`${API}/storefront-types/${editingSFType.id}`, { ...newSFType, projectId });
        toast.success('Storefront type updated');
      } else {
        const response = await axios.post(`${API}/storefront-types`, { ...newSFType, projectId, csiItemId: activeCsiTab });
        createdTypeId = response.data.id;
        toast.success('Storefront type created');
        
        // Upload pending drawing if exists
        if (pendingSFTypePicture?.file && createdTypeId) {
          try {
            setSFTypePictureUploading(createdTypeId);
            const formData = new FormData();
            formData.append('file', pendingSFTypePicture.file);
            
            await axios.post(
              `${API}/storefront-types/${createdTypeId}/upload-drawing`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Drawing uploaded successfully');
          } catch (picError) {
            toast.error('Type created but drawing upload failed');
          } finally {
            setSFTypePictureUploading(null);
            if (pendingSFTypePicture?.previewUrl) {
              URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
            }
            setPendingSFTypePicture(null);
          }
        }
      }
      fetchProjectData();
      setShowSFTypeDialog(false);
      setEditingSFType(null);
      setSFTypePictureUrl(null);
      if (pendingSFTypePicture?.previewUrl) {
        URL.revokeObjectURL(pendingSFTypePicture.previewUrl);
      }
      setPendingSFTypePicture(null);
      setNewSFType({
        name: '',
        manufacturer: '',
        model: '',
        material: '',
        finishType: '',
        interiorFinishColor: '',
        exteriorFinishColor: '',
        sectionWidth_in: null,
        sectionHeight_in: null,
        roWidth_in: null,
        roHeight_in: null,
        productWarrantyYears: null,
        hardwareWarrantyYears: null,
        finishWarrantyYears: null,
        installationWarrantyYears: null
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to save storefront type'));
      }
    } finally {
      setSfOperationLoading(false);
    }
  };

  const handleEditSFType = (sfType) => {
    setEditingSFType(sfType);
    setSFTypePictureUrl(null); // Clear any previous clipboard upload preview
    setNewSFType({
      name: sfType.name,
      manufacturer: sfType.manufacturer,
      model: sfType.model,
      material: sfType.material || '',
      finishType: sfType.finishType,
      interiorFinishColor: sfType.interiorFinishColor || '',
      exteriorFinishColor: sfType.exteriorFinishColor || '',
      sectionWidth_in: sfType.sectionWidth_in || null,
      sectionHeight_in: sfType.sectionHeight_in || null,
      roWidth_in: sfType.roWidth_in || null,
      roHeight_in: sfType.roHeight_in || null,
      productWarrantyYears: sfType.productWarrantyYears || null,
      hardwareWarrantyYears: sfType.hardwareWarrantyYears || null,
      finishWarrantyYears: sfType.finishWarrantyYears || null,
      installationWarrantyYears: sfType.installationWarrantyYears || null,
      markerColor: sfType.markerColor || '#39FF14'
    });
    setShowSFTypeDialog(true);
  };

  const handleDeleteSFType = (sfTypeId) => {
    setDeleteSFTypeId(sfTypeId);
    setShowDeleteSFTypeModal(true);
  };

  const confirmDeleteSFType = async () => {
    if (!deleteSFTypeId) return;
    try {
      await axios.delete(`${API}/storefront-types/${deleteSFTypeId}`);
      toast.success('Storefront type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete storefront type');
    } finally {
      setDeleteSFTypeId(null);
      setShowDeleteSFTypeModal(false);
    }
  };

  // Storefront Glass Type Handlers
  const handleCreateSFGlassType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (sfOperationLoading) return;
    
    setSfOperationLoading(true);
    try {
      const payload = {
        ...newSFGlassType,
        gridSize_in: newSFGlassType.hasGrid ? parseNumericInput(newSFGlassType.gridSize_in) : null,
        warrantyYears: parseIntegerInput(newSFGlassType.warrantyYears),
      };
      if (editingSFGlassType) {
        await axios.put(`${API}/storefront-glass-types/${editingSFGlassType.id}`, { ...payload, projectId });
        toast.success('Glass type updated');
      } else {
        await axios.post(`${API}/storefront-glass-types`, { ...payload, projectId, csiItemId: activeCsiTab });
        toast.success('Glass type created');
      }
      fetchProjectData();
      setShowSFGlassTypeDialog(false);
      setEditingSFGlassType(null);
      setNewSFGlassType({
        name: '',
        manufacturer: '',
        model: '',
        thickness_in: null,
        specifications: [],
        colorCode: '',
        argonFilled: false,
        lowECoated: false,
        build: '',
        hasGrid: false,
        gridSize_in: '',
        warrantyYears: ''
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save glass type');
      }
    } finally {
      setSfOperationLoading(false);
    }
  };

  const handleEditSFGlassType = (glassType) => {
    setEditingSFGlassType(glassType);
    setNewSFGlassType({
      name: glassType.name,
      manufacturer: glassType.manufacturer,
      model: glassType.model,
      thickness_in: glassType.thickness_in,
      specifications: glassType.specifications || [],
      colorCode: glassType.colorCode || '',
      argonFilled: glassType.argonFilled || false,
      lowECoated: glassType.lowECoated || false,
      warrantyYears: glassType.warrantyYears?.toString() ?? '',
      build: glassType.build || '',
      hasGrid: glassType.hasGrid || false,
      gridSize_in: glassType.gridSize_in?.toString() ?? ''
    });
    setShowSFGlassTypeDialog(true);
  };

  const handleDeleteSFGlassType = (glassTypeId) => {
    setDeleteSFGlassId(glassTypeId);
    setShowDeleteSFGlassModal(true);
  };

  const confirmDeleteSFGlass = async () => {
    if (!deleteSFGlassId) return;
    try {
      await axios.delete(`${API}/storefront-glass-types/${deleteSFGlassId}`);
      toast.success('Glass type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(describeGlassTypeConflict(error), { duration: 9000 });
    } finally {
      setDeleteSFGlassId(null);
      setShowDeleteSFGlassModal(false);
    }
  };

  // Storefront Metal Panel Type Handlers
  const handleCreateSFMetalType = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (sfOperationLoading) return;
    
    setSfOperationLoading(true);
    try {
      const payload = { ...newSFMetalType, thickness_mm: parseNumericInput(newSFMetalType.thickness_mm) };
      if (editingSFMetalType) {
        await axios.put(`${API}/storefront-metal-panel-types/${editingSFMetalType.id}`, { ...payload, projectId });
        toast.success('Metal panel type updated');
      } else {
        await axios.post(`${API}/storefront-metal-panel-types`, { ...payload, projectId, csiItemId: activeCsiTab });
        toast.success('Metal panel type created');
      }
      fetchProjectData();
      setShowSFMetalTypeDialog(false);
      setEditingSFMetalType(null);
      setNewSFMetalType({
        name: '',
        manufacturer: '',
        model: '',
        thickness_mm: '',
        colorCode: ''
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save metal panel type');
      }
    } finally {
      setSfOperationLoading(false);
    }
  };

  const handleEditSFMetalType = (metalType) => {
    setEditingSFMetalType(metalType);
    setNewSFMetalType({
      name: metalType.name,
      manufacturer: metalType.manufacturer,
      model: metalType.model,
      thickness_mm: metalType.thickness_mm?.toString() ?? '',
      colorCode: metalType.colorCode || ''
    });
    setShowSFMetalTypeDialog(true);
  };

  const handleDeleteSFMetalType = (metalTypeId) => {
    setDeleteSFMetalId(metalTypeId);
    setShowDeleteSFMetalModal(true);
  };

  const confirmDeleteSFMetal = async () => {
    if (!deleteSFMetalId) return;
    try {
      await axios.delete(`${API}/storefront-metal-panel-types/${deleteSFMetalId}`);
      toast.success('Metal panel type deleted');
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete metal panel type');
    } finally {
      setDeleteSFMetalId(null);
      setShowDeleteSFMetalModal(false);
    }
  };

  // Storefront Panel Handlers
  const emptySFPanelForm = {
    storefrontTypeId: '',
    label: '',
    location: '',
    width_in: '',
    height_in: '',
    roWidth_in: '',
    roHeight_in: '',
    lites: [],
    liteMarks: [],
    liteMarksTypeId: null
  };

  const handleCreateSFPanel = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    // Prevent double-submit
    if (sfOperationLoading) return;

    // Dimension validation: 0 and negative not allowed
    const dimError = validatePositiveDimensions({
      'Storefront Width (in)': newSFPanel.width_in,
      'Storefront Height (in)': newSFPanel.height_in,
      'Rough Opening Width (in)': newSFPanel.roWidth_in,
      'Rough Opening Height (in)': newSFPanel.roHeight_in
    });
    if (dimError) {
      toast.error(dimError);
      return;
    }

    const toNumber = (value) => (value === '' || value === null || value === undefined ? null : parseNumericInput(value));
    const lites = (newSFPanel.lites || []).map(lite => ({
      id: lite.id || newLiteId(),
      liteType: lite.liteType || 'glass',
      width_in: toNumber(lite.width_in),
      height_in: toNumber(lite.height_in),
      glassTypeId: lite.liteType === 'glass' && lite.glassTypeId ? lite.glassTypeId : null,
      metalPanelTypeId: lite.liteType === 'metalPanel' && lite.metalPanelTypeId ? lite.metalPanelTypeId : null
    }));
    const liteIds = new Set(lites.map(l => l.id));
    // Drop marks whose lite no longer exists so nothing is orphaned
    const liteMarks = (newSFPanel.liteMarks || [])
      .filter(mark => liteIds.has(mark.liteId))
      .map(mark => ({ id: mark.id, liteId: mark.liteId, x: Number(mark.x), y: Number(mark.y) }));

    setSfOperationLoading(true);
    try {
      // Both steps are saved together: schedule info + dimensions/lites
      const payload = {
        projectId,
        storefrontTypeId: newSFPanel.storefrontTypeId,
        label: newSFPanel.label,
        location: newSFPanel.location,
        width_in: toNumber(newSFPanel.width_in),
        height_in: toNumber(newSFPanel.height_in),
        roWidth_in: toNumber(newSFPanel.roWidth_in),
        roHeight_in: toNumber(newSFPanel.roHeight_in),
        lites,
        liteMarks,
        liteMarksTypeId: liteMarks.length > 0 ? newSFPanel.storefrontTypeId : null
      };
      if (editingPanel) {
        await axios.put(`${API}/storefront-panel-list/${editingPanel.id}`, payload);
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API}/storefront-panel-list`, { ...payload, csiItemId: activeCsiTab });
        toast.success('Schedule created');
      }
      fetchProjectData();
      setShowSFPanelDialog(false);
      setEditingPanel(null);
      setNewSFPanel(emptySFPanelForm);
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to save schedule'));
      }
    } finally {
      setSfOperationLoading(false);
    }
  };

  const handleEditSFPanel = (panel) => {
    // Legacy single-panel records (panelType=GL/MT) are shown as one lite
    let lites = [];
    if (Array.isArray(panel.lites) && panel.lites.length > 0) {
      lites = panel.lites.map(lite => ({
        id: lite.id || newLiteId(),
        liteType: lite.liteType || 'glass',
        width_in: lite.width_in ?? '',
        height_in: lite.height_in ?? '',
        glassTypeId: lite.glassTypeId || '',
        metalPanelTypeId: lite.metalPanelTypeId || ''
      }));
    } else if (panel.panelType) {
      lites = [{
        id: newLiteId(),
        liteType: panel.panelType === 'MT' ? 'metalPanel' : 'glass',
        width_in: panel.width_in ?? '',
        height_in: panel.height_in ?? '',
        glassTypeId: panel.glassTypeId || '',
        metalPanelTypeId: panel.metalTypeId || ''
      }];
    }
    const liteIds = new Set(lites.map(l => l.id));
    // Marks only apply to the SF Type they were placed on
    const marksMatchType = !panel.liteMarksTypeId || panel.liteMarksTypeId === panel.storefrontTypeId;
    const liteMarks = marksMatchType
      ? (panel.liteMarks || []).filter(mark => liteIds.has(mark.liteId))
      : [];
    setEditingPanel(panel);
    setNewSFPanel({
      storefrontTypeId: panel.storefrontTypeId,
      label: panel.label,
      location: panel.location || '',
      width_in: panel.width_in ?? '',
      height_in: panel.height_in ?? '',
      roWidth_in: panel.roWidth_in ?? '',
      roHeight_in: panel.roHeight_in ?? '',
      lites,
      liteMarks,
      liteMarksTypeId: panel.liteMarksTypeId || null
    });
    setShowSFPanelDialog(true);
  };

  const handleSFPanelDialogOpenChange = (open) => {
    setShowSFPanelDialog(open);
    if (!open) {
      setEditingPanel(null);
      setNewSFPanel(emptySFPanelForm);
    }
  };

  const handleDeleteSFPanel = (panelId) => {
    setDeleteSFPanelId(panelId);
    setShowDeleteSFPanelModal(true);
  };

  const confirmDeleteSFPanel = async () => {
    if (!deleteSFPanelId) return;
    try {
      await axios.delete(`${API}/storefront-panel-list/${deleteSFPanelId}`);
      toast.success('Panel deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete panel');
    } finally {
      setDeleteSFPanelId(null);
      setShowDeleteSFPanelModal(false);
    }
  };


  // Storefront Drawing Handlers
  const handleSFDrawingUpload = async (e, sfTypeId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setSFTypePictureUploading(sfTypeId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/storefront-types/${sfTypeId}/upload-drawing`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Drawing uploaded successfully');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to upload drawing');
    } finally {
      setSFTypePictureUploading(null);
    }
  };

  const handleSFDrawingClick = (e, sfType) => {
    if (!sfType.drawingCloudinary?.urls?.medium && !sfType.drawingUrl) return;
    
    // Close any open marker popup first
    setActiveMarkerPopup(null);
    
    setActiveSFTypeForMarker(sfType);
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setPendingMarker({ x, y, width: 5, height: 5 });
    setShowMarkerDialog(true);
    setMarkerContext('storefront');
  };

  // Delete SF Drawing handlers
  const handleDeleteSFDrawing = (sfTypeId) => {
    setDeleteSFDrawingId(sfTypeId);
    setShowDeleteSFDrawingModal(true);
  };

  const confirmDeleteSFDrawing = async () => {
    if (!deleteSFDrawingId) return;
    try {
      await axios.delete(`${API}/storefront-types/${deleteSFDrawingId}/drawing`);
      toast.success('Drawing deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete drawing');
    } finally {
      setDeleteSFDrawingId(null);
      setShowDeleteSFDrawingModal(false);
    }
  };

  // Storefront Panel Excel Import Handlers
  const handleSFPanelExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are allowed');
      return;
    }
    
    setUploadingPanelExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/storefront-panel-list/upload-excel/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPanelExcelPreview(response.data);
      setShowPanelExcelDialog(true);
      toast.success(`Parsed ${response.data.totalRows} rows from Excel`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploadingPanelExcel(false);
      e.target.value = '';
    }
  };

  const handleSFPanelBulkImport = async () => {
    if (!panelExcelPreview) return;
    
    // Validate all rows have required selections
    const incomplete = panelExcelPreview.rows.filter(r => 
      !r.storefrontTypeId || !r.panelType || 
      (r.panelType === 'GL' && !r.glassTypeId) ||
      (r.panelType === 'MT' && !r.metalTypeId)
    );
    
    if (incomplete.length > 0) {
      toast.error(`${incomplete.length} rows need SF type and panel type selection`);
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/storefront-panel-list/bulk-import/${projectId}`,
        {
          csiItemId: activeCsiTab,
          panels: panelExcelPreview.rows
        }
      );
      
      toast.success(`Successfully imported ${response.data.imported} panels`);
      setShowPanelExcelDialog(false);
      setPanelExcelPreview(null);
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import panels');
    }
  };



  const handleCWDrawingUpload = async (e, cwTypeId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCWTypePictureUploading(cwTypeId);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(
        `${API}/curtainwall-types/${cwTypeId}/upload-drawing`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Drawing uploaded successfully');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to upload drawing');
    } finally {
      setCWTypePictureUploading(null);
    }
  };

  const handleDeleteAllSFPanels = async () => {
    setShowDeleteAllSFPanelsModal(true);
  };

  const confirmDeleteAllSFPanels = async () => {
    try {
      await Promise.all(
        storefrontPanelList.map(panel => axios.delete(`${API}/storefront-panel-list/${panel.id}`))
      );
      toast.success('All panels deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete all panels');
    } finally {
      setShowDeleteAllSFPanelsModal(false);
    }
  };

  const handlePanelExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are allowed');
      return;
    }
    
    setUploadingPanelExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/panel-list/upload-excel/${projectId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setPanelExcelPreview(response.data);
      setShowPanelExcelDialog(true);
      toast.success(`Parsed ${response.data.totalRows} rows from Excel`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setUploadingPanelExcel(false);
      e.target.value = '';
    }
  };

  const handlePanelBulkImport = async () => {
    if (!panelExcelPreview) return;
    
    // Validate all rows have required selections
    const incomplete = panelExcelPreview.rows.filter(r => 
      !r.curtainwallTypeId || !r.panelType || 
      (r.panelType === 'GL' && !r.glassTypeId) ||
      (r.panelType === 'MT' && !r.metalTypeId)
    );
    
    if (incomplete.length > 0) {
      toast.error(`${incomplete.length} rows need CW type and panel type selection`);
      return;
    }
    
    try {
      // Add csiItemId to all rows
      const rowsWithCsiItem = panelExcelPreview.rows.map(row => ({
        ...row,
        csiItemId: activeCsiTab
      }));
      
      const response = await axios.post(
        `${API}/panel-list/bulk-import/${projectId}`,
        { rows: rowsWithCsiItem }
      );
      
      toast.success(`Imported ${response.data.created} panels`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} errors occurred`);
        console.error('Import errors:', response.data.errors);
      }
      
      await fetchProjectData();
      setShowPanelExcelDialog(false);
      setPanelExcelPreview(null);
    } catch (error) {
      toast.error('Failed to import panels');
    }
  };

  const handleUpdatePanelPreviewRow = (rowIndex, field, value) => {
    setPanelExcelPreview(prev => ({
      ...prev,
      rows: prev.rows.map((row, idx) => 
        idx === rowIndex ? { ...row, [field]: value } : row
      )
    }));
  };

  const handleCreatePanel = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (cwOperationLoading) return;

    setCwOperationLoading(true);
    try {
      if (editingPanel) {
        // Only update basic schedule fields; dimensions/lites are managed via Curtainwall Details modal
        await axios.put(`${API}/panel-list/${editingPanel.id}`, {
          projectId,
          curtainwallTypeId: newPanel.curtainwallTypeId,
          label: newPanel.label,
          location: newPanel.location
        });
        toast.success('Schedule updated');
      } else {
        await axios.post(`${API}/panel-list`, {
          projectId,
          csiItemId: activeCsiTab,
          curtainwallTypeId: newPanel.curtainwallTypeId,
          label: newPanel.label,
          location: newPanel.location
        });
        toast.success('Schedule created');
      }
      fetchProjectData();
      setShowPanelDialog(false);
      setEditingPanel(null);
      setNewPanel({
        curtainwallTypeId: '',
        label: '',
        location: ''
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to save schedule'));
      }
    } finally {
      setCwOperationLoading(false);
    }
  };

  const handleEditPanel = (panel) => {
    setEditingPanel(panel);
    setNewPanel({
      curtainwallTypeId: panel.curtainwallTypeId,
      label: panel.label,
      location: panel.location || ''
    });
    setShowPanelDialog(true);
  };

  const handleDeletePanel = (panelId) => {
    setDeletePanelId(panelId);
    setShowDeletePanelModal(true);
  };
  
  const confirmDeletePanel = async () => {
    if (!deletePanelId) return;
    try {
      await axios.delete(`${API}/panel-list/${deletePanelId}`);
      toast.success('Panel deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete panel');
    } finally {
      setDeletePanelId(null);
    }
  };

  // ---- CW Panel Detail (Curtainwall Details modal: dimensions + lites) ----
  const openCWPanelDetail = (panel) => {
    setSelectedCWPanelDetail(panel);
    setShowCWPanelDetailDialog(true);
  };

  const handleSaveCWPanelDetail = async (detailData) => {
    if (!selectedCWPanelDetail) return;

    // Dimension validation: 0 and negative not allowed (native HTML5 balloon shows; lite dims validated by dialog)
    const dimError = validatePositiveDimensions({
      'Curtainwall Width (in)': detailData.width_in,
      'Curtainwall Height (in)': detailData.height_in,
      'Rough Opening Width (in)': detailData.roWidth_in,
      'Rough Opening Height (in)': detailData.roHeight_in
    });
    if (dimError) {
      throw new Error(dimError);
    }

    const lites = (detailData.lites || []).map(lite => ({
      liteType: lite.liteType || 'glass',
      width_in: lite.width_in === '' || lite.width_in === null ? null : parseFloat(lite.width_in),
      height_in: lite.height_in === '' || lite.height_in === null ? null : parseFloat(lite.height_in),
      glassTypeId: lite.liteType === 'glass' && lite.glassTypeId ? lite.glassTypeId : null,
      metalPanelTypeId: lite.liteType === 'metalPanel' && lite.metalPanelTypeId ? lite.metalPanelTypeId : null
    }));

    try {
      await axios.put(`${API}/panel-list/${selectedCWPanelDetail.id}`, {
        projectId: selectedCWPanelDetail.projectId,
        curtainwallTypeId: selectedCWPanelDetail.curtainwallTypeId,
        label: selectedCWPanelDetail.label,
        location: selectedCWPanelDetail.location,
        width_in: detailData.width_in,
        height_in: detailData.height_in,
        roWidth_in: detailData.roWidth_in,
        roHeight_in: detailData.roHeight_in,
        lites
      });

      toast.success('Curtainwall details saved');
      fetchProjectData();
      setShowCWPanelDetailDialog(false);
      setSelectedCWPanelDetail(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unexpected error occurred'));
      throw error; // re-throw so dialog knows save failed
    }
  };

  const handleDeleteAllPanels = () => {
    setShowDeleteAllPanelsModal(true);
  };
  
  const confirmDeleteAllPanels = async () => {
    try {
      await axios.delete(`${API}/panel-list/project/${projectId}/all`);
      toast.success('All panels deleted successfully');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete all panels');
      console.error('Delete all panels error:', error);
    }
  };
  
  const handleDeletePicture = (typeId) => {
    setDeletePictureTypeId(typeId);
    setShowDeletePictureModal(true);
  };
  
  const confirmDeletePicture = async () => {
    if (!deletePictureTypeId) return;
    try {
      await axios.delete(`${API}/window-types/${deletePictureTypeId}/picture`);
      toast.success('Picture deleted');
      fetchProjectData();
      // Clear the picture URL in modal if it was showing the deleted picture
      setTypePictureUrl(null);
    } catch (error) {
      toast.error('Failed to delete picture');
    } finally {
      setDeletePictureTypeId(null);
      setShowDeletePictureModal(false);
    }
  };
  
  const handleDeleteDrawing = (cwId) => {
    setDeleteDrawingCWId(cwId);
    setShowDeleteDrawingModal(true);
  };
  
  const confirmDeleteDrawing = async () => {
    if (!deleteDrawingCWId) return;
    try {
      await axios.delete(`${API}/curtainwall-types/${deleteDrawingCWId}/drawing`);
      toast.success('Drawing deleted');
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to delete drawing');
    } finally {
      setDeleteDrawingCWId(null);
      setShowDeleteDrawingModal(false);
    }
  };

  const handleDeleteDoorTypePicture = (doorTypeId) => {
    setDeleteDoorTypePictureId(doorTypeId);
    setShowDeleteDoorTypePictureModal(true);
  };
  
  const confirmDeleteDoorTypePicture = async () => {
    if (!deleteDoorTypePictureId) return;
    try {
      await axios.delete(`${API}/door-types/${deleteDoorTypePictureId}/picture`);
      toast.success('Picture deleted');
      // Update local state to remove the picture immediately
      setDoorTypes(prev => prev.map(dt => 
        dt.id === deleteDoorTypePictureId 
          ? { ...dt, pictureCloudinary: null, pictureUrl: null }
          : dt
      ));
      // Clear the picture URL in modal if it was showing the deleted picture
      setDoorTypePictureUrl(null);
    } catch (error) {
      toast.error('Failed to delete picture');
    } finally {
      setDeleteDoorTypePictureId(null);
      setShowDeleteDoorTypePictureModal(false);
    }
  };

  const handleDeleteDoorType = (doorTypeId) => {
    setDeleteDoorTypeId(doorTypeId);
    setShowDeleteDoorTypeModal(true);
  };

  const confirmDeleteDoorType = async () => {
    if (!deleteDoorTypeId) return;
    try {
      await axios.delete(`${API}/door-types/${deleteDoorTypeId}`);
      setDoorTypes(prev => prev.filter(dt => dt.id !== deleteDoorTypeId));
      toast.success('Door type deleted');
    } catch (error) {
      toast.error('Failed to delete door type');
    } finally {
      setDeleteDoorTypeId(null);
      setShowDeleteDoorTypeModal(false);
    }
  };

  // Door Glass Types Functions
  const fetchDoorGlassTypes = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/door-glass-types/${projectId}/${activeCsiTab}`);
      setDoorGlassTypes(response.data);
    } catch (error) {
      console.error('Error fetching door glass types:', error);
    }
  };

  const handleDeleteDoorGlassType = (glassTypeId) => {
    setDeleteDoorGlassTypeId(glassTypeId);
    setShowDeleteDoorGlassTypeModal(true);
  };

  const confirmDeleteDoorGlassType = async () => {
    if (!deleteDoorGlassTypeId) return;
    try {
      await axios.delete(`${API}/door-glass-types/${deleteDoorGlassTypeId}`);
      setDoorGlassTypes(prev => prev.filter(gt => gt.id !== deleteDoorGlassTypeId));
      toast.success('Glass type deleted');
    } catch (error) {
      toast.error(describeGlassTypeConflict(error), { duration: 9000 });
    } finally {
      setDeleteDoorGlassTypeId(null);
      setShowDeleteDoorGlassTypeModal(false);
    }
  };

  // Door Metal Panel Types Functions
  const fetchDoorMetalPanelTypes = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/door-metal-panel-types/${projectId}/${activeCsiTab}`);
      setDoorMetalPanelTypes(response.data);
    } catch (error) {
      console.error('Error fetching door metal panel types:', error);
    }
  };

  const handleDeleteDoorMetalPanelType = (metalTypeId) => {
    setDeleteDoorMetalPanelTypeId(metalTypeId);
    setShowDeleteDoorMetalPanelTypeModal(true);
  };

  const confirmDeleteDoorMetalPanelType = async () => {
    if (!deleteDoorMetalPanelTypeId) return;
    try {
      await axios.delete(`${API}/door-metal-panel-types/${deleteDoorMetalPanelTypeId}`);
      setDoorMetalPanelTypes(prev => prev.filter(mt => mt.id !== deleteDoorMetalPanelTypeId));
      toast.success('Metal panel type deleted');
    } catch (error) {
      toast.error('Failed to delete metal panel type');
    } finally {
      setDeleteDoorMetalPanelTypeId(null);
      setShowDeleteDoorMetalPanelTypeModal(false);
    }
  };

  // Window Metal Panel Types Functions
  const fetchWindowMetalPanelTypes = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/window-metal-panel-types/${projectId}/${activeCsiTab}`);
      setWindowMetalPanelTypes(response.data);
    } catch (error) {
      console.error('Error fetching window metal panel types:', error);
    }
  };

  const handleDeleteWindowMetalPanelType = (metalTypeId) => {
    setDeleteWindowMetalPanelTypeId(metalTypeId);
    setShowDeleteWindowMetalPanelTypeModal(true);
  };

  const confirmDeleteWindowMetalPanelType = async () => {
    if (!deleteWindowMetalPanelTypeId) return;
    try {
      await axios.delete(`${API}/window-metal-panel-types/${deleteWindowMetalPanelTypeId}`);
      setWindowMetalPanelTypes(prev => prev.filter(mt => mt.id !== deleteWindowMetalPanelTypeId));
      toast.success('Metal panel type deleted');
    } catch (error) {
      toast.error('Failed to delete metal panel type');
    } finally {
      setDeleteWindowMetalPanelTypeId(null);
      setShowDeleteWindowMetalPanelTypeModal(false);
    }
  };

  // Door Hardware Sets Functions
  const fetchDoorHardwareSets = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/door-hardware-sets/${projectId}/${activeCsiTab}`);
      setDoorHardwareSets(response.data);
    } catch (error) {
      console.error('Error fetching door hardware sets:', error);
    }
  };

  const handleDeleteDoorHardwareSet = (hardwareSetId) => {
    setDeleteDoorHardwareSetId(hardwareSetId);
    setShowDeleteDoorHardwareSetModal(true);
  };

  const confirmDeleteDoorHardwareSet = async () => {
    if (!deleteDoorHardwareSetId) return;
    try {
      await axios.delete(`${API}/door-hardware-sets/${deleteDoorHardwareSetId}`);
      setDoorHardwareSets(prev => prev.filter(hs => hs.id !== deleteDoorHardwareSetId));
      toast.success('Hardware set deleted');
    } catch (error) {
      toast.error('Failed to delete hardware set');
    } finally {
      setDeleteDoorHardwareSetId(null);
      setShowDeleteDoorHardwareSetModal(false);
    }
  };

  // Shower Hardware Sets Functions
  const fetchShowerHardwareSets = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/shower-hardware-sets/${projectId}/${activeCsiTab}`);
      setShowerHardwareSets(response.data);
    } catch (error) {
      console.error('Error fetching shower hardware sets:', error);
    }
  };

  const handleDeleteShowerHardwareSet = (hardwareSetId) => {
    setDeleteShowerHardwareSetId(hardwareSetId);
    setShowDeleteShowerHardwareSetModal(true);
  };

  const confirmDeleteShowerHardwareSet = async () => {
    if (!deleteShowerHardwareSetId) return;
    try {
      await axios.delete(`${API}/shower-hardware-sets/${deleteShowerHardwareSetId}`);
      setShowerHardwareSets(prev => prev.filter(hs => hs.id !== deleteShowerHardwareSetId));
      toast.success('Hardware set deleted');
    } catch (error) {
      toast.error('Failed to delete hardware set');
    } finally {
      setDeleteShowerHardwareSetId(null);
      setShowDeleteShowerHardwareSetModal(false);
    }
  };

  // Door Schedules Functions
  const fetchDoorSchedules = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/door-schedules/${projectId}/${activeCsiTab}`);
      setDoorSchedules(response.data);
    } catch (error) {
      console.error('Error fetching door schedules:', error);
    }
  };

  const handleDeleteDoorSchedule = (scheduleId) => {
    setDeleteDoorScheduleId(scheduleId);
    setShowDeleteDoorScheduleModal(true);
  };

  const confirmDeleteDoorSchedule = async () => {
    if (!deleteDoorScheduleId) return;
    try {
      await axios.delete(`${API}/door-schedules/${deleteDoorScheduleId}`);
      setDoorSchedules(prev => prev.filter(s => s.id !== deleteDoorScheduleId));
      toast.success('Door schedule deleted');
    } catch (error) {
      toast.error('Failed to delete door schedule');
    } finally {
      setDeleteDoorScheduleId(null);
      setShowDeleteDoorScheduleModal(false);
    }
  };

  const handleCreateDoorSchedule = async () => {
    if (!newDoorSchedule.doorTypeId || !newDoorSchedule.label.trim()) {
      toast.error('Type and Label are required');
      return;
    }

    // Prevent double-submit
    if (doorOperationLoading) return;
    
    setDoorOperationLoading(true);
    try {
      // Filter out empty lites and format data
      const validLites = (newDoorSchedule.lites || [])
        .filter(lite => lite.glassTypeId || lite.metalPanelTypeId || lite.width_in || lite.height_in)
        .map(lite => ({
          id: lite.id || newLiteId(),
          liteType: lite.liteType || 'glass',
          glassTypeId: lite.liteType === 'glass' ? (lite.glassTypeId || null) : null,
          metalPanelTypeId: lite.liteType === 'metalPanel' ? (lite.metalPanelTypeId || null) : null,
          width_in: lite.width_in ? parseFloat(lite.width_in) : null,
          height_in: lite.height_in ? parseFloat(lite.height_in) : null
        }));
      const liteIds = new Set(validLites.map(l => l.id));
      // Drop marks whose lite no longer exists so nothing is orphaned
      const liteMarks = (newDoorSchedule.liteMarks || [])
        .filter(mark => liteIds.has(mark.liteId))
        .map(mark => ({ id: mark.id, liteId: mark.liteId, x: Number(mark.x), y: Number(mark.y) }));

      const scheduleData = {
        projectId,
        csiItemId: activeCsiTab,
        doorTypeId: newDoorSchedule.doorTypeId,
        hardwareSetId: newDoorSchedule.hardwareSetId || null,
        label: newDoorSchedule.label.trim(),
        location: newDoorSchedule.location?.trim() || null,
        doorWidth_in: newDoorSchedule.doorWidth_in ? parseFloat(newDoorSchedule.doorWidth_in) : null,
        doorHeight_in: newDoorSchedule.doorHeight_in ? parseFloat(newDoorSchedule.doorHeight_in) : null,
        roWidth_in: newDoorSchedule.roWidth_in ? parseFloat(newDoorSchedule.roWidth_in) : null,
        roHeight_in: newDoorSchedule.roHeight_in ? parseFloat(newDoorSchedule.roHeight_in) : null,
        lites: validLites,
        liteMarks,
        liteMarksTypeId: liteMarks.length > 0 ? newDoorSchedule.doorTypeId : null
      };

      if (editingDoorSchedule) {
        const response = await axios.put(`${API}/door-schedules/${editingDoorSchedule.id}`, scheduleData);
        setDoorSchedules(prev => prev.map(s => s.id === editingDoorSchedule.id ? response.data : s));
        toast.success('Door schedule updated');
      } else {
        const response = await axios.post(`${API}/door-schedules`, scheduleData);
        setDoorSchedules(prev => [...prev, response.data]);
        toast.success('Door schedule created');
      }

      setShowDoorScheduleDialog(false);
      setEditingDoorSchedule(null);
      setNewDoorSchedule({
        doorTypeId: '',
        hardwareSetId: '',
        label: '',
        location: '',
        doorWidth_in: '',
        doorHeight_in: '',
        roWidth_in: '',
        roHeight_in: '',
        lites: [{ liteType: 'glass', glassTypeId: '', metalPanelTypeId: '', width_in: '', height_in: '' }]
      });
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save door schedule');
      }
    } finally {
      setDoorOperationLoading(false);
    }
  };

  // Door Detail Dialog handlers
  const openDoorDetail = (door) => {
    setSelectedDoorDetail(door);
    setShowDoorDetailDialog(true);
  };

  const handleSaveDoorDetail = async (detailData) => {
    if (!selectedDoorDetail) return;

    try {
      // Format lites data
      const formattedLites = detailData.lites
        ? detailData.lites.map(lite => ({
            liteType: lite.liteType || 'glass',
            glassTypeId: lite.liteType === 'glass' ? (lite.glassTypeId || null) : null,
            metalPanelTypeId: lite.liteType === 'metalPanel' ? (lite.metalPanelTypeId || null) : null,
            width_in: lite.width_in || null,
            height_in: lite.height_in || null
          }))
        : [];

      await axios.put(`${API}/door-schedules/${selectedDoorDetail.id}`, {
        projectId: selectedDoorDetail.projectId,
        csiItemId: selectedDoorDetail.csiItemId,
        doorTypeId: selectedDoorDetail.doorTypeId,
        hardwareSetId: selectedDoorDetail.hardwareSetId,
        label: selectedDoorDetail.label,
        location: selectedDoorDetail.location,
        doorWidth_in: detailData.doorWidth_in,
        doorHeight_in: detailData.doorHeight_in,
        roWidth_in: detailData.roWidth_in,
        roHeight_in: detailData.roHeight_in,
        lites: formattedLites
      });

      // Update local state
      setDoorSchedules(prev => prev.map(d => 
        d.id === selectedDoorDetail.id 
          ? { ...d, ...detailData, lites: formattedLites }
          : d
      ));

      toast.success('Door details saved successfully');
      setShowDoorDetailDialog(false);
      setSelectedDoorDetail(null);
    } catch (error) {
      toast.error('Failed to save door details');
      console.error('Save door detail error:', error);
    }
  };

  // ============================================
  // MIRROR TYPES HANDLERS (08 00 01 - Mirrors)
  // ============================================
  
  const fetchMirrorTypes = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/mirror-types/${projectId}/${activeCsiTab}`);
      setMirrorTypes(response.data);
    } catch (error) {
      console.error('Error fetching mirror types:', error);
    }
  };

  const fetchMirrorSchedules = async () => {
    if (!projectId || !activeCsiTab) return;
    try {
      const response = await axios.get(`${API}/mirror-schedules/${projectId}/${activeCsiTab}`);
      setMirrorSchedules(response.data);
    } catch (error) {
      console.error('Error fetching mirror schedules:', error);
    }
  };

  const handleCreateMirrorType = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newMirrorType.name.trim() || !newMirrorType.manufacturer.trim() || !newMirrorType.model.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Prevent double-submit
    if (mirrorOperationLoading) return;
    
    setMirrorOperationLoading(true);
    try {
      const payload = {
        projectId,
        csiItemId: activeCsiTab,
        name: newMirrorType.name.trim(),
        manufacturer: newMirrorType.manufacturer.trim(),
        model: newMirrorType.model.trim(),
        mirrorThickness: newMirrorType.mirrorThickness?.trim() || null,
        frameMaterial: newMirrorType.frameMaterial?.trim() || null,
        frameFinish: newMirrorType.frameFinish?.trim() || null,
        description: newMirrorType.description?.trim() || null,
        mirrorWarrantyYears: newMirrorType.mirrorWarrantyYears || null,
        installationWarrantyYears: newMirrorType.installationWarrantyYears || null,
        markerColor: newMirrorType.markerColor || '#3B82F6'
      };
      
      let savedMirrorType;
      if (editingMirrorType) {
        const response = await axios.put(`${API}/mirror-types/${editingMirrorType.id}`, payload);
        savedMirrorType = response.data;
        setMirrorTypes(prev => prev.map(mt => mt.id === editingMirrorType.id ? savedMirrorType : mt));
        toast.success('Mirror type updated successfully');
      } else {
        const response = await axios.post(`${API}/mirror-types`, payload);
        savedMirrorType = response.data;
        setMirrorTypes(prev => [...prev, savedMirrorType]);
        toast.success('Mirror type created successfully');
      }
      
      // Upload pending picture if exists
      if (pendingMirrorTypePicture?.file && savedMirrorType) {
        setMirrorTypePictureUploading(savedMirrorType.id);
        try {
          const formData = new FormData();
          formData.append('file', pendingMirrorTypePicture.file);
          const uploadRes = await axios.post(`${API}/mirror-types/${savedMirrorType.id}/upload-picture`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          // Update mirror type with picture URL
          setMirrorTypes(prev => prev.map(mt => 
            mt.id === savedMirrorType.id 
              ? { ...mt, pictureUrl: uploadRes.data.pictureUrl, pictureCloudinary: uploadRes.data.pictureCloudinary }
              : mt
          ));
          toast.success('Picture uploaded successfully');
        } catch (uploadError) {
          console.error('Picture upload error:', uploadError);
          toast.error('Failed to upload picture');
        } finally {
          setMirrorTypePictureUploading(null);
        }
      }
      
      // Reset dialog
      resetMirrorTypeDialog();
      
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save mirror type');
      }
    } finally {
      setMirrorOperationLoading(false);
    }
  };

  const handleDeleteMirrorType = (mirrorTypeId) => {
    setDeleteMirrorTypeId(mirrorTypeId);
    setShowDeleteMirrorTypeModal(true);
  };

  const executeDeleteMirrorType = async () => {
    if (!deleteMirrorTypeId) return;
    try {
      await axios.delete(`${API}/mirror-types/${deleteMirrorTypeId}`);
      setMirrorTypes(prev => prev.filter(mt => mt.id !== deleteMirrorTypeId));
      toast.success('Mirror type deleted successfully');
    } catch (error) {
      toast.error('Failed to delete mirror type');
    } finally {
      setDeleteMirrorTypeId(null);
      setShowDeleteMirrorTypeModal(false);
    }
  };

  const handleDeleteMirrorTypePicture = async (mirrorTypeId) => {
    if (!mirrorTypeId) return;
    try {
      // Clear picture by updating with null
      await axios.put(`${API}/mirror-types/${mirrorTypeId}`, {
        ...mirrorTypes.find(mt => mt.id === mirrorTypeId),
        projectId,
        csiItemId: activeCsiTab
      });
      // Note: Actual Cloudinary deletion would need a separate endpoint
      setMirrorTypePictureUrl(null);
      setMirrorTypes(prev => prev.map(mt => 
        mt.id === mirrorTypeId ? { ...mt, pictureUrl: null, pictureCloudinary: null } : mt
      ));
      toast.success('Picture removed');
    } catch (error) {
      toast.error('Failed to remove picture');
    }
  };

  // ============================================
  // MIRROR SCHEDULE HANDLERS
  // ============================================

  const handleCreateMirrorSchedule = async (e) => {
    e.preventDefault();
    
    if (!newMirrorSchedule.label.trim() || !newMirrorSchedule.mirrorTypeId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (mirrorOperationLoading) return;
    
    setMirrorOperationLoading(true);
    try {
      const payload = {
        projectId,
        csiItemId: activeCsiTab,
        mirrorTypeId: newMirrorSchedule.mirrorTypeId,
        label: newMirrorSchedule.label.trim(),
        location: newMirrorSchedule.location?.trim() || null,
        notes: newMirrorSchedule.notes?.trim() || null
      };
      
      if (editingMirrorSchedule) {
        await axios.put(`${API}/mirror-schedules/${editingMirrorSchedule.id}`, payload);
        toast.success('Mirror schedule updated');
      } else {
        await axios.post(`${API}/mirror-schedules`, payload);
        toast.success('Mirror schedule created');
      }
      
      resetMirrorScheduleDialog();
      await fetchMirrorSchedules();
    } catch (error) {
      console.error('Error saving mirror schedule:', error);
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save mirror schedule');
      }
    } finally {
      setMirrorOperationLoading(false);
    }
  };

  const handleDeleteMirrorSchedule = async (schedule, e) => {
    e?.stopPropagation();
    openConfirm({
      title: 'Delete Schedule',
      description: `Are you sure you want to delete mirror schedule "${schedule.label}"? This action cannot be undone and will delete all associated markers.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/mirror-schedules/${schedule.id}`);
          toast.success('Mirror schedule deleted');
          await fetchMirrorSchedules();
        } catch (error) {
          toast.error('Failed to delete schedule');
        }
        closeConfirm();
      },
    });
  };

  const handleBulkDeleteMirrorSchedules = async () => {
    if (selectedMirrorScheduleIds.length === 0) return;
    openConfirm({
      title: 'Delete Schedules',
      description: `Are you sure you want to delete ${selectedMirrorScheduleIds.length} selected schedule(s)?`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await axios.post(`${API}/mirror-schedules/bulk-delete`, {
            scheduleIds: selectedMirrorScheduleIds
          });
          toast.success(`${selectedMirrorScheduleIds.length} schedule(s) deleted`);
          clearMirrorScheduleSelections();
          await fetchMirrorSchedules();
        } catch (error) {
          toast.error('Failed to delete schedules');
        }
        closeConfirm();
      },
    });
  };

  const handlePrintAllMirrorScheduleQR = async () => {
    if (mirrorSchedules.length === 0) {
      toast.info('No mirror schedules to print');
      return;
    }
    try {
      const csiItemId = activeCsiItem?.id;
      window.open(`${API}/mirror-schedules/${projectId}/${csiItemId}/print-all-qr`, '_blank');
    } catch (err) {
      toast.error('Failed to print QR labels');
    }
  };

  const handlePrintMirrorScheduleQR = async (scheduleId) => {
    try {
      const response = await axios.get(`${API}/mirror-schedules/${scheduleId}/print-label`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('mirror');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintMirrorScheduleLabel = async (scheduleId) => {
    try {
      const response = await axios.get(`${API}/mirror-schedules/${scheduleId}/print-label`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPrintPreviewUrl(url);
      setPrintPreviewType('mirror');
      setShowPrintPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  const handlePrintAllDoorScheduleQR = async () => {
    if (doorSchedules.length === 0) {
      toast.info('No door schedules to print');
      return;
    }
    try {
      const csiItemId = activeCsiItem?.id;
      window.open(`${API}/door-schedules/${projectId}/${csiItemId}/print-all-qr`, '_blank');
    } catch (err) {
      toast.error('Failed to print QR labels');
    }
  };

  const handlePrintAllShowerScheduleQR = async () => {
    if (showerSchedules.length === 0) {
      toast.info('No shower schedules to print');
      return;
    }
    try {
      const csiItemId = activeCsiItem?.id;
      window.open(`${API}/shower-schedules/${projectId}/${csiItemId}/print-all-qr`, '_blank');
    } catch (err) {
      toast.error('Failed to print QR labels');
    }
  };

  const handleDoorExcelUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    setDoorExcelUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/door-schedules/upload-excel/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setDoorExcelPreview({ ...response.data, doorTypes: doorTypes });
      setShowDoorExcelDialog(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setDoorExcelUploading(false);
    }
  };

  const handleDoorBulkImport = async (skipDuplicates) => {
    if (!doorExcelPreview) return;
    const rowsToImport = skipDuplicates
      ? doorExcelPreview.rows.filter(r => !r.isDuplicate)
      : doorExcelPreview.rows;
    try {
      const response = await axios.post(`${API}/door-schedules/bulk-import/${projectId}`, {
        rows: rowsToImport,
        skipDuplicates,
        csiItemId: activeCsiItem?.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Imported ${response.data.created} door schedules. ${response.data.skipped > 0 ? `Skipped ${response.data.skipped} duplicates.` : ''}`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowDoorExcelDialog(false);
      setDoorExcelPreview(null);
      fetchDoorSchedules(projectId, activeCsiItem?.id);
    } catch (err) {
      toast.error('Failed to import door schedules');
    }
  };

  const handleShowerExcelUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    setShowerExcelUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/shower-schedules/upload-excel/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setShowerExcelPreview({ ...response.data, showerTypes: showerTypes });
      setShowShowerExcelDialog(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setShowerExcelUploading(false);
    }
  };

  const handleShowerBulkImport = async (skipDuplicates) => {
    if (!showerExcelPreview) return;
    const rowsToImport = skipDuplicates
      ? showerExcelPreview.rows.filter(r => !r.isDuplicate)
      : showerExcelPreview.rows;
    try {
      const response = await axios.post(`${API}/shower-schedules/bulk-import/${projectId}`, {
        rows: rowsToImport,
        skipDuplicates,
        csiItemId: activeCsiItem?.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Imported ${response.data.created} shower schedules. ${response.data.skipped > 0 ? `Skipped ${response.data.skipped} duplicates.` : ''}`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowShowerExcelDialog(false);
      setShowerExcelPreview(null);
      fetchShowerSchedules(projectId, activeCsiItem?.id);
    } catch (err) {
      toast.error('Failed to import shower schedules');
    }
  };

  const handleMirrorExcelUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    setMirrorExcelUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/mirror-schedules/upload-excel/${projectId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setMirrorExcelPreview({ ...response.data, mirrorTypes: mirrorTypes });
      setShowMirrorExcelDialog(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse Excel file');
    } finally {
      setMirrorExcelUploading(false);
    }
  };

  const handleMirrorBulkImport = async (skipDuplicates) => {
    if (!mirrorExcelPreview) return;
    const rowsToImport = skipDuplicates
      ? mirrorExcelPreview.rows.filter(r => !r.isDuplicate)
      : mirrorExcelPreview.rows;
    try {
      const response = await axios.post(`${API}/mirror-schedules/bulk-import/${projectId}`, {
        rows: rowsToImport,
        skipDuplicates,
        csiItemId: activeCsiItem?.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Imported ${response.data.created} mirror schedules. ${response.data.skipped > 0 ? `Skipped ${response.data.skipped} duplicates.` : ''}`);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowMirrorExcelDialog(false);
      setMirrorExcelPreview(null);
      fetchMirrorSchedules(projectId, activeCsiItem?.id);
    } catch (err) {
      toast.error('Failed to import mirror schedules');
    }
  };

  // Filter and sort mirror schedules
  const filteredMirrorSchedules = mirrorSchedules
    .filter(schedule => {
      const matchesSearch = !mirrorScheduleSearch || 
        schedule.label?.toLowerCase().includes(mirrorScheduleSearch.toLowerCase());
      const matchesType = !mirrorScheduleFilters.type || 
        schedule.mirrorTypeId === mirrorScheduleFilters.type;
      const matchesLocation = !mirrorScheduleFilters.location || 
        schedule.location?.toLowerCase().includes(mirrorScheduleFilters.location.toLowerCase());
      return matchesSearch && matchesType && matchesLocation;
    })
    .sort((a, b) => {
      switch (mirrorScheduleSortBy) {
        case 'createdAt_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'label_asc':
          return (a.label || '').localeCompare(b.label || '');
        case 'label_desc':
          return (b.label || '').localeCompare(a.label || '');
        case 'type_asc':
        case 'type_desc': {
          const typeA = mirrorTypes.find(t => t.id === a.mirrorTypeId)?.name || '';
          const typeB = mirrorTypes.find(t => t.id === b.mirrorTypeId)?.name || '';
          return mirrorScheduleSortBy === 'type_asc' 
            ? typeA.localeCompare(typeB) 
            : typeB.localeCompare(typeA);
        }
        default: // createdAt_desc
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  // Mirror Detail handlers
  const handleSaveMirrorDetail = async (detailData) => {
    if (!selectedMirrorScheduleForDetail) return;
    
    try {
      await axios.put(`${API}/mirror-schedules/${selectedMirrorScheduleForDetail.id}`, {
        projectId: selectedMirrorScheduleForDetail.projectId,
        csiItemId: selectedMirrorScheduleForDetail.csiItemId,
        mirrorTypeId: selectedMirrorScheduleForDetail.mirrorTypeId,
        label: selectedMirrorScheduleForDetail.label,
        location: selectedMirrorScheduleForDetail.location,
        notes: selectedMirrorScheduleForDetail.notes,
        mirrorWidth_in: detailData.mirrorWidth_in,
        mirrorHeight_in: detailData.mirrorHeight_in
      });
      
      toast.success('Mirror details updated');
      await fetchMirrorSchedules();
      
      // Close modal after successful save
      setShowMirrorScheduleDetailModal(false);
      setSelectedMirrorScheduleForDetail(null);
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error('Failed to update mirror details');
      }
      console.error('Mirror detail save error:', error);
    }
  };

  const handleUploadMirrorSchedulePicture = async (file) => {
    if (!selectedMirrorScheduleForDetail) return;
    
    setMirrorSchedulePictureUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/mirror-schedules/${selectedMirrorScheduleForDetail.id}/upload-picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Picture uploaded successfully');
      await fetchMirrorSchedules();
      
      // Update selected mirror with new picture
      setSelectedMirrorScheduleForDetail(prev => ({
        ...prev,
        pictureUrl: response.data.pictureUrl,
        pictureCloudinary: response.data.pictureCloudinary
      }));
    } catch (error) {
      toast.error('Failed to upload picture');
      console.error('Mirror picture upload error:', error);
    } finally {
      setMirrorSchedulePictureUploading(false);
    }
  };

  const handleDeleteMirrorSchedulePicture = async (scheduleId) => {
    try {
      await axios.delete(`${API}/mirror-schedules/${scheduleId}/picture`);
      toast.success('Picture deleted');
      await fetchMirrorSchedules();
      
      // Update selected mirror
      setSelectedMirrorScheduleForDetail(prev => ({
        ...prev,
        pictureUrl: null,
        pictureCloudinary: null
      }));
    } catch (error) {
      toast.error('Failed to delete picture');
    }
  };

  // ============================================
  // SHOWER TYPE HANDLERS
  // ============================================

  const handleCreateShowerType = async (e) => {
    e.preventDefault();
    
    if (!newShowerType.name?.trim() || !newShowerType.manufacturer?.trim() || 
        !newShowerType.operationType?.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (showerOperationLoading) return;
    
    setShowerOperationLoading(true);
    try {
      const payload = {
        projectId,
        csiItemId: activeCsiTab,
        name: newShowerType.name.trim(),
        manufacturer: newShowerType.manufacturer.trim(),
        model: newShowerType.model?.trim() || '',
        operationType: newShowerType.operationType.trim(),
        showerStyle: newShowerType.showerStyle || null,
        fgFinish: newShowerType.fgFinish?.trim() || null,
        fgManufacturer: newShowerType.fgManufacturer?.trim() || null,
        ucFinishType: newShowerType.ucFinishType?.trim() || null,
        ucFinish: newShowerType.ucFinish?.trim() || null,
        ucManufacturer: newShowerType.ucManufacturer?.trim() || null,
        finishWarranty: newShowerType.finishWarranty ? parseInt(newShowerType.finishWarranty) : null,
        glassWarranty: newShowerType.glassWarranty ? parseInt(newShowerType.glassWarranty) : null,
        hardwareWarranty: newShowerType.hardwareWarranty ? parseInt(newShowerType.hardwareWarranty) : null,
        installationWarranty: newShowerType.installationWarranty ? parseInt(newShowerType.installationWarranty) : null,
        markerColor: newShowerType.markerColor || '#10B981'
      };
      
      let savedShowerType;
      if (editingShowerType) {
        const response = await axios.put(`${API}/shower-types/${editingShowerType.id}`, payload);
        savedShowerType = response.data;
        toast.success('Shower type updated');
      } else {
        const response = await axios.post(`${API}/shower-types`, payload);
        savedShowerType = response.data;
        toast.success('Shower type created');
      }
      
      // Upload picture if pending
      if (pendingShowerTypePicture?.file && savedShowerType) {
        const formData = new FormData();
        formData.append('file', pendingShowerTypePicture.file);
        await axios.post(`${API}/shower-types/${savedShowerType.id}/upload-picture`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      resetShowerTypeDialog();
      
      // Refresh shower types
      const response = await axios.get(`${API}/shower-types/${projectId}/${activeCsiTab}`);
      setShowerTypes(response.data);
    } catch (error) {
      console.error('Error saving shower type:', error);
      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
        toast.error(error.response.data.detail.message);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save shower type');
      }
    } finally {
      setShowerOperationLoading(false);
    }
  };

  const handleDeleteShowerType = async (showerTypeId) => {
    const typeName = showerTypes.find(st => st.id === showerTypeId)?.name || '';
    openConfirm({
      title: 'Delete Shower Type',
      description: `Are you sure you want to delete shower type "${typeName}"? This action cannot be undone and will delete all associated markers.`,
      confirmText: 'Delete',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/shower-types/${showerTypeId}`);
          toast.success('Shower type deleted');
          setShowerTypes(prev => prev.filter(st => st.id !== showerTypeId));
          setSelectedShowerTypeIds(prev => prev.filter(id => id !== showerTypeId));
        } catch (error) {
          toast.error('Failed to delete shower type');
        }
        closeConfirm();
      },
    });
  };

  const handleDeleteShowerTypePicture = async (showerTypeId) => {
    try {
      await axios.delete(`${API}/shower-types/${showerTypeId}/picture`);
      toast.success('Picture deleted');
      setShowerTypes(prev => prev.map(st => 
        st.id === showerTypeId ? { ...st, pictureUrl: null, pictureCloudinary: null } : st
      ));
    } catch (error) {
      toast.error('Failed to delete picture');
    }
  };

  const handleShowerTypePictureUpload = async (e, showerTypeId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setShowerTypePictureUploading(showerTypeId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/shower-types/${showerTypeId}/upload-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowerTypes(prev => prev.map(st =>
        st.id === showerTypeId ? {
          ...st,
          pictureUrl: response.data.pictureUrl,
          pictureCloudinary: response.data.pictureCloudinary
        } : st
      ));
      toast.success('Picture uploaded');
    } catch (error) {
      toast.error('Failed to upload picture');
    } finally {
      setShowerTypePictureUploading(null);
    }
  };

  const handlePrintAllPanelLabels = async () => {
    try {
      toast.info('Generating all QR labels...');
      const response = await axios.get(`${API}/panel-list/project/${projectId}/print-all-labels`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `All_Panel_Labels_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('All QR labels generated successfully!');
    } catch (error) {
      toast.error('Failed to generate QR labels');
      console.error('Print all labels error:', error);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects/${projectId}/invite`, inviteData);
      toast.success('User invited successfully');
      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'general_contractor' });
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  // Share CSI Item with Company
  const handleSearchCompanies = async (query) => {
    setShareCompanySearch(query);
    setHighlightedIndex(-1); // Reset highlighted index when search changes
    
    if (query.length < 2) {
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      return;
    }
    
    try {
      setSearchingCompanies(true);
      const response = await axios.get(`${API}/admin/companies/search`, {
        params: { q: query }
      });
      setCompanySuggestions(response.data.companies || []);
      setShowCompanySuggestions(true);
    } catch (error) {
      console.error('Error searching companies:', error);
      setCompanySuggestions([]);
    } finally {
      setSearchingCompanies(false);
    }
  };

  const handleSelectCompany = async (company, csiItem) => {
    try {
      await axios.put(`${API}/projects/${projectId}/csi-items/${csiItem.id}/share`, {
        companyId: company.id
      });
      
      toast.success(`${csiItem.itemTitle} shared with ${company.companyName || company.name}`);
      setShareCompanySearch('');
      setCompanySuggestions([]);
      setShowCompanySuggestions(false);
      setHighlightedIndex(-1);
      
      // Refresh CSI items to get updated assignedCompanyId
      fetchProjectData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    }
  };

  // Alias for Storefront share tab
  const handleShareCSIItemWithCompany = (company) => {
    const csiItem = csiItems.find(item => item.id === activeCsiTab);
    if (csiItem) {
      handleSelectCompany(company, csiItem);
    }
  };

  const handleCompanySearchKeyDown = (e, csiItem) => {
    if (!showCompanySuggestions || companySuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < companySuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : companySuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < companySuggestions.length) {
          handleSelectCompany(companySuggestions[highlightedIndex], csiItem);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowCompanySuggestions(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleUnshareCSI = (csiItem) => {
    if (unshareGuardRef.current) return;
    setUnshareItem(csiItem);
    setShowUnshareModal(true);
  };
  
  const confirmUnshare = async () => {
    if (!unshareItem || unshareGuardRef.current) return;
    unshareGuardRef.current = true;
    
    const item = unshareItem;
    setShowUnshareModal(false);
    setUnshareItem(null);
    
    try {
      await axios.put(`${API}/projects/${projectId}/csi-items/${item.id}/share`, {
        companyId: null
      });
      
      toast.success(`${item.itemTitle} unshared successfully`);
      fetchProjectData();
    } catch (error) {
      toast.error('Failed to unshare');
    } finally {
      setTimeout(() => { unshareGuardRef.current = false; }, 500);
    }
  };

  // Check if user can manage project (owner or same company)
  // IMPORTANT: Calculate this BEFORE any early returns to keep hooks consistent
  const canManageProject = project?.createdBy === user?.id || 
    (project?.companyId && user?.companyId && project.companyId === user.companyId);

  // Check if user can edit CSI items (owner, same company, OR has access to CURRENT CSI item)
  // Use useMemo to recalculate when dependencies change
  // IMPORTANT: This hook must be called on EVERY render, before any early returns
  const canEditCSIItems = React.useMemo(() => {
    // If user can manage project, they have full access
    if (canManageProject) {
      return true;
    }
    
    // Find the currently active CSI item
    const currentCsiItem = csiItems?.find(item => item.id === activeCsiTab);
    
    // Check if user has access to the CURRENT active CSI item
    const hasAccessToCurrentCSI = currentCsiItem?.assignedCompanyId === user?.companyId;
    
    console.log('🔍 Access Control Debug:', {
      userCompanyId: user?.companyId,
      activeCsiTab,
      currentCsiItem: currentCsiItem ? 'Found' : 'Not Found',
      currentCsiItemId: currentCsiItem?.id,
      assignedCompanyId: currentCsiItem?.assignedCompanyId,
      hasAccessToCurrentCSI,
      canManageProject,
      finalDecision: canManageProject || hasAccessToCurrentCSI
    });
    
    return hasAccessToCurrentCSI;
  }, [canManageProject, csiItems, activeCsiTab, user?.companyId]);

  // Filter and search window list - MUST be calculated before early return
  const filteredWindows = windowList.filter(window => {
    // Search filter (by label only)
    if (windowSearch) {
      const searchLower = windowSearch.toLowerCase();
      if (!window.label.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Window Type filter
    if (windowFilters.type) {
      if (window.windowTypeId !== windowFilters.type) {
        return false;
      }
    }

    // Location filter
    if (windowFilters.location) {
      const locationLower = windowFilters.location.toLowerCase();
      if (!window.location || !window.location.toLowerCase().includes(locationLower)) {
        return false;
      }
    }

    // Label filter
    if (windowFilters.label) {
      const labelLower = windowFilters.label.toLowerCase();
      if (!window.label.toLowerCase().includes(labelLower)) {
        return false;
      }
    }

    return true;
  }).sort((a, b) => {
    // Sorting logic for Windows
    switch (windowSortBy) {
      case 'label_asc':
        return (a.label || '').localeCompare(b.label || '');
      case 'label_desc':
        return (b.label || '').localeCompare(a.label || '');
      case 'type_asc':
        const typeA = windowGlassTypes.find(t => t.id === a.windowTypeId)?.name || '';
        const typeB = windowGlassTypes.find(t => t.id === b.windowTypeId)?.name || '';
        return typeA.localeCompare(typeB);
      case 'type_desc':
        const typeA2 = windowGlassTypes.find(t => t.id === a.windowTypeId)?.name || '';
        const typeB2 = windowGlassTypes.find(t => t.id === b.windowTypeId)?.name || '';
        return typeB2.localeCompare(typeA2);
      case 'quantity_asc':
        const qtyA = windowElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === a.id).length || 0), 0);
        const qtyB = windowElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === b.id).length || 0), 0);
        return qtyA - qtyB;
      case 'quantity_desc':
        const qtyA2 = windowElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === a.id).length || 0), 0);
        const qtyB2 = windowElevations.reduce((count, elev) => count + (elev.markers?.filter(m => m.windowListId === b.id).length || 0), 0);
        return qtyB2 - qtyA2;
      case 'createdAt_asc':
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
      case 'createdAt_desc':
      default:
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }
  });

  // Check if current user can edit an entity based on team relationship
  // UPDATED: Users with access to the CSI item can edit ALL content within that CSI item
  const canUserEdit = (entityCreatedBy) => {
    if (!user) return false;
    
    // If user can edit the current CSI item, they can edit ALL entities within it
    // This allows shared contractors (e.g., B company) to manage content created by A company
    if (canEditCSIItems) return true;
    
    // User is the creator
    if (entityCreatedBy === user.id) return true;
    
    // User is admin and entity was created by their sub-user
    // (This would require checking if entityCreatedBy has parentUserId === user.id, but we don't have that data in frontend)
    // For now, allow if user is glazing contractor (admin or sub-user with same permissions)
    return canManageProject;
  };

  const downloadQRCode = (qrUrl, label) => {
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `QR_${label}.png`;
    link.click();
  };

  // Early return for loading state - AFTER all hooks and computations
  if (loading) {
    return <LoadingSpinner />;
  }

  // Access denied / project not found
  if (accessDenied || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center" data-testid="access-denied-view">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <ShieldAlert className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Available</h2>
            <p className="text-gray-600 mb-6">This project is no longer available or your access has been removed.</p>
            <Button onClick={() => navigate('/dashboard')} data-testid="access-denied-back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Common Header */}
      <Header />
      
      {/* Project Info Section */}
      <div className="border-b border-slate-200 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="border-l border-slate-300 pl-4 flex items-center gap-4">
              {/* Project Image - always show with placeholder fallback */}
              <div className="flex-shrink-0">
                <img
                  src={getProjectImageUrl(project, 'thumbnail', API)}
                  alt={project.name}
                  className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                  onError={(e) => { e.target.src = getProjectPlaceholderUrl(API); }}
                />
              </div>
              {/* Project Info */}
              <div>
                <h1 className="text-xl font-bold text-slate-900">{project?.name}</h1>
                {project?.address && <p className="text-sm text-slate-600">{project?.address}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="project-detail-main">
        
        {/* Division Filter Select Box */}
        {csiItems.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-4">
              <Label className="text-sm font-medium">Filter by Division:</Label>
              <Select value={selectedDivision} onValueChange={setSelectedDivision}>
                <SelectTrigger className="w-[450px]">
                  <SelectValue placeholder="Select Division" />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    // Get unique divisions with their codes and titles
                    const divisionsMap = new Map();
                    csiItems.forEach(item => {
                      if (!divisionsMap.has(item.divisionCode)) {
                        divisionsMap.set(item.divisionCode, {
                          code: item.divisionCode,
                          title: item.divisionTitle || ''
                        });
                      }
                    });
                    
                    return Array.from(divisionsMap.values()).map(division => (
                      <SelectItem key={division.code} value={division.code}>
                        {division.code}{division.title ? ` - ${division.title}` : ''}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
              
              {/* Shared By Information - Only show to shared users, not project owner or super admin */}
              {project?.companyName && 
               project?.createdBy !== user?.id && 
               user?.companyId && 
               project?.companyId !== user?.companyId && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Share2 className="w-4 h-4" />
                  <span>Shared by: <span className="font-medium">{project.companyName}</span></span>
                </div>
              )}
              
              {/* Show Configure button for project owner or same company members */}
              {(project?.createdBy === user?.id || 
                (project?.companyId && user?.companyId && project.companyId === user.companyId)) && (
                <button
                  onClick={() => openConfigModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  Configure Project
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Dynamic CSI Item Tabs */}
        {csiItems.length > 0 ? (
          <Tabs value={activeCsiTab || csiItems[0]?.id} onValueChange={(newTab) => {
            setActiveCsiTab(newTab);
            // Get default sub-tab based on CSI item type
            const tabItem = csiItems.find(item => item.id === newTab);
            // Get default sub-tab based on CSI item type
            const defaultSubTab = getDefaultSubTab(tabItem);
            setActiveSubTab(defaultSubTab);
            navigate(`/project/${projectId}/${newTab}/${defaultSubTab}`);
          }} className="space-y-6">
            <TabsList className="flex flex-wrap justify-start w-full gap-1 h-auto bg-transparent p-0">
              {csiItems
                .filter(item => item.divisionCode === selectedDivision)
                .map(item => {
                  // Check if user can see note (same company, not shared user)
                  const canSeeNote = item.note && 
                    (project?.createdBy === user?.id || 
                     (project?.companyId && user?.companyId && project.companyId === user.companyId));
                  const isSharedUser = item.assignedCompanyId && item.assignedCompanyId === user?.companyId;
                  const showNoteIcon = canSeeNote && !isSharedUser;
                  
                  return (
                    <TabsTrigger 
                      key={item.id} 
                      value={item.id} 
                      className="text-base px-6 py-2.5 rounded-t-lg border border-gray-300 border-b-0 data-[state=active]:bg-orange-50 data-[state=active]:border-orange-400 data-[state=active]:text-orange-900 data-[state=active]:font-semibold data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                    >
                      <span className="flex items-center gap-2">
                        {item.itemTitle}
                        {showNoteIcon && (
                          <span 
                            className="inline-flex"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTooltipPosition({
                                x: rect.left + rect.width / 2,
                                y: rect.top - 8
                              });
                              setTooltipContent(item.note);
                              setTooltipVisible(true);
                            }}
                            onMouseLeave={() => {
                              setTooltipVisible(false);
                            }}
                          >
                            <StickyNote className="w-4 h-4 text-orange-600" />
                          </span>
                        )}
                      </span>
                    </TabsTrigger>
                  );
                })}
            </TabsList>

          {/* CSI ITEM TAB CONTENTS */}
          {csiItems
            .filter(item => item.divisionCode === selectedDivision)
            .map(csiItem => {
              // Check CSI item types
              const isWindows = csiItem.itemCode === '08 50 00';
              const isStorefront = csiItem.itemCode === '08 40 01';
              const isCurtainwall = csiItem.itemCode === '08 40 02';
              const isDoors = csiItem.itemCode === '08 10 00';
              const isMirrors = csiItem.itemCode === '08 00 01';
              const isElectricalSystems = csiItem.itemCode === '26 01 00';
              const isCommunicationSystems = csiItem.itemCode === '27 01 00';
              
              // ============================================
              // MIRRORS DIVISION (08 00 01)
              // ============================================
              if (isMirrors) {
                const filteredMirrorTypes = mirrorTypes.filter(mt => mt.csiItemId === csiItem.id);
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
                      setActiveSubTab(newSubTab);
                      navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
                    }} className="space-y-6">
                      <TabsList className="flex items-center w-full gap-2">
                        <div className="flex-1 flex w-full">
                          <TabsTrigger value="mirror-types" data-testid="mirror-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Scan className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Mirror Types ({filteredMirrorTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="mirror-schedules" data-testid="mirror-schedules-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Table2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Schedules ({mirrorSchedules.length})
                          </TabsTrigger>
                          <TabsTrigger value="mirror-floor-plans" data-testid="mirror-floor-plans-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <MapIcon className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Floor Plans ({mirrorElevations.length})
                          </TabsTrigger>
                          <TabsTrigger value="mirror-submittals" data-testid="mirror-submittals-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <FileText className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Submittals ({submittals.length})
                          </TabsTrigger>
                          <TabsTrigger value="mirror-warranty" data-testid="mirror-warranty-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Warranty
                          </TabsTrigger>
                        </div>
                        {canManageProject && (
                          <TabsTrigger value="share" data-testid="mirror-share-tab" className="flex-shrink-0">
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </TabsTrigger>
                        )}
                      </TabsList>
                      
                      {/* Sharing Info - Hide for shared users (not project owner) */}
                      {canManageProject && (
                        <div className="text-xs text-slate-600 -mt-2">
                          Shared with: {csiItem.assignedCompanyName ? (
                            <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                          ) : (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </div>
                      )}

                      {/* Mirror Types Tab */}
                      <TabsContent value="mirror-types">
                        <MirrorTypesSection
                          mirrorTypes={filteredMirrorTypes}
                          finishTypes={[]}
                          selectedMirrorTypeIds={selectedMirrorTypeIds}
                          newMirrorType={newMirrorType}
                          editingMirrorType={editingMirrorType}
                          showMirrorTypeDialog={showMirrorTypeDialog}
                          mirrorTypePictureUrl={mirrorTypePictureUrl}
                          pendingMirrorTypePicture={pendingMirrorTypePicture}
                          mirrorTypePictureUploading={mirrorTypePictureUploading}
                          setMirrorTypePictureUploading={setMirrorTypePictureUploading}
                          operationLoading={mirrorOperationLoading}
                          canEditCSIItems={canEditCSIItems}
                          canManageProject={canManageProject}
                          setNewMirrorType={setNewMirrorType}
                          setShowMirrorTypeDialog={setShowMirrorTypeDialog}
                          setEditingMirrorType={setEditingMirrorType}
                          setMirrorTypePictureUrl={setMirrorTypePictureUrl}
                          setPendingMirrorTypePicture={setPendingMirrorTypePicture}
                          setMirrorTypes={setMirrorTypes}
                          selectAllMirrorTypes={() => selectAllMirrorTypes(filteredMirrorTypes)}
                          confirmBulkDelete={confirmBulkDelete}
                          toggleMirrorTypeSelection={toggleMirrorTypeSelection}
                          handleCreateMirrorType={handleCreateMirrorType}
                          handleDeleteMirrorType={handleDeleteMirrorType}
                          handleDeleteMirrorTypePicture={handleDeleteMirrorTypePicture}
                          openEditMirrorTypeDialog={openEditMirrorTypeDialog}
                          resetMirrorTypeDialog={resetMirrorTypeDialog}
                          onViewDrawing={setViewingDrawing}
                          onPasteFromClipboard={handleMirrorTypePasteFromClipboard}
                          API={API}
                          axios={axios}
                          toast={toast}
                        />
                      </TabsContent>

                      {/* Mirror Schedules Tab */}
                      <TabsContent value="mirror-schedules">
                        <MirrorSchedulesSection
                          mirrorSchedules={mirrorSchedules}
                          filteredMirrorSchedules={filteredMirrorSchedules}
                          mirrorTypes={mirrorTypes}
                          selectedMirrorScheduleIds={selectedMirrorScheduleIds}
                          canEditCSIItems={canEditCSIItems}
                          operationLoading={mirrorOperationLoading}
                          showMirrorScheduleDialog={showMirrorScheduleDialog}
                          editingMirrorSchedule={editingMirrorSchedule}
                          newMirrorSchedule={newMirrorSchedule}
                          mirrorScheduleSearch={mirrorScheduleSearch}
                          mirrorScheduleFilters={mirrorScheduleFilters}
                          mirrorScheduleSortBy={mirrorScheduleSortBy}
                          onSelectAll={() => selectAllMirrorSchedules(mirrorSchedules)}
                          onConfirmBulkDelete={handleBulkDeleteMirrorSchedules}
                          onToggleSelection={(id, e) => toggleMirrorScheduleSelection(id, e)}
                          onDialogOpenChange={(open) => {
                            if (!open) {
                              resetMirrorScheduleDialog();
                            }
                            setShowMirrorScheduleDialog(open);
                          }}
                          onFormChange={setNewMirrorSchedule}
                          onFormSubmit={handleCreateMirrorSchedule}
                          onEdit={openEditMirrorScheduleDialog}
                          onDelete={handleDeleteMirrorSchedule}
                          onOpenDetail={openMirrorScheduleDetail}
                          onPrintAllQR={handlePrintAllMirrorScheduleQR}
                          onPrintSingleQR={handlePrintMirrorScheduleQR}
                          onPrintLabel={handlePrintMirrorScheduleLabel}
                          onSearchChange={setMirrorScheduleSearch}
                          onFiltersChange={setMirrorScheduleFilters}
                          onSortChange={setMirrorScheduleSortBy}
                          onClearFilters={() => {
                            setMirrorScheduleSearch('');
                            setMirrorScheduleFilters({ type: '', location: '' });
                          }}
                          onExcelUpload={handleMirrorExcelUpload}
                          uploadingExcel={mirrorExcelUploading}
                        />
                      </TabsContent>

                      {/* Mirror Floor Plans Tab */}
                      <TabsContent value="mirror-floor-plans">
                        <MirrorFloorPlansSection
                          mirrorElevations={mirrorElevations}
                          mirrorSchedules={mirrorSchedules}
                          mirrorTypes={mirrorTypes}
                          selectedMirrorElevation={selectedMirrorElevation}
                          selectedMirrorElevationIds={selectedMirrorElevationIds}
                          canEditCSIItems={canEditCSIItems}
                          uploadingElevation={uploadingElevation}
                          bulkDeletingElevations={bulkDeletingElevations}
                          dragJustEndedRef={dragJustEndedRef}
                          deletingSingleElevation={deletingSingleElevation}
                          elevationUploading={elevationUploading}
                          uploadProgress={uploadProgress}
                          uploadProgressPercent={uploadProgressPercent}
                          elevationZoom={elevationZoom}
                          elevationPan={elevationPan}
                          isPanning={isPanning}
                          bulkMarkerMode={mirrorBulkMarkerMode}
                          bulkMarkerSchedule={mirrorBulkMarkerSchedule}
                          pendingBulkMarkers={pendingMirrorBulkMarkers}
                          savingBulkMarkers={savingMirrorBulkMarkers}
                          activeMarkerPopup={activeMarkerPopup}
                          onSelectAll={() => {
                            if (selectedMirrorElevationIds.length === mirrorElevations.length) {
                              setSelectedMirrorElevationIds([]);
                            } else {
                              setSelectedMirrorElevationIds(mirrorElevations.map(e => e.id));
                            }
                          }}
                          onConfirmBulkDelete={() => confirmBulkDelete('mirrorElevation')}
                          onToggleSelection={(id, e) => {
                            if (e?.target?.checked) {
                              setSelectedMirrorElevationIds(prev => [...prev, id]);
                            } else {
                              setSelectedMirrorElevationIds(prev => prev.filter(i => i !== id));
                            }
                          }}
                          onElevationClick={(elevation) => {
                            setElevationZoom(1);
                            setElevationPan({ x: 0, y: 0 });
                            setSelectedMirrorElevation(elevation);
                            setSelectedElevation(elevation);
                            setMarkerContext('mirror');
                            navigate(`/project/${projectId}/${activeCsiTab}/mirror-floor-plans/${elevation.id}`);
                          }}
                          onDeleteElevation={(elevation, e) => confirmDeleteElevation(elevation, e)}
                          onBack={() => {
                            setSelectedMirrorElevation(null);
                            setSelectedElevation(null);
                            setMirrorBulkMarkerMode(false);
                            setMirrorBulkMarkerSchedule('');
                            setPendingMirrorBulkMarkers([]);
                            handleResetZoom();
                            navigate(`/project/${projectId}/${activeCsiTab}/mirror-floor-plans`);
                          }}
                          onPaste={() => handleElevationPasteFromClipboard('mirror')}
                          onUpload={(e) => handleElevationUpload(e, 'mirror')}
                          onZoomIn={handleZoomIn}
                          onZoomOut={handleZoomOut}
                          onResetZoom={handleResetZoom}
                          onPanStart={handlePanStart}
                          onPanMove={handlePanMove}
                          onPanEnd={handlePanEnd}
                          onImageClick={(e) => {
                            setMarkerContext('mirror');
                            handleImageClick(e, 'mirror');
                          }}
                          onMarkerClick={(e, marker) => {
                            setMarkerContext('mirror');
                            handleMarkerClick(e, marker, 'mirror');
                          }}
                          onMarkerDelete={(markerId) => {
                            setMarkerContext('mirror');
                            handleDeleteMarker(markerId, 'mirror');
                          }}
                          onMarkerMouseDown={(e, marker) => {
                            setMarkerContext('mirror');
                            handleMarkerMouseDown(e, marker);
                          }}
                          onMarkerMouseMove={handleMarkerMouseMove}
                          onMarkerMouseUp={handleMarkerMouseUp}
                          onSetActiveMarkerPopup={setActiveMarkerPopup}
                          onBulkMarkerModeToggle={(enabled) => setMirrorBulkMarkerMode(enabled)}
                          onBulkMarkerScheduleChange={setMirrorBulkMarkerSchedule}
                          onBulkMarkerClick={(e) => handleBulkMarkerClick(e, 'mirror')}
                          onSaveBulkMarkers={() => handleSaveBulkMarkers('mirror')}
                          onCancelBulkMarkers={() => {
                            setPendingMirrorBulkMarkers([]);
                            setMirrorBulkMarkerMode(false);
                            setMirrorBulkMarkerSchedule('');
                          }}
                          onRemovePendingMarker={(id) => setPendingMirrorBulkMarkers(prev => prev.filter(m => m.id !== id))}
                          getElevationImageUrl={getElevationImageUrl}
                          navigate={navigate}
                          projectId={projectId}
                          activeCsiTab={activeCsiTab}
                          activeSubTab={activeSubTab}
                        />
                      </TabsContent>

                      {/* Submittals Tab */}
                      <TabsContent value="mirror-submittals">
                        <SubmittalsSection
                          tabValue="mirror-submittals"
                          submittals={submittals}
                          selectedSubmittalIds={selectedSubmittalIds}
                          canManageProject={canManageProject}
                          canEditCSIItems={canEditCSIItems}
                          selectAllItems={selectAllItems}
                          confirmBulkDelete={confirmBulkDelete}
                          toggleItemSelection={toggleItemSelection}
                          setShowAddSubmittalDialog={setShowAddSubmittalDialog}
                          setSubmittalToDelete={setSubmittalToDelete}
                          setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
                          setTooltipPosition={setTooltipPosition}
                          setTooltipContent={setTooltipContent}
                          setTooltipVisible={setTooltipVisible}
                          API={API}
                        />
                      </TabsContent>

                      {/* Warranty Tab */}
                      <TabsContent value="mirror-warranty">
                        <WarrantySection
                          divisionType="mirror"
                          title="Mirror Warranty Management"
                          project={project}
                          projectId={projectId}
                          warrantyStartDate={project?.mirrorWarrantyStartDate}
                          warrantyDateField="mirrorWarrantyStartDate"
                          types={filteredMirrorTypes}
                          typesTitle="Mirror Types Warranty"
                          getTypeLabel={(type) => `${type.manufacturer} - ${type.model}`}
                          getWarrantyFields={(type) => [
                            { label: 'Mirror Warranty', years: type.mirrorWarrantyYears },
                            { label: 'Installation Warranty', years: type.installationWarrantyYears }
                          ]}
                          glassTypes={[]}
                          glassTypesTitle=""
                          getGlassLabel={() => ''}
                          getGlassWarrantyYears={() => 0}
                          showGlassManufacturer={false}
                          showGlassType={false}
                          showGlassModel={false}
                          canEdit={canEditCSIItems}
                          onRefresh={fetchProjectData}
                          testIdPrefix="mirror"
                        />
                      </TabsContent>

                      {/* Share Tab */}
                      {canManageProject && (
                        <ShareSection
                          tabValue="share"
                          inputIdPrefix="mirror"
                          csiItem={csiItem}
                          project={project}
                          user={user}
                          shareCompanySearch={shareCompanySearch}
                          showCompanySuggestions={showCompanySuggestions}
                          companySuggestions={companySuggestions}
                          highlightedIndex={highlightedIndex}
                          searchingCompanies={searchingCompanies}
                          handleSearchCompanies={handleSearchCompanies}
                          handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                          setShowCompanySuggestions={setShowCompanySuggestions}
                          setHighlightedIndex={setHighlightedIndex}
                          handleSelectCompany={handleSelectCompany}
                          handleUnshareCSI={handleUnshareCSI}
                        />
                      )}
                    </Tabs>
                  </TabsContent>
                );
              }
              
              // ============================================
              // SHOWERS DIVISION (08 00 02)
              // ============================================
              const isShowers = csiItem.itemCode === '08 00 02';
              if (isShowers) {
                const filteredShowerTypes = showerTypes.filter(st => st.csiItemId === csiItem.id);
                const filteredShowerGlassTypes = showerGlassTypes.filter(gt => gt.csiItemId === csiItem.id);
                const filteredShowerHardwareSets = showerHardwareSets.filter(hs => hs.csiItemId === csiItem.id);
                const filteredShowerSchedules = showerSchedules.filter(s => s.csiItemId === csiItem.id);
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
                      setActiveSubTab(newSubTab);
                      navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
                    }} className="space-y-6">
                      <TabsList className="flex items-center w-full gap-2">
                        <div className="flex-1 flex w-full">
                          <TabsTrigger value="shower-types" data-testid="shower-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Droplets className="h-3.5 w-3.5 hidden sm:block" />
                            Shower Types ({filteredShowerTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-glass-types" data-testid="shower-glass-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Gem className="h-3.5 w-3.5 hidden sm:block" />
                            Glass Types ({filteredShowerGlassTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-hardware-sets" data-testid="shower-hardware-sets-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Wrench className="h-3.5 w-3.5 hidden sm:block" />
                            Hardware Sets ({filteredShowerHardwareSets.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-schedule" data-testid="shower-schedule-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Table2 className="h-3.5 w-3.5 hidden sm:block" />
                            Schedule ({filteredShowerSchedules.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-floor-plans" data-testid="shower-floor-plans-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <MapIcon className="h-3.5 w-3.5 hidden sm:block" />
                            Floor Plans ({showerElevations.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-submittals" data-testid="shower-submittals-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <FileText className="h-3.5 w-3.5 hidden sm:block" />
                            Submittals ({submittals.length})
                          </TabsTrigger>
                          <TabsTrigger value="shower-warranty" data-testid="shower-warranty-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <ShieldCheck className="h-3.5 w-3.5 hidden sm:block" />
                            Warranty
                          </TabsTrigger>
                        </div>
                        {canManageProject && (
                          <TabsTrigger value="share" data-testid="shower-share-tab" className="flex-shrink-0">
                            <Share2 className="h-4 w-4 mr-1" />
                            Share
                          </TabsTrigger>
                        )}
                      </TabsList>
                      
                      {/* Sharing Info */}
                      {canManageProject && (
                        <div className="text-xs text-slate-600 -mt-2">
                          Shared with: {csiItem.assignedCompanyName ? (
                            <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                          ) : (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </div>
                      )}

                      {/* Shower Types Tab */}
                      <TabsContent value="shower-types">
                        <ShowerTypesSection
                          showerTypes={filteredShowerTypes}
                          selectedShowerTypeIds={selectedShowerTypeIds}
                          newShowerType={newShowerType}
                          editingShowerType={editingShowerType}
                          showShowerTypeDialog={showShowerTypeDialog}
                          showerTypePictureUrl={showerTypePictureUrl}
                          pendingShowerTypePicture={pendingShowerTypePicture}
                          showerTypePictureUploading={showerTypePictureUploading}
                          operationLoading={showerOperationLoading}
                          canEditCSIItems={canEditCSIItems}
                          canUserEdit={(createdBy) => user?.role === 'super_admin' || createdBy === user?.id}
                          setNewShowerType={setNewShowerType}
                          setShowShowerTypeDialog={setShowShowerTypeDialog}
                          setEditingShowerType={setEditingShowerType}
                          setShowerTypePictureUrl={setShowerTypePictureUrl}
                          setPendingShowerTypePicture={setPendingShowerTypePicture}
                          setShowerTypes={setShowerTypes}
                          selectAllShowerTypes={() => selectAllShowerTypes(filteredShowerTypes)}
                          confirmBulkDelete={confirmBulkDelete}
                          toggleShowerTypeSelection={toggleShowerTypeSelection}
                          handleCreateShowerType={handleCreateShowerType}
                          handleDeleteShowerType={handleDeleteShowerType}
                          handleDeleteShowerTypePicture={handleDeleteShowerTypePicture}
                          openEditShowerTypeDialog={openEditShowerTypeDialog}
                          resetShowerTypeDialog={resetShowerTypeDialog}
                          onViewDrawing={setViewingDrawing}
                          onPasteFromClipboard={() => toast.info('Paste from clipboard - coming soon')}
                          onPictureUpload={handleShowerTypePictureUpload}
                          API={API}
                          axios={axios}
                          toast={toast}
                        />
                      </TabsContent>

                      {/* Shower Glass Types Tab */}
                      <TabsContent value="shower-glass-types">
                        <ShowerGlassTypesSection
                          filteredShowerGlassTypes={filteredShowerGlassTypes}
                          selectedShowerGlassTypeIds={selectedShowerGlassTypeIds}
                          expandedShowerGlassTypeIds={expandedShowerGlassTypeIds}
                          canEditCSIItems={canEditCSIItems}
                          canManageProject={canManageProject}
                          operationLoading={showerOperationLoading}
                          showShowerGlassTypeDialog={showShowerGlassTypeDialog}
                          editingShowerGlassType={editingShowerGlassType}
                          newShowerGlassType={newShowerGlassType}
                          onSelectAll={() => selectAllItems('showerGlassType')}
                          onConfirmBulkDelete={() => confirmBulkDelete('showerGlassType')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'showerGlassType', e)}
                          onDialogOpenChange={(open) => {
                            if (!open) {
                              setShowShowerGlassTypeDialog(false);
                              setEditingShowerGlassType(null);
                              resetNewShowerGlassType();
                            } else {
                              setShowShowerGlassTypeDialog(true);
                            }
                          }}
                          onFormChange={setNewShowerGlassType}
                          onFormSubmit={async (e) => {
                            e.preventDefault();
                            if (!newShowerGlassType.name.trim()) {
                              toast.error('Glass Type Name is required');
                              return;
                            }
                            setShowerOperationLoading(true);
                            try {
                              const payload = {
                                projectId,
                                csiItemId: activeCsiTab,
                                name: newShowerGlassType.name,
                                manufacturer: newShowerGlassType.manufacturer || null,
                                glassBuild: newShowerGlassType.glassBuild || null,
                                glassThickness: newShowerGlassType.glassThickness || null,
                                features: newShowerGlassType.features || [],
                                color: newShowerGlassType.color || null,
                                warrantyYears: parseIntegerInput(newShowerGlassType.warrantyYears)
                              };
                              if (editingShowerGlassType) {
                                await axios.put(`${API}/shower-glass-types/${editingShowerGlassType.id}`, payload);
                                toast.success('Glass type updated');
                              } else {
                                await axios.post(`${API}/shower-glass-types`, payload);
                                toast.success('Glass type created');
                              }
                              const res = await axios.get(`${API}/shower-glass-types/${projectId}/${activeCsiTab}`);
                              setShowerGlassTypes(res.data);
                              setShowShowerGlassTypeDialog(false);
                              setEditingShowerGlassType(null);
                              resetNewShowerGlassType();
                            } catch (error) {
                              if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                                toast.error(error.response.data.detail.message);
                              } else {
                                toast.error(editingShowerGlassType ? 'Failed to update glass type' : 'Failed to create glass type');
                              }
                            } finally {
                              setShowerOperationLoading(false);
                            }
                          }}
                          onEdit={(gt) => {
                            setEditingShowerGlassType(gt);
                            setNewShowerGlassType({
                              name: gt.name,
                              manufacturer: gt.manufacturer || '',
                              glassBuild: gt.glassBuild || '',
                              glassThickness: gt.glassThickness || '',
                              features: gt.features || [],
                              color: gt.color || '',
                              warrantyYears: gt.warrantyYears?.toString() || ''
                            });
                            setShowShowerGlassTypeDialog(true);
                          }}
                          onDelete={(gt) => {
                            openConfirm({
                              title: 'Delete Glass Type',
                              description: `Are you sure you want to delete glass type "${gt.name}"?`,
                              confirmText: 'Delete',
                              variant: 'destructive',
                              onConfirm: async () => {
                                try {
                                  await axios.delete(`${API}/shower-glass-types/${gt.id}`);
                                  setShowerGlassTypes(prev => prev.filter(g => g.id !== gt.id));
                                  toast.success('Glass type deleted');
                                } catch (error) {
                                  toast.error(describeGlassTypeConflict(error), { duration: 9000 });
                                }
                                closeConfirm();
                              },
                            });
                          }}
                          onToggleExpand={(id) => {
                            setExpandedShowerGlassTypeIds(prev =>
                              prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                            );
                          }}
                        />
                      </TabsContent>

                      {/* Shower Hardware Sets Tab */}
                      <TabsContent value="shower-hardware-sets">
                        <ShowerHardwareSetsSection
                          filteredShowerHardwareSets={filteredShowerHardwareSets}
                          selectedShowerHardwareSetIds={selectedShowerHardwareSetIds}
                          canEditCSIItems={canEditCSIItems}
                          operationLoading={showerOperationLoading}
                          onSelectAll={() => selectAllItems('showerHardwareSet')}
                          onConfirmBulkDelete={() => confirmBulkDelete('showerHardwareSet')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'showerHardwareSet', e)}
                          onAddNew={() => {
                            setEditingShowerHardwareSet(null);
                            setNewShowerHardwareSet({ name: '', items: [] });
                            setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                            setShowerHardwareSetStep(1);
                            setEditingShowerHardwareItemIndex(null);
                            setShowShowerHardwareSetDialog(true);
                          }}
                          onEdit={(hardwareSet) => {
                            setEditingShowerHardwareSet(hardwareSet);
                            setNewShowerHardwareSet({ name: hardwareSet.name, items: hardwareSet.items || [] });
                            setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                            setShowerHardwareSetStep(2);
                            setEditingShowerHardwareItemIndex(null);
                            setShowShowerHardwareSetDialog(true);
                          }}
                          onDelete={handleDeleteShowerHardwareSet}
                        />
                      </TabsContent>

                      {/* Shower Schedule Tab */}
                      <TabsContent value="shower-schedule">
                        <ShowerSchedulesSection
                          filteredShowerSchedules={filteredShowerSchedules}
                          filteredShowerTypes={filteredShowerTypes}
                          filteredShowerHardwareSets={filteredShowerHardwareSets}
                          selectedShowerScheduleIds={selectedShowerScheduleIds}
                          canEditCSIItems={canEditCSIItems}
                          operationLoading={showerOperationLoading}
                          search={showerScheduleSearch}
                          onSearchChange={setShowerScheduleSearch}
                          filters={showerScheduleFilters}
                          onFiltersChange={setShowerScheduleFilters}
                          sortBy={showerScheduleSortBy}
                          onSortChange={setShowerScheduleSortBy}
                          onClearFilters={() => {
                            setShowerScheduleSearch('');
                            setShowerScheduleFilters({ type: '', location: '' });
                            setShowerScheduleSortBy('createdAt_desc');
                          }}
                          onSelectAll={() => selectAllItems('showerSchedule')}
                          onConfirmBulkDelete={() => confirmBulkDelete('showerSchedule')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'showerSchedule', e)}
                          onAddNew={() => {
                            setEditingShowerSchedule(null);
                            setNewShowerSchedule({ label: '', showerTypeId: '', hardwareSetId: '' });
                            setShowShowerScheduleDialog(true);
                          }}
                          onEdit={(schedule) => {
                            setEditingShowerSchedule(schedule);
                            setNewShowerSchedule({ label: schedule.label, showerTypeId: schedule.showerTypeId, hardwareSetId: schedule.hardwareSetId || '' });
                            setShowShowerScheduleDialog(true);
                          }}
                          onDelete={(id) => {
                            const sched = showerSchedules.find(s => s.id === id);
                            openConfirm({
                              title: 'Delete Schedule',
                              description: `Are you sure you want to delete shower schedule "${sched?.label || ''}"? This action cannot be undone and will delete all associated markers.`,
                              confirmText: 'Delete',
                              variant: 'destructive',
                              onConfirm: async () => {
                                try {
                                  await axios.delete(`${API}/shower-schedules/${id}`);
                                  setShowerSchedules(prev => prev.filter(s => s.id !== id));
                                  toast.success('Schedule deleted');
                                } catch { toast.error('Failed to delete schedule'); }
                                closeConfirm();
                              },
                            });
                          }}
                          onOpenDetail={(schedule) => {
                            setSelectedShowerDetail(schedule);
                            setShowShowerDetailDialog(true);
                          }}
                          onPrintLabel={async (scheduleId) => {
                            try {
                              const response = await axios.get(`${API}/shower-schedules/${scheduleId}/print-label`, {
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                              setPrintPreviewUrl(url);
                              setPrintPreviewType('shower');
                              setShowPrintPreviewDialog(true);
                            } catch (error) {
                              toast.error('Failed to generate PDF');
                            }
                          }}
                          onPrintAllQR={handlePrintAllShowerScheduleQR}
                          onExcelUpload={handleShowerExcelUpload}
                          uploadingExcel={showerExcelUploading}
                        />
                      </TabsContent>

                      {/* Shower Floor Plans Tab */}
                      <TabsContent value="shower-floor-plans">
                        <ShowerFloorPlansSection
                          showerElevations={showerElevations}
                          showerSchedules={filteredShowerSchedules}
                          showerTypes={filteredShowerTypes}
                          selectedShowerElevation={selectedShowerElevation}
                          selectedShowerElevationIds={selectedShowerElevationIds}
                          canEditCSIItems={canEditCSIItems}
                          uploadingElevation={uploadingElevation}
                          dragJustEndedRef={dragJustEndedRef}
                          bulkDeletingElevations={bulkDeletingElevations}
                          deletingSingleElevation={deletingSingleElevation}
                          elevationUploading={elevationUploading}
                          uploadProgress={uploadProgress}
                          uploadProgressPercent={uploadProgressPercent}
                          elevationZoom={elevationZoom}
                          elevationPan={elevationPan}
                          isPanning={isPanning}
                          bulkMarkerMode={showerBulkMarkerMode}
                          bulkMarkerSchedule={showerBulkMarkerSchedule}
                          pendingBulkMarkers={pendingShowerBulkMarkers}
                          savingBulkMarkers={savingShowerBulkMarkers}
                          activeMarkerPopup={activeMarkerPopup}
                          onSelectAll={() => {
                            if (selectedShowerElevationIds.length === showerElevations.length) {
                              setSelectedShowerElevationIds([]);
                            } else {
                              setSelectedShowerElevationIds(showerElevations.map(e => e.id));
                            }
                          }}
                          onConfirmBulkDelete={() => confirmBulkDelete('showerElevation')}
                          onToggleSelection={(id, e) => {
                            if (e?.target?.checked) {
                              setSelectedShowerElevationIds(prev => [...prev, id]);
                            } else {
                              setSelectedShowerElevationIds(prev => prev.filter(i => i !== id));
                            }
                          }}
                          onElevationClick={(elevation) => {
                            setElevationZoom(1);
                            setElevationPan({ x: 0, y: 0 });
                            setSelectedShowerElevation(elevation);
                            setSelectedElevation(elevation);
                            setMarkerContext('shower');
                            navigate(`/project/${projectId}/${activeCsiTab}/shower-floor-plans/${elevation.id}`);
                          }}
                          onDeleteElevation={(elevation, e) => confirmDeleteElevation(elevation, e)}
                          onBack={() => {
                            setSelectedShowerElevation(null);
                            setSelectedElevation(null);
                            setShowerBulkMarkerMode(false);
                            setShowerBulkMarkerSchedule('');
                            setPendingShowerBulkMarkers([]);
                            handleResetZoom();
                            navigate(`/project/${projectId}/${activeCsiTab}/shower-floor-plans`);
                          }}
                          onPaste={() => handleElevationPasteFromClipboard('shower')}
                          onUpload={(e) => handleElevationUpload(e, 'shower')}
                          onZoomIn={handleZoomIn}
                          onZoomOut={handleZoomOut}
                          onResetZoom={handleResetZoom}
                          onPanStart={handlePanStart}
                          onPanMove={handlePanMove}
                          onPanEnd={handlePanEnd}
                          onImageClick={(e) => {
                            setMarkerContext('shower');
                            handleImageClick(e, 'shower');
                          }}
                          onMarkerClick={(e, marker) => {
                            setMarkerContext('shower');
                            handleMarkerClick(e, marker, 'shower');
                          }}
                          onMarkerDelete={(markerId) => {
                            setMarkerContext('shower');
                            handleDeleteMarker(markerId, 'shower');
                          }}
                          onMarkerMouseDown={(e, marker) => {
                            setMarkerContext('shower');
                            handleMarkerMouseDown(e, marker);
                          }}
                          onMarkerMouseMove={handleMarkerMouseMove}
                          onMarkerMouseUp={handleMarkerMouseUp}
                          onSetActiveMarkerPopup={setActiveMarkerPopup}
                          onBulkMarkerModeToggle={(enabled) => setShowerBulkMarkerMode(enabled)}
                          onBulkMarkerScheduleChange={setShowerBulkMarkerSchedule}
                          onBulkMarkerClick={(e) => handleBulkMarkerClick(e, 'shower')}
                          onSaveBulkMarkers={() => handleSaveBulkMarkers('shower')}
                          onCancelBulkMarkers={() => {
                            setPendingShowerBulkMarkers([]);
                            setShowerBulkMarkerMode(false);
                            setShowerBulkMarkerSchedule('');
                          }}
                          onRemovePendingMarker={(id) => setPendingShowerBulkMarkers(prev => prev.filter(m => m.id !== id))}
                          getElevationImageUrl={getElevationImageUrl}
                          navigate={navigate}
                          projectId={projectId}
                          activeCsiTab={activeCsiTab}
                          activeSubTab={activeSubTab}
                        />
                      </TabsContent>

                      {/* Submittals Tab */}
                      <SubmittalsSection
                        tabValue="shower-submittals"
                        submittals={submittals}
                        selectedSubmittalIds={selectedSubmittalIds}
                        canManageProject={canManageProject}
                        canEditCSIItems={canEditCSIItems}
                        selectAllItems={selectAllItems}
                        confirmBulkDelete={confirmBulkDelete}
                        toggleItemSelection={toggleItemSelection}
                        setShowAddSubmittalDialog={setShowAddSubmittalDialog}
                        setSubmittalToDelete={setSubmittalToDelete}
                        setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
                        setTooltipPosition={setTooltipPosition}
                        setTooltipContent={setTooltipContent}
                        setTooltipVisible={setTooltipVisible}
                        API={API}
                      />

                      {/* Shower Warranty Tab */}
                      <TabsContent value="shower-warranty">
                        <WarrantySection
                          divisionType="shower"
                          title="Shower Warranty Management"
                          project={project}
                          projectId={projectId}
                          warrantyStartDate={project?.showerWarrantyStartDate}
                          warrantyDateField="showerWarrantyStartDate"
                          types={filteredShowerTypes}
                          typesTitle="Shower Types Warranty"
                          getTypeLabel={(type) => `${type.manufacturer || ''} ${type.model ? '- ' + type.model : ''}`.trim()}
                          getWarrantyFields={(type) => [
                            { label: 'Finish Warranty', years: parseInt(type.finishWarranty) || 0 },
                            { label: 'Glass Warranty', years: parseInt(type.glassWarranty) || 0 },
                            { label: 'Hardware Warranty', years: parseInt(type.hardwareWarranty) || 0 },
                            { label: 'Installation Warranty', years: parseInt(type.installationWarranty) || 0 }
                          ]}
                          glassTypes={filteredShowerGlassTypes}
                          glassTypesTitle="Glass Types Warranty"
                          getGlassLabel={(glass) => glass.name}
                          getGlassWarrantyYears={(glass) => glass.warrantyYears || 0}
                          showGlassManufacturer={true}
                          showGlassType={false}
                          showGlassModel={false}
                          canEdit={canEditCSIItems}
                          onRefresh={fetchProjectData}
                          testIdPrefix="shower"
                        />
                      </TabsContent>

                      {/* Share Tab */}
                      {canManageProject && (
                        <ShareSection
                          tabValue="share"
                          inputIdPrefix="shower"
                          csiItem={csiItem}
                          project={project}
                          user={user}
                          shareCompanySearch={shareCompanySearch}
                          showCompanySuggestions={showCompanySuggestions}
                          companySuggestions={companySuggestions}
                          highlightedIndex={highlightedIndex}
                          searchingCompanies={searchingCompanies}
                          handleSearchCompanies={handleSearchCompanies}
                          handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                          setShowCompanySuggestions={setShowCompanySuggestions}
                          setHighlightedIndex={setHighlightedIndex}
                          handleSelectCompany={handleSelectCompany}
                          handleUnshareCSI={handleUnshareCSI}
                        />
                      )}
                    </Tabs>
                  </TabsContent>
                );
              }
              
              // ============================================
              // THERMAL INSULATION (07 20 00)
              // ============================================
              const isThermalInsulation = csiItem.itemCode === '07 20 00';
              if (isThermalInsulation) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <ThermalInsulationsSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              // ============================================
              // AIR BARRIERS (07 20 02)
              // ============================================
              const isAirBarrier = csiItem.itemCode === '07 20 02';
              if (isAirBarrier) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <AirBarriersSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              // ============================================
              // WEATHER BARRIERS (07 20 01)
              // ============================================
              const isWeatherBarrier = csiItem.itemCode === '07 20 01';
              if (isWeatherBarrier) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <WeatherBarriersSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              // ============================================
              // ROOFING & SHEET METAL FLASHING (07 60 01)
              // ============================================
              const isRoofing = csiItem.itemCode === '07 40 00';
              if (isRoofing) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <RoofingSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              // ============================================
              // FLASHING AND SHEET METAL (07 60 00)
              // ============================================
              const isFlashing = csiItem.itemCode === '07 60 00';
              if (isFlashing) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <FlashingSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              // ============================================
              // JOINT PROTECTION (07 90 00)
              // ============================================
              const isJointProtection = csiItem.itemCode === '07 90 00';
              if (isJointProtection) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <JointProtectionSection
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      companyId={user?.companyId}
                      projectId={projectId}
                      csiItem={csiItem}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              }

              if (isDoors) {
                const filteredDoorTypes = doorTypes.filter(dt => dt.csiItemId === csiItem.id);
                const filteredDoorGlassTypes = doorGlassTypes.filter(gt => gt.csiItemId === csiItem.id);
                const filteredDoorMetalPanelTypes = doorMetalPanelTypes.filter(mt => mt.csiItemId === csiItem.id);
                const filteredDoorHardwareSets = doorHardwareSets.filter(hs => hs.csiItemId === csiItem.id);
                const filteredDoorSchedules = doorSchedules.filter(ds => ds.csiItemId === csiItem.id);
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
                      setActiveSubTab(newSubTab);
                      navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
                    }} className="space-y-6">
                      <TabsList className="flex items-center w-full gap-2">
                        <div className="flex-1 flex w-full">
                          <TabsTrigger value="door-types" data-testid="door-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <DoorOpen className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Door Types ({filteredDoorTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-glass-types" data-testid="door-glass-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Gem className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Glass Types ({filteredDoorGlassTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-metal-panels" data-testid="door-metal-panels-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Grid3x3 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Metal Panels ({filteredDoorMetalPanelTypes.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-hardware-sets" data-testid="door-hardware-sets-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Wrench className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Hardware Sets ({filteredDoorHardwareSets.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-schedules" data-testid="door-schedules-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <Table2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Schedules ({filteredDoorSchedules.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-floor-plans" data-testid="door-floor-plans-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <MapIcon className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Floor Plans ({doorElevations.filter(e => e.csiItemId === activeCsiTab).length})
                          </TabsTrigger>
                          <TabsTrigger value="door-submittals" data-testid="door-submittals-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <FileText className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Submittals ({submittals.length})
                          </TabsTrigger>
                          <TabsTrigger value="door-warranty" data-testid="door-warranty-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                            <ShieldCheck className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                            Warranty
                          </TabsTrigger>
                        </div>
                        {/* Hide Share tab for shared users (not project owner) */}
                        {canManageProject && (
                          <TabsTrigger value="share" data-testid="door-share-tab" className="flex-shrink-0">
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </TabsTrigger>
                        )}
                      </TabsList>
                      
                      {/* Sharing Info - Hide for shared users (not project owner) */}
                      {canManageProject && (
                        <div className="text-xs text-slate-600 -mt-2">
                          Shared with: {csiItem.assignedCompanyName ? (
                            <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                          ) : (
                            <span className="text-slate-400">N/A</span>
                          )}
                        </div>
                      )}

                      {/* Door Types Tab */}
                      <TabsContent value="door-types">
                        <DoorTypesSection
                          filteredDoorTypes={filteredDoorTypes}
                          selectedDoorTypeIds={selectedDoorTypeIds}
                          canEditCSIItems={canEditCSIItems}
                          doorTypePictureUploading={doorTypePictureUploading}
                          operationLoading={doorOperationLoading}
                          API={API}
                          onSelectAll={() => selectAllItems('doorType')}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorType')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'doorType', e)}
                          onAddNew={() => {
                            setEditingDoorType(null);
                            setDoorTypePictureUrl(null);
                            setPendingDoorTypePicture(null);
                            setNewDoorType({
                              name: '',
                              doorType: '',
                              manufacturer: '',
                              model: '',
                              doorMaterial: '',
                              operationType: '',
                              finishInterior: '',
                              finishExterior: '',
                              finishType: '',
                              fireRated: '',
                              frameWarrantyYears: '',
                              finishWarrantyYears: '',
                              hardwareWarrantyYears: '',
                              installationWarrantyYears: '',
                              markerColor: '#39FF14'
                            });
                            setShowDoorTypeDialog(true);
                          }}
                          onEdit={(doorType) => {
                            setEditingDoorType(doorType);
                            setDoorTypePictureUrl(null);
                            setNewDoorType({
                              name: doorType.name,
                              doorType: doorType.doorType,
                              manufacturer: doorType.manufacturer,
                              model: doorType.model,
                              doorMaterial: doorType.doorMaterial,
                              operationType: doorType.operationType,
                              finishInterior: doorType.finishInterior,
                              finishExterior: doorType.finishExterior,
                              finishType: doorType.finishType,
                              fireRated: doorType.fireRated?.toString() || '',
                              frameWarrantyYears: doorType.frameWarrantyYears?.toString() || '',
                              finishWarrantyYears: doorType.finishWarrantyYears?.toString() || '',
                              hardwareWarrantyYears: doorType.hardwareWarrantyYears?.toString() || '',
                              installationWarrantyYears: doorType.installationWarrantyYears?.toString() || '',
                              markerColor: doorType.markerColor || '#39FF14'
                            });
                            setShowDoorTypeDialog(true);
                          }}
                          onDelete={handleDeleteDoorType}
                          onPictureUpload={handleDoorTypePictureUpload}
                          onPasteFromClipboard={handleDoorTypePasteFromClipboard}
                          onDeletePicture={handleDeleteDoorTypePicture}
                          onViewDrawing={setViewingDrawing}
                          onColorPickerOpenChange={(typeId, open) => {
                            setDoorTypes(prev => prev.map(t => 
                              t.id === typeId ? { ...t, colorPickerOpen: open, pendingMarkerColor: open ? t.pendingMarkerColor : undefined } : t
                            ));
                          }}
                          onColorChange={(typeId, newColor) => {
                            setDoorTypes(prev => prev.map(t => 
                              t.id === typeId ? { ...t, pendingMarkerColor: newColor } : t
                            ));
                          }}
                          onColorSave={async (type) => {
                            setDoorTypes(prev => prev.map(t => 
                              t.id === type.id ? { ...t, savingColor: true } : t
                            ));
                            try {
                              await axios.put(`${API}/door-types/${type.id}`, {
                                projectId: type.projectId,
                                csiItemId: type.csiItemId,
                                name: type.name,
                                doorType: type.doorType,
                                manufacturer: type.manufacturer,
                                model: type.model,
                                doorMaterial: type.doorMaterial,
                                operationType: type.operationType,
                                finishInterior: type.finishInterior,
                                finishExterior: type.finishExterior,
                                finishType: type.finishType,
                                fireRated: type.fireRated,
                                frameWarrantyYears: type.frameWarrantyYears,
                                finishWarrantyYears: type.finishWarrantyYears,
                                hardwareWarrantyYears: type.hardwareWarrantyYears,
                                installationWarrantyYears: type.installationWarrantyYears,
                                markerColor: type.pendingMarkerColor
                              });
                              setDoorTypes(prev => prev.map(t => 
                                t.id === type.id ? { ...t, markerColor: type.pendingMarkerColor, pendingMarkerColor: undefined, savingColor: false, colorPickerOpen: false } : t
                              ));
                              toast.success('Marker color saved');
                            } catch (error) {
                              setDoorTypes(prev => prev.map(t => 
                                t.id === type.id ? { ...t, savingColor: false } : t
                              ));
                              toast.error('Failed to update marker color');
                            }
                          }}
                        />
                      </TabsContent>

                      {/* Door Glass Types Tab */}
                      <TabsContent value="door-glass-types">
                        <DoorGlassTypesSection
                          filteredDoorGlassTypes={filteredDoorGlassTypes}
                          selectedDoorGlassTypeIds={selectedDoorGlassTypeIds}
                          expandedDoorGlassTypeIds={expandedDoorGlassTypeIds}
                          canEditCSIItems={canEditCSIItems}
                          canManageProject={canManageProject}
                          operationLoading={doorOperationLoading}
                          showDoorGlassTypeDialog={showDoorGlassTypeDialog}
                          editingDoorGlassType={editingDoorGlassType}
                          newDoorGlassType={newDoorGlassType}
                          doorGridSizeRef={null}
                          onSelectAll={() => selectAllItems('doorGlassType')}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorGlassType')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'doorGlassType', e)}
                          onDialogOpenChange={(open) => {
                            if (!open) {
                              setShowDoorGlassTypeDialog(false);
                              setEditingDoorGlassType(null);
                              setNewDoorGlassType({
                                name: '',
                                manufacturer: '',
                                model: '',
                                glassBuild: '',
                                glassThickness: '',
                                features: [],
                                hasGrid: false,
                                gridSize: '',
                                warrantyYears: ''
                              });
                            } else {
                              setShowDoorGlassTypeDialog(true);
                            }
                          }}
                          onFormChange={setNewDoorGlassType}
                          onFormSubmit={async (e) => {
                            e.preventDefault();
                            if (!newDoorGlassType.name.trim()) {
                              toast.error('Glass Type Name is required');
                              return;
                            }
                            if (newDoorGlassType.hasGrid && !newDoorGlassType.gridSize) {
                              toast.error('Grid Size is required when glass has grid');
                              return;
                            }
                            
                            setDoorOperationLoading(true);
                            try {
                              const payload = {
                                projectId,
                                csiItemId: activeCsiTab,
                                name: newDoorGlassType.name,
                                manufacturer: newDoorGlassType.manufacturer || null,
                                model: newDoorGlassType.model || null,
                                glassBuild: newDoorGlassType.glassBuild || null,
                                glassThickness: newDoorGlassType.glassThickness ? parseFloat(newDoorGlassType.glassThickness) : null,
                                features: newDoorGlassType.features || [],
                                hasGrid: newDoorGlassType.hasGrid,
                                gridSize: newDoorGlassType.hasGrid ? parseNumericInput(newDoorGlassType.gridSize) : null,
                                warrantyYears: parseIntegerInput(newDoorGlassType.warrantyYears)
                              };
                              
                              if (editingDoorGlassType) {
                                await axios.put(`${API}/door-glass-types/${editingDoorGlassType.id}`, payload);
                                toast.success('Glass type updated');
                              } else {
                                await axios.post(`${API}/door-glass-types`, payload);
                                toast.success('Glass type created');
                              }
                              
                              fetchDoorGlassTypes();
                              setShowDoorGlassTypeDialog(false);
                              setEditingDoorGlassType(null);
                              setNewDoorGlassType({
                                name: '',
                                manufacturer: '',
                                model: '',
                                glassBuild: '',
                                glassThickness: '',
                                features: [],
                                hasGrid: false,
                                gridSize: '',
                                warrantyYears: ''
                              });
                            } catch (error) {
                              if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                                toast.error(error.response.data.detail.message);
                              } else {
                                toast.error(editingDoorGlassType ? 'Failed to update glass type' : 'Failed to create glass type');
                              }
                            } finally {
                              setDoorOperationLoading(false);
                            }
                          }}
                          onEdit={(glassType) => {
                            setEditingDoorGlassType(glassType);
                            setNewDoorGlassType({
                              name: glassType.name,
                              manufacturer: glassType.manufacturer || '',
                              model: glassType.model || '',
                              glassBuild: glassType.glassBuild || '',
                              glassThickness: glassType.glassThickness?.toString() || '',
                              features: glassType.features || [],
                              hasGrid: glassType.hasGrid || false,
                              gridSize: glassType.gridSize?.toString() || '',
                              warrantyYears: glassType.warrantyYears?.toString() || ''
                            });
                            setShowDoorGlassTypeDialog(true);
                          }}
                          onDelete={handleDeleteDoorGlassType}
                          onToggleExpand={(id) => {
                            setExpandedDoorGlassTypeIds(prev => 
                              prev.includes(id) 
                                ? prev.filter(i => i !== id) 
                                : [...prev, id]
                            );
                          }}
                        />
                      </TabsContent>

                      {/* Door Metal Panels Tab */}
                      <TabsContent value="door-metal-panels">
                        <DoorMetalPanelsSection
                          filteredDoorMetalPanelTypes={filteredDoorMetalPanelTypes}
                          selectedDoorMetalPanelTypeIds={selectedDoorMetalPanelTypeIds}
                          canEditCSIItems={canEditCSIItems}
                          canManageProject={canManageProject}
                          operationLoading={doorOperationLoading}
                          showDoorMetalPanelTypeDialog={showDoorMetalPanelTypeDialog}
                          editingDoorMetalPanelType={editingDoorMetalPanelType}
                          newDoorMetalPanelType={newDoorMetalPanelType}
                          onSelectAll={() => selectAllItems('doorMetalPanelType')}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorMetalPanelType')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'doorMetalPanelType', e)}
                          onDialogOpenChange={(open) => {
                            if (!open) {
                              setShowDoorMetalPanelTypeDialog(false);
                              setEditingDoorMetalPanelType(null);
                              setNewDoorMetalPanelType({
                                name: '',
                                manufacturer: '',
                                model: '',
                                thickness: '',
                                colorCode: ''
                              });
                            } else {
                              setShowDoorMetalPanelTypeDialog(true);
                            }
                          }}
                          onFormChange={setNewDoorMetalPanelType}
                          onFormSubmit={async (e) => {
                            e.preventDefault();
                            if (!newDoorMetalPanelType.name.trim()) {
                              toast.error('Name is required');
                              return;
                            }
                            
                            setDoorOperationLoading(true);
                            try {
                              const payload = {
                                projectId,
                                csiItemId: activeCsiTab,
                                name: newDoorMetalPanelType.name,
                                manufacturer: newDoorMetalPanelType.manufacturer || null,
                                model: newDoorMetalPanelType.model || null,
                                thickness: parseNumericInput(newDoorMetalPanelType.thickness),
                                colorCode: newDoorMetalPanelType.colorCode || null
                              };
                              
                              if (editingDoorMetalPanelType) {
                                await axios.put(`${API}/door-metal-panel-types/${editingDoorMetalPanelType.id}`, payload);
                                toast.success('Metal panel type updated');
                              } else {
                                await axios.post(`${API}/door-metal-panel-types`, payload);
                                toast.success('Metal panel type created');
                              }
                              
                              fetchDoorMetalPanelTypes();
                              setShowDoorMetalPanelTypeDialog(false);
                              setEditingDoorMetalPanelType(null);
                              setNewDoorMetalPanelType({
                                name: '',
                                manufacturer: '',
                                model: '',
                                thickness: '',
                                colorCode: ''
                              });
                            } catch (error) {
                              if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                                toast.error(error.response.data.detail.message);
                              } else {
                                toast.error(editingDoorMetalPanelType ? 'Failed to update metal panel type' : 'Failed to create metal panel type');
                              }
                            } finally {
                              setDoorOperationLoading(false);
                            }
                          }}
                          onEdit={(metalType) => {
                            setEditingDoorMetalPanelType(metalType);
                            setNewDoorMetalPanelType({
                              name: metalType.name,
                              manufacturer: metalType.manufacturer || '',
                              model: metalType.model || '',
                              thickness: metalType.thickness?.toString() || '',
                              colorCode: metalType.colorCode || ''
                            });
                            setShowDoorMetalPanelTypeDialog(true);
                          }}
                          onDelete={handleDeleteDoorMetalPanelType}
                        />
                      </TabsContent>

                      {/* Door Hardware Sets Tab */}
                      <TabsContent value="door-hardware-sets">
                        <DoorHardwareSetsSection
                          filteredDoorHardwareSets={filteredDoorHardwareSets}
                          selectedDoorHardwareSetIds={selectedDoorHardwareSetIds}
                          canEditCSIItems={canEditCSIItems}
                          operationLoading={doorOperationLoading}
                          onSelectAll={() => selectAllItems('doorHardwareSet')}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorHardwareSet')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'doorHardwareSet', e)}
                          onAddNew={() => {
                            setEditingDoorHardwareSet(null);
                            setNewDoorHardwareSet({ name: '', items: [] });
                            setNewDoorHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                            setDoorHardwareSetStep(1);
                            setEditingDoorHardwareItemIndex(null);
                            setShowDoorHardwareSetDialog(true);
                          }}
                          onEdit={(hardwareSet) => {
                            setEditingDoorHardwareSet(hardwareSet);
                            setNewDoorHardwareSet({
                              name: hardwareSet.name || hardwareSet.hardwareSet,
                              items: hardwareSet.items || []
                            });
                            setNewDoorHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                            setDoorHardwareSetStep(2);
                            setEditingDoorHardwareItemIndex(null);
                            setShowDoorHardwareSetDialog(true);
                          }}
                          onDelete={handleDeleteDoorHardwareSet}
                        />
                      </TabsContent>

                      {/* Door Schedules Tab */}
                      <TabsContent value="door-schedules">
                        <DoorSchedulesSection
                          filteredDoorSchedules={filteredDoorSchedules}
                          filteredDoorTypes={filteredDoorTypes}
                          filteredDoorHardwareSets={filteredDoorHardwareSets}
                          selectedDoorScheduleIds={selectedDoorScheduleIds}
                          canEditCSIItems={canEditCSIItems}
                          operationLoading={doorOperationLoading}
                          API={API}
                          search={doorScheduleSearch}
                          onSearchChange={setDoorScheduleSearch}
                          filters={doorScheduleFilters}
                          onFiltersChange={setDoorScheduleFilters}
                          sortBy={doorScheduleSortBy}
                          onSortChange={setDoorScheduleSortBy}
                          onClearFilters={() => {
                            setDoorScheduleSearch('');
                            setDoorScheduleFilters({ type: '', location: '' });
                            setDoorScheduleSortBy('createdAt_desc');
                          }}
                          onSelectAll={() => selectAllItems('doorSchedule')}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorSchedule')}
                          onToggleSelection={(id, e) => toggleItemSelection(id, 'doorSchedule', e)}
                          doorElevations={doorElevations.filter(e => e.csiItemId === activeCsiTab)}
                          projectId={projectId}
                          onAddNew={() => {
                            setEditingDoorSchedule(null);
                            setDoorScheduleStep(1);
                            setDoorLiteTouched({});
                            setNewDoorSchedule(emptyDoorSchedule());
                            setShowDoorScheduleDialog(true);
                          }}
                          onEdit={(schedule) => {
                            setEditingDoorSchedule(schedule);
                            setDoorScheduleStep(1);
                            setDoorLiteTouched({});
                            setNewDoorSchedule(doorScheduleToForm(schedule));
                            setShowDoorScheduleDialog(true);
                          }}
                          onDelete={handleDeleteDoorSchedule}
                          onPrintLabel={async (scheduleId) => {
                            try {
                              const response = await axios.get(`${API}/door-schedules/${scheduleId}/print-label`, {
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                              setPrintPreviewUrl(url);
                              setPrintPreviewType('door');
                              setShowPrintPreviewDialog(true);
                            } catch (error) {
                              toast.error('Failed to generate PDF');
                            }
                          }}
                          onPrintAllQR={handlePrintAllDoorScheduleQR}
                          onExcelUpload={handleDoorExcelUpload}
                          uploadingExcel={doorExcelUploading}
                        />
                      </TabsContent>

                      {/* Door Floor Plans Tab */}
                      <TabsContent value="door-floor-plans">
                        <DoorFloorPlansSection
                          doorElevations={doorElevations.filter(e => e.csiItemId === activeCsiTab)}
                          doorList={filteredDoorSchedules}
                          doorTypes={filteredDoorTypes}
                          selectedDoorElevation={selectedDoorElevation}
                          selectedDoorElevationIds={selectedDoorElevationIds}
                          canEditCSIItems={canEditCSIItems}
                          uploadingElevation={uploadingElevation}
                          dragJustEndedRef={dragJustEndedRef}
                          bulkDeletingElevations={bulkDeletingElevations}
                          deletingSingleElevation={deletingSingleElevation}
                          elevationUploading={elevationUploading}
                          uploadProgress={uploadProgress}
                          uploadProgressPercent={uploadProgressPercent}
                          elevationZoom={elevationZoom}
                          elevationPan={elevationPan}
                          isPanning={isPanning}
                          bulkMarkerMode={doorBulkMarkerMode}
                          bulkMarkerSchedule={doorBulkMarkerSchedule}
                          pendingBulkMarkers={pendingDoorBulkMarkers}
                          savingBulkMarkers={savingDoorBulkMarkers}
                          activeMarkerPopup={activeMarkerPopup}
                          onSelectAll={() => {
                            const doorElevs = doorElevations.filter(e => e.csiItemId === activeCsiTab);
                            const allIds = doorElevs.map(e => e.id);
                            setSelectedDoorElevationIds(
                              selectedDoorElevationIds.length === allIds.length ? [] : allIds
                            );
                          }}
                          onConfirmBulkDelete={() => confirmBulkDelete('doorElevation')}
                          onToggleSelection={(id, e) => {
                            if (e?.target?.checked || !selectedDoorElevationIds.includes(id)) {
                              setSelectedDoorElevationIds([...selectedDoorElevationIds, id]);
                            } else {
                              setSelectedDoorElevationIds(selectedDoorElevationIds.filter(x => x !== id));
                            }
                          }}
                          onElevationClick={(elevation) => {
                            setElevationZoom(1);
                            setElevationPan({ x: 0, y: 0 });
                            setSelectedDoorElevation(elevation);
                            setSelectedElevation(elevation);
                            setMarkerContext('door');
                            navigate(`/project/${projectId}/${activeCsiTab}/door-floor-plans/${elevation.id}`);
                          }}
                          onDeleteElevation={(elevation, e) => confirmDeleteElevation(elevation, e)}
                          onBack={() => {
                            setSelectedDoorElevation(null);
                            setSelectedElevation(null);
                            setDoorBulkMarkerMode(false);
                            setPendingDoorBulkMarkers([]);
                            setDoorBulkMarkerSchedule('');
                            navigate(`/project/${projectId}/${activeCsiTab}/door-floor-plans`);
                          }}
                          onPaste={() => handleElevationPasteFromClipboard('door')}
                          onUpload={(e) => handleElevationUpload(e, 'door')}
                          onZoomIn={() => setElevationZoom(prev => Math.min(prev + 0.25, 3))}
                          onZoomOut={() => setElevationZoom(prev => Math.max(prev - 0.25, 0.5))}
                          onResetZoom={() => { setElevationZoom(1); setElevationPan({ x: 0, y: 0 }); }}
                          onPanStart={handlePanStart}
                          onPanMove={handlePanMove}
                          onPanEnd={handlePanEnd}
                          onImageClick={(e) => {
                            setMarkerContext('door');
                            handleImageClick(e, 'door');
                          }}
                          onMarkerClick={(e, marker) => {
                            setMarkerContext('door');
                            handleMarkerClick(e, marker, 'door');
                          }}
                          onMarkerDelete={(markerId) => {
                            setMarkerContext('door');
                            handleDeleteMarker(markerId, 'door');
                          }}
                          onMarkerMouseDown={(e, marker) => {
                            setMarkerContext('door');
                            handleMarkerMouseDown(e, marker);
                          }}
                          onMarkerMouseMove={handleMarkerMouseMove}
                          onMarkerMouseUp={handleMarkerMouseUp}
                          onSetActiveMarkerPopup={setActiveMarkerPopup}
                          onBulkMarkerModeToggle={(enabled) => {
                            setDoorBulkMarkerMode(enabled);
                            if (!enabled) {
                              setPendingDoorBulkMarkers([]);
                              setDoorBulkMarkerSchedule('');
                            }
                          }}
                          onBulkMarkerScheduleChange={setDoorBulkMarkerSchedule}
                          onBulkMarkerClick={(e) => handleBulkMarkerClick(e, 'door')}
                          onSaveBulkMarkers={() => handleSaveBulkMarkers('door')}
                          onCancelBulkMarkers={() => {
                            setDoorBulkMarkerMode(false);
                            setPendingDoorBulkMarkers([]);
                            setDoorBulkMarkerSchedule('');
                          }}
                          onRemovePendingMarker={(id) => setPendingDoorBulkMarkers(prev => prev.filter(m => m.id !== id))}
                          getElevationImageUrl={getElevationImageUrl}
                          navigate={navigate}
                          projectId={projectId}
                          activeCsiTab={activeCsiTab}
                          activeSubTab={activeSubTab}
                        />
                      </TabsContent>


                      {/* Door Submittals Tab */}
                      <SubmittalsSection
                        tabValue="door-submittals"
                        submittals={submittals}
                        selectedSubmittalIds={selectedSubmittalIds}
                        canManageProject={canManageProject}
                        canEditCSIItems={canEditCSIItems}
                        selectAllItems={selectAllItems}
                        confirmBulkDelete={confirmBulkDelete}
                        toggleItemSelection={toggleItemSelection}
                        setShowAddSubmittalDialog={setShowAddSubmittalDialog}
                        setSubmittalToDelete={setSubmittalToDelete}
                        setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
                        setTooltipPosition={setTooltipPosition}
                        setTooltipContent={setTooltipContent}
                        setTooltipVisible={setTooltipVisible}
                        API={API}
                      />

                      {/* Door Warranty Tab */}
                      <TabsContent value="door-warranty">
                        <WarrantySection
                          divisionType="door"
                          title="Door Warranty Management"
                          project={project}
                          projectId={projectId}
                          warrantyStartDate={project?.doorWarrantyStartDate}
                          warrantyDateField="doorWarrantyStartDate"
                          types={filteredDoorTypes}
                          typesTitle="Door Types Warranty"
                          getTypeLabel={(type) => `${type.manufacturer} - ${type.model}`}
                          getWarrantyFields={(type) => [
                            { label: 'Frame Warranty', years: type.frameWarrantyYears },
                            { label: 'Finish Warranty', years: type.finishWarrantyYears },
                            { label: 'Hardware Warranty', years: type.hardwareWarrantyYears },
                            { label: 'Installation Warranty', years: type.installationWarrantyYears }
                          ]}
                          glassTypes={doorGlassTypes.filter(gt => gt.csiItemId === activeCsiTab)}
                          glassTypesTitle="Glass Types Warranty"
                          getGlassLabel={(glass) => glass.name}
                          getGlassWarrantyYears={(glass) => glass.warrantyYears || 0}
                          showGlassManufacturer={true}
                          showGlassType={true}
                          showGlassModel={false}
                          canEdit={canEditCSIItems}
                          onRefresh={fetchProjectData}
                          testIdPrefix="door"
                        />
                      </TabsContent>

                      {/* Share Tab for Doors */}
                      {/* Share Tab for Doors */}
                      <ShareSection
                        tabValue="share"
                        inputIdPrefix="door"
                        csiItem={csiItem}
                        project={project}
                        user={user}
                        shareCompanySearch={shareCompanySearch}
                        showCompanySuggestions={showCompanySuggestions}
                        companySuggestions={companySuggestions}
                        highlightedIndex={highlightedIndex}
                        searchingCompanies={searchingCompanies}
                        handleSearchCompanies={handleSearchCompanies}
                        handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                        setShowCompanySuggestions={setShowCompanySuggestions}
                        setHighlightedIndex={setHighlightedIndex}
                        handleSelectCompany={handleSelectCompany}
                        handleUnshareCSI={handleUnshareCSI}
                      />
                    </Tabs>
                  </TabsContent>
                );
              }
              
              if (isWindows) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
            {/* Removed Sharing Info */}
            <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
              setActiveSubTab(newSubTab);
              navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
            }} className="space-y-6">
              <TabsList className="flex items-center w-full gap-2">
                <div className="flex-1 flex w-full">
                  <TabsTrigger value="types" data-testid="types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <LayoutGrid className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Window Types ({windowTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="glass-types" data-testid="glass-types-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Gem className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Glass Types ({windowGlassTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="metal-panels" data-testid="window-metal-panels-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <PanelTop className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Metal Panels ({windowMetalPanelTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="windows" data-testid="windows-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Table2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Schedules ({windowList.length})
                  </TabsTrigger>
                  <TabsTrigger value="elevations" data-testid="elevations-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Building2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Elevations ({windowElevations.length})
                  </TabsTrigger>
                  <TabsTrigger value="submittals" data-testid="submittals-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <FileText className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Submittals ({submittals.length})
                  </TabsTrigger>
                  <TabsTrigger value="warranty" data-testid="warranty-tab" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Warranty
                  </TabsTrigger>
                </div>
                {/* Hide Share tab for shared users (not project owner) */}
                {canManageProject && (
                  <TabsTrigger value="share" data-testid="share-tab" className="flex-shrink-0">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </TabsTrigger>
                )}
              </TabsList>
              
              {/* Sharing Info - Hide for shared users (not project owner) */}
              {canManageProject && (
                <div className="text-xs text-slate-600 -mt-2">
                  Shared with: {csiItem.assignedCompanyName ? (
                    <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                  ) : (
                    <span className="text-slate-400">N/A</span>
                  )}
                </div>
              )}

          {/* Window Types Tab */}
          <TabsContent value="types">
            <WindowTypesSection
              windowTypes={windowTypes}
              selectedWindowTypeIds={selectedWindowTypeIds}
              canEditCSIItems={canEditCSIItems}
              windowTypePictureUploading={windowTypePictureUploading}
              operationLoading={windowOperationLoading}
              API={API}
              showTypeDialog={showTypeDialog}
              editingType={editingType}
              newType={newType}
              typePictureUrl={typePictureUrl}
              pendingWindowTypePicture={pendingWindowTypePicture}
              onSelectAll={() => selectAllItems('windowType')}
              onConfirmBulkDelete={() => confirmBulkDelete('windowType')}
              onToggleSelection={(id, e) => toggleItemSelection(id, 'windowType', e)}
              onDialogOpenChange={handleCloseTypeDialog}
              onNewTypeChange={setNewType}
              onCreateType={handleCreateType}
              onEditType={handleEditType}
              onDeleteType={confirmDeleteWindowType}
              onPictureUpload={(e, typeId) => {
                const effectiveTypeId = typeId || editingType?.id;
                if (effectiveTypeId) {
                  handleTypePictureUpload(e, effectiveTypeId);
                } else {
                  // For new window type - save file for later upload
                  const file = e.target.files?.[0];
                  if (file) {
                    if (pendingWindowTypePicture?.previewUrl) {
                      URL.revokeObjectURL(pendingWindowTypePicture.previewUrl);
                    }
                    const previewUrl = URL.createObjectURL(file);
                    setPendingWindowTypePicture({ file, previewUrl });
                  }
                }
                e.target.value = ''; // Reset input
              }}
              onPictureDelete={handleDeletePicture}
              onPasteFromClipboard={handleWindowTypePasteFromClipboard}
              onPendingPictureRemove={() => {
                if (pendingWindowTypePicture?.previewUrl) {
                  URL.revokeObjectURL(pendingWindowTypePicture.previewUrl);
                }
                setPendingWindowTypePicture(null);
              }}
              onViewDrawing={setViewingDrawing}
              onColorPickerOpenChange={(typeId, open) => {
                setWindowTypes(prev => prev.map(t => 
                  t.id === typeId ? { ...t, colorPickerOpen: open, pendingMarkerColor: open ? t.pendingMarkerColor : undefined } : t
                ));
              }}
              onColorChange={(typeId, newColor) => {
                setWindowTypes(prev => prev.map(t => 
                  t.id === typeId ? { ...t, pendingMarkerColor: newColor } : t
                ));
              }}
              onColorSave={async (type) => {
                setWindowTypes(prev => prev.map(t => 
                  t.id === type.id ? { ...t, savingColor: true } : t
                ));
                try {
                  await axios.put(`${API}/window-types/${type.id}`, {
                    ...type,
                    markerColor: type.pendingMarkerColor
                  });
                  setWindowTypes(prev => prev.map(t => 
                    t.id === type.id ? { ...t, markerColor: type.pendingMarkerColor, pendingMarkerColor: undefined, savingColor: false, colorPickerOpen: false } : t
                  ));
                  toast.success('Marker color saved');
                } catch (error) {
                  setWindowTypes(prev => prev.map(t => 
                    t.id === type.id ? { ...t, savingColor: false } : t
                  ));
                  toast.error('Failed to update marker color');
                }
              }}
              canUserEdit={canUserEdit}
            />
          </TabsContent>


          {/* Glass Types Tab */}
          <TabsContent value="glass-types">
            <WindowGlassTypesSection
              windowGlassTypes={windowGlassTypes}
              selectedWindowGlassTypeIds={selectedWindowGlassTypeIds}
              expandedWindowGlassTypeIds={expandedWindowGlassTypeIds}
              canEditCSIItems={canEditCSIItems}
              canManageProject={canManageProject}
              glassTypeSpecs={glassTypeSpecs}
              showGlassTypeDialog={showGlassTypeDialog}
              editingGlassType={editingGlassType}
              newWindowGlassType={newWindowGlassType}
              windowGridSizeRef={windowGridSizeRef}
              onSelectAll={() => selectAllItems('windowGlassType')}
              onConfirmBulkDelete={() => confirmBulkDelete('windowGlassType')}
              onToggleSelection={(id, e) => toggleItemSelection(id, 'windowGlassType', e)}
              onDialogOpenChange={handleCloseWindowGlassTypeDialog}
              onFormChange={setNewWindowGlassType}
              onFormSubmit={handleCreateWindowGlassType}
              onEdit={(glassType) => {
                setEditingGlassType(glassType);
                setNewWindowGlassType({
                  name: glassType.name,
                  glassManufacturer: glassType.glassManufacturer || '',
                  glassModel: glassType.glassModel || '',
                  glassBuild: glassType.glassBuild || '',
                  glassThickness_in: glassType.glassThickness_in || null,
                  colorCode: glassType.colorCode || '',
                  glassTypes: glassType.glassTypes || [],
                  warrantyYears: glassType.warrantyYears?.toString() ?? '',
                  hasGrid: glassType.hasGrid || false,
                  gridSize_in: glassType.gridSize_in?.toString() ?? ''
                });
                setShowGlassTypeDialog(true);
              }}
              onDelete={confirmDeleteWindowGlassType}
              onToggleExpand={(id) => {
                setExpandedWindowGlassTypeIds(prev => 
                  prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                );
              }}
            />
          </TabsContent>

          {/* Window Metal Panels Tab */}
          <TabsContent value="metal-panels">
            <WindowMetalPanelsSection
              windowMetalPanelTypes={windowMetalPanelTypes}
              selectedWindowMetalPanelTypeIds={selectedWindowMetalPanelTypeIds}
              canEditCSIItems={canEditCSIItems}
              canManageProject={canManageProject}
              operationLoading={windowOperationLoading}
              showWindowMetalPanelTypeDialog={showWindowMetalPanelTypeDialog}
              editingWindowMetalPanelType={editingWindowMetalPanelType}
              newWindowMetalPanelType={newWindowMetalPanelType}
              onSelectAll={() => selectAllItems('windowMetalPanelType')}
              onConfirmBulkDelete={() => confirmBulkDelete('windowMetalPanelType')}
              onToggleSelection={(id, e) => toggleItemSelection(id, 'windowMetalPanelType', e)}
              onDialogOpenChange={(open) => {
                if (!open) {
                  setShowWindowMetalPanelTypeDialog(false);
                  setEditingWindowMetalPanelType(null);
                  setNewWindowMetalPanelType({
                    name: '',
                    manufacturer: '',
                    model: '',
                    thickness: '',
                    colorCode: ''
                  });
                } else {
                  setShowWindowMetalPanelTypeDialog(true);
                }
              }}
              onFormChange={setNewWindowMetalPanelType}
              onFormSubmit={async (e) => {
                e.preventDefault();
                if (!newWindowMetalPanelType.name.trim()) {
                  toast.error('Name is required');
                  return;
                }
                
                setWindowOperationLoading(true);
                try {
                  const payload = {
                    projectId,
                    csiItemId: activeCsiTab,
                    name: newWindowMetalPanelType.name,
                    manufacturer: newWindowMetalPanelType.manufacturer || null,
                    model: newWindowMetalPanelType.model || null,
                    thickness: parseNumericInput(newWindowMetalPanelType.thickness),
                    colorCode: newWindowMetalPanelType.colorCode || null
                  };
                  
                  if (editingWindowMetalPanelType) {
                    await axios.put(`${API}/window-metal-panel-types/${editingWindowMetalPanelType.id}`, payload);
                    toast.success('Metal panel type updated');
                  } else {
                    await axios.post(`${API}/window-metal-panel-types`, payload);
                    toast.success('Metal panel type created');
                  }
                  
                  fetchWindowMetalPanelTypes();
                  setShowWindowMetalPanelTypeDialog(false);
                  setEditingWindowMetalPanelType(null);
                  setNewWindowMetalPanelType({
                    name: '',
                    manufacturer: '',
                    model: '',
                    thickness: '',
                    colorCode: ''
                  });
                } catch (error) {
                  if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                    toast.error(error.response.data.detail.message);
                  } else {
                    toast.error(editingWindowMetalPanelType ? 'Failed to update metal panel type' : 'Failed to create metal panel type');
                  }
                } finally {
                  setWindowOperationLoading(false);
                }
              }}
              onEdit={(metalType) => {
                setEditingWindowMetalPanelType(metalType);
                setNewWindowMetalPanelType({
                  name: metalType.name,
                  manufacturer: metalType.manufacturer || '',
                  model: metalType.model || '',
                  thickness: metalType.thickness?.toString() || '',
                  colorCode: metalType.colorCode || ''
                });
                setShowWindowMetalPanelTypeDialog(true);
              }}
              onDelete={handleDeleteWindowMetalPanelType}
            />
          </TabsContent>

          {/* Window List Tab */}
          <TabsContent value="windows">
            <WindowScheduleSection
              windowList={windowList}
              filteredWindows={filteredWindows}
              windowTypes={windowTypes}
              windowGlassTypes={windowGlassTypes}
              windowMetalPanelTypes={windowMetalPanelTypes}
              windowElevations={windowElevations}
              selectedWindowListIds={selectedWindowListIds}
              canEditCSIItems={canEditCSIItems}
              uploadingExcel={uploadingExcel}
              operationLoading={windowOperationLoading}
              showWindowDialog={showWindowDialog}
              editingWindow={editingWindow}
              newWindow={newWindow}
              windowSearch={windowSearch}
              windowFilters={windowFilters}
              windowSortBy={windowSortBy}
              onSelectAll={() => selectAllItems('windowList')}
              onConfirmBulkDelete={() => confirmBulkDelete('windowList')}
              onToggleSelection={(id, e) => toggleItemSelection(id, 'windowList', e)}
              onDialogOpenChange={handleCloseWindowDialog}
              onFormChange={setNewWindow}
              onFormSubmit={handleCreateWindow}
              onEdit={handleEditWindow}
              onDelete={confirmDeleteWindow}
              onPrintAllQR={handlePrintAllQR}
              onPrintSingleQR={handlePrintSingleQR}
              onPrintLabel={handlePrintWindowLabel}
              onExcelUpload={handleExcelUpload}
              onSearchChange={setWindowSearch}
              onFiltersChange={setWindowFilters}
              onSortChange={setWindowSortBy}
              onClearFilters={() => {
                setWindowSearch('');
                setWindowFilters({ type: '', location: '', label: '' });
                setWindowSortBy('createdAt_desc');
              }}
            />
          </TabsContent>

          {/* Elevations Tab */}
          <TabsContent value="elevations">
            <WindowElevationsSection
              windowElevations={windowElevations}
              windowList={windowList}
              windowTypes={windowTypes}
              selectedWindowElevation={selectedWindowElevation}
              selectedWindowElevationIds={selectedWindowElevationIds}
              canEditCSIItems={canEditCSIItems}
              uploadingElevation={uploadingElevation}
              bulkDeletingElevations={bulkDeletingElevations}
              dragJustEndedRef={dragJustEndedRef}
              deletingSingleElevation={deletingSingleElevation}
              elevationUploading={elevationUploading}
              uploadProgress={uploadProgress}
              uploadProgressPercent={uploadProgressPercent}
              elevationZoom={elevationZoom}
              elevationPan={elevationPan}
              isPanning={isPanning}
              bulkMarkerMode={bulkMarkerMode}
              bulkMarkerSchedule={bulkMarkerSchedule}
              pendingBulkMarkers={pendingBulkMarkers}
              savingBulkMarkers={savingBulkMarkers}
              activeMarkerPopup={activeMarkerPopup}
              onSelectAll={() => selectAllElevations('window')}
              onConfirmBulkDelete={() => confirmBulkDelete('window')}
              onToggleSelection={(id, e) => toggleElevationSelection(id, 'window', e)}
              onElevationClick={(elevation) => handleElevationClick(elevation, 'window')}
              onDeleteElevation={confirmDeleteElevation}
              onBack={() => {
                setSelectedWindowElevation(null);
                setSelectedElevation(null);
                navigate(`/project/${projectId}/${activeCsiTab}/${activeSubTab}`, { replace: true });
              }}
              onPaste={() => handleElevationPasteFromClipboard('window')}
              onUpload={(e) => handleElevationUpload(e, 'window')}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onResetZoom={handleResetZoom}
              onPanStart={handlePanStart}
              onPanMove={handlePanMove}
              onPanEnd={handlePanEnd}
              onImageClick={(e) => {
                setMarkerContext('window');
                handleImageClick(e, 'window');
              }}
              onMarkerClick={handleMarkerClick}
              onMarkerDelete={handleDeleteMarker}
              onMarkerMouseDown={handleMarkerMouseDown}
              onMarkerMouseMove={handleMarkerMouseMove}
              onMarkerMouseUp={handleMarkerMouseUp}
              onSetActiveMarkerPopup={setActiveMarkerPopup}
              onBulkMarkerModeToggle={setBulkMarkerMode}
              onBulkMarkerScheduleChange={setBulkMarkerSchedule}
              onBulkMarkerClick={handleBulkMarkerClick}
              onSaveBulkMarkers={() => handleSaveBulkMarkers('window')}
              onCancelBulkMarkers={handleCancelBulkMarkers}
              onRemovePendingMarker={handleRemovePendingMarker}
              getElevationImageUrl={getElevationImageUrl}
              navigate={navigate}
              projectId={projectId}
              activeCsiTab={activeCsiTab}
              activeSubTab={activeSubTab}
            />
          </TabsContent>


          {/* Submittals Tab */}
          <SubmittalsSection
            tabValue="submittals"
            submittals={submittals}
            selectedSubmittalIds={selectedSubmittalIds}
            canManageProject={canManageProject}
            canEditCSIItems={canEditCSIItems}
            selectAllItems={selectAllItems}
            confirmBulkDelete={confirmBulkDelete}
            toggleItemSelection={toggleItemSelection}
            setShowAddSubmittalDialog={setShowAddSubmittalDialog}
            setSubmittalToDelete={setSubmittalToDelete}
            setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
            setTooltipPosition={setTooltipPosition}
            setTooltipContent={setTooltipContent}
            setTooltipVisible={setTooltipVisible}
            API={API}
          />

          {/* Warranty Tab */}
          <TabsContent value="warranty">
            <WarrantySection
              divisionType="window"
              title="Window Warranty Management"
              project={project}
              projectId={projectId}
              warrantyStartDate={project?.windowWarrantyStartDate}
              warrantyDateField="windowWarrantyStartDate"
              types={windowTypes}
              typesTitle="Window Types Warranty"
              getTypeLabel={(type) => `${type.manufacturer} - ${type.model}`}
              getWarrantyFields={(type) => [
                { label: 'Frame Warranty', years: type.frameWarrantyYears },
                { label: 'Finish Warranty', years: type.finishWarrantyYears },
                { label: 'Hardware Warranty', years: type.hardwareWarrantyYears },
                { label: 'Installation Warranty', years: type.installationWarrantyYears }
              ]}
              glassTypes={windowGlassTypes}
              glassTypesTitle="Glass Types Warranty"
              getGlassLabel={(glass) => glass.name}
              getGlassWarrantyYears={(glass) => glass.warrantyYears || 0}
              showGlassManufacturer={true}
              showGlassType={true}
              showGlassModel={false}
              canEdit={canEditCSIItems}
              onRefresh={fetchProjectData}
              testIdPrefix="window"
            />
          </TabsContent>

          {/* Share Tab */}
          {/* Share Tab */}
          <ShareSection
            tabValue="share"
            inputIdPrefix="window"
            csiItem={csiItem}
            project={project}
            user={user}
            shareCompanySearch={shareCompanySearch}
            showCompanySuggestions={showCompanySuggestions}
            companySuggestions={companySuggestions}
            highlightedIndex={highlightedIndex}
            searchingCompanies={searchingCompanies}
            handleSearchCompanies={handleSearchCompanies}
            handleCompanySearchKeyDown={handleCompanySearchKeyDown}
            setShowCompanySuggestions={setShowCompanySuggestions}
            setHighlightedIndex={setHighlightedIndex}
            handleSelectCompany={handleSelectCompany}
            handleUnshareCSI={handleUnshareCSI}
          />
        </Tabs>
          </TabsContent>

                  );
              } else if (isCurtainwall) {
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
            {/* Removed Sharing Info */}
            <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
              setActiveSubTab(newSubTab);
              navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
            }} className="space-y-6">
              <TabsList className="flex items-center w-full gap-2">
                <div className="flex-1 flex w-full">
                  <TabsTrigger value="cw-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <LayoutGrid className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    CW Types ({curtainwallTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-glass-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Gem className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Glass Types ({glassTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-metal-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Grid3x3 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Metal Panels ({metalPanelTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-panel-list" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Table2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Schedules ({panelList.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-elevations" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Building2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Elevations ({curtainwallElevations.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-submittals" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <FileText className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Submittals ({submittals.length})
                  </TabsTrigger>
                  <TabsTrigger value="cw-warranty" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Warranty
                  </TabsTrigger>
                </div>
                {/* Hide Share tab for shared users (not project owner) */}
                {canManageProject && (
                  <TabsTrigger value="cw-share" data-testid="cw-share-tab" className="flex-shrink-0">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </TabsTrigger>
                )}
              </TabsList>
              
              {/* Sharing Info - Hide for shared users (not project owner) */}
              {canManageProject && (
                <div className="text-xs text-slate-600 -mt-2">
                  Shared with: {csiItem.assignedCompanyName ? (
                    <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                  ) : (
                    <span className="text-slate-400">N/A</span>
                  )}
                </div>
              )}

              {/* Curtainwall Types */}
              <CWTypesSection
                curtainwallTypes={curtainwallTypes}
                curtainwallElevations={curtainwallElevations}
                selectedCurtainwallTypeIds={selectedCurtainwallTypeIds}
                newCWType={newCWType}
                editingCWType={editingCWType}
                showCWTypeDialog={showCWTypeDialog}
                cwTypePictureUrl={cwTypePictureUrl}
                pendingCWTypePicture={pendingCWTypePicture}
                cwTypePictureUploading={cwTypePictureUploading}
                operationLoading={cwOperationLoading}
                canEditCSIItems={canEditCSIItems}
                canManageProject={canManageProject}
                setNewCWType={setNewCWType}
                setShowCWTypeDialog={setShowCWTypeDialog}
                setEditingCWType={setEditingCWType}
                setCWTypePictureUrl={setCWTypePictureUrl}
                setPendingCWTypePicture={setPendingCWTypePicture}
                setCurtainwallTypes={setCurtainwallTypes}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateCWType={handleCreateCWType}
                handleEditCWType={handleEditCWType}
                handleDeleteCWType={handleDeleteCWType}
                handleCWDrawingUpload={handleCWDrawingUpload}
                handleCWTypePasteFromClipboard={handleCWTypePasteFromClipboard}
                handleDeleteDrawing={handleDeleteDrawing}
                handleOpenCWDrawingView={handleOpenCWDrawingView}
                API={API}
                axios={axios}
                toast={toast}
              />
              {/* Glass Types */}
              <CWGlassTypesSection
                glassTypes={glassTypes}
                selectedCWGlassTypeIds={selectedCWGlassTypeIds}
                expandedCWGlassTypeIds={expandedCWGlassTypeIds}
                newGlassType={newGlassType}
                editingGlassType={editingGlassType}
                showGlassTypeDialog={showGlassTypeDialog}
                operationLoading={cwOperationLoading}
                canEditCSIItems={canEditCSIItems}
                canManageProject={canManageProject}
                cwGridSizeRef={cwGridSizeRef}
                setNewGlassType={setNewGlassType}
                setShowGlassTypeDialog={setShowGlassTypeDialog}
                setEditingGlassType={setEditingGlassType}
                setExpandedCWGlassTypeIds={setExpandedCWGlassTypeIds}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateGlassType={handleCreateGlassType}
                handleEditGlassType={handleEditGlassType}
                handleDeleteGlassType={handleDeleteGlassType}
              />
              {/* Metal Panel Types */}
              <CWMetalTypesSection
                metalPanelTypes={metalPanelTypes}
                selectedMetalPanelTypeIds={selectedMetalPanelTypeIds}
                newMetalType={newMetalType}
                editingMetalType={editingMetalType}
                showMetalTypeDialog={showMetalTypeDialog}
                operationLoading={cwOperationLoading}
                canEditCSIItems={canEditCSIItems}
                canManageProject={canManageProject}
                setNewMetalType={setNewMetalType}
                setShowMetalTypeDialog={setShowMetalTypeDialog}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateMetalType={handleCreateMetalType}
                handleEditMetalType={handleEditMetalType}
                handleDeleteMetalType={handleDeleteMetalType}
              />
              {/* Panel List */}
              <CWPanelListSection
                panelList={panelList}
                curtainwallTypes={curtainwallTypes}
                glassTypes={glassTypes}
                metalPanelTypes={metalPanelTypes}
                curtainwallElevations={curtainwallElevations}
                selectedPanelListIds={selectedPanelListIds}
                newPanel={newPanel}
                editingPanel={editingPanel}
                showPanelDialog={showPanelDialog}
                uploadingPanelExcel={uploadingPanelExcel}
                showPanelExcelDialog={showPanelExcelDialog}
                panelExcelPreview={panelExcelPreview}
                cwScheduleSearch={cwScheduleSearch}
                cwScheduleFilters={cwScheduleFilters}
                cwScheduleSortBy={cwScheduleSortBy}
                operationLoading={cwOperationLoading}
                canEditCSIItems={canEditCSIItems}
                setNewPanel={setNewPanel}
                setShowPanelDialog={setShowPanelDialog}
                setShowPanelExcelDialog={setShowPanelExcelDialog}
                setPanelExcelPreview={setPanelExcelPreview}
                setCwScheduleSearch={setCwScheduleSearch}
                setCwScheduleFilters={setCwScheduleFilters}
                setCwScheduleSortBy={setCwScheduleSortBy}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreatePanel={handleCreatePanel}
                handleEditPanel={handleEditPanel}
                handleDeletePanel={handleDeletePanel}
                handlePanelExcelUpload={handlePanelExcelUpload}
                handlePrintAllPanelLabels={handlePrintAllPanelLabels}
                handlePrintPanelLabel={handlePrintPanelLabel}
                handlePrintPanelQR={handlePrintPanelQR}
                handleUpdatePanelPreviewRow={handleUpdatePanelPreviewRow}
                handlePanelBulkImport={handlePanelBulkImport}
                onOpenDetail={openCWPanelDetail}
              />
              {/* Curtainwall Elevations Tab */}
              <CWElevationsSection
                curtainwallElevations={curtainwallElevations}
                curtainwallTypes={curtainwallTypes}
                panelList={panelList}
                selectedCurtainwallElevation={selectedCurtainwallElevation}
                selectedCurtainwallElevationIds={selectedCurtainwallElevationIds}
                selectedElevation={selectedElevation}
                cwBulkMarkerMode={cwBulkMarkerMode}
                cwBulkMarkerType={cwBulkMarkerType}
                pendingCwBulkMarkers={pendingCwBulkMarkers}
                savingCwBulkMarkers={savingCwBulkMarkers}
                elevationZoom={elevationZoom}
                elevationPan={elevationPan}
                isPanning={isPanning}
                activeMarkerPopup={activeMarkerPopup}
                uploadingElevation={uploadingElevation}
                bulkDeletingElevations={bulkDeletingElevations}
                deletingSingleElevation={deletingSingleElevation}
                uploadProgress={uploadProgress}
                uploadProgressPercent={uploadProgressPercent}
                elevationUploading={elevationUploading}
                canEditCSIItems={canEditCSIItems}
                canManageProject={canManageProject}
                projectId={projectId}
                activeCsiTab={activeCsiTab}
                activeSubTab={activeSubTab}
                navigate={navigate}
                selectAllElevations={selectAllElevations}
                toggleElevationSelection={toggleElevationSelection}
                setSelectedCurtainwallElevation={setSelectedCurtainwallElevation}
                setSelectedElevation={setSelectedElevation}
                confirmBulkDelete={confirmBulkDelete}
                confirmDeleteElevation={confirmDeleteElevation}
                handleElevationUpload={handleElevationUpload}
                handleElevationPasteFromClipboard={handleElevationPasteFromClipboard}
                handleElevationClick={handleElevationClick}
                handleImageClick={handleImageClick}
                handleMarkerClick={handleMarkerClick}
                setCwBulkMarkerMode={setCwBulkMarkerMode}
                setCwBulkMarkerType={setCwBulkMarkerType}
                handleCwBulkMarkerClick={handleCwBulkMarkerClick}
                handleSaveCwBulkMarkers={handleSaveCwBulkMarkers}
                handleCancelCwBulkMarkers={handleCancelCwBulkMarkers}
                handleRemovePendingCwMarker={handleRemovePendingCwMarker}
                handleZoomIn={handleZoomIn}
                handleZoomOut={handleZoomOut}
                handleResetZoom={handleResetZoom}
                handlePanStart={handlePanStart}
                handlePanMove={handlePanMove}
                handlePanEnd={handlePanEnd}
                handleMarkerMouseDown={handleMarkerMouseDown}
                handleMarkerMouseMove={handleMarkerMouseMove}
                handleMarkerMouseUp={handleMarkerMouseUp}
                handleDeleteMarker={handleDeleteMarker}
                setActiveMarkerPopup={setActiveMarkerPopup}
                setMarkerContext={setMarkerContext}
                getElevationImageUrl={getElevationImageUrl}
              />

              {/* Curtainwall Submittals Tab */}
              <SubmittalsSection
                tabValue="cw-submittals"
                submittals={submittals}
                selectedSubmittalIds={selectedSubmittalIds}
                canManageProject={canManageProject}
                canEditCSIItems={canEditCSIItems}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                setShowAddSubmittalDialog={setShowAddSubmittalDialog}
                setSubmittalToDelete={setSubmittalToDelete}
                setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
                setTooltipPosition={setTooltipPosition}
                setTooltipContent={setTooltipContent}
                setTooltipVisible={setTooltipVisible}
                API={API}
              />

              {/* Curtainwall Warranty Tab */}
              <TabsContent value="cw-warranty">
                <WarrantySection
                  divisionType="curtainwall"
                  title="Curtainwall Warranty Management"
                  project={project}
                  projectId={projectId}
                  warrantyStartDate={project?.curtainwallWarrantyStartDate}
                  warrantyDateField="curtainwallWarrantyStartDate"
                  types={curtainwallTypes}
                  typesTitle="Curtainwall Types Warranty"
                  getTypeLabel={(type) => `${type.name} - ${type.manufacturer}`}
                  getWarrantyFields={(type) => [
                    { label: 'Product Warranty', years: type.productWarrantyYears },
                    { label: 'Hardware Warranty', years: type.hardwareWarrantyYears },
                    { label: 'Finish Warranty', years: type.finishWarrantyYears },
                    { label: 'Installation Warranty', years: type.installationWarrantyYears }
                  ]}
                  glassTypes={glassTypes}
                  glassTypesTitle="Glass Types Warranty"
                  getGlassLabel={(glass) => glass.name}
                  getGlassWarrantyYears={(glass) => glass.warrantyYears}
                  showGlassManufacturer={true}
                  showGlassType={false}
                  showGlassModel={true}
                  canEdit={canEditCSIItems}
                  onRefresh={fetchProjectData}
                  testIdPrefix="cw"
                />
              </TabsContent>

              {/* Curtainwall Share Tab */}
              {/* Curtainwall Share Tab */}
              <ShareSection
                tabValue="cw-share"
                inputIdPrefix="cw"
                csiItem={csiItem}
                project={project}
                user={user}
                shareCompanySearch={shareCompanySearch}
                showCompanySuggestions={showCompanySuggestions}
                companySuggestions={companySuggestions}
                highlightedIndex={highlightedIndex}
                searchingCompanies={searchingCompanies}
                handleSearchCompanies={handleSearchCompanies}
                handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                setShowCompanySuggestions={setShowCompanySuggestions}
                setHighlightedIndex={setHighlightedIndex}
                handleSelectCompany={handleSelectCompany}
                handleUnshareCSI={handleUnshareCSI}
              />
            </Tabs>
          </TabsContent>
                );
              } else if (isStorefront) {
                // Storefront section - same structure as Curtain Walls but with SF prefix
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
            {/* Removed Sharing Info */}
            <Tabs value={activeSubTab} onValueChange={(newSubTab) => {
              setActiveSubTab(newSubTab);
              navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
            }} className="space-y-6">
              <TabsList className="flex items-center w-full gap-2">
                <div className="flex-1 flex w-full">
                  <TabsTrigger value="sf-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <LayoutGrid className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    SF Types ({storefrontTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-glass-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Gem className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Glass Types ({storefrontGlassTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-metal-types" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Grid3x3 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Metal Panels ({storefrontMetalPanelTypes.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-panel-list" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Table2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Schedules ({storefrontPanelList.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-elevations" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <Building2 className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Elevations ({storefrontElevations.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-submittals" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <FileText className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Submittals ({submittals.length})
                  </TabsTrigger>
                  <TabsTrigger value="sf-warranty" className="flex-1 flex items-center justify-center gap-1.5 text-xs sm:text-sm">
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
                    Warranty
                  </TabsTrigger>
                </div>
                {/* Hide Share tab for shared users (not project owner) */}
                {canManageProject && (
                  <TabsTrigger value="sf-share" className="flex-shrink-0">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </TabsTrigger>
                )}
              </TabsList>
              
              {/* Sharing Info - Hide for shared users (not project owner) */}
              {canManageProject && (
                <div className="text-xs text-slate-600 -mt-2">
                  Shared with: {csiItem.assignedCompanyName ? (
                    <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
                  ) : (
                    <span className="text-slate-400">N/A</span>
                  )}
                </div>
              )}

              {/* Storefront Types */}
              <SFTypesSection
                storefrontTypes={storefrontTypes}
                selectedStorefrontTypeIds={selectedStorefrontTypeIds}
                newSFType={newSFType}
                editingSFType={editingSFType}
                showSFTypeDialog={showSFTypeDialog}
                sfTypePictureUrl={sfTypePictureUrl}
                pendingSFTypePicture={pendingSFTypePicture}
                sfTypePictureUploading={sfTypePictureUploading}
                operationLoading={sfOperationLoading}
                canManageProject={canManageProject}
                setNewSFType={setNewSFType}
                setShowSFTypeDialog={setShowSFTypeDialog}
                setEditingSFType={setEditingSFType}
                setSFTypePictureUrl={setSFTypePictureUrl}
                setPendingSFTypePicture={setPendingSFTypePicture}
                setStorefrontTypes={setStorefrontTypes}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateSFType={handleCreateSFType}
                handleEditSFType={handleEditSFType}
                handleDeleteSFType={handleDeleteSFType}
                handleSFDrawingUpload={handleSFDrawingUpload}
                handleSFTypePasteFromClipboard={handleSFTypePasteFromClipboard}
                handleDeleteSFDrawing={handleDeleteSFDrawing}
                handleOpenSFDrawingView={handleOpenSFDrawingView}
                API={API}
                axios={axios}
                toast={toast}
              />

              {/* Glass Types Tab */}
              {/* SF Glass Types */}
              <SFGlassTypesSection
                storefrontGlassTypes={storefrontGlassTypes}
                selectedSFGlassTypeIds={selectedSFGlassTypeIds}
                expandedSFGlassTypeIds={expandedSFGlassTypeIds}
                newSFGlassType={newSFGlassType}
                editingSFGlassType={editingSFGlassType}
                showSFGlassTypeDialog={showSFGlassTypeDialog}
                operationLoading={sfOperationLoading}
                canManageProject={canManageProject}
                sfGridSizeRef={sfGridSizeRef}
                setNewSFGlassType={setNewSFGlassType}
                setShowSFGlassTypeDialog={setShowSFGlassTypeDialog}
                setEditingSFGlassType={setEditingSFGlassType}
                setExpandedSFGlassTypeIds={setExpandedSFGlassTypeIds}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateSFGlassType={handleCreateSFGlassType}
                handleEditSFGlassType={handleEditSFGlassType}
                handleDeleteSFGlassType={handleDeleteSFGlassType}
              />
              {/* SF Metal Types */}
              <SFMetalTypesSection
                storefrontMetalPanelTypes={storefrontMetalPanelTypes}
                selectedSFMetalTypeIds={selectedSFMetalPanelTypeIds}
                newSFMetalType={newSFMetalType}
                editingSFMetalType={editingSFMetalType}
                showSFMetalTypeDialog={showSFMetalTypeDialog}
                operationLoading={sfOperationLoading}
                canManageProject={canManageProject}
                setNewSFMetalType={setNewSFMetalType}
                setShowSFMetalTypeDialog={setShowSFMetalTypeDialog}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateSFMetalType={handleCreateSFMetalType}
                handleEditSFMetalType={handleEditSFMetalType}
                handleDeleteSFMetalType={handleDeleteSFMetalType}
              />
              {/* SF Panel List */}
              <SFPanelListSection
                storefrontPanelList={storefrontPanelList}
                storefrontTypes={storefrontTypes}
                storefrontGlassTypes={storefrontGlassTypes}
                storefrontMetalPanelTypes={storefrontMetalPanelTypes}
                storefrontElevations={storefrontElevations}
                selectedSFPanelListIds={selectedSFPanelListIds}
                newSFPanel={newSFPanel}
                editingSFPanel={editingPanel}
                showSFPanelDialog={showSFPanelDialog}
                uploadingSFPanelExcel={uploadingPanelExcel}
                showSFPanelExcelDialog={showPanelExcelDialog}
                sfPanelExcelPreview={panelExcelPreview}
                sfScheduleSearch={sfScheduleSearch}
                sfScheduleFilters={sfScheduleFilters}
                sfScheduleSortBy={sfScheduleSortBy}
                operationLoading={sfOperationLoading}
                canManageProject={canManageProject}
                setNewSFPanel={setNewSFPanel}
                setShowSFPanelDialog={handleSFPanelDialogOpenChange}
                setShowSFPanelExcelDialog={setShowPanelExcelDialog}
                setSFPanelExcelPreview={setPanelExcelPreview}
                setSfScheduleSearch={setSfScheduleSearch}
                setSfScheduleFilters={setSfScheduleFilters}
                setSfScheduleSortBy={setSfScheduleSortBy}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                handleCreateSFPanel={handleCreateSFPanel}
                handleEditSFPanel={handleEditSFPanel}
                handleDeleteSFPanel={handleDeleteSFPanel}
                handleSFPanelExcelUpload={handleSFPanelExcelUpload}
                handlePrintAllSFPanelLabels={handlePrintAllPanelLabels}
                handlePrintSFPanelLabel={handlePrintSFPanelLabel}
                handlePrintSFPanelQR={handlePrintSFPanelQR}
                handleUpdateSFPanelPreviewRow={handleUpdatePanelPreviewRow}
                handleSFPanelBulkImport={handleSFPanelBulkImport}
              />
              {/* SF Elevations */}
              <SFElevationsSection
                storefrontElevations={storefrontElevations}
                storefrontTypes={storefrontTypes}
                storefrontPanelList={storefrontPanelList}
                selectedStorefrontElevation={selectedStorefrontElevation}
                selectedStorefrontElevationIds={selectedStorefrontElevationIds}
                selectedElevation={selectedElevation}
                sfBulkMarkerMode={sfBulkMarkerMode}
                sfBulkMarkerSchedule={sfBulkMarkerSchedule}
                pendingSfBulkMarkers={pendingSfBulkMarkers}
                savingSfBulkMarkers={savingSfBulkMarkers}
                elevationZoom={elevationZoom}
                elevationPan={elevationPan}
                isPanning={isPanning}
                activeMarkerPopup={activeMarkerPopup}
                uploadingElevation={uploadingElevation}
                bulkDeletingElevations={bulkDeletingElevations}
                deletingSingleElevation={deletingSingleElevation}
                uploadProgress={uploadProgress}
                uploadProgressPercent={uploadProgressPercent}
                elevationUploading={elevationUploading}
                canManageProject={canManageProject}
                projectId={projectId}
                activeCsiTab={activeCsiTab}
                activeSubTab={activeSubTab}
                navigate={navigate}
                selectAllElevations={selectAllElevations}
                toggleElevationSelection={toggleElevationSelection}
                setSelectedStorefrontElevation={setSelectedStorefrontElevation}
                setSelectedElevation={setSelectedElevation}
                confirmBulkDelete={confirmBulkDelete}
                confirmDeleteElevation={confirmDeleteElevation}
                handleElevationUpload={handleElevationUpload}
                handleElevationPasteFromClipboard={handleElevationPasteFromClipboard}
                handleElevationClick={handleElevationClick}
                handleImageClick={handleImageClick}
                handleMarkerClick={handleMarkerClick}
                setSfBulkMarkerMode={setSfBulkMarkerMode}
                setSfBulkMarkerSchedule={setSfBulkMarkerSchedule}
                handleSfBulkMarkerClick={handleSfBulkMarkerClick}
                handleSaveSfBulkMarkers={handleSaveSfBulkMarkers}
                handleCancelSfBulkMarkers={handleCancelSfBulkMarkers}
                handleRemovePendingSfMarker={handleRemovePendingSfMarker}
                handleZoomIn={handleZoomIn}
                handleZoomOut={handleZoomOut}
                handleResetZoom={handleResetZoom}
                handlePanStart={handlePanStart}
                handlePanMove={handlePanMove}
                handlePanEnd={handlePanEnd}
                handleMarkerMouseDown={handleMarkerMouseDown}
                handleMarkerMouseMove={handleMarkerMouseMove}
                handleMarkerMouseUp={handleMarkerMouseUp}
                handleDeleteMarker={handleDeleteMarker}
                setActiveMarkerPopup={setActiveMarkerPopup}
                setMarkerContext={setMarkerContext}
                getElevationImageUrl={getElevationImageUrl}
              />

              {/* SF Submittals */}
              <SubmittalsSection
                tabValue="sf-submittals"
                submittals={submittals}
                selectedSubmittalIds={selectedSubmittalIds}
                canManageProject={canManageProject}
                canEditCSIItems={canEditCSIItems}
                selectAllItems={selectAllItems}
                confirmBulkDelete={confirmBulkDelete}
                toggleItemSelection={toggleItemSelection}
                setShowAddSubmittalDialog={setShowAddSubmittalDialog}
                setSubmittalToDelete={setSubmittalToDelete}
                setShowDeleteSubmittalModal={setShowDeleteSubmittalModal}
                setTooltipPosition={setTooltipPosition}
                setTooltipContent={setTooltipContent}
                setTooltipVisible={setTooltipVisible}
                API={API}
              />

              {/* Warranty Tab */}
              <TabsContent value="sf-warranty">
                <WarrantySection
                  divisionType="storefront"
                  title="Storefront Warranty Management"
                  project={project}
                  projectId={projectId}
                  warrantyStartDate={project?.storefrontWarrantyStartDate}
                  warrantyDateField="storefrontWarrantyStartDate"
                  types={storefrontTypes}
                  typesTitle="Storefront Types Warranty"
                  getTypeLabel={(type) => `${type.name} - ${type.manufacturer}`}
                  getWarrantyFields={(type) => [
                    { label: 'Product Warranty', years: type.productWarrantyYears },
                    { label: 'Hardware Warranty', years: type.hardwareWarrantyYears },
                    { label: 'Finish Warranty', years: type.finishWarrantyYears },
                    { label: 'Installation Warranty', years: type.installationWarrantyYears }
                  ]}
                  glassTypes={storefrontGlassTypes}
                  glassTypesTitle="Glass Types Warranty"
                  getGlassLabel={(glass) => glass.name}
                  getGlassWarrantyYears={(glass) => glass.warrantyYears}
                  showGlassManufacturer={true}
                  showGlassType={false}
                  showGlassModel={true}
                  canEdit={canManageProject}
                  onRefresh={fetchProjectData}
                  testIdPrefix="sf"
                />
              </TabsContent>

              {/* Share Tab */}
              {/* Share Tab */}
              <ShareSection
                tabValue="sf-share"
                inputIdPrefix="sf"
                csiItem={csiItem}
                project={project}
                user={user}
                shareCompanySearch={shareCompanySearch}
                showCompanySuggestions={showCompanySuggestions}
                companySuggestions={companySuggestions}
                highlightedIndex={highlightedIndex}
                searchingCompanies={searchingCompanies}
                handleSearchCompanies={handleSearchCompanies}
                handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                setShowCompanySuggestions={setShowCompanySuggestions}
                setHighlightedIndex={setHighlightedIndex}
                handleSelectCompany={handleSelectCompany}
                handleUnshareCSI={handleUnshareCSI}
              />
            </Tabs>
          </TabsContent>
                );
              } else if (isElectricalSystems) {
                // ============================================
                // ELECTRICAL SYSTEMS (26 01 00)
                // ============================================
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <ElectricalSystemsSection
                      activeSubTab={activeSubTab}
                      onSubTabChange={(newSubTab) => {
                        setActiveSubTab(newSubTab);
                        navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
                      }}
                      projectId={projectId}
                      csiItem={csiItem}
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              } else if (isCommunicationSystems) {
                // ============================================
                // COMMUNICATION SYSTEMS (27 01 00)
                // ============================================
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <CommunicationSystemsSection
                      activeSubTab={activeSubTab}
                      onSubTabChange={(newSubTab) => {
                        setActiveSubTab(newSubTab);
                        navigate(`/project/${projectId}/${activeCsiTab}/${newSubTab}`);
                      }}
                      projectId={projectId}
                      csiItem={csiItem}
                      canEditCSIItems={canEditCSIItems}
                      canManageProject={canManageProject}
                      project={project}
                      user={user}
                      shareCompanySearch={shareCompanySearch}
                      showCompanySuggestions={showCompanySuggestions}
                      companySuggestions={companySuggestions}
                      highlightedIndex={highlightedIndex}
                      searchingCompanies={searchingCompanies}
                      handleSearchCompanies={handleSearchCompanies}
                      handleCompanySearchKeyDown={handleCompanySearchKeyDown}
                      setShowCompanySuggestions={setShowCompanySuggestions}
                      setHighlightedIndex={setHighlightedIndex}
                      handleSelectCompany={handleSelectCompany}
                      handleUnshareCSI={handleUnshareCSI}
                    />
                  </TabsContent>
                );
              } else {
                // For future CSI items, show placeholder
                return (
                  <TabsContent key={csiItem.id} value={csiItem.id} className="space-y-6">
                    <div className="text-center py-12">
                      <h2 className="text-2xl font-bold mb-4">{csiItem.itemTitle}</h2>
                      <p className="text-gray-600">Content for this specification coming soon...</p>
                    </div>
                  </TabsContent>
                );
              }
            })}
        </Tabs>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="mb-6">
                <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Specifications Added Yet</h3>
              <p className="text-gray-600 mb-6">
                This project does not have any CSI MasterFormat specifications yet. 
                Add specifications to organize your project&apos;s windows, curtain walls, and other components.
              </p>
            </div>
            
            {/* Inline Configure Section - Always visible when no CSI items for owner or same company members */}
            {(project?.createdBy === user?.id || 
              (project?.companyId && user?.companyId && project.companyId === user.companyId)) && (
              <div className="space-y-6 max-w-6xl mx-auto">
                {/* Division Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Division
                  </label>
                  <select
                    value={configDivision}
                    onChange={(e) => {
                      setConfigDivision(e.target.value);
                      loadAvailableItems(e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select a division...</option>
                    {allDivisions.map((div) => (
                      <option key={div.code} value={div.code}>
                        {div.code} - {div.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-2 gap-6">
                  {/* Available Items */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Available Items</h3>
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-[400px] max-h-[400px] overflow-y-auto">
                      {loadingAvailableItems ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-gray-500">Loading items...</div>
                        </div>
                      ) : availableItems.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-gray-500 text-center">
                            {configDivision ? 'No items found in this division' : 'Select a division to view items'}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {availableItems.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg hover:border-orange-300 transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-1.5">
                                  <span className="font-medium text-gray-900 text-sm truncate">{item.title}</span>
                                  <span className="text-xs text-gray-500 flex-shrink-0">({item.code})</span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddItem(item)}
                                className="ml-2 px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors flex-shrink-0"
                              >
                                Add
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selected Division Items */}
                  {(() => {
                    const divisionItems = configDivision
                      ? selectedItems.filter(item => item.divisionCode === configDivision)
                      : selectedItems;
                    return (
                      <div>
                        <h3 className="text-lg font-semibold mb-3">Selected Division Items ({divisionItems.length})</h3>
                        <div className="border border-gray-300 rounded-lg p-3 bg-gray-50 min-h-[400px] max-h-[400px] overflow-y-auto">
                          {divisionItems.length === 0 ? (
                            <div className="flex items-center justify-center h-full">
                              <div className="text-gray-500 text-center">
                                No items selected for this division yet.
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {divisionItems.map((item, index) => (
                                <div
                                  key={item.tempId}
                                  className="p-2 bg-white border border-gray-200 rounded-lg"
                                >
                                  <div className="flex items-start gap-2">
                                    <button
                                      onClick={() => handleRemoveItem(item.tempId)}
                                      className="p-1 rounded text-red-500 hover:bg-red-600 hover:text-white transition-colors"
                                      title="Remove item"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-baseline justify-between gap-1.5">
                                        <div className="flex items-baseline gap-1.5 min-w-0">
                                          <span className="font-medium text-gray-900 text-sm truncate">{item.itemTitle}</span>
                                          <span className="text-xs text-gray-500 flex-shrink-0">({item.itemCode})</span>
                                        </div>
                                        {item.assignedCompanyName && (
                                          <span className="text-[10px] text-blue-600 flex-shrink-0">Shared with: {item.assignedCompanyName}</span>
                                        )}
                                      </div>
                                      
                                      {/* Note - editable for all items */}
                                      {item.tempId && (
                                        <DebouncedNoteTextarea
                                          value={itemNotes[item.tempId] || ''}
                                          onChange={handleItemNoteChange}
                                          tempId={item.tempId}
                                          placeholder="Add note..."
                                          maxLength={70}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <button
                    onClick={handleSaveConfiguration}
                    disabled={selectedItems.length === 0}
                    className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Specifications
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Project Configuration Modal */}
        <ProjectConfigModal
          open={showConfigModal}
          onOpenChange={(open) => {
            if (!open && hasUnsavedConfigChanges) {
              setShowUnsavedConfigChangesModal(true);
            } else if (!open) {
              closeConfigModal();
            }
          }}
          hasUnsavedChanges={hasUnsavedConfigChanges}
          onShowUnsavedModal={() => setShowUnsavedConfigChangesModal(true)}
          onClose={closeConfigModal}
          configTab={configTab}
          onTabChange={setConfigTab}
          saving={configSaving}
          saveProgress={configSaveProgress}
          saveProgressPercent={configSaveProgressPercent}
          projectName={configProjectName}
          onProjectNameChange={setConfigProjectName}
          projectAddress={configProjectAddress}
          onProjectAddressChange={setConfigProjectAddress}
          projectImagePreview={configProjectImagePreview}
          onImageSelect={handleConfigProjectImageSelect}
          onImageRemove={removeConfigProjectImage}
          imageInputRef={configImageInputRef}
          onImagePaste={handleConfigProjectImagePaste}
          projectLatitude={configProjectLatitude}
          projectLongitude={configProjectLongitude}
          onLocationChange={(lat, lng) => { setConfigProjectLatitude(lat); setConfigProjectLongitude(lng); }}
          onLocationClear={() => { setConfigProjectLatitude(null); setConfigProjectLongitude(null); }}
          configDivision={configDivision}
          onDivisionChange={(value) => {
            setConfigDivision(value);
            loadAvailableItems(value);
          }}
          allDivisions={allDivisions}
          availableItems={availableItems}
          loadingAvailableItems={loadingAvailableItems}
          selectedItems={selectedItems}
          itemNotes={itemNotes}
          onItemNoteChange={handleItemNoteChange}
          onAddItem={handleAddItem}
          onRemoveItem={handleRemoveItem}
          onSave={handleSaveConfiguration}
        />
        
        {/* Config Project - Image Crop Modal */}
        <ImageCropModal
          open={showConfigProjectCropModal}
          onClose={handleConfigCropModalClose}
          imageSrc={configProjectImageToCrop}
          onCropComplete={handleConfigProjectCropComplete}
          aspectRatio={4 / 3}
        />
        
        {/* Config Project - PDF Page Selector Modal */}
        <PDFPageSelector
          open={showConfigProjectPDFSelector}
          onClose={() => setShowConfigProjectPDFSelector(false)}
          pdfFile={configProjectImageFile}
          onPageSelect={handleConfigProjectPDFPageSelect}
        />
        
        {/* Unsaved Config Changes Confirmation Modal */}
        <ConfirmationModal
          isOpen={showUnsavedConfigChangesModal}
          onClose={handleContinueConfigEditing}
          onConfirm={handleDiscardConfigChanges}
          title="Unsaved Changes"
          message="You have unsaved changes in your project configuration. Are you sure you want to discard them?"
          type="warning"
          confirmText="Discard Changes"
          cancelText="Continue Editing"
        />
        
        {/* Navigation Guard Confirmation Dialog */}
        <ConfirmDialog
          open={!!pendingNavPath && !showUnsavedConfigChangesModal}
          onClose={handleNavStay}
          onConfirm={handleNavLeave}
          title="Unsaved Changes"
          description="You have unsaved changes. If you leave now, your changes will be lost."
          confirmText="Leave without saving"
          cancelText="Stay"
          variant="destructive"
        />
        
        {/* Marker Dialog */}
        <Dialog open={showMarkerDialog} onOpenChange={setShowMarkerDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {markerContext === 'curtainwall' 
                  ? 'Add Type Marker' 
                  : markerContext === 'storefront'
                  ? 'Add Schedule Marker'
                  : markerContext === 'door'
                  ? 'Add Schedule Marker'
                  : markerContext === 'mirror'
                  ? 'Add Schedule Marker'
                  : markerContext === 'shower'
                  ? 'Add Schedule Marker'
                  : 'Add Schedule Marker'}
              </DialogTitle>
              <DialogDescription>
                {markerContext === 'curtainwall' 
                  ? 'Select a curtain wall type to mark on this elevation'
                  : markerContext === 'storefront'
                  ? 'Select a storefront schedule to mark on this elevation'
                  : markerContext === 'door'
                  ? 'Select a door schedule to mark on this floor plan'
                  : markerContext === 'mirror'
                  ? 'Select a mirror schedule to mark on this floor plan'
                  : markerContext === 'shower'
                  ? 'Select a shower schedule to mark on this floor plan'
                  : 'Select a schedule from the schedule list to mark on this elevation'
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                {(markerContext === 'window' || markerContext === 'storefront' || markerContext === 'door' || markerContext === 'mirror' || markerContext === 'shower') && (
                  <Label className="mb-2 block">Select Schedule</Label>
                )}
                <Select
                  value={selectedWindowForMarker}
                  onValueChange={setSelectedWindowForMarker}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      markerContext === 'curtainwall' 
                        ? "Choose a Type..." 
                        : "Choose a Schedule..."
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {markerContext === 'curtainwall' ? (
                      // Show CW Types list for Curtain Walls
                      curtainwallTypes.map((cwType) => (
                        <SelectItem key={cwType.id} value={cwType.id}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm flex-shrink-0" 
                              style={{ backgroundColor: cwType.markerColor || '#39FF14' }}
                            />
                            {cwType.name}
                          </div>
                        </SelectItem>
                      ))
                    ) : markerContext === 'storefront' ? (
                      // Show SF Schedules for Storefronts (operational records)
                      storefrontPanelList.map((panel) => {
                        const sfType = storefrontTypes.find(t => t.id === panel.storefrontTypeId);
                        return (
                          <SelectItem key={panel.id} value={panel.id} data-testid={`sf-marker-schedule-option-${panel.id}`}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: sfType?.markerColor || '#39FF14' }}
                              />
                              {panel.label}
                              <span className="text-xs text-slate-500">
                                {[sfType?.name, panel.location].filter(Boolean).join(' • ')}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })
                    ) : markerContext === 'door' ? (
                      // Show Door Schedules for Door Floor Plans
                      doorSchedules.map((door) => {
                        const doorType = doorTypes.find(dt => dt.id === door.doorTypeId);
                        const typeIndex = doorTypes.findIndex(dt => dt.id === door.doorTypeId);
                        return (
                          <SelectItem key={door.id} value={door.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: doorType?.markerColor || '#39FF14' }}
                              />
                              {door.label} (Type {typeIndex + 1})
                            </div>
                          </SelectItem>
                        );
                      })
                    ) : markerContext === 'mirror' ? (
                      // Show Mirror Schedules for Mirror Floor Plans
                      mirrorSchedules.map((mirror) => {
                        const mirrorType = mirrorTypes.find(mt => mt.id === mirror.mirrorTypeId);
                        const typeIndex = mirrorTypes.findIndex(mt => mt.id === mirror.mirrorTypeId);
                        return (
                          <SelectItem key={mirror.id} value={mirror.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: mirrorType?.markerColor || '#3B82F6' }}
                              />
                              {mirror.label} (Type {typeIndex + 1})
                            </div>
                          </SelectItem>
                        );
                      })
                    ) : markerContext === 'shower' ? (
                      // Show Shower Schedules for Shower Floor Plans
                      showerSchedules.map((shower) => {
                        const showerType = showerTypes.find(st => st.id === shower.showerTypeId);
                        const typeIndex = showerTypes.findIndex(st => st.id === shower.showerTypeId);
                        return (
                          <SelectItem key={shower.id} value={shower.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: showerType?.markerColor || '#10B981' }}
                              />
                              {shower.label} (Type {typeIndex + 1})
                            </div>
                          </SelectItem>
                        );
                      })
                    ) : (
                      // Show Window Schedules for Windows
                      windowList.map((window) => {
                        const windowType = windowTypes.find(wt => wt.id === window.windowTypeId);
                        const typeIndex = windowTypes.findIndex(wt => wt.id === window.windowTypeId);
                        return (
                          <SelectItem key={window.id} value={window.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: windowType?.markerColor || '#39FF14' }}
                              />
                              {window.label} (Type {typeIndex + 1})
                            </div>
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddMarker} 
                  className="flex-1"
                  disabled={!selectedWindowForMarker}
                >
                  Add Marker
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowMarkerDialog(false);
                    setPendingMarker(null);
                    setSelectedWindowForMarker('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Elevation Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteElevationDialog}
          onClose={() => {
            setShowDeleteElevationDialog(false);
            setElevationToDelete(null);
          }}
          onConfirm={handleDeleteElevation}
          type="danger"
          title={`Delete ${elevationToDelete ? (getEntityLabel(elevationToDelete.type).charAt(0).toUpperCase() + getEntityLabel(elevationToDelete.type).slice(1)) : 'Elevation'}`}
          message={elevationToDelete ? `Are you sure you want to delete "${elevationToDelete.elevationName || elevationToDelete.filename}"? This action cannot be undone and will delete all associated markers.` : ''}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Bulk Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showBulkDeleteDialog}
          onClose={() => {
            setShowBulkDeleteDialog(false);
            setBulkDeleteType(null);
          }}
          onConfirm={['window', 'curtainwall', 'storefront', 'doorElevation', 'mirrorElevation', 'showerElevation'].includes(bulkDeleteType) ? handleBulkDeleteElevations : handleBulkDelete}
          type="danger"
          title="Delete Multiple Items"
          message={`Are you sure you want to delete ${getSelectedCount()} items? This action cannot be undone.`}
          confirmText="Delete All"
          cancelText="Cancel"
        />

        {/* Delete Window Type Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteWindowTypeDialog}
          onClose={() => {
            setShowDeleteWindowTypeDialog(false);
            setWindowTypeToDelete(null);
          }}
          onConfirm={handleDeleteWindowType}
          type="danger"
          title="Delete Window Type"
          message={windowTypeToDelete ? `Are you sure you want to delete Window Type ${windowTypes.findIndex(t => t.id === windowTypeToDelete.id) + 1}? This action cannot be undone. Note: Window types in use cannot be deleted.` : ''}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Delete Window Glass Type Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteGlassTypeDialog}
          onClose={() => {
            setShowDeleteGlassTypeDialog(false);
            setGlassTypeToDelete(null);
          }}
          onConfirm={handleDeleteWindowGlassType}
          type="danger"
          title="Delete Glass Type"
          message={glassTypeToDelete ? `Are you sure you want to delete glass type "${glassTypeToDelete.name}"? This action cannot be undone.` : ''}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Delete Window Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteWindowDialog}
          onClose={() => {
            setShowDeleteWindowDialog(false);
            setWindowToDelete(null);
          }}
          onConfirm={handleDeleteWindow}
          type="danger"
          title="Delete Window"
          message={windowToDelete ? `Are you sure you want to delete window "${windowToDelete.label}"? This action cannot be undone and will delete all associated markers.` : ''}
          confirmText="Delete"
          cancelText="Cancel"
        />

        {/* Delete All Windows Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteAllWindowsDialog}
          onClose={() => setShowDeleteAllWindowsDialog(false)}
          onConfirm={handleDeleteAllWindows}
          type="danger"
          title="Delete All Windows"
          message={`Are you sure you want to delete all ${windowList.length} windows? This action cannot be undone and will delete all associated markers.`}
          confirmText="Delete All"
          cancelText="Cancel"
        />

        {/* Excel Import Preview Dialog */}
        <Dialog open={showExcelUploadDialog} onOpenChange={setShowExcelUploadDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Windows from Excel</DialogTitle>
              <DialogDescription>
                Review and match window types for each row. {excelPreview?.rows.filter(r => r.isDuplicate).length > 0 && <span className="text-red-600 font-medium">Some labels already exist.</span>}
              </DialogDescription>
            </DialogHeader>
            
            {excelPreview && (
              <div className="mt-4">
                {excelPreview.columnMapping && (
                  <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                    <p className="font-semibold mb-2">AI Detected Column Mapping:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• <strong>Labels:</strong> {excelPreview.columnMapping.labelColumn || 'Not found'}</li>
                      <li>• <strong>Location:</strong> {excelPreview.columnMapping.locationColumn || 'Not found'}</li>
                      <li>• <strong>Notes:</strong> {excelPreview.columnMapping.notesColumn || 'Not found'}</li>
                    </ul>
                  </div>
                )}
                <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                  <p><strong>Total Rows:</strong> {excelPreview.totalRows}</p>
                  <p><strong>Auto-matched:</strong> {excelPreview.rows.filter(r => r.status === 'matched').length}</p>
                  <p><strong>Need Matching:</strong> {excelPreview.rows.filter(r => r.status === 'unmapped').length}</p>
                  <p><strong>Database Duplicates:</strong> {excelPreview.rows.filter(r => r.isDuplicate).length} (will skip if &quot;Skip Duplicates&quot; selected)</p>
                  <p><strong>Excel Duplicates:</strong> {excelPreview.rows.filter(r => r.isDuplicateInExcel).length} (duplicate labels within your Excel file)</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Dimensions</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Window Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {excelPreview.rows.map((row, idx) => (
                        <tr key={idx} className={row.isDuplicate ? 'bg-red-50' : (row.isDuplicateInExcel ? 'bg-orange-50' : '')}>
                          <td className="px-4 py-3 text-sm">{row.rowIndex + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {row.windowWidth_in && row.windowHeight_in 
                              ? `${row.windowWidth_in}&quot; × ${row.windowHeight_in}&quot;`
                              : (row.windowWidth_in || row.windowHeight_in ? 'Partial' : '-')}
                            {row.roWidth_in && row.roHeight_in && (
                              <div className="text-xs text-slate-500">RO: {row.roWidth_in}&quot; × {row.roHeight_in}&quot;</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">{row.location}</td>
                          <td className="px-4 py-3 text-sm">{row.notes}</td>
                          <td className="px-4 py-3 text-sm">
                            <Select
                              value={row.windowTypeId || row.matchedWindowTypeId || ''}
                              onValueChange={(value) => handleUpdatePreviewRow(idx, 'windowTypeId', value)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {excelPreview.windowTypes.map((type, typeIdx) => (
                                  <SelectItem key={type.id} value={type.id}>
                                    Type {typeIdx + 1} - {type.manufacturer}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.isDuplicate && <span className="text-red-600 font-medium">Duplicate (DB)</span>}
                            {!row.isDuplicate && row.isDuplicateInExcel && <span className="text-orange-600 font-medium">Duplicate (Excel)</span>}
                            {!row.isDuplicate && !row.isDuplicateInExcel && row.matchedWindowTypeId && <span className="text-green-600">Matched</span>}
                            {!row.isDuplicate && !row.isDuplicateInExcel && !row.matchedWindowTypeId && <span className="text-yellow-600">Unmapped</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button 
                    onClick={() => handleBulkImport(true)}
                    className="flex-1"
                  >
                    Import (Skip Duplicates)
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handleBulkImport(false)}
                    className="flex-1"
                  >
                    Import All
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowExcelUploadDialog(false);
                      setExcelPreview(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Curtainwall Panel Detail Dialog (dimensions + lites) */}
        <CurtainwallPanelDetailDialog
          open={showCWPanelDetailDialog}
          onOpenChange={setShowCWPanelDetailDialog}
          panel={selectedCWPanelDetail}
          onSave={handleSaveCWPanelDetail}
          cwGlassTypes={glassTypes}
          cwMetalTypes={metalPanelTypes}
        />

        {/* Door Excel Import Dialog */}
        <Dialog open={showDoorExcelDialog} onOpenChange={setShowDoorExcelDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Doors from Excel</DialogTitle>
              <DialogDescription>
                Review and match door types for each row. {doorExcelPreview?.rows.filter(r => r.isDuplicate).length > 0 && <span className="text-red-600 font-medium">Some labels already exist.</span>}
              </DialogDescription>
            </DialogHeader>
            {doorExcelPreview && (
              <div className="mt-4">
                {doorExcelPreview.columnMapping && (
                  <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                    <p className="font-semibold mb-2">AI Detected Column Mapping:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• <strong>Labels:</strong> {doorExcelPreview.columnMapping.labelColumn || 'Not found'}</li>
                      <li>• <strong>Location:</strong> {doorExcelPreview.columnMapping.locationColumn || 'Not found'}</li>
                    </ul>
                  </div>
                )}
                <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                  <p><strong>Total Rows:</strong> {doorExcelPreview.totalRows}</p>
                  <p><strong>Database Duplicates:</strong> {doorExcelPreview.rows.filter(r => r.isDuplicate).length}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {doorExcelPreview.rows.map((row, idx) => (
                        <tr key={idx} className={row.isDuplicate ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 text-sm">{row.rowIndex}</td>
                          <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                          <td className="px-4 py-3 text-sm">{row.location || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <Select
                              value={row.doorTypeId || ''}
                              onValueChange={(value) => {
                                setDoorExcelPreview(prev => {
                                  const rows = [...prev.rows];
                                  rows[idx] = { ...rows[idx], doorTypeId: value };
                                  return { ...prev, rows };
                                });
                              }}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {doorTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.isDuplicate ? <span className="text-red-600 font-medium">Duplicate</span> : <span className="text-green-600">Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => handleDoorBulkImport(true)} className="flex-1">Import (Skip Duplicates)</Button>
                  <Button variant="outline" onClick={() => handleDoorBulkImport(false)} className="flex-1">Import All</Button>
                  <Button variant="outline" onClick={() => { setShowDoorExcelDialog(false); setDoorExcelPreview(null); }}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Shower Excel Import Dialog */}
        <Dialog open={showShowerExcelDialog} onOpenChange={setShowShowerExcelDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Showers from Excel</DialogTitle>
              <DialogDescription>
                Review and match shower types for each row. {showerExcelPreview?.rows.filter(r => r.isDuplicate).length > 0 && <span className="text-red-600 font-medium">Some labels already exist.</span>}
              </DialogDescription>
            </DialogHeader>
            {showerExcelPreview && (
              <div className="mt-4">
                {showerExcelPreview.columnMapping && (
                  <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                    <p className="font-semibold mb-2">AI Detected Column Mapping:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• <strong>Labels:</strong> {showerExcelPreview.columnMapping.labelColumn || 'Not found'}</li>
                      <li>• <strong>Location:</strong> {showerExcelPreview.columnMapping.locationColumn || 'Not found'}</li>
                    </ul>
                  </div>
                )}
                <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                  <p><strong>Total Rows:</strong> {showerExcelPreview.totalRows}</p>
                  <p><strong>Database Duplicates:</strong> {showerExcelPreview.rows.filter(r => r.isDuplicate).length}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Shower Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {showerExcelPreview.rows.map((row, idx) => (
                        <tr key={idx} className={row.isDuplicate ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 text-sm">{row.rowIndex}</td>
                          <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                          <td className="px-4 py-3 text-sm">{row.location || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <Select
                              value={row.showerTypeId || ''}
                              onValueChange={(value) => {
                                setShowerExcelPreview(prev => {
                                  const rows = [...prev.rows];
                                  rows[idx] = { ...rows[idx], showerTypeId: value };
                                  return { ...prev, rows };
                                });
                              }}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {showerTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.isDuplicate ? <span className="text-red-600 font-medium">Duplicate</span> : <span className="text-green-600">Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => handleShowerBulkImport(true)} className="flex-1">Import (Skip Duplicates)</Button>
                  <Button variant="outline" onClick={() => handleShowerBulkImport(false)} className="flex-1">Import All</Button>
                  <Button variant="outline" onClick={() => { setShowShowerExcelDialog(false); setShowerExcelPreview(null); }}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Mirror Excel Import Dialog */}
        <Dialog open={showMirrorExcelDialog} onOpenChange={setShowMirrorExcelDialog}>
          <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Mirrors from Excel</DialogTitle>
              <DialogDescription>
                Review and match mirror types for each row. {mirrorExcelPreview?.rows.filter(r => r.isDuplicate).length > 0 && <span className="text-red-600 font-medium">Some labels already exist.</span>}
              </DialogDescription>
            </DialogHeader>
            {mirrorExcelPreview && (
              <div className="mt-4">
                {mirrorExcelPreview.columnMapping && (
                  <div className="mb-4 p-3 bg-blue-50 rounded text-sm">
                    <p className="font-semibold mb-2">AI Detected Column Mapping:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• <strong>Labels:</strong> {mirrorExcelPreview.columnMapping.labelColumn || 'Not found'}</li>
                      <li>• <strong>Location:</strong> {mirrorExcelPreview.columnMapping.locationColumn || 'Not found'}</li>
                    </ul>
                  </div>
                )}
                <div className="mb-4 p-3 bg-slate-50 rounded text-sm">
                  <p><strong>Total Rows:</strong> {mirrorExcelPreview.totalRows}</p>
                  <p><strong>Database Duplicates:</strong> {mirrorExcelPreview.rows.filter(r => r.isDuplicate).length}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Label</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Mirror Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {mirrorExcelPreview.rows.map((row, idx) => (
                        <tr key={idx} className={row.isDuplicate ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 text-sm">{row.rowIndex}</td>
                          <td className="px-4 py-3 text-sm font-medium">{row.label}</td>
                          <td className="px-4 py-3 text-sm">{row.location || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <Select
                              value={row.mirrorTypeId || ''}
                              onValueChange={(value) => {
                                setMirrorExcelPreview(prev => {
                                  const rows = [...prev.rows];
                                  rows[idx] = { ...rows[idx], mirrorTypeId: value };
                                  return { ...prev, rows };
                                });
                              }}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue placeholder="Select type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {mirrorTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {row.isDuplicate ? <span className="text-red-600 font-medium">Duplicate</span> : <span className="text-green-600">Ready</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => handleMirrorBulkImport(true)} className="flex-1">Import (Skip Duplicates)</Button>
                  <Button variant="outline" onClick={() => handleMirrorBulkImport(false)} className="flex-1">Import All</Button>
                  <Button variant="outline" onClick={() => { setShowMirrorExcelDialog(false); setMirrorExcelPreview(null); }}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <DoorDetailDialog
          open={showDoorDetailDialog}
          onOpenChange={setShowDoorDetailDialog}
          door={selectedDoorDetail}
          onSave={handleSaveDoorDetail}
          doorGlassTypes={doorGlassTypes.filter(gt => gt.csiItemId === activeCsiTab)}
          doorMetalPanelTypes={doorMetalPanelTypes.filter(mp => mp.csiItemId === activeCsiTab)}
        />

        {/* Mirror Detail Dialog */}
        <MirrorDetailDialog
          open={showMirrorScheduleDetailModal}
          onOpenChange={setShowMirrorScheduleDetailModal}
          mirror={selectedMirrorScheduleForDetail}
          onSave={handleSaveMirrorDetail}
          onUploadPicture={handleUploadMirrorSchedulePicture}
          onDeletePicture={handleDeleteMirrorSchedulePicture}
          uploading={mirrorSchedulePictureUploading}
        />

        {/* Print Preview Dialog */}
        <Dialog open={showPrintPreviewDialog} onOpenChange={(open) => !open && handleClosePrintPreview()}>
          <DialogContent
            className="max-w-5xl w-[95vw] h-[90vh] max-h-[90vh] flex flex-col gap-0 overflow-hidden p-4 sm:p-6"
            data-testid="print-preview-dialog"
          >
            <DialogHeader className="shrink-0 pr-8">
              <DialogTitle>
                {printPreviewType === 'panel' || printPreviewType === 'cw-panel' || printPreviewType === 'cw-panel-qr' 
                  ? 'Curtainwall Card' 
                  : printPreviewType === 'sf-panel' || printPreviewType === 'sf-panel-qr'
                  ? 'Storefront Card'
                  : printPreviewType === 'door'
                  ? 'Door Card'
                  : 'Window Card'}
              </DialogTitle>
              <DialogDescription>
                Preview the printable spec sheet with QR codes
              </DialogDescription>
            </DialogHeader>
            
            {printPreviewUrl && (
              <div className="flex-1 min-h-0 flex flex-col mt-4">
                <object
                  data={printPreviewUrl}
                  type="application/pdf"
                  className="flex-1 min-h-0 w-full border rounded overflow-auto"
                  title="Label Preview"
                  data-testid="print-preview-pdf"
                >
                  <p className="p-4 text-center text-gray-500">
                    PDF preview not available. Please use the download button below.
                  </p>
                </object>
                <div
                  className="shrink-0 flex flex-col sm:flex-row gap-2 pt-4"
                  data-testid="print-preview-footer"
                >
                  <Button
                    onClick={handleDownloadFromPreview}
                    className="h-10 w-full sm:flex-1"
                    data-testid="print-preview-download-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClosePrintPreview}
                    className="h-10 w-full sm:w-auto"
                    data-testid="print-preview-close-btn"
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Curtainwall Type Drawing Marker Selection Dialog */}
        <Dialog open={showCWDrawingMarkerDialog} onOpenChange={setShowCWDrawingMarkerDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Schedule Marker</DialogTitle>
              <DialogDescription>
                Select a schedule to mark on {activeCWTypeForMarker?.name} drawing
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Select
                  value={selectedPanelForMarker}
                  onValueChange={setSelectedPanelForMarker}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a Schedule..." />
                  </SelectTrigger>
                  <SelectContent>
                    {panelList.length > 0 ? (
                      panelList.map((panel) => {
                        const cwType = curtainwallTypes.find(cw => cw.id === panel.curtainwallTypeId);
                        return (
                          <SelectItem key={panel.id} value={panel.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: cwType?.markerColor || '#39FF14' }}
                              />
                              {panel.label}
                            </div>
                          </SelectItem>
                        );
                      })
                    ) : (
                      <div className="p-2 text-sm text-gray-500">
                        No schedules available
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleAddCWDrawingMarker} 
                  className="flex-1"
                  disabled={!selectedPanelForMarker}
                >
                  Add Marker
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowCWDrawingMarkerDialog(false);
                    setPendingCWMarker(null);
                    setSelectedPanelForMarker('');
                    setActiveCWTypeForMarker(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>


        {/* Window Type Drawing Fullscreen Dialog */}
        <Dialog open={!!viewingDrawing} onOpenChange={(open) => !open && setViewingDrawing(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{viewingDrawing?.title || 'Drawing'}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4">
              {viewingDrawing?.url && (
                <img
                  src={viewingDrawing.url}
                  alt={viewingDrawing?.title || 'Drawing'}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Curtainwall Type Drawing Dialog */}
        <Dialog open={showCWDrawingDialog} onOpenChange={(open) => {
          setShowCWDrawingDialog(open);
          if (!open) {
            setCwDrawingZoom(1);
            setCwDrawingPan({ x: 0, y: 0 });
            // Reset bulk marker state when closing
            setCwDrawingBulkMode(false);
            setCwDrawingBulkSchedule('');
            setPendingCwDrawingMarkers([]);
          }
        }}>
          <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedCWTypeForView?.name || 'Curtainwall Type'} Drawing</span>
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 mr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCwZoomOut}
                    disabled={cwDrawingZoom <= 0.5}
                    title="Zoom Out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium w-12 text-center">
                    {Math.round(cwDrawingZoom * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCwZoomIn}
                    disabled={cwDrawingZoom >= 3}
                    title="Zoom In"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCwResetZoom}
                    disabled={isZoomAtDefault(cwDrawingZoom)}
                    title="Reset zoom to 100%"
                    aria-label="Reset zoom to 100%"
                    data-testid="cw-drawing-reset-zoom-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </DialogTitle>
              <DialogDescription>
                {cwDrawingBulkMode 
                  ? 'Click on the image to add multiple markers. Press Done when finished.'
                  : `Click and drag to pan when zoomed in. ${selectedCWTypeForView?.drawingMarkers?.length || 0} schedule marker${(selectedCWTypeForView?.drawingMarkers?.length || 0) !== 1 ? 's' : ''}`
                }
              </DialogDescription>
            </DialogHeader>
            
            {/* Bulk Marker Controls */}
            {canEditCSIItems && (
              <div className="flex items-center gap-2 py-2 border-b">
                {!cwDrawingBulkMode ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCwDrawingBulkMode(true)}
                    disabled={panelList.length === 0}
                    title="Add Multiple Schedule Markers"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Multiple Schedule Markers
                  </Button>
                ) : (
                  <>
                    <Select
                      value={cwDrawingBulkSchedule}
                      onValueChange={setCwDrawingBulkSchedule}
                      disabled={savingCwDrawingMarkers}
                    >
                      <SelectTrigger className="w-[220px]">
                        <SelectValue placeholder="Select Schedule..." />
                      </SelectTrigger>
                      <SelectContent>
                        {panelList.map((panel) => (
                          <SelectItem key={panel.id} value={panel.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-sm flex-shrink-0" 
                                style={{ backgroundColor: curtainwallTypes.find(cw => cw.id === panel.curtainwallTypeId)?.markerColor || '#39FF14' }}
                              />
                              {panel.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleSaveCwDrawingBulkMarkers}
                      disabled={pendingCwDrawingMarkers.length === 0 || savingCwDrawingMarkers}
                    >
                      {savingCwDrawingMarkers ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>Done ({pendingCwDrawingMarkers.length})</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelCwDrawingBulkMarkers}
                      disabled={savingCwDrawingMarkers}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            )}
            
            {selectedCWTypeForView && (
              <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                {/* Drawing Image with Schedule Markers - supports both Cloudinary and legacy */}
                {(selectedCWTypeForView.drawingCloudinary?.urls?.large || selectedCWTypeForView.drawingUrl) ? (
                  <div 
                    className="border rounded-lg overflow-hidden bg-gray-50"
                    style={{
                      cursor: cwDrawingZoom > 1 ? (isCwPanning ? 'grabbing' : 'grab') : 'default'
                    }}
                    onMouseDown={(e) => {
                      if (cwDrawingZoom > 1) {
                        handleCwPanStart(e);
                      }
                    }}
                  >
                    <div 
                      className="relative inline-block w-full transition-transform"
                      style={{
                        transform: `scale(${cwDrawingZoom}) translate(${cwDrawingPan.x}px, ${cwDrawingPan.y}px)`,
                        transformOrigin: 'center center'
                      }}
                      onMouseMove={(e) => {
                        handleCwPanMove(e);
                        handleCWMarkerMouseMove(e, selectedCWTypeForView);
                      }}
                      onMouseUp={(e) => {
                        handleCwPanEnd();
                        handleMarkerDragEnd();
                      }}
                      onMouseLeave={(e) => {
                        handleCwPanEnd();
                        handleMarkerDragEnd();
                      }}
                    >
                      <img 
                        src={selectedCWTypeForView.drawingCloudinary?.urls?.large || `${API}${selectedCWTypeForView.drawingUrl}?t=${selectedCWTypeForView.updatedAt || selectedCWTypeForView.createdAt || 'default'}`}
                        alt={`${selectedCWTypeForView.name} Drawing`}
                        className="w-full h-auto max-h-[65vh] object-contain"
                        draggable={false}
                        onClick={(e) => {
                          // Don't open marker dialog if we were panning
                          if (wasCwPanningRef.current || isCwPanning) return;
                          
                          if (canEditCSIItems) {
                            if (cwDrawingBulkMode && cwDrawingBulkSchedule) {
                              // Bulk marker mode - adjust for zoom and pan
                              const rect = e.currentTarget.getBoundingClientRect();
                              const clickX = e.clientX - rect.left;
                              const clickY = e.clientY - rect.top;
                              // Adjust for zoom and pan
                              const adjustedX = (clickX / cwDrawingZoom - cwDrawingPan.x) / (rect.width / cwDrawingZoom) * 100;
                              const adjustedY = (clickY / cwDrawingZoom - cwDrawingPan.y) / (rect.height / cwDrawingZoom) * 100;
                              // Clamp values to 0-100
                              const x = Math.max(0, Math.min(100, adjustedX));
                              const y = Math.max(0, Math.min(100, adjustedY));
                              setPendingCWMarker({ x, y });
                              setActiveCWTypeForMarker(selectedCWTypeForView);
                              if (cwBulkMarkerType) {
                                handleCwDrawingBulkClick(e, selectedCWTypeForView);
                              }
                            } else if (!cwDrawingBulkMode) {
                              // Normal single marker mode - adjust for zoom and pan
                              const rect = e.currentTarget.getBoundingClientRect();
                              const clickX = e.clientX - rect.left;
                              const clickY = e.clientY - rect.top;
                              // Adjust for zoom and pan
                              const adjustedX = (clickX / cwDrawingZoom - cwDrawingPan.x) / (rect.width / cwDrawingZoom) * 100;
                              const adjustedY = (clickY / cwDrawingZoom - cwDrawingPan.y) / (rect.height / cwDrawingZoom) * 100;
                              // Clamp values to 0-100
                              const x = Math.max(0, Math.min(100, adjustedX));
                              const y = Math.max(0, Math.min(100, adjustedY));
                              setPendingCWMarker({ x, y });
                              setActiveCWTypeForMarker(selectedCWTypeForView);
                              setShowCWDrawingMarkerDialog(true);
                            }
                          }
                        }}
                        style={{ cursor: canEditCSIItems ? 'crosshair' : undefined, pointerEvents: 'auto' }}
                      />
                      
                      {/* Markers Container - absolute positioned over the image */}
                      <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
                      
                      {/* Display Panel Markers with Eye Buttons */}
                      {selectedCWTypeForView.drawingMarkers && selectedCWTypeForView.drawingMarkers.map((marker) => {
                        const isDragging = draggingMarker && draggingMarker.id === marker.id;
                        const currentMarker = isDragging ? draggingMarker : marker;
                        const viewMarkerColor = selectedCWTypeForView.markerColor || '#39FF14';
                        
                        return (
                          <div
                            key={marker.id}
                            className="absolute"
                            style={{
                              left: `${currentMarker.x}%`,
                              top: `${currentMarker.y}%`,
                              transform: 'translate(-50%, -50%)',
                              zIndex: isDragging ? 50 : (activeMarkerPopup === marker.id ? 40 : 20),
                              padding: '15px',
                              cursor: canEditCSIItems ? 'move' : 'pointer',
                              pointerEvents: 'auto'
                            }}
                            onMouseDown={(e) => {
                              // Only start drag if not clicking on popup
                              if (canEditCSIItems && !e.target.closest('.marker-popup')) {
                                handleCWMarkerMouseDown(e, marker, selectedCWTypeForView.id);
                              }
                            }}
                          >
                            {/* Visible marker box - Click to toggle popup */}
                            <div 
                              style={{
                                width: '14px',
                                height: '14px',
                                border: `2px solid ${viewMarkerColor}`,
                                backgroundColor: activeMarkerPopup === marker.id ? viewMarkerColor : `${viewMarkerColor}1A`,
                                boxShadow: `0 0 8px ${viewMarkerColor}99`,
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = viewMarkerColor}
                              onMouseLeave={(e) => {
                                if (activeMarkerPopup !== marker.id) {
                                  e.currentTarget.style.backgroundColor = `${viewMarkerColor}1A`;
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setActiveMarkerPopup(activeMarkerPopup === marker.id ? null : marker.id);
                              }}
                            />
                            {/* Click-based Popup Controls */}
                            {activeMarkerPopup === marker.id && (
                              <MarkerActionsPopup zoom={cwDrawingZoom}>
                                {/* Eye icon */}
                                <button
                                  onClick={(e) => {
                                    setActiveMarkerPopup(null);
                                    handleMarkerEyeClick(e, marker.panelId, 'curtainwall');
                                  }}
                                  className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors flex items-center justify-center"
                                  title="Detayları Görüntüle"
                                  style={{ minWidth: '24px', minHeight: '24px' }}
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {/* Delete button */}
                                {canEditCSIItems && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveMarkerPopup(null);
                                      handleDeleteCWDrawingMarker(selectedCWTypeForView.id, marker.id);
                                    }}
                                    className="p-1 bg-red-500 hover:bg-red-600 text-white rounded transition-colors flex items-center justify-center"
                                    title="Marker'ı Sil"
                                    style={{ minWidth: '24px', minHeight: '24px' }}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                                {/* Close Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMarkerPopup(null);
                                  }}
                                  className="p-1 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors flex items-center justify-center"
                                  title="Kapat"
                                  style={{ minWidth: '24px', minHeight: '24px' }}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              </MarkerActionsPopup>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Pending Bulk Markers for CW Drawing */}
                      {pendingCwDrawingMarkers.map((marker) => (
                        <div
                          key={marker.id}
                          className="absolute group z-20"
                          style={{
                            left: `${marker.x}%`,
                            top: `${marker.y}%`,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'auto'
                          }}
                        >
                          <div 
                            style={{
                              width: '10px',
                              height: '10px',
                              border: `2px dashed ${marker.color}`,
                              backgroundColor: `${marker.color}33`,
                              boxShadow: `0 0 8px ${marker.color}99`
                            }}
                          />
                          {/* Remove button on hover */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemovePendingCwDrawingMarker(marker.id);
                            }}
                            className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs hidden group-hover:flex items-center justify-center"
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-12 text-center bg-gray-50">
                    <p className="text-gray-500">No drawing available for this curtainwall type</p>
                  </div>
                )}

                {/* CW Type Details */}
                <div className="border rounded-lg p-4 bg-white space-y-2">
                  <h3 className="font-semibold text-lg">{selectedCWTypeForView.name}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="font-medium">Manufacturer:</span> {selectedCWTypeForView.manufacturer || 'N/A'}</div>
                    <div><span className="font-medium">Model:</span> {selectedCWTypeForView.model || 'N/A'}</div>
                    <div><span className="font-medium">Material:</span> {selectedCWTypeForView.material || 'N/A'}</div>
                    <div><span className="font-medium">Finish Type:</span> {selectedCWTypeForView.finishType || 'N/A'}</div>
                    <div><span className="font-medium">Interior Color:</span> {selectedCWTypeForView.interiorFinishColor || 'N/A'}</div>
                    <div><span className="font-medium">Exterior Color:</span> {selectedCWTypeForView.exteriorFinishColor || 'N/A'}</div>
                  </div>
                  
                  {/* Show marker count */}
                  {selectedCWTypeForView.drawingMarkers && selectedCWTypeForView.drawingMarkers.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      {selectedCWTypeForView.drawingMarkers.length} schedule marker{selectedCWTypeForView.drawingMarkers.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Close button - always visible at bottom */}
            <div className="flex gap-2 pt-4 border-t flex-shrink-0">
              <Button 
                variant="outline" 
                onClick={() => setShowCWDrawingDialog(false)}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Storefront Type Drawing Dialog */}
        <Dialog open={showSFDrawingDialog} onOpenChange={(open) => {
          setShowSFDrawingDialog(open);
          if (!open) {
            setSfDrawingZoom(1);
            setSfDrawingPan({ x: 0, y: 0 });
          }
        }}>
          <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedSFTypeForView?.name || 'Storefront Type'} Drawing</span>
                {/* Zoom Controls */}
                <div className="flex items-center gap-2 mr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSfZoomOut}
                    disabled={sfDrawingZoom <= 0.5}
                    title="Zoom Out"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium w-12 text-center">
                    {Math.round(sfDrawingZoom * 100)}%
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSfZoomIn}
                    disabled={sfDrawingZoom >= 3}
                    title="Zoom In"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSfResetZoom}
                    disabled={isZoomAtDefault(sfDrawingZoom)}
                    title="Reset zoom to 100%"
                    aria-label="Reset zoom to 100%"
                    data-testid="sf-drawing-reset-zoom-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </DialogTitle>
              <DialogDescription>
                Click and drag to pan when zoomed in.
              </DialogDescription>
            </DialogHeader>
            
            {selectedSFTypeForView && (
              <div className="flex-1 overflow-y-auto mt-4 space-y-4">
                {/* Drawing Image (view only) - supports both Cloudinary and legacy */}
                {(selectedSFTypeForView.drawingCloudinary?.urls?.large || selectedSFTypeForView.drawingUrl) ? (
                  <div 
                    className="border rounded-lg overflow-hidden bg-gray-50"
                    style={{
                      cursor: sfDrawingZoom > 1 ? (isSfPanning ? 'grabbing' : 'grab') : 'default'
                    }}
                    onMouseDown={(e) => {
                      if (sfDrawingZoom > 1) {
                        handleSfPanStart(e);
                      }
                    }}
                  >
                    <div 
                      className="relative inline-block w-full transition-transform"
                      style={{
                        transform: `scale(${sfDrawingZoom}) translate(${sfDrawingPan.x}px, ${sfDrawingPan.y}px)`,
                        transformOrigin: 'center center'
                      }}
                      onMouseMove={handleSfPanMove}
                      onMouseUp={handleSfPanEnd}
                      onMouseLeave={handleSfPanEnd}
                    >
                      <img 
                        src={selectedSFTypeForView.drawingCloudinary?.urls?.large || `${API}${selectedSFTypeForView.drawingUrl}?t=${selectedSFTypeForView.updatedAt || selectedSFTypeForView.createdAt || 'default'}`}
                        alt={`${selectedSFTypeForView.name} Drawing`}
                        className="w-full h-auto max-h-[65vh] object-contain"
                        draggable={false}
                      />
                      
                    </div>
                  </div>
                ) : (
                  <div className="border rounded-lg p-12 text-center bg-gray-50">
                    <p className="text-gray-500">No drawing available for this storefront type</p>
                  </div>
                )}

                {/* SF Type Details */}
                <div className="border rounded-lg p-4 bg-white space-y-2">
                  <h3 className="font-semibold text-lg">{selectedSFTypeForView.name}</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="font-medium">Manufacturer:</span> {selectedSFTypeForView.manufacturer || 'N/A'}</div>
                    <div><span className="font-medium">Model:</span> {selectedSFTypeForView.model || 'N/A'}</div>
                    <div><span className="font-medium">Material:</span> {selectedSFTypeForView.material || 'N/A'}</div>
                    <div><span className="font-medium">Finish Type:</span> {selectedSFTypeForView.finishType || 'N/A'}</div>
                    <div><span className="font-medium">Interior Color:</span> {selectedSFTypeForView.interiorFinishColor || 'N/A'}</div>
                    <div><span className="font-medium">Exterior Color:</span> {selectedSFTypeForView.exteriorFinishColor || 'N/A'}</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Close button - always visible at bottom */}
            <div className="flex gap-2 pt-4 border-t flex-shrink-0">
              <Button 
                variant="outline" 
                onClick={() => setShowSFDrawingDialog(false)}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
      
      {/* Unshare CSI Item Modal */}
      <ConfirmationModal
        isOpen={showUnshareModal}
        onClose={() => setShowUnshareModal(false)}
        onConfirm={confirmUnshare}
        type="warning"
        title="Unshare Specification"
        message={unshareItem ? `Are you sure you want to unshare "${unshareItem.itemTitle}" with "${unshareItem.assignedCompanyName}"? This company will no longer have access to this specification.` : ''}
        confirmText="Unshare"
        cancelText="Cancel"
      />
      
      {/* Delete Curtainwall Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteCWTypeModal}
        onClose={() => setShowDeleteCWTypeModal(false)}
        onConfirm={confirmDeleteCWType}
        type="danger"
        title="Delete Curtainwall Type"
        message="Are you sure you want to delete this curtainwall type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Glass Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteGlassModal}
        onClose={() => setShowDeleteGlassModal(false)}
        onConfirm={confirmDeleteGlassType}
        type="danger"
        title="Delete Glass Type"
        message="Are you sure you want to delete this glass type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Metal Panel Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteMetalModal}
        onClose={() => setShowDeleteMetalModal(false)}
        onConfirm={confirmDeleteMetalType}
        type="danger"
        title="Delete Metal Panel Type"
        message="Are you sure you want to delete this metal panel type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Panel Modal */}
      <ConfirmationModal
        isOpen={showDeletePanelModal}
        onClose={() => setShowDeletePanelModal(false)}
        onConfirm={confirmDeletePanel}
        type="danger"
        title="Delete Panel"
        message="Are you sure you want to delete this panel? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete All Panels Modal */}
      <ConfirmationModal
        isOpen={showDeleteAllPanelsModal}
        onClose={() => setShowDeleteAllPanelsModal(false)}
        onConfirm={confirmDeleteAllPanels}
        type="danger"
        title="Delete All Panels"
        message={`Are you sure you want to delete all ${panelList.length} panels? This action cannot be undone and will permanently delete all panel data.`}
        confirmText="Delete All"
        cancelText="Cancel"
      />
      
      {/* Delete Storefront Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteSFTypeModal}
        onClose={() => setShowDeleteSFTypeModal(false)}
        onConfirm={confirmDeleteSFType}
        type="danger"
        title="Delete Storefront Type"
        message="Are you sure you want to delete this storefront type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Storefront Glass Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteSFGlassModal}
        onClose={() => setShowDeleteSFGlassModal(false)}
        onConfirm={confirmDeleteSFGlass}
        type="danger"
        title="Delete Glass Type"
        message="Are you sure you want to delete this glass type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Storefront Metal Panel Type Modal */}
      <ConfirmationModal
        isOpen={showDeleteSFMetalModal}
        onClose={() => setShowDeleteSFMetalModal(false)}
        onConfirm={confirmDeleteSFMetal}
        type="danger"
        title="Delete Metal Panel Type"
        message="Are you sure you want to delete this metal panel type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Storefront Panel Modal */}
      <ConfirmationModal
        isOpen={showDeleteSFPanelModal}
        onClose={() => setShowDeleteSFPanelModal(false)}
        onConfirm={confirmDeleteSFPanel}
        type="danger"
        title="Delete Panel"
        message="Are you sure you want to delete this panel? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Picture Modal */}
      <ConfirmationModal
        isOpen={showDeletePictureModal}
        onClose={() => setShowDeletePictureModal(false)}
        onConfirm={confirmDeletePicture}
        type="danger"
        title="Delete Picture"
        message="Are you sure you want to delete this window type picture? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Drawing Modal */}
      <ConfirmationModal
        isOpen={showDeleteDrawingModal}
        onClose={() => setShowDeleteDrawingModal(false)}
        onConfirm={confirmDeleteDrawing}
        type="danger"
        title="Delete Drawing"
        message="Are you sure you want to delete this curtainwall drawing? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete SF Drawing Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteSFDrawingModal}
        onClose={() => {
          setShowDeleteSFDrawingModal(false);
          setDeleteSFDrawingId(null);
        }}
        onConfirm={confirmDeleteSFDrawing}
        type="danger"
        title="Delete Drawing"
        message="Are you sure you want to delete this storefront drawing? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Door Type Picture Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorTypePictureModal}
        onClose={() => {
          setShowDeleteDoorTypePictureModal(false);
          setDeleteDoorTypePictureId(null);
        }}
        onConfirm={confirmDeleteDoorTypePicture}
        type="danger"
        title="Delete Drawing"
        message="Are you sure you want to delete this door type drawing? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Door Type Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorTypeModal}
        onClose={() => {
          setShowDeleteDoorTypeModal(false);
          setDeleteDoorTypeId(null);
        }}
        onConfirm={confirmDeleteDoorType}
        type="danger"
        title="Delete Door Type"
        message="Are you sure you want to delete this door type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete All SF Panels Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteAllSFPanelsModal}
        onClose={() => setShowDeleteAllSFPanelsModal(false)}
        onConfirm={confirmDeleteAllSFPanels}
        type="danger"
        title="Delete All Panels"
        message={`Delete all ${storefrontPanelList.length} panels? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
      />
      
      {/* Add Submittal Dialog */}
      <Dialog open={showAddSubmittalDialog} onOpenChange={setShowAddSubmittalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Submittal</DialogTitle>
            <DialogDescription>
              Upload a submittal document for this CSI item
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!submittalFile) {
                toast.error('File is required');
                return;
              }
              setUploadingSubmittal(true);
              try {
                const formData = new FormData();
                formData.append('title', submittalTitle);
                if (submittalDescription) {
                  formData.append('description', submittalDescription);
                }
                formData.append('file', submittalFile);
                
                await axios.post(
                  `${API}/projects/${projectId}/csi-items/${activeCsiTab}/submittals`,
                  formData
                );
                
                toast.success('Submittal added successfully');
                setShowAddSubmittalDialog(false);
                setSubmittalTitle('');
                setSubmittalDescription('');
                setSubmittalFile(null);
                fetchSubmittals(activeCsiTab);
              } catch (error) {
                if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                  toast.error(error.response.data.detail.message);
                } else {
                  toast.error(error.response?.data?.detail || 'Failed to add submittal');
                }
              } finally {
                setUploadingSubmittal(false);
              }
            }}
            className="space-y-4 mt-4"
          >
            <div className="space-y-2">
              <Label htmlFor="submittal-title">Title *</Label>
              <Input
                id="submittal-title"
                data-testid="submittal-title-input"
                value={submittalTitle}
                onChange={(e) => setSubmittalTitle(e.target.value)}
                placeholder="e.g., Product Specifications"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="submittal-description">Description</Label>
              <Input
                id="submittal-description"
                data-testid="submittal-description-input"
                value={submittalDescription}
                onChange={(e) => setSubmittalDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="submittal-file">File *</Label>
              <Input
                id="submittal-file"
                data-testid="submittal-file-input"
                type="file"
                onChange={(e) => setSubmittalFile(e.target.files[0])}
                required
              />
              {!submittalFile && (
                <p className="text-xs text-red-500">File is required to create a submittal</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={uploadingSubmittal || !submittalFile}
                className="flex-1"
                data-testid="submittal-submit-btn"
              >
                {uploadingSubmittal ? 'Uploading...' : 'Add Submittal'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddSubmittalDialog(false);
                  setSubmittalTitle('');
                  setSubmittalDescription('');
                  setSubmittalFile(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Submittal Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteSubmittalModal}
        onClose={() => {
          setShowDeleteSubmittalModal(false);
          setSubmittalToDelete(null);
        }}
        onConfirm={async () => {
          if (!submittalToDelete) return;
          try {
            await axios.delete(
              `${API}/projects/${projectId}/csi-items/${activeCsiTab}/submittals/${submittalToDelete.id}`
            );
            toast.success('Submittal deleted successfully');
            fetchSubmittals(activeCsiTab);
          } catch (error) {
            toast.error('Failed to delete submittal');
          } finally {
            setShowDeleteSubmittalModal(false);
            setSubmittalToDelete(null);
          }
        }}
        type="danger"
        title="Delete Submittal"
        message={`Are you sure you want to delete "${submittalToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Elevation Page Selector Modal */}
      <Dialog 
        open={showElevationPageSelector} 
        onOpenChange={(open) => {
          // Prevent closing modal during upload
          if (!open && uploadingElevation) return;
          setShowElevationPageSelector(open);
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Pages for Elevations</DialogTitle>
            <DialogDescription>
              PDF: {elevationPreviewData?.filename} ({elevationPreviewData?.totalPages} pages)
              <br />
              Select the pages you want to save as elevations and provide a name for each selected page.
              <br />
              <span className="text-red-600 text-sm">* Name is required for all selected pages</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-slate-600">
                {selectedElevationPages.length} of {elevationPreviewData?.totalPages || 0} pages selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedElevationPages(elevationPreviewData?.pages.map((_, idx) => idx) || [])}
                >
                  Select All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedElevationPages([])}
                >
                  Deselect All
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {elevationPreviewData?.pages.map((page) => (
                <div
                  key={page.pageIndex}
                  className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                    selectedElevationPages.includes(page.pageIndex)
                      ? 'border-[#FF6600] bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleElevationPageSelection(page.pageIndex)}
                >
                  <div className="relative">
                    <img
                      src={page.thumbnailUrl}
                      alt={`Page ${page.pageNumber}`}
                      className="w-full h-auto rounded"
                    />
                    {selectedElevationPages.includes(page.pageIndex) && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-[#FF6600] rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">✓</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-slate-700 mb-1">
                      Page {page.pageNumber}
                    </div>
                    {selectedElevationPages.includes(page.pageIndex) && (
                      <div>
                        <label className="text-xs text-slate-600 mb-1 block">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder={`e.g., North Elevation`}
                          value={elevationPageNames[page.pageIndex] || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateElevationPageName(page.pageIndex, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          required
                          className={`w-full px-2 py-1 text-xs border rounded focus:outline-none focus:border-[#FF6600] ${
                            selectedElevationPages.includes(page.pageIndex) && 
                            (!elevationPageNames[page.pageIndex] || elevationPageNames[page.pageIndex].trim() === '')
                              ? 'border-red-300 bg-red-50'
                              : 'border-gray-300'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Loading indicator - DRY component */}
            {uploadingElevation && (
              <div className="my-3">
                <ElevationLoadingIndicator 
                  message={uploadProgress || `Creating ${selectedElevationPages.length} elevation(s)...`}
                  progressPercent={uploadProgressPercent}
                  variant="banner"
                />
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowElevationPageSelector(false);
                  setElevationPreviewData(null);
                  setSelectedElevationPages([]);
                  setElevationPageNames({});
                }}
                disabled={uploadingElevation}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateElevationsFromPages}
                disabled={
                  selectedElevationPages.length === 0 || 
                  uploadingElevation ||
                  selectedElevationPages.some(pageIdx => !elevationPageNames[pageIdx] || elevationPageNames[pageIdx].trim() === '')
                }
                className="bg-[#FF6600] hover:bg-[#ff8533]"
              >
                {uploadingElevation ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  `Create ${selectedElevationPages.length} Elevation(s)`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Panel Print Preview Modal */}
      <Dialog open={showPanelPrintPreview} onOpenChange={(open) => {
        if (!open) handleClosePanelPrintPreview();
      }}>
        <DialogContent className="max-w-4xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Panel Print Preview</span>
              <div className="flex gap-2 mr-8">
                {panelPrintPreviewUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = panelPrintPreviewUrl;
                      link.download = 'panel-label.pdf';
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4 mr-1" /> Download PDF
                  </Button>
                )}
              </div>
            </DialogTitle>
            <DialogDescription>
              Panel QR code and details for printing
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {panelPrintPreviewLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-red-600 mb-4" />
                <p className="text-slate-600">Loading preview...</p>
              </div>
            ) : panelPrintPreviewUrl ? (
              <div className="border rounded-lg overflow-hidden bg-white" style={{ height: '70vh' }}>
                <iframe
                  src={panelPrintPreviewUrl + '#toolbar=1&navpanes=0'}
                  className="w-full h-full"
                  title="Panel Print Preview"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center py-16 text-slate-500">
                <p>No preview available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Door Type Dialog */}
      <Dialog open={showDoorTypeDialog} onOpenChange={(open) => {
        if (open && !editingDoorType) {
          // Clear previous clipboard upload preview and reset form when opening Add dialog
          setDoorTypePictureUrl(null);
          if (pendingDoorTypePicture?.previewUrl) {
            URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
          }
          setPendingDoorTypePicture(null);
          setNewDoorType({
            name: '',
            doorType: '',
            manufacturer: '',
            model: '',
            doorMaterial: '',
            operationType: '',
            finishInterior: '',
            finishExterior: '',
            finishType: '',
            fireRated: '',
            frameWarrantyYears: '',
            finishWarrantyYears: '',
            hardwareWarrantyYears: '',
            installationWarrantyYears: '',
            markerColor: '#39FF14'
          });
        }
        if (!open) {
          setShowDoorTypeDialog(false);
          setEditingDoorType(null);
          setDoorTypePictureUrl(null);
          // Clean up pending picture
          if (pendingDoorTypePicture?.previewUrl) {
            URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
          }
          setPendingDoorTypePicture(null);
          setNewDoorType({
            name: '',
            doorType: '',
            manufacturer: '',
            model: '',
            doorMaterial: '',
            operationType: '',
            finishInterior: '',
            finishExterior: '',
            finishType: '',
            fireRated: '',
            frameWarrantyYears: '',
            finishWarrantyYears: '',
            hardwareWarrantyYears: '',
            installationWarrantyYears: '',
            markerColor: '#39FF14'
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingDoorType ? 'Edit Door Type' : 'Create Door Type'}</DialogTitle>
            <DialogDescription>
              Define door specifications using imperial measurements
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={async (e) => {
            e.preventDefault();
            
            // Validation
            if (!newDoorType.name || !newDoorType.doorType || !newDoorType.manufacturer || 
                !newDoorType.model || !newDoorType.doorMaterial || !newDoorType.operationType ||
                !newDoorType.finishInterior || !newDoorType.finishExterior || !newDoorType.finishType ||
                !newDoorType.fireRated) {
              toast.error('Please fill in all required fields');
              return;
            }
            
            if (hasWarrantyErrors([newDoorType.frameWarrantyYears, newDoorType.finishWarrantyYears, newDoorType.hardwareWarrantyYears, newDoorType.installationWarrantyYears])) {
              toast.error('Warranty values must be greater than 0');
              return;
            }
            
            if (doorOperationLoading) return;
            setDoorOperationLoading(true);
            
            const activeCSIItem = csiItems.find(item => item.id === activeCsiTab);
            
            const doorTypeData = {
              projectId,
              csiItemId: activeCSIItem?.id,
              name: newDoorType.name,
              doorType: newDoorType.doorType,
              manufacturer: newDoorType.manufacturer,
              model: newDoorType.model,
              doorMaterial: newDoorType.doorMaterial,
              operationType: newDoorType.operationType,
              finishInterior: newDoorType.finishInterior,
              finishExterior: newDoorType.finishExterior,
              finishType: newDoorType.finishType,
              fireRated: parseInt(newDoorType.fireRated),
              frameWarrantyYears: newDoorType.frameWarrantyYears ? parseInt(newDoorType.frameWarrantyYears) : null,
              finishWarrantyYears: newDoorType.finishWarrantyYears ? parseInt(newDoorType.finishWarrantyYears) : null,
              hardwareWarrantyYears: newDoorType.hardwareWarrantyYears ? parseInt(newDoorType.hardwareWarrantyYears) : null,
              installationWarrantyYears: newDoorType.installationWarrantyYears ? parseInt(newDoorType.installationWarrantyYears) : null,
              markerColor: newDoorType.markerColor || '#39FF14'
            };
            
            try {
              if (editingDoorType) {
                const response = await axios.put(`${API}/door-types/${editingDoorType.id}`, doorTypeData);
                setDoorTypes(prev => prev.map(dt => dt.id === editingDoorType.id ? response.data : dt));
                toast.success('Door type updated');
              } else {
                const response = await axios.post(`${API}/door-types`, doorTypeData);
                const newDoorTypeId = response.data.id;
                setDoorTypes(prev => [...prev, response.data]);
                toast.success('Door type created');
                
                // Upload pending picture if exists
                if (pendingDoorTypePicture?.file && newDoorTypeId) {
                  try {
                    setDoorTypePictureUploading(newDoorTypeId);
                    const formData = new FormData();
                    formData.append('file', pendingDoorTypePicture.file);
                    
                    const uploadResponse = await axios.post(
                      `${API}/door-types/${newDoorTypeId}/upload-picture`,
                      formData,
                      { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    
                    // Update the door type with the uploaded picture
                    setDoorTypes(prev => prev.map(dt => 
                      dt.id === newDoorTypeId 
                        ? { ...dt, pictureCloudinary: uploadResponse.data.pictureCloudinary, pictureUrl: null }
                        : dt
                    ));
                    toast.success('Picture uploaded successfully');
                  } catch (uploadError) {
                    toast.error('Door type created but picture upload failed');
                  } finally {
                    setDoorTypePictureUploading(null);
                    // Clean up pending picture
                    if (pendingDoorTypePicture?.previewUrl) {
                      URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
                    }
                    setPendingDoorTypePicture(null);
                  }
                }
              }
              setShowDoorTypeDialog(false);
              setEditingDoorType(null);
              // Reset pending picture state on dialog close
              if (pendingDoorTypePicture?.previewUrl) {
                URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
              }
              setPendingDoorTypePicture(null);
            } catch (error) {
              if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                toast.error(error.response.data.detail.message);
              } else {
                toast.error(error.response?.data?.detail || 'Failed to save door type');
              }
            } finally {
              setDoorOperationLoading(false);
            }
          }}>
            <div className="space-y-6 py-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h4 className="font-semibold text-sm text-gray-700">Basic Information</h4>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Marker Color</Label>
                    <input
                      type="color"
                      value={newDoorType.markerColor || '#39FF14'}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, markerColor: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                      title="Choose marker color for floor plans"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="doorTypeName">Type *</Label>
                    <Input
                      id="doorTypeName"
                      placeholder="e.g., DT1, DT2, Main Entry Door"
                      value={newDoorType.name}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="doorTypeSelect">Door Style *</Label>
                    <Select
                      value={newDoorType.doorType}
                      onValueChange={(value) => setNewDoorType(prev => ({ ...prev, doorType: value }))}
                    >
                      <SelectTrigger id="doorTypeSelect">
                        <SelectValue placeholder="Select door style" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Flush">Flush</SelectItem>
                        <SelectItem value="Half Glass">Half Glass</SelectItem>
                        <SelectItem value="Full Glass">Full Glass</SelectItem>
                        <SelectItem value="Narrow Lite">Narrow Lite</SelectItem>
                        <SelectItem value="Store Front">Store Front</SelectItem>
                        <SelectItem value="All Glass Door">All Glass Door</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="doorManufacturer">Manufacturer *</Label>
                    <Input
                      id="doorManufacturer"
                      value={newDoorType.manufacturer}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, manufacturer: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="doorModel">Model *</Label>
                    <Input
                      id="doorModel"
                      value={newDoorType.model}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, model: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="doorMaterial">Door Material *</Label>
                    <Select
                      value={newDoorType.doorMaterial}
                      onValueChange={(value) => setNewDoorType(prev => ({ ...prev, doorMaterial: value }))}
                    >
                      <SelectTrigger id="doorMaterial">
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Wood">Wood</SelectItem>
                        <SelectItem value="Hollow Metal">Hollow Metal</SelectItem>
                        <SelectItem value="Aluminum">Aluminum</SelectItem>
                        <SelectItem value="Steel">Steel</SelectItem>
                        <SelectItem value="Glass">Glass</SelectItem>
                        <SelectItem value="PVC">PVC</SelectItem>
                        <SelectItem value="Fiberglass">Fiberglass</SelectItem>
                        <SelectItem value="Wood / Glass">Wood / Glass</SelectItem>
                        <SelectItem value="Aluminum / Glass">Aluminum / Glass</SelectItem>
                        <SelectItem value="Steel / Glass">Steel / Glass</SelectItem>
                        <SelectItem value="Hollow Metal / Glass">Hollow Metal / Glass</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="operationType">Operation Type *</Label>
                    <Select
                      value={newDoorType.operationType}
                      onValueChange={(value) => setNewDoorType(prev => ({ ...prev, operationType: value }))}
                    >
                      <SelectTrigger id="operationType">
                        <SelectValue placeholder="Select operation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Swing">Swing</SelectItem>
                        <SelectItem value="Sliding">Sliding</SelectItem>
                        <SelectItem value="Folding">Folding</SelectItem>
                        <SelectItem value="Revolving">Revolving</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Finishes */}
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-gray-700 border-b pb-2">Finishes</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="finishInterior">Finish Interior *</Label>
                    <Input
                      id="finishInterior"
                      value={newDoorType.finishInterior}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, finishInterior: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="finishExterior">Finish Exterior *</Label>
                    <Input
                      id="finishExterior"
                      value={newDoorType.finishExterior}
                      onChange={(e) => setNewDoorType(prev => ({ ...prev, finishExterior: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="finishType">Finish Type *</Label>
                    <Select
                      value={newDoorType.finishType}
                      onValueChange={(value) => setNewDoorType(prev => ({ ...prev, finishType: value }))}
                    >
                      <SelectTrigger id="finishType">
                        <SelectValue placeholder="Select finish type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Paint">Paint</SelectItem>
                        <SelectItem value="Powder Coated">Powder Coated</SelectItem>
                        <SelectItem value="Clad">Clad</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="fireRated">Fire Rated (minutes) *</Label>
                    <Select
                      value={newDoorType.fireRated}
                      onValueChange={(value) => setNewDoorType(prev => ({ ...prev, fireRated: value }))}
                    >
                      <SelectTrigger id="fireRated">
                        <SelectValue placeholder="Select fire rating" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No</SelectItem>
                        <SelectItem value="20">20 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="45">45 minutes</SelectItem>
                        <SelectItem value="60">60 minutes</SelectItem>
                        <SelectItem value="90">90 minutes</SelectItem>
                        <SelectItem value="120">120 minutes</SelectItem>
                        <SelectItem value="180">180 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Warranties */}
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-gray-700 border-b pb-2">Warranties (Years)</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="frameWarranty">Frame Warranty</Label>
                    <NumericInput
                      id="frameWarranty"
                      placeholder="Years"
                      value={newDoorType.frameWarrantyYears}
                      onChange={(v) => setNewDoorType(prev => ({ ...prev, frameWarrantyYears: v }))}
                      className={getWarrantyError(newDoorType.frameWarrantyYears) ? 'border-red-500' : ''}
                      data-testid="door-frame-warranty-input"
                    />
                    {getWarrantyError(newDoorType.frameWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newDoorType.frameWarrantyYears)}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="finishWarranty">Finish Warranty</Label>
                    <NumericInput
                      id="finishWarranty"
                      placeholder="Years"
                      value={newDoorType.finishWarrantyYears}
                      onChange={(v) => setNewDoorType(prev => ({ ...prev, finishWarrantyYears: v }))}
                      className={getWarrantyError(newDoorType.finishWarrantyYears) ? 'border-red-500' : ''}
                      data-testid="door-finish-warranty-input"
                    />
                    {getWarrantyError(newDoorType.finishWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newDoorType.finishWarrantyYears)}</p>}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hardwareWarranty">Hardware Warranty</Label>
                    <NumericInput
                      id="hardwareWarranty"
                      placeholder="Years"
                      value={newDoorType.hardwareWarrantyYears}
                      onChange={(v) => setNewDoorType(prev => ({ ...prev, hardwareWarrantyYears: v }))}
                      className={getWarrantyError(newDoorType.hardwareWarrantyYears) ? 'border-red-500' : ''}
                      data-testid="door-hardware-warranty-input"
                    />
                    {getWarrantyError(newDoorType.hardwareWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newDoorType.hardwareWarrantyYears)}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="installationWarranty">Installation Warranty</Label>
                    <NumericInput
                      id="installationWarranty"
                      placeholder="Years"
                      value={newDoorType.installationWarrantyYears}
                      onChange={(v) => setNewDoorType(prev => ({ ...prev, installationWarrantyYears: v }))}
                      className={getWarrantyError(newDoorType.installationWarrantyYears) ? 'border-red-500' : ''}
                      data-testid="door-installation-warranty-input"
                    />
                    {getWarrantyError(newDoorType.installationWarrantyYears) && <p className="text-xs text-red-500">{getWarrantyError(newDoorType.installationWarrantyYears)}</p>}
                  </div>
                </div>
              </div>

              {/* Picture Upload */}
              <div className="space-y-4">
                <h4 className="font-semibold text-sm text-gray-700 border-b pb-2">Type Picture</h4>
                {/* Show existing picture for edit mode */}
                {editingDoorType && (doorTypePictureUrl || (editingDoorType?.pictureCloudinary?.urls?.medium) || (editingDoorType?.pictureUrl)) && (
                  <div className="mb-4">
                    <img 
                      src={doorTypePictureUrl || editingDoorType?.pictureCloudinary?.urls?.medium || `${API}${editingDoorType?.pictureUrl}?t=${editingDoorType?.updatedAt || editingDoorType?.createdAt || 'default'}`} 
                      alt="Door type" 
                      className="max-w-full h-auto max-h-64 border rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = e.target.nextSibling;
                        if (placeholder) placeholder.style.display = 'block';
                      }}
                    />
                    <div 
                      style={{ display: 'none' }} 
                      className="p-8 bg-slate-100 border-2 border-dashed rounded text-center text-slate-500"
                    >
                      <p>Image not found or failed to load</p>
                      <p className="text-sm mt-2">Please upload a new picture</p>
                    </div>
                  </div>
                )}
                {/* Show pending picture preview for add mode */}
                {!editingDoorType && pendingDoorTypePicture?.previewUrl && (
                  <div className="mb-4">
                    <div className="relative inline-block">
                      <img 
                        src={pendingDoorTypePicture.previewUrl} 
                        alt="Pending door type picture" 
                        className="max-w-full h-auto max-h-64 border rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
                          setPendingDoorTypePicture(null);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-green-600 mt-2">✓ Image ready - will be uploaded when door type is created</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Upload Picture (PNG/JPEG/PDF)</Label>
                  <div className="flex gap-2">
                    {/* Paste button - works for both Add and Edit modes */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleDoorTypePasteFromClipboard(editingDoorType?.id || null)}
                      title="Paste image from clipboard"
                      disabled={doorTypePictureUploading === editingDoorType?.id}
                    >
                      {doorTypePictureUploading === editingDoorType?.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardPaste className="h-4 w-4 mr-2" />
                      )}
                      Paste
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('door-type-picture-modal').click()}
                      disabled={doorTypePictureUploading === editingDoorType?.id}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {(doorTypePictureUrl || editingDoorType?.pictureCloudinary?.urls?.medium || editingDoorType?.pictureUrl || pendingDoorTypePicture) ? 'Change Picture' : 'Upload Picture'}
                    </Button>
                    {(doorTypePictureUrl || editingDoorType?.pictureCloudinary?.urls?.medium || editingDoorType?.pictureUrl) && editingDoorType && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => handleDeleteDoorTypePicture(editingDoorType.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Picture
                      </Button>
                    )}
                    <input
                      id="door-type-picture-modal"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,application/pdf"
                      onChange={(e) => {
                        if (editingDoorType) {
                          handleDoorTypePictureUpload(e, editingDoorType.id);
                        } else {
                          // For new door type - save file for later upload
                          const file = e.target.files?.[0];
                          if (file) {
                            // Clean up old preview if exists
                            if (pendingDoorTypePicture?.previewUrl) {
                              URL.revokeObjectURL(pendingDoorTypePicture.previewUrl);
                            }
                            const previewUrl = URL.createObjectURL(file);
                            setPendingDoorTypePicture({ file, previewUrl });
                            toast.success('Image ready - will be uploaded when door type is created');
                          }
                        }
                        e.target.value = ''; // Reset input
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {!editingDoorType && !pendingDoorTypePicture && (
                    <p className="text-sm text-slate-500">Picture will be uploaded after creating the type</p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowDoorTypeDialog(false)} disabled={doorOperationLoading}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#FF6600] hover:bg-[#ff8533]" disabled={doorOperationLoading}>
                {doorOperationLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editingDoorType ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingDoorType ? 'Update Door Type' : 'Create Door Type'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Door Glass Type Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorGlassTypeModal}
        onClose={() => {
          setShowDeleteDoorGlassTypeModal(false);
          setDeleteDoorGlassTypeId(null);
        }}
        onConfirm={confirmDeleteDoorGlassType}
        type="danger"
        title="Delete Glass Type"
        message="Are you sure you want to delete this glass type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Door Metal Panel Type Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorMetalPanelTypeModal}
        onClose={() => {
          setShowDeleteDoorMetalPanelTypeModal(false);
          setDeleteDoorMetalPanelTypeId(null);
        }}
        onConfirm={confirmDeleteDoorMetalPanelType}
        type="danger"
        title="Delete Metal Panel Type"
        message="Are you sure you want to delete this metal panel type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Window Metal Panel Type Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteWindowMetalPanelTypeModal}
        onClose={() => {
          setShowDeleteWindowMetalPanelTypeModal(false);
          setDeleteWindowMetalPanelTypeId(null);
        }}
        onConfirm={confirmDeleteWindowMetalPanelType}
        type="danger"
        title="Delete Metal Panel Type"
        message="Are you sure you want to delete this metal panel type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Delete Mirror Type Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteMirrorTypeModal}
        onClose={() => {
          setShowDeleteMirrorTypeModal(false);
          setDeleteMirrorTypeId(null);
        }}
        onConfirm={executeDeleteMirrorType}
        type="danger"
        title="Delete Mirror Type"
        message="Are you sure you want to delete this mirror type? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      {/* Door Hardware Set Dialog - Two-Step Modal */}
      <Dialog open={showDoorHardwareSetDialog} onOpenChange={(open) => {
        if (!open) {
          setShowDoorHardwareSetDialog(false);
          setEditingDoorHardwareSet(null);
          setNewDoorHardwareSet({ name: '', items: [] });
          setNewDoorHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
          setDoorHardwareSetStep(1);
          setEditingDoorHardwareItemIndex(null);
        }
      }}>
        <DialogContent className={doorHardwareSetStep === 1 ? "max-w-md" : "max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"}>
          <DialogHeader>
            <DialogTitle>
              {editingDoorHardwareSet ? 'Edit Hardware Set' : (doorHardwareSetStep === 1 ? 'Add Hardware Set' : `Hardware Set: ${newDoorHardwareSet.name}`)}
            </DialogTitle>
            {doorHardwareSetStep === 1 && (
              <DialogDescription>Enter the hardware set name to get started</DialogDescription>
            )}
          </DialogHeader>
          
          {/* Step 1: Name Only */}
          {doorHardwareSetStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="doorHardwareSetName">Hardware Set Name *</Label>
                <Input
                  id="doorHardwareSetName"
                  placeholder="e.g., HS1, HS2, Entry Door Set"
                  value={newDoorHardwareSet.name}
                  onChange={(e) => setNewDoorHardwareSet(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowDoorHardwareSetDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    if (!newDoorHardwareSet.name.trim()) {
                      toast.error('Hardware Set Name is required');
                      return;
                    }
                    setDoorHardwareSetStep(2);
                  }}
                  className="bg-[#FF6600] hover:bg-[#ff8533]"
                  disabled={!newDoorHardwareSet.name.trim()}
                >
                  Create
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 2: Full Form with Items */}
          {doorHardwareSetStep === 2 && (
            <div className="flex flex-col flex-1 min-h-0 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg flex items-center gap-3 shrink-0">
                <Label className="text-sm text-slate-500 whitespace-nowrap">Hardware Set Name:</Label>
                <Input
                  value={newDoorHardwareSet.name}
                  onChange={(e) => setNewDoorHardwareSet(prev => ({ ...prev, name: e.target.value }))}
                  className="h-8 max-w-xs font-semibold"
                />
              </div>
              
              {/* Add Item Form - creation only */}
              <div className="border rounded-lg p-4 bg-white shrink-0">
                <h4 className="font-semibold text-sm text-gray-700 mb-4">
                  Add New Item
                </h4>
                <div className="flex gap-2 items-end">
                  <div className="w-[70px]">
                    <Label className="text-xs">Qty *</Label>
                    <NumericInput
                      placeholder="Qty"
                      value={newDoorHardwareItem.quantity}
                      onChange={(v) => setNewDoorHardwareItem(prev => ({ ...prev, quantity: v }))}
                      className="h-9"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs">Description *</Label>
                    <Input
                      placeholder="Description"
                      value={newDoorHardwareItem.description}
                      onChange={(e) => setNewDoorHardwareItem(prev => ({ ...prev, description: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[140px]">
                    <Label className="text-xs">Catalog No</Label>
                    <Input
                      placeholder="Optional"
                      value={newDoorHardwareItem.catalogNumber}
                      onChange={(e) => setNewDoorHardwareItem(prev => ({ ...prev, catalogNumber: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[120px]">
                    <Label className="text-xs">Finish *</Label>
                    <Input
                      placeholder="Finish"
                      value={newDoorHardwareItem.finish}
                      onChange={(e) => setNewDoorHardwareItem(prev => ({ ...prev, finish: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[140px]">
                    <Label className="text-xs">Manufacturer *</Label>
                    <Input
                      placeholder="Manufacturer"
                      value={newDoorHardwareItem.manufacturer}
                      onChange={(e) => setNewDoorHardwareItem(prev => ({ ...prev, manufacturer: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 bg-[#FF6600] hover:bg-[#ff8533]"
                    disabled={!newDoorHardwareItem.description.trim() || !newDoorHardwareItem.finish.trim() || !newDoorHardwareItem.manufacturer.trim() || !newDoorHardwareItem.quantity}
                    onClick={() => {
                      if (!newDoorHardwareItem.description.trim()) {
                        toast.error('Description is required');
                        return;
                      }
                      if (!newDoorHardwareItem.finish.trim()) {
                        toast.error('Finish is required');
                        return;
                      }
                      if (!newDoorHardwareItem.manufacturer.trim()) {
                        toast.error('Manufacturer is required');
                        return;
                      }
                      if (!newDoorHardwareItem.quantity || parseInt(newDoorHardwareItem.quantity) <= 0) {
                        toast.error('Quantity must be a positive number');
                        return;
                      }
                      
                      const newItem = {
                        id: `temp-${Date.now()}`,
                        description: newDoorHardwareItem.description,
                        catalogNumber: newDoorHardwareItem.catalogNumber || null,
                        finish: newDoorHardwareItem.finish,
                        manufacturer: newDoorHardwareItem.manufacturer,
                        quantity: parseInt(newDoorHardwareItem.quantity)
                      };
                      
                      setNewDoorHardwareSet(prev => ({
                        ...prev,
                        items: [...prev.items, newItem]
                      }));
                      
                      // Clear form
                      setNewDoorHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Items List */}
              {newDoorHardwareSet.items.length > 0 && (
                <div className="border rounded-lg overflow-y-auto flex-1 min-h-0" data-testid="door-hardware-items-scroll">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b sticky top-0 z-10">
                      <tr>
                        <th className="text-center p-3 font-semibold w-[70px]">Qty</th>
                        <th className="text-left p-3 font-semibold">Description</th>
                        <th className="text-left p-3 font-semibold">Catalog No</th>
                        <th className="text-left p-3 font-semibold">Finish</th>
                        <th className="text-left p-3 font-semibold">Manufacturer</th>
                        <th className="text-center p-3 font-semibold w-[90px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newDoorHardwareSet.items.map((item, index) => (
                        editingDoorHardwareItemIndex === index ? (
                          <tr key={item.id || index} className="border-b last:border-b-0 bg-orange-50/60" data-testid={`door-hardware-item-edit-row-${index}`}>
                            <td className="p-2">
                              <NumericInput
                                placeholder="Qty"
                                value={doorHardwareItemDraft.quantity}
                                onChange={(v) => setDoorHardwareItemDraft(prev => ({ ...prev, quantity: v }))}
                                className="h-9"
                                data-testid={`door-hardware-item-edit-quantity-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder="Description"
                                value={doorHardwareItemDraft.description}
                                onChange={(e) => setDoorHardwareItemDraft(prev => ({ ...prev, description: e.target.value }))}
                                className="h-9"
                                data-testid={`door-hardware-item-edit-description-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder="Optional"
                                value={doorHardwareItemDraft.catalogNumber}
                                onChange={(e) => setDoorHardwareItemDraft(prev => ({ ...prev, catalogNumber: e.target.value }))}
                                className="h-9"
                                data-testid={`door-hardware-item-edit-catalog-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder="Finish"
                                value={doorHardwareItemDraft.finish}
                                onChange={(e) => setDoorHardwareItemDraft(prev => ({ ...prev, finish: e.target.value }))}
                                className="h-9"
                                data-testid={`door-hardware-item-edit-finish-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                placeholder="Manufacturer"
                                value={doorHardwareItemDraft.manufacturer}
                                onChange={(e) => setDoorHardwareItemDraft(prev => ({ ...prev, manufacturer: e.target.value }))}
                                className="h-9"
                                data-testid={`door-hardware-item-edit-manufacturer-${index}`}
                              />
                            </td>
                            <td className="p-2">
                              <div className="flex justify-center gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 bg-[#FF6600] hover:bg-[#ff8533]"
                                  data-testid={`door-hardware-item-save-${index}`}
                                  onClick={() => {
                                    if (!doorHardwareItemDraft.description.trim()) {
                                      toast.error('Description is required');
                                      return;
                                    }
                                    if (!doorHardwareItemDraft.finish.trim()) {
                                      toast.error('Finish is required');
                                      return;
                                    }
                                    if (!doorHardwareItemDraft.manufacturer.trim()) {
                                      toast.error('Manufacturer is required');
                                      return;
                                    }
                                    if (!doorHardwareItemDraft.quantity || parseInt(doorHardwareItemDraft.quantity) <= 0) {
                                      toast.error('Quantity must be a positive number');
                                      return;
                                    }
                                    
                                    setNewDoorHardwareSet(prev => ({
                                      ...prev,
                                      items: prev.items.map((existing, idx) => idx === index ? {
                                        ...existing,
                                        description: doorHardwareItemDraft.description,
                                        catalogNumber: doorHardwareItemDraft.catalogNumber || null,
                                        finish: doorHardwareItemDraft.finish,
                                        manufacturer: doorHardwareItemDraft.manufacturer,
                                        quantity: parseInt(doorHardwareItemDraft.quantity)
                                      } : existing)
                                    }));
                                    setEditingDoorHardwareItemIndex(null);
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-8"
                                  data-testid={`door-hardware-item-cancel-${index}`}
                                  onClick={() => setEditingDoorHardwareItemIndex(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                        <tr key={item.id || index} className="border-b last:border-b-0 hover:bg-slate-50">
                          <td className="p-3 text-center">{item.quantity}</td>
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-slate-600">{item.catalogNumber || '-'}</td>
                          <td className="p-3">{item.finish}</td>
                          <td className="p-3">{item.manufacturer}</td>
                          <td className="p-3">
                            <div className="flex justify-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                data-testid={`door-hardware-item-edit-${index}`}
                                onClick={() => {
                                  setEditingDoorHardwareItemIndex(index);
                                  setDoorHardwareItemDraft({
                                    description: item.description,
                                    catalogNumber: item.catalogNumber || '',
                                    finish: item.finish,
                                    manufacturer: item.manufacturer,
                                    quantity: item.quantity.toString()
                                  });
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`door-hardware-item-delete-${index}`}
                                onClick={() => {
                                  setNewDoorHardwareSet(prev => ({
                                    ...prev,
                                    items: prev.items.filter((_, idx) => idx !== index)
                                  }));
                                  if (editingDoorHardwareItemIndex !== null) {
                                    setEditingDoorHardwareItemIndex(null);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {newDoorHardwareSet.items.length === 0 && (
                <div className="text-center py-8 text-slate-500 border rounded-lg bg-slate-50">
                  <p>No items added yet. Use the form above to add hardware items.</p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t shrink-0">
                <Button type="button" variant="outline" onClick={() => setShowDoorHardwareSetDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (newDoorHardwareSet.items.length === 0) {
                      toast.error('Please add at least one item');
                      return;
                    }
                    
                    try {
                      const payload = {
                        projectId,
                        csiItemId: activeCsiTab,
                        name: newDoorHardwareSet.name,
                        items: newDoorHardwareSet.items
                      };
                      
                      if (editingDoorHardwareSet) {
                        await axios.put(`${API}/door-hardware-sets/${editingDoorHardwareSet.id}`, payload);
                        toast.success('Hardware set updated');
                      } else {
                        await axios.post(`${API}/door-hardware-sets`, payload);
                        toast.success('Hardware set created');
                      }
                      
                      fetchDoorHardwareSets();
                      setShowDoorHardwareSetDialog(false);
                      setEditingDoorHardwareSet(null);
                      setNewDoorHardwareSet({ name: '', items: [] });
                      setDoorHardwareSetStep(1);
                    } catch (error) {
                      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                        toast.error(error.response.data.detail.message);
                      } else {
                        toast.error(editingDoorHardwareSet ? 'Failed to update hardware set' : 'Failed to create hardware set');
                      }
                    }
                  }}
                  className="bg-[#FF6600] hover:bg-[#ff8533]"
                >
                  {editingDoorHardwareSet ? 'Update' : 'OK'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Door Hardware Set Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorHardwareSetModal}
        onClose={() => {
          setShowDeleteDoorHardwareSetModal(false);
          setDeleteDoorHardwareSetId(null);
        }}
        onConfirm={confirmDeleteDoorHardwareSet}
        type="danger"
        title="Delete Hardware Set"
        message="Are you sure you want to delete this hardware set? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Shower Hardware Set Dialog (mirrors Door pattern, manufacturer optional) */}
      <Dialog open={showShowerHardwareSetDialog} onOpenChange={(open) => {
        if (!open) {
          setShowShowerHardwareSetDialog(false);
          setEditingShowerHardwareSet(null);
          setNewShowerHardwareSet({ name: '', items: [] });
          setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
          setShowerHardwareSetStep(1);
          setEditingShowerHardwareItemIndex(null);
        }
      }}>
        <DialogContent className={showerHardwareSetStep === 1 ? "max-w-md" : "max-w-6xl"}>
          <DialogHeader>
            <DialogTitle>
              {editingShowerHardwareSet ? 'Edit Hardware Set' : (showerHardwareSetStep === 1 ? 'Add Hardware Set' : `Hardware Set: ${newShowerHardwareSet.name}`)}
            </DialogTitle>
            {showerHardwareSetStep === 1 && (
              <DialogDescription>Enter the hardware set name to get started</DialogDescription>
            )}
          </DialogHeader>
          
          {/* Step 1: Name Only */}
          {showerHardwareSetStep === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="showerHardwareSetName">Hardware Set Name *</Label>
                <Input
                  id="showerHardwareSetName"
                  placeholder="e.g., HS1, HS2, Entry Door Set"
                  value={newShowerHardwareSet.name}
                  onChange={(e) => setNewShowerHardwareSet(prev => ({ ...prev, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowShowerHardwareSetDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    if (!newShowerHardwareSet.name.trim()) {
                      toast.error('Hardware Set Name is required');
                      return;
                    }
                    setShowerHardwareSetStep(2);
                  }}
                  className="bg-[#FF6600] hover:bg-[#ff8533]"
                  disabled={!newShowerHardwareSet.name.trim()}
                >
                  Create
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 2: Full Form with Items */}
          {showerHardwareSetStep === 2 && (
            <div className="space-y-6">
              <div className="bg-slate-50 p-3 rounded-lg flex items-center gap-3">
                <Label className="text-sm text-slate-500 whitespace-nowrap">Hardware Set Name:</Label>
                <Input
                  value={newShowerHardwareSet.name}
                  onChange={(e) => setNewShowerHardwareSet(prev => ({ ...prev, name: e.target.value }))}
                  className="h-8 max-w-xs font-semibold"
                />
              </div>
              
              {/* Add Item Form */}
              <div className="border rounded-lg p-4 bg-white">
                <h4 className="font-semibold text-sm text-gray-700 mb-4">
                  {editingShowerHardwareItemIndex !== null ? 'Edit Item' : 'Add New Item'}
                </h4>
                <div className="flex gap-2 items-end">
                  <div className="w-[70px]">
                    <Label className="text-xs">Qty *</Label>
                    <NumericInput
                      placeholder="Qty"
                      value={newShowerHardwareItem.quantity}
                      onChange={(v) => setNewShowerHardwareItem(prev => ({ ...prev, quantity: v }))}
                      className="h-9"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs">Description *</Label>
                    <Input
                      placeholder="Description"
                      value={newShowerHardwareItem.description}
                      onChange={(e) => setNewShowerHardwareItem(prev => ({ ...prev, description: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[140px]">
                    <Label className="text-xs">Catalog No</Label>
                    <Input
                      placeholder="Optional"
                      value={newShowerHardwareItem.catalogNumber}
                      onChange={(e) => setNewShowerHardwareItem(prev => ({ ...prev, catalogNumber: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[120px]">
                    <Label className="text-xs">Finish *</Label>
                    <Input
                      placeholder="Finish"
                      value={newShowerHardwareItem.finish}
                      onChange={(e) => setNewShowerHardwareItem(prev => ({ ...prev, finish: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="w-[140px]">
                    <Label className="text-xs">Manufacturer</Label>
                    <Input
                      placeholder="Optional"
                      value={newShowerHardwareItem.manufacturer}
                      onChange={(e) => setNewShowerHardwareItem(prev => ({ ...prev, manufacturer: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-9 bg-[#FF6600] hover:bg-[#ff8533]"
                    disabled={!newShowerHardwareItem.description.trim() || !newShowerHardwareItem.finish.trim() || !newShowerHardwareItem.quantity}
                    onClick={() => {
                      if (!newShowerHardwareItem.description.trim()) {
                        toast.error('Description is required');
                        return;
                      }
                      if (!newShowerHardwareItem.finish.trim()) {
                        toast.error('Finish is required');
                        return;
                      }
                      if (!newShowerHardwareItem.quantity || parseInt(newShowerHardwareItem.quantity) <= 0) {
                        toast.error('Quantity must be a positive number');
                        return;
                      }
                      
                      const newItem = {
                        id: editingShowerHardwareItemIndex !== null ? newShowerHardwareSet.items[editingShowerHardwareItemIndex].id : `temp-${Date.now()}`,
                        description: newShowerHardwareItem.description,
                        catalogNumber: newShowerHardwareItem.catalogNumber || null,
                        finish: newShowerHardwareItem.finish,
                        manufacturer: newShowerHardwareItem.manufacturer || '',
                        quantity: parseInt(newShowerHardwareItem.quantity)
                      };
                      
                      if (editingShowerHardwareItemIndex !== null) {
                        setNewShowerHardwareSet(prev => ({
                          ...prev,
                          items: prev.items.map((item, idx) => idx === editingShowerHardwareItemIndex ? newItem : item)
                        }));
                        setEditingShowerHardwareItemIndex(null);
                      } else {
                        setNewShowerHardwareSet(prev => ({
                          ...prev,
                          items: [...prev.items, newItem]
                        }));
                      }
                      
                      setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                    }}
                  >
                    {editingShowerHardwareItemIndex !== null ? 'Update' : 'Add'}
                  </Button>
                  {editingShowerHardwareItemIndex !== null && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-9"
                      onClick={() => {
                        setEditingShowerHardwareItemIndex(null);
                        setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Items List */}
              {newShowerHardwareSet.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-center p-3 font-semibold w-[60px]">Qty</th>
                        <th className="text-left p-3 font-semibold">Description</th>
                        <th className="text-left p-3 font-semibold">Catalog No</th>
                        <th className="text-left p-3 font-semibold">Finish</th>
                        <th className="text-left p-3 font-semibold">Manufacturer</th>
                        <th className="text-center p-3 font-semibold w-[80px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newShowerHardwareSet.items.map((item, index) => (
                        <tr key={item.id || index} className="border-b last:border-b-0 hover:bg-slate-50">
                          <td className="p-3 text-center">{item.quantity}</td>
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-slate-600">{item.catalogNumber || '-'}</td>
                          <td className="p-3">{item.finish}</td>
                          <td className="p-3">{item.manufacturer || '-'}</td>
                          <td className="p-3">
                            <div className="flex justify-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  setEditingShowerHardwareItemIndex(index);
                                  setNewShowerHardwareItem({
                                    description: item.description,
                                    catalogNumber: item.catalogNumber || '',
                                    finish: item.finish,
                                    manufacturer: item.manufacturer || '',
                                    quantity: item.quantity.toString()
                                  });
                                }}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  setNewShowerHardwareSet(prev => ({
                                    ...prev,
                                    items: prev.items.filter((_, idx) => idx !== index)
                                  }));
                                  if (editingShowerHardwareItemIndex === index) {
                                    setEditingShowerHardwareItemIndex(null);
                                    setNewShowerHardwareItem({ description: '', catalogNumber: '', finish: '', manufacturer: '', quantity: '' });
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {newShowerHardwareSet.items.length === 0 && (
                <div className="text-center py-8 text-slate-500 border rounded-lg bg-slate-50">
                  <p>No items added yet. Use the form above to add hardware items.</p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowShowerHardwareSetDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (newShowerHardwareSet.items.length === 0) {
                      toast.error('Please add at least one item');
                      return;
                    }
                    
                    try {
                      const payload = {
                        projectId,
                        csiItemId: activeCsiTab,
                        name: newShowerHardwareSet.name,
                        items: newShowerHardwareSet.items
                      };
                      
                      if (editingShowerHardwareSet) {
                        await axios.put(`${API}/shower-hardware-sets/${editingShowerHardwareSet.id}`, payload);
                        toast.success('Hardware set updated');
                      } else {
                        await axios.post(`${API}/shower-hardware-sets`, payload);
                        toast.success('Hardware set created');
                      }
                      
                      fetchShowerHardwareSets();
                      setShowShowerHardwareSetDialog(false);
                      setEditingShowerHardwareSet(null);
                      setNewShowerHardwareSet({ name: '', items: [] });
                      setShowerHardwareSetStep(1);
                    } catch (error) {
                      if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                        toast.error(error.response.data.detail.message);
                      } else {
                        toast.error(editingShowerHardwareSet ? 'Failed to update hardware set' : 'Failed to create hardware set');
                      }
                    }
                  }}
                  className="bg-[#FF6600] hover:bg-[#ff8533]"
                >
                  {editingShowerHardwareSet ? 'Update' : 'OK'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Shower Hardware Set Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteShowerHardwareSetModal}
        onClose={() => { setShowDeleteShowerHardwareSetModal(false); setDeleteShowerHardwareSetId(null); }}
        onConfirm={confirmDeleteShowerHardwareSet}
        type="danger" title="Delete Hardware Set" message="Are you sure you want to delete this hardware set? This action cannot be undone." confirmText="Delete" cancelText="Cancel"
      />

      {/* Shower Schedule Create/Edit Dialog */}
      <Dialog open={showShowerScheduleDialog} onOpenChange={(open) => {
        if (!open) { setShowShowerScheduleDialog(false); setEditingShowerSchedule(null); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingShowerSchedule ? 'Edit Schedule' : 'Add Schedule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!newShowerSchedule.label?.trim()) { toast.error('Label is required'); return; }
            if (!newShowerSchedule.showerTypeId) { toast.error('Shower Type is required'); return; }
            setShowerOperationLoading(true);
            try {
              const payload = { projectId, csiItemId: activeCsiTab, label: newShowerSchedule.label, showerTypeId: newShowerSchedule.showerTypeId, hardwareSetId: newShowerSchedule.hardwareSetId || null };
              if (editingShowerSchedule) {
                payload.showerWidth_in = editingShowerSchedule.showerWidth_in;
                payload.showerHeight_in = editingShowerSchedule.showerHeight_in;
                payload.roWidth_in = editingShowerSchedule.roWidth_in;
                payload.roHeight_in = editingShowerSchedule.roHeight_in;
                payload.lites = editingShowerSchedule.lites || [];
                await axios.put(`${API}/shower-schedules/${editingShowerSchedule.id}`, payload);
                toast.success('Schedule updated');
              } else {
                await axios.post(`${API}/shower-schedules`, payload);
                toast.success('Schedule created');
              }
              const res = await axios.get(`${API}/shower-schedules/${projectId}/${activeCsiTab}`);
              setShowerSchedules(res.data);
              setShowShowerScheduleDialog(false);
              setEditingShowerSchedule(null);
            } catch (error) {
              if (error.response?.status === 409 && error.response?.data?.detail?.field) {
                toast.error(error.response.data.detail.message);
              } else {
                toast.error('Failed to save schedule');
              }
            }
            finally { setShowerOperationLoading(false); }
          }} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input placeholder="e.g., S-101" value={newShowerSchedule.label} onChange={(e) => setNewShowerSchedule(prev => ({ ...prev, label: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label>Shower Type *</Label>
              <Select value={newShowerSchedule.showerTypeId} onValueChange={(val) => setNewShowerSchedule(prev => ({ ...prev, showerTypeId: val }))}>
                <SelectTrigger><SelectValue placeholder="Select shower type" /></SelectTrigger>
                <SelectContent>
                  {showerTypes.filter(st => st.csiItemId === activeCsiTab).map(st => (
                    <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Hardware Set</Label>
              <Select value={newShowerSchedule.hardwareSetId || 'none'} onValueChange={(val) => setNewShowerSchedule(prev => ({ ...prev, hardwareSetId: val === 'none' ? '' : val }))}>
                <SelectTrigger><SelectValue placeholder="Select hardware set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {showerHardwareSets.filter(hs => hs.csiItemId === activeCsiTab).map(hs => (
                    <SelectItem key={hs.id} value={hs.id}>{hs.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowShowerScheduleDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#FF6600] hover:bg-[#ff8533]">{editingShowerSchedule ? 'Save Changes' : 'Add Schedule'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shower Detail Dialog */}
      <ShowerDetailDialog
        open={showShowerDetailDialog}
        onOpenChange={(open) => { if (!open) { setShowShowerDetailDialog(false); setSelectedShowerDetail(null); } }}
        shower={selectedShowerDetail}
        showerGlassTypes={showerGlassTypes.filter(gt => gt.csiItemId === activeCsiTab)}
        onSave={async (formData) => {
          if (!selectedShowerDetail) return;
          try {
            const payload = {
              projectId, csiItemId: activeCsiTab,
              showerTypeId: selectedShowerDetail.showerTypeId,
              hardwareSetId: selectedShowerDetail.hardwareSetId,
              label: selectedShowerDetail.label,
              showerWidth_in: formData.showerWidth_in,
              showerHeight_in: formData.showerHeight_in,
              roWidth_in: formData.roWidth_in,
              roHeight_in: formData.roHeight_in,
              lites: formData.lites.map(l => ({ glassTypeId: l.glassTypeId || null, width_in: l.width_in, height_in: l.height_in }))
            };
            await axios.put(`${API}/shower-schedules/${selectedShowerDetail.id}`, payload);
            const res = await axios.get(`${API}/shower-schedules/${projectId}/${activeCsiTab}`);
            setShowerSchedules(res.data);
            setShowShowerDetailDialog(false);
            setSelectedShowerDetail(null);
            toast.success('Shower details saved');
          } catch { toast.error('Failed to save details'); }
        }}
      />

      {/* Door Schedule Dialog - Basic Info Only */}
      <Dialog open={showDoorScheduleDialog} onOpenChange={(open) => {
        if (!open) {
          setShowDoorScheduleDialog(false);
          setEditingDoorSchedule(null);
          setDoorScheduleStep(1);
          setDoorLiteTouched({});
          setNewDoorSchedule(emptyDoorSchedule());
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="door-schedule-dialog">
          <DialogHeader>
            <DialogTitle>{editingDoorSchedule ? 'Edit Door Schedule' : 'Add Door Schedule'}</DialogTitle>
            <DialogDescription>
              {editingDoorSchedule
                ? 'Update schedule information, dimensions, lites and their marks on the Door Type image'
                : doorScheduleStep === 1
                ? 'Step 1 of 2 - Schedule information'
                : 'Step 2 of 2 - Dimensions, lites and Door Type image marking'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            submitDoorScheduleStep();
          }}>
            <div className={`grid gap-4 py-4 ${!editingDoorSchedule && doorScheduleStep !== 1 ? 'hidden' : ''}`}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="doorScheduleLabel">Label *</Label>
                  <Input
                    id="doorScheduleLabel"
                    value={newDoorSchedule.label}
                    onChange={(e) => setNewDoorSchedule(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g., D-101"
                    required
                    data-testid="door-schedule-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select
                    value={newDoorSchedule.doorTypeId}
                    onValueChange={(value) => setNewDoorSchedule(prev => {
                      if (value === prev.doorTypeId) return prev;
                      const existingMarks = prev.liteMarks || [];
                      if (existingMarks.length > 0 && !window.confirm(
                        `This schedule has ${existingMarks.length} mark${existingMarks.length === 1 ? '' : 's'} on the current Door Type image. Changing the Door Type discards ${existingMarks.length === 1 ? 'it' : 'them'}. Continue?`
                      )) {
                        return prev;
                      }
                      return { ...prev, doorTypeId: value, liteMarks: [], liteMarksTypeId: value };
                    })}
                  >
                    <SelectTrigger data-testid="door-schedule-type-select">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {doorTypes.filter(dt => dt.csiItemId === activeCsiTab).map(dt => (
                        <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Hardware Set</Label>
                  <Select
                    value={newDoorSchedule.hardwareSetId || "none"}
                    onValueChange={(value) => setNewDoorSchedule(prev => ({ ...prev, hardwareSetId: value === "none" ? "" : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Hardware Set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {doorHardwareSets.filter(hs => hs.csiItemId === activeCsiTab).map(hs => (
                        <SelectItem key={hs.id} value={hs.id}>{hs.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="doorScheduleLocation">Location</Label>
                  <Input
                    id="doorScheduleLocation"
                    value={newDoorSchedule.location}
                    onChange={(e) => setNewDoorSchedule(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., Main Entrance"
                  />
                </div>
              </div>
            </div>
            {(editingDoorSchedule || doorScheduleStep === 2) && (
              <div className="py-2" data-testid="door-schedule-details-step">
                <ScheduleDetailsStep
                  value={newDoorSchedule}
                  onChange={(next) => setNewDoorSchedule(next)}
                  glassTypes={doorGlassTypes.filter(gt => gt.csiItemId === activeCsiTab)}
                  metalPanelTypes={doorMetalPanelTypes.filter(mp => mp.csiItemId === activeCsiTab)}
                  touched={doorLiteTouched}
                  setTouched={setDoorLiteTouched}
                  dimensionsTitle="Door Dimensions"
                  dimensionFields={DOOR_DIMENSION_FIELDS}
                  litesHint="Add glass or metal panel lites for this door"
                  type={selectedDoorTypeForSchedule}
                  typeImageUrl={selectedDoorTypeImageUrl}
                  markingTitle="Door Type Image Marking"
                  typeLabel="Door Type"
                  markTestIdPrefix="door-mark"
                  liteTestIdPrefix="door-lite"
                  showUnmarkedWarning
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => {
                setShowDoorScheduleDialog(false);
                setEditingDoorSchedule(null);
              }}>
                Cancel
              </Button>
              {!editingDoorSchedule && doorScheduleStep === 2 && (
                <Button type="button" variant="outline" onClick={() => setDoorScheduleStep(1)} data-testid="door-schedule-back-btn">
                  Back
                </Button>
              )}
              <Button
                type="button"
                className="bg-[#FF6600] hover:bg-[#ff8533]"
                disabled={
                  !newDoorSchedule.doorTypeId ||
                  !newDoorSchedule.label.trim() ||
                  doorOperationLoading ||
                  ((editingDoorSchedule || doorScheduleStep === 2) && hasLiteErrors(newDoorSchedule.lites))
                }
                onClick={submitDoorScheduleStep}
                data-testid="door-schedule-submit-btn"
              >
                {editingDoorSchedule ? 'Update' : doorScheduleStep === 1 ? 'Next' : 'Add Schedule'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Door Schedule Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteDoorScheduleModal}
        onClose={() => {
          setShowDeleteDoorScheduleModal(false);
          setDeleteDoorScheduleId(null);
        }}
        onConfirm={confirmDeleteDoorSchedule}
        type="danger"
        title="Delete Door Schedule"
        message={`Are you sure you want to delete door schedule "${doorSchedules.find(s => s.id === deleteDoorScheduleId)?.label || ''}"? This action cannot be undone and will delete all associated markers.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Clipboard Paste Modal - Reusable Component */}
      <ClipboardPasteModal
        isOpen={!!clipboardPasteTarget}
        onClose={() => setClipboardPasteTarget(null)}
        onUpload={handleClipboardUpload}
        title={
          clipboardPasteTarget?.type === 'doorType' || clipboardPasteTarget?.type === 'newDoorType' 
            ? 'Paste Door Type Drawing' 
            : clipboardPasteTarget?.type === 'windowType' || clipboardPasteTarget?.type === 'newWindowType'
            ? 'Paste Window Type Drawing'
            : clipboardPasteTarget?.type === 'cwType' || clipboardPasteTarget?.type === 'newCWType'
            ? 'Paste Curtain Wall Drawing'
            : clipboardPasteTarget?.type === 'sfType' || clipboardPasteTarget?.type === 'newSFType'
            ? 'Paste Storefront Drawing'
            : clipboardPasteTarget?.type === 'elevation'
            ? 'Paste Elevation Image'
            : 'Paste Image'
        }
        uploading={
          (clipboardPasteTarget?.type === 'doorType' && clipboardPasteTarget?.id && doorTypePictureUploading === clipboardPasteTarget?.id) ||
          (clipboardPasteTarget?.type === 'windowType' && clipboardPasteTarget?.id && windowTypePictureUploading === clipboardPasteTarget?.id) ||
          (clipboardPasteTarget?.type === 'cwType' && clipboardPasteTarget?.id && cwTypePictureUploading === clipboardPasteTarget?.id) ||
          (clipboardPasteTarget?.type === 'sfType' && clipboardPasteTarget?.id && sfTypePictureUploading === clipboardPasteTarget?.id) ||
          (clipboardPasteTarget?.type === 'elevation' && elevationUploading === clipboardPasteTarget?.id)
        }
      />
      
      {/* Tooltip Portal - Renders on top of everything */}
      {tooltipVisible && createPortal(
        <div
          style={{
            position: 'fixed',
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 99999,
            pointerEvents: 'none'
          }}
          className="px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap shadow-lg"
        >
          Note: {tooltipContent}
          <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></span>
        </div>,
        document.body
      )}
      {/* Global Confirm Dialog */}
      <ConfirmDialog {...confirmState} onClose={closeConfirm} />
    </div>
  );
};

export default ProjectDetail;